// src/pages/AdminDashboard.js
import React, { useState, useEffect, useRef, createContext, useContext, useCallback, useMemo, useDeferredValue } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import AppNav from '../components/AppNav';
import { LandingShell } from './LandingPage';
import AboutMe from './AboutMe';
import TermsPage from './TermsPage';
import { fetchSiteContent, updateSiteContent, fetchPrograms, createProgram, updateProgram, deleteProgram, fetchPods, createPod, updatePod, applyPodMembershipDelta, movePodMember, deletePod, fetchAllUserStats, firebaseEnabled, db, getCachedSiteContent } from '../services/firebase';
import { fetchVideosFromCollection, fetchProgramImages } from '../services/bunnyStream';
import { fetchSeries, createSeries, updateSeries, deleteSeries, orderSeriesVideos, slugifySeriesTitle, seriesShareUrl, isSlugTaken, indexSeriesDays, isSeriesExtra,
    buildSeriesReleaseSchedule } from '../services/series';
import { fetchProgramVideos, updateProgramVideo, deleteProgramVideo } from '../services/programVideos';
import ProgramRecorder from '../components/ProgramRecorder';
import { getAllProgramWaitlists, removeFromProgramWaitlist } from '../services/programWaitlist';
import { FormattingHint, RichTextArea } from '../components/FormattedText';
import FormattedText from '../components/FormattedText';
import { getAllNotifications, createNotification, updateNotification, deleteNotification, isExpired, daysRemaining, cleanAudience, isForMembersOnly } from '../services/inAppNotifications';
import { getAllCoachPosts, createCoachPost, updateCoachPost, deleteCoachPost } from '../services/coachPosts';
import CoachPostImageField from '../components/CoachPostImageField';
import { InAppNotificationCard } from '../components/InAppNotification';
import { normalizePostImageSize } from '../components/CoachPostImage';
import { fetchMonthlyCompletions } from '../services/monthlyCompletions';
import { fetchInsightsSummary, fetchInsightsBusiness } from '../services/insights';
import { subscribeToReplyQueue, fetchSkippedDrafts, fetchReplyAssistantConfig, updateReplyAssistantConfig, markDraftSkipped, restoreDraft, sendDraftReply, undoSentReply, requestRedraft, generateVoiceProfile, runReplySweepNow } from '../services/replyAssistant';
import AskInsights from '../components/insights/AskInsights';
import ModerationTab from '../components/ModerationTab';
import PurchasesTab from '../components/PurchasesTab';
import { subscribeAttentionPurchases } from '../services/purchases';
import { subscribeNeedsReview } from '../services/moderation';
import { useMemberPhotos, getCachedMemberTagline, clearMemberProfile } from '../services/memberProfiles';
import theme, { hexToRgba, applyThemeToDOM, DEFAULT_COLORS, HEADING_FONT_OPTIONS, BODY_FONT_OPTIONS, loadGoogleFonts } from '../styles/theme';
import { trainerWallClockToUTC, formatViewerLocalTime } from '../utils/timezone';
import { FaSpinner } from 'react-icons/fa';

// Auth context — provides getToken to all admin sub-components
const AdminAuthContext = createContext(null);
function useAdminAuth() { return useContext(AdminAuthContext); }

// ⌘K deep-link search plumbing. ADMIN_FIELD_INDEX is populated per-tab by each
// SectionList as it renders; the palette reads it to jump to an exact field.
const ADMIN_FIELD_INDEX = {};
// Flatten a tab's sections → searchable field records. Used to EAGERLY pre-seed the
// index at module load (so every field is searchable from a cold start, not just
// after a tab has been opened once).
const indexFields = (sections) => sections.flatMap((s) => s.items.map((it) => ({ section: s.section, label: it.label, key: it.key })));
const AdminDeepLinkContext = createContext(null);
// Reply Assistant queue — ONE Firestore listener at the dashboard root feeds both
// the nav count pill and the Replies tab (docs/decisions/adr-comment-reply-assistant.md).
const ReplyQueueContext = createContext(null);
// Valid `?tab=` deep-link targets (e.g. the Replies nudge links to /admin?tab=replies).
const ADMIN_TAB_KEYS = new Set(['homepage', 'about', 'join', 'dashboard', 'workouts', 'legal', 'programs', 'series', 'pods', 'users', 'waitlists', 'purchases', 'replies', 'moderation', 'notifications', 'emails', 'reports', 'insights', 'theme', 'settings']);
function initialAdminTab() {
    try {
        const t = new URLSearchParams(window.location.search).get('tab');
        return t && ADMIN_TAB_KEYS.has(t) ? t : 'homepage';
    } catch (e) { return 'homepage'; }
}

// Line icons for the left rail — keyed to tab keys. Feather-style, stroke-based
// (inherit color from the parent <svg stroke>). Gives the nav a modern, scannable feel.
const NAV_ICONS = {
    homepage: (<><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></>),
    about: (<><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" /></>),
    join: (<><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" /></>),
    dashboard: (<><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>),
    workouts: (<><path d="M22 12h-4l-3 8L9 4l-3 8H2" /></>),
    legal: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /></>),
    programs: (<><path d="M12 2 2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></>),
    series: (<><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><circle cx="3.5" cy="6" r="1" /><circle cx="3.5" cy="12" r="1" /><circle cx="3.5" cy="18" r="1" /></>),
    pods: (<><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></>),
    users: (<><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></>),
    waitlists: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
    replies: (<><path d="M21 12a8 8 0 0 1-8 8H8l-5 3 1.5-4.5A8 8 0 1 1 21 12z" /><path d="M13 9.5 10.5 12 13 14.5" /><path d="M10.5 12h4" /></>),
    purchases: (<><circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" /><path d="M2.5 3.5h2.6l2.4 11.2a1.6 1.6 0 0 0 1.6 1.3h8.2a1.6 1.6 0 0 0 1.6-1.2l1.6-6.8H6.3" /></>),
    moderation: (<><path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z" /><polyline points="9 12 11 14 15 10" /></>),
    notifications: (<><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>),
    emails: (<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>),
    reports: (<><line x1="6" y1="20" x2="6" y2="12" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="18" y1="20" x2="18" y2="9" /></>),
    theme: (<><path d="M12 2s7 6.5 7 11.5A7 7 0 0 1 5 13.5C5 8.5 12 2 12 2z" /></>),
    settings: (<><line x1="4" y1="21" x2="4" y2="13" /><line x1="4" y1="9" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="11" /><line x1="12" y1="7" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="15" /><line x1="20" y1="11" x2="20" y2="3" /><line x1="1.5" y1="11" x2="6.5" y2="11" /><line x1="9.5" y1="7" x2="14.5" y2="7" /><line x1="17.5" y1="15" x2="22.5" y2="15" /></>),
};
function NavIcon({ k, color }) {
    return (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            {NAV_ICONS[k] || null}
        </svg>
    );
}

// Helper: fetch with admin auth token
async function adminFetch(url, getToken, options) {
    const token = await getToken();
    const headers = { ...(options?.headers || {}), 'Authorization': `Bearer ${token}` };
    return fetch(url, { ...options, headers });
}

// ── Pod membership ⇒ program access (owner rule, 2026-10-06) ─────────────────
// Anyone in a pod must be able to open that pod's program: pod membership
// lives on the pod doc, program access on the member's Clerk publicMetadata
// (purchasedPrograms), and Admin used to update only the first — so a member
// could sit in a pod yet be locked out of the program. Grants go through the
// existing admin-only /api/grant-access, which is idempotent (granting to
// someone who already has access changes nothing). Admin + friend roles
// already see everything, so they never need a grant.
function lacksProgramAccess(u, programId) {
    if (!u || !programId) return false;
    if (u.role === 'admin' || u.role === 'friend') return false;
    return !(Array.isArray(u.purchasedPrograms) && u.purchasedPrograms.includes(programId));
}
async function grantProgramToUsers(userIds, programId, getToken) {
    const granted = [];
    const failed = [];
    for (const userId of userIds) {
        try {
            const r = await adminFetch('/api/grant-access', getToken, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, programId, action: 'grant' }),
            });
            (r.ok ? granted : failed).push(userId);
        } catch (e) { failed.push(userId); }
    }
    return { granted, failed };
}
// Which participants need a grant: anyone we know lacks access, plus anyone we
// can't see (unknown ⇒ grant; it's harmless if they already have it).
function participantsNeedingAccess(participantIds, users, programId) {
    if (!programId) return [];
    return (participantIds || []).filter((id) => {
        const u = users.find((x) => x.id === id);
        return !u || lacksProgramAccess(u, programId);
    });
}
function PodAccessNotice({ notice, onRetry, onDismiss }) {
    if (!notice) return null;
    const bad = notice.failed && notice.failed.length > 0;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, marginBottom: 14, fontFamily: 'var(--mom-font-sans)', fontSize: 13,
            background: bad ? hexToRgba('#dc2626', 0.08) : hexToRgba(theme.colors.terracotta, 0.08),
            color: bad ? '#991b1b' : theme.colors.charcoal }}>
            <span style={{ flex: 1 }}>{notice.text}</span>
            {bad && onRetry && <button className="mom-btn mom-btn-outline" onClick={onRetry} style={{ padding: '6px 14px', fontSize: 12 }}>Retry</button>}
            <button onClick={onDismiss} aria-label="Dismiss" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: theme.colors.warmGray }}>×</button>
        </div>
    );
}


function AdminDashboard({ demoMode }) {
    const { getToken } = useAuth();
    const getAuthToken = useCallback(() => getToken(), [getToken]);
    const [activeTab, setActiveTab] = useState(initialAdminTab);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [deepLinkTarget, setDeepLinkTarget] = useState(null);
    const [openGroup, setOpenGroup] = useState(null); // rail accordion: null = follow the active group
    const [hoveredNav, setHoveredNav] = useState(null);
    const [searchRecords, setSearchRecords] = useState([]);
    const unsavedRef = React.useRef(false);
    // Reply Assistant queue (admin-only surface; single listener, unsubscribed on unmount).
    const [replyQueue, setReplyQueue] = useState(null);
    useEffect(() => {
        if (demoMode) { setReplyQueue([]); return undefined; }
        const unsub = subscribeToReplyQueue(setReplyQueue);
        return () => { try { unsub(); } catch (e) { /* already gone */ } };
    }, [demoMode]);
    const replyQueueCtx = useMemo(() => ({ queue: replyQueue || [], loading: replyQueue === null }), [replyQueue]);
    const replyQueueCount = replyQueue ? replyQueue.length : 0;
    // Member-replies moderation: "Needs you" count for the nav pill (admin-only
    // listener; the queue is tiny). Unsubscribed on unmount.
    const [moderationCount, setModerationCount] = useState(0);
    // Program purchases that didn't reach their member (past the grace window).
    const [purchaseCount, setPurchaseCount] = useState(0);
    useEffect(() => {
        if (demoMode) return undefined;
        const unsub = subscribeAttentionPurchases((rows) => setPurchaseCount(rows.length));
        return () => { try { unsub(); } catch (e) { /* already gone */ } };
    }, [demoMode]);
    useEffect(() => {
        if (demoMode) return undefined;
        const unsub = subscribeNeedsReview((rows) => setModerationCount(rows.length));
        return () => { try { unsub(); } catch (e) { /* already gone */ } };
    }, [demoMode]);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 1023px)');
        const onChange = (e) => setIsMobile(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
    }, []);
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); setQuery(''); setPaletteOpen(true); }
            else if (e.key === 'Escape') setPaletteOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
    // Index your actual content (programs, series) so ⌘K finds them by name, not just settings.
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            fetchPrograms().catch(() => []),
            fetchSeries().catch(() => []),
        ]).then(([progs, ser]) => {
            if (cancelled) return;
            setSearchRecords([
                ...(progs || []).map((p) => ({ kind: 'content', label: p.name || 'Untitled program', ctype: 'Program', tab: 'programs' })),
                ...(ser || []).map((s) => ({ kind: 'content', label: s.title || 'Untitled series', ctype: 'Series', tab: 'series' })),
            ]);
        });
        return () => { cancelled = true; };
    }, []);
    const switchTab = (key) => {
        if (key === activeTab) { setDrawerOpen(false); return; }
        if (unsavedRef.current && !window.confirm('You have unsaved changes. Leave without saving?')) return;
        unsavedRef.current = false;
        setActiveTab(key);
        setOpenGroup(null);
        setDrawerOpen(false);
    };
    // Grouped navigation — left rail (desktop) / slide-over drawer (mobile). Keys are
    // unchanged from the legacy flat tabs so every render-switch branch below still matches.
    const tabGroups = [
        { label: 'Pages', items: [
            { key: 'homepage', label: 'Homepage' },
            { key: 'about', label: 'About Me' },
            { key: 'join', label: 'Join Page' },
            { key: 'dashboard', label: 'Member Dashboard' },
            { key: 'workouts', label: 'Workouts' },
            { key: 'legal', label: 'Legal' },
        ] },
        { label: 'Content', items: [
            { key: 'programs', label: 'Programs' },
            { key: 'series', label: 'Series' },
            { key: 'pods', label: 'Pods' },
        ] },
        { label: 'People', items: [
            { key: 'users', label: 'Members' },
            { key: 'waitlists', label: 'Waitlists' },
            { key: 'purchases', label: 'Purchases' },
        ] },
        { label: 'Messaging', items: [
            { key: 'replies', label: 'Replies' },
            { key: 'moderation', label: 'Moderation' },
            { key: 'notifications', label: 'Notifications' },
            { key: 'emails', label: 'Emails' },
        ] },
        { label: 'Insights', items: [
            { key: 'insights', label: 'Insights' },
            // The pre-2026-11 dashboard, kept for comparison until the new one is trusted.
            { key: 'reports', label: 'Insights (old)' },
        ] },
        { label: 'Branding', items: [
            { key: 'theme', label: 'Theme & Fonts' },
        ] },
        { label: 'Settings', items: [
            { key: 'settings', label: 'Settings' },
        ] },
    ];
    const activeLabel = tabGroups.flatMap((g) => g.items).find((t) => t.key === activeTab)?.label || '';
    const activeGroupLabel = tabGroups.find((g) => g.items.some((it) => it.key === activeTab))?.label;
    const effectiveOpenGroup = openGroup === null ? activeGroupLabel : openGroup;
    // Search index for the ⌘K "find a setting" palette — keyed to the same tab keys.
    const searchKeywords = {
        homepage: 'hero headline tiles featured program testimonials cta landing page',
        about: 'bio story danielle photo about me',
        join: 'subscribe marketing signup membership landing lifestyle faq stats',
        dashboard: 'welcome streak motivation challenge member home quick link',
        workouts: 'subscribe banner price tab labels aired completion streak shared class page locked video schedule on demand shorts yearly annual plan switch',
        legal: 'terms privacy waiver consent participation release',
        programs: 'cohort self-paced bunny videos weeks faq price recorded program',
        series: 'challenge free self-paced collection days',
        pods: 'cohort meetings zoom waitlist start lock date participants',
        users: 'members access role grant subscription friend admin',
        waitlists: 'waitlist sale launch enrollment',
        notifications: 'push announcement coach posts danielle corner alert',
        emails: 'flodesk welcome automation transactional segment',
        reports: 'monthly completions analytics subscribers threshold',
        theme: 'colors fonts brand palette typography appearance',
        settings: 'features flags billing cancellation account contact maintenance sync mux recording',
    };
    const allDestinations = tabGroups.flatMap((g) => g.items.map((it) => ({ ...it, group: g.label })));
    const paletteQuery = query.trim().toLowerCase();
    const paletteResults = paletteQuery
        ? allDestinations.filter((it) => (it.label + ' ' + it.group + ' ' + (searchKeywords[it.key] || '')).toLowerCase().includes(paletteQuery))
        : allDestinations;
    const labelForTab = (tk) => allDestinations.find((d) => d.key === tk)?.label || tk;
    // Field-level results from the live index — let search jump straight to a field.
    const fieldResults = paletteQuery
        ? Object.entries(ADMIN_FIELD_INDEX).flatMap(([tk, arr]) =>
            arr.filter((fld) => (fld.label + ' ' + fld.section).toLowerCase().includes(paletteQuery))
                .map((fld) => ({ ...fld, tabKey: tk }))).slice(0, 8)
        : [];
    const gotoField = (tk, key) => { switchTab(tk); setDeepLinkTarget(key); setPaletteOpen(false); setQuery(''); };
    const deepLinkValue = useMemo(() => ({ target: deepLinkTarget, clear: () => setDeepLinkTarget(null) }), [deepLinkTarget]);
    // ⌘K palette: one flat ordered result list (fields first, then destinations) for keyboard nav.
    const contentResults = paletteQuery
        ? searchRecords.filter((r) => r.label.toLowerCase().includes(paletteQuery)).slice(0, 8)
        : [];
    const combinedResults = [
        ...fieldResults.map((f) => ({ kind: 'field', ...f })),
        ...contentResults,
        ...paletteResults.map((r) => ({ kind: 'dest', ...r })),
    ];
    const runResult = (r) => {
        if (!r) return;
        if (r.kind === 'field') gotoField(r.tabKey, r.key);
        else if (r.kind === 'content') { switchTab(r.tab); setPaletteOpen(false); setQuery(''); }
        else { switchTab(r.key); setPaletteOpen(false); setQuery(''); }
    };
    // Bold the matched substring in a result label.
    const highlightMatch = (text, q) => {
        if (!q || !text) return text;
        const i = text.toLowerCase().indexOf(q);
        if (i === -1) return text;
        return (<>{text.slice(0, i)}<span style={{ fontWeight: 800 }}>{text.slice(i, i + q.length)}</span>{text.slice(i + q.length)}</>);
    };
    useEffect(() => { setSelectedIndex(0); }, [query]);

    const [previewMode, setPreviewMode] = useState(() => typeof window !== 'undefined' && localStorage.getItem('mom-preview-as-user') === 'true');
    const togglePreview = () => {
        const next = !previewMode;
        setPreviewMode(next);
        if (next) { localStorage.setItem('mom-preview-as-user', 'true'); }
        else { localStorage.removeItem('mom-preview-as-user'); }
    };

    // Rail is a plain JSX element (NOT an inner component) reused by the desktop aside
    // and the mobile drawer — only one of the two mounts at a time.
    const rail = (
        <nav>
            <div style={{ padding: '4px 14px 12px' }}>
                <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 20, color: theme.colors.charcoal }}>Moments admin</span>
            </div>
            <button onClick={() => { setQuery(''); setPaletteOpen(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: 'calc(100% - 8px)', margin: '0 4px 16px', padding: '9px 12px', borderRadius: 10, border: `1px solid ${theme.colors.creamDark}`, background: theme.colors.white, cursor: 'pointer', color: theme.colors.warmGray, fontFamily: 'var(--mom-font-sans)', fontSize: 13 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <span style={{ flex: 1, textAlign: 'left' }}>Find a setting</span>
                <span style={{ fontSize: 11, color: theme.colors.warmGray, border: `1px solid ${theme.colors.creamDark}`, borderRadius: 6, padding: '1px 5px' }}>⌘K</span>
            </button>
            {tabGroups.map((g, gi) => {
                const groupOpen = effectiveOpenGroup === g.label;
                // Action counts roll up to the group header while it's collapsed, so a
                // to-do inside a closed group is never invisible. Only Replies has a
                // count today; add to this map for any future item.
                const itemCounts = { replies: replyQueueCount, moderation: moderationCount, purchases: purchaseCount };
                const groupCount = g.items.reduce((n, t) => n + (itemCounts[t.key] || 0), 0);
                return (
                    <div key={g.label} style={{ marginTop: gi === 0 ? 0 : 10 }}>
                        {/* A one-page group is a link, not an accordion: the header opens
                            that page directly (switchTab also expands the group via
                            openGroup=null), and the chevron is dropped so it reads as such. */}
                        <button onClick={() => (g.items.length === 1 ? switchTab(g.items[0].key) : setOpenGroup(groupOpen ? '__none__' : g.label))}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '6px 12px', background: 'none', border: 'none', cursor: 'pointer' }}>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: groupOpen ? theme.colors.charcoal : theme.colors.warmGray }}>{g.label}</span>
                            {!groupOpen && groupCount > 0 && (
                                <span style={{ marginLeft: 'auto', marginRight: 8, fontFamily: 'var(--mom-font-sans)', fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 10, background: hexToRgba(theme.colors.terracotta, 0.12), color: theme.colors.terracotta, letterSpacing: 0 }}>{groupCount}</span>
                            )}
                            {g.items.length > 1 && (
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={theme.colors.warmGray} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: groupOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}><polyline points="6 9 12 15 18 9" /></svg>
                            )}
                        </button>
                        {groupOpen && g.items.map((t) => {
                            const active = activeTab === t.key;
                            const hov = hoveredNav === t.key && !active;
                            return (
                                <button key={t.key} onClick={() => switchTab(t.key)}
                                    onMouseEnter={() => setHoveredNav(t.key)} onMouseLeave={() => setHoveredNav(null)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', marginBottom: 1, fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, fontWeight: active ? 600 : 500, color: theme.colors.charcoal, background: active ? hexToRgba(theme.colors.terracotta, 0.12) : (hov ? hexToRgba(theme.colors.charcoal, 0.045) : 'transparent'), transition: 'background 0.13s' }}>
                                    <NavIcon k={t.key} color={active ? theme.colors.terracotta : theme.colors.warmGray} />
                                    {t.label}
                                    {t.key === 'replies' && replyQueueCount > 0 && (
                                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: hexToRgba(theme.colors.terracotta, active ? 0.18 : 0.12), color: theme.colors.terracotta }}>{replyQueueCount}</span>
                                    )}
                                    {t.key === 'purchases' && purchaseCount > 0 && (
                                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: hexToRgba(theme.colors.terracotta, active ? 0.18 : 0.12), color: theme.colors.terracotta }}>{purchaseCount}</span>
                                    )}
                                    {t.key === 'moderation' && moderationCount > 0 && (
                                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: hexToRgba(theme.colors.terracotta, active ? 0.18 : 0.12), color: theme.colors.terracotta }}>{moderationCount}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                );
            })}
            <div style={{ borderTop: `1px solid ${theme.colors.creamDark}`, marginTop: 14, padding: '14px 14px 0' }}>
                <button onClick={togglePreview}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '9px 12px', borderRadius: 10, border: `1.5px solid ${previewMode ? theme.colors.charcoal : theme.colors.creamDark}`, background: previewMode ? theme.colors.charcoal : theme.colors.white, color: previewMode ? theme.colors.white : theme.colors.charcoal, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600 }}>
                    {previewMode ? 'Previewing as user' : 'Preview as user'}
                </button>
            </div>
        </nav>
    );

    return (
        <AdminAuthContext.Provider value={getAuthToken}>
        <ReplyQueueContext.Provider value={replyQueueCtx}>
          <AdminDeepLinkContext.Provider value={deepLinkValue}>
            <div className="mom-page-shell" style={{ background: theme.colors.cream, minHeight: '100vh', overflowX: 'hidden' }}>
                <AppNav demoMode={demoMode} />

                {isMobile && (
                    <div style={{ position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: hexToRgba(theme.colors.white, 0.85), backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderBottom: `1px solid ${theme.colors.creamDark}` }}>
                        <button onClick={() => setDrawerOpen(true)} aria-label="Open admin menu"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10, border: `1px solid ${theme.colors.creamDark}`, background: theme.colors.white, cursor: 'pointer', flexShrink: 0 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.colors.charcoal} strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
                        </button>
                        <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 18, color: theme.colors.charcoal, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeLabel}</span>
                    </div>
                )}

                <div style={{ maxWidth: 1160, margin: '0 auto', display: 'flex', gap: isMobile ? 0 : 32, padding: isMobile ? '20px 16px 96px' : '32px 24px 96px', boxSizing: 'border-box', width: '100%' }}>
                    {!isMobile && (
                        <aside style={{ width: 236, flexShrink: 0, position: 'sticky', top: 24, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 48px)', overflowY: 'auto' }}>
                            {rail}
                        </aside>
                    )}

                    <main style={{ flex: 1, minWidth: 0 }}>
                        {!firebaseEnabled && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.terracotta, marginBottom: 12 }}>Firebase not connected — changes won't persist</p>}
                        {previewMode && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.terracotta, marginBottom: 12 }}>Preview mode is on — other pages now show what a regular user would see. Toggle off when done.</p>}

                        {activeTab === 'homepage' && <HomepageTab unsavedRef={unsavedRef} />}
                        {activeTab === 'about' && <AboutMeTab unsavedRef={unsavedRef} />}
                        {activeTab === 'join' && <JoinPageTab unsavedRef={unsavedRef} />}
                        {activeTab === 'dashboard' && <DashboardTab unsavedRef={unsavedRef} />}
                        {activeTab === 'programs' && <ProgramsTab unsavedRef={unsavedRef} />}
                        {activeTab === 'series' && <SeriesTab unsavedRef={unsavedRef} />}
                        {activeTab === 'pods' && <PodsTab unsavedRef={unsavedRef} />}
                        {activeTab === 'workouts' && <WorkoutsTab unsavedRef={unsavedRef} />}
                        {activeTab === 'users' && <UsersTab />}
                        {activeTab === 'reports' && <InsightsTab />}
                        {activeTab === 'insights' && <AskInsights getToken={getAuthToken} onOpenOld={() => switchTab('reports')} />}
                        {activeTab === 'waitlists' && <WaitlistsTab />}
                        {activeTab === 'theme' && <ThemeTab unsavedRef={unsavedRef} />}
                        {activeTab === 'replies' && <RepliesTab />}
                        {activeTab === 'moderation' && <ModerationTab getToken={getAuthToken} />}
                        {activeTab === 'purchases' && <PurchasesTab getToken={getAuthToken} />}
                        {activeTab === 'notifications' && <NotificationsTab />}
                        {activeTab === 'emails' && <EmailsTab unsavedRef={unsavedRef} />}
                        {activeTab === 'legal' && <LegalTab unsavedRef={unsavedRef} />}
                        {activeTab === 'settings' && <SettingsTab unsavedRef={unsavedRef} />}
                    </main>
                </div>

                {isMobile && drawerOpen && (
                    <div onClick={() => setDrawerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.4)' }}>
                        <div onClick={(e) => e.stopPropagation()}
                            style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '82%', maxWidth: 320, background: theme.colors.cream, boxShadow: '0 20px 60px rgba(0,0,0,0.28)', padding: '20px 14px', overflowY: 'auto' }}>
                            {rail}
                        </div>
                    </div>
                )}

                {paletteOpen && (
                    <div onClick={() => setPaletteOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '10vh 16px 16px' }}>
                        <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, background: theme.colors.white, borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.32)', overflow: 'hidden' }}>
                            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${theme.colors.creamDark}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={theme.colors.warmGray} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                                <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex((i) => Math.min(i + 1, combinedResults.length - 1)); }
                                        else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex((i) => Math.max(i - 1, 0)); }
                                        else if (e.key === 'Enter') { e.preventDefault(); runResult(combinedResults[selectedIndex]); }
                                    }}
                                    placeholder="Find a setting…" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: theme.colors.charcoal }} />
                            </div>
                            <div style={{ maxHeight: '52vh', overflowY: 'auto', padding: 8 }}>
                                {paletteResults.length === 0 && fieldResults.length === 0 && contentResults.length === 0 && (
                                    <p style={{ padding: 16, fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>No matches.</p>
                                )}
                                {fieldResults.length > 0 && (
                                    <>
                                        <p style={{ padding: '6px 12px 4px', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.colors.warmGray }}>Settings</p>
                                        {fieldResults.map((fld, i) => (
                                            <button key={fld.tabKey + ':' + fld.key} onClick={() => gotoField(fld.tabKey, fld.key)} onMouseEnter={() => setSelectedIndex(i)}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: selectedIndex === i ? hexToRgba(theme.colors.terracotta, 0.1) : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{highlightMatch(fld.label, paletteQuery)}</span>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, flexShrink: 0 }}>{labelForTab(fld.tabKey)} › {fld.section}</span>
                                            </button>
                                        ))}
                                    </>
                                )}
                                {contentResults.length > 0 && (
                                    <>
                                        <p style={{ padding: '10px 12px 4px', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.colors.warmGray }}>Content</p>
                                        {contentResults.map((r, i) => {
                                            const gi = fieldResults.length + i;
                                            return (
                                            <button key={'c:' + r.tab + ':' + r.label + ':' + i} onClick={() => { switchTab(r.tab); setPaletteOpen(false); setQuery(''); }} onMouseEnter={() => setSelectedIndex(gi)}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: selectedIndex === gi ? hexToRgba(theme.colors.terracotta, 0.1) : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{highlightMatch(r.label, paletteQuery)}</span>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>{r.ctype}</span>
                                            </button>
                                            );
                                        })}
                                    </>
                                )}
                                {paletteResults.length > 0 && (
                                    <>
                                        {(fieldResults.length > 0 || contentResults.length > 0) && <p style={{ padding: '10px 12px 4px', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.colors.warmGray }}>Pages</p>}
                                        {paletteResults.map((r, i) => {
                                            const gi = fieldResults.length + contentResults.length + i;
                                            return (
                                            <button key={r.key} onClick={() => { switchTab(r.key); setPaletteOpen(false); setQuery(''); }} onMouseEnter={() => setSelectedIndex(gi)}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', background: selectedIndex === gi ? hexToRgba(theme.colors.terracotta, 0.1) : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal }}>{highlightMatch(r.label, paletteQuery)}</span>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>{r.group}</span>
                                            </button>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                            {combinedResults.length > 0 && (
                                <div style={{ padding: '9px 16px', borderTop: `1px solid ${theme.colors.creamDark}`, display: 'flex', gap: 16, fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>
                                    <span><strong style={{ fontWeight: 700 }}>↑↓</strong> navigate</span>
                                    <span><strong style={{ fontWeight: 700 }}>↵</strong> open</span>
                                    <span><strong style={{ fontWeight: 700 }}>esc</strong> close</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
          </AdminDeepLinkContext.Provider>
        </ReplyQueueContext.Provider>
        </AdminAuthContext.Provider>
    );
}

function Spinner() {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><FaSpinner style={{ fontSize: 28, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} /></div>;
}

function InfoCard({ title, desc }) {
    // Premium, emoji-free: a quiet brand accent bar replaces the decorative emoji.
    return (
        <div style={{ background: theme.colors.cream, borderRadius: 12, padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'stretch' }}>
            <div style={{ width: 3, borderRadius: 2, background: theme.colors.terracotta, flexShrink: 0 }} />
            <div>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 4 }}>{title}</p>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, lineHeight: 1.6 }}>{desc}</p>
            </div>
        </div>
    );
}

// ── Program Editor Section (collapsible card within program editor) ──
function ProgramEditorSection({ title, subtitle, defaultOpen = true, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={{ borderTop: `1px solid ${theme.colors.creamDark}`, marginTop: 8 }}>
            <div onClick={() => setOpen(!open)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setOpen(!open); }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '16px 0 8px' }}>
                <div>
                    <h4 style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700, color: theme.colors.charcoal }}>{title}</h4>
                    {subtitle && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 2 }}>{subtitle}</p>}
                </div>
                <span style={{ fontSize: 16, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0, marginLeft: 12 }}>▾</span>
            </div>
            {open && <div style={{ paddingBottom: 8 }}>{children}</div>}
        </div>
    );
}

// ── Collapsible Section ──────────────────────────────────────
// Single field renderer — THE one premium field card used across the whole panel.
// Module-scope (not a nested component) per the no-inner-components rule. The
// `data-field` anchor lets the ⌘K palette deep-link/scroll straight to a field.
// The formatting toolbar (via RichTextArea) is intentionally scoped to `markdown`
// fields only — short/plain fields stay clean inputs.
// Shared image-picker field — self-contained (fetches + manages its own picker),
// so any tab can use a `type: 'photo'` field and they all look + behave identically.
function AdminPhotoField({ value, onChange, shape = 'square' }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const browse = async () => {
        if (images.length > 0) { setPickerOpen((o) => !o); return; }
        setLoading(true);
        try { const imgs = await fetchProgramImages(); setImages(imgs || []); } catch (e) { /* ignore */ }
        setLoading(false);
        setPickerOpen(true);
    };
    const radius = shape === 'circle' ? '50%' : 12;
    return (
        <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                {value && (
                    <div style={{ width: 64, height: 64, borderRadius: radius, overflow: 'hidden', flexShrink: 0, border: `2px solid ${theme.colors.creamDark}` }}>
                        <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                    </div>
                )}
                <input type="text" className="mom-input" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL…" style={{ fontSize: 13, flex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="mom-btn mom-btn-ghost" onClick={browse} style={{ fontSize: 12, color: theme.colors.terracotta }}>{loading ? 'Loading…' : 'Browse uploaded images'}</button>
                {value && <button className="mom-btn mom-btn-ghost" onClick={() => onChange('')} style={{ fontSize: 12, color: theme.colors.danger }}>Remove</button>}
            </div>
            {pickerOpen && images.length > 0 && (
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8 }}>
                    {images.map((img, i) => {
                        const url = img.url || img;
                        return (
                            <div key={i} onClick={() => { onChange(url); setPickerOpen(false); }}
                                style={{ cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: value === url ? `2px solid ${theme.colors.terracotta}` : `1px solid ${theme.colors.creamDark}`, aspectRatio: '1' }}>
                                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function AdminField({ f, content, handleChange, highlight }) {
    // Long-form controls span the full row; short inputs pack two-up in the grid.
    const fullSpan = f.markdown || f.type === 'textarea' || f.type === 'row' || f.type === 'photo';
    return (
        <>
            {f.groupLabel && (
                <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${theme.colors.creamDark}`, margin: '4px 0 18px', paddingTop: 18 }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, color: theme.colors.charcoal, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{f.groupLabel}</p>
                </div>
            )}
            <div data-field={f.key} style={{ gridColumn: fullSpan ? '1 / -1' : 'auto', marginBottom: 22, scrollMarginTop: 90, borderRadius: 10, transition: 'box-shadow 0.3s', boxShadow: highlight ? `0 0 0 3px ${hexToRgba(theme.colors.terracotta, 0.55)}` : 'none' }}>
                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, marginBottom: f.hint ? 3 : 7, display: 'block' }}>{f.label}</label>
                {f.hint && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11.5, color: theme.colors.warmGray, marginBottom: 8, lineHeight: 1.45 }}>{f.hint}</p>}
                {f.type === 'toggle' ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!content[f.key]} onChange={(e) => handleChange(f.key, e.target.checked)} />
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal }}>{f.toggleLabel || 'Enabled'}</span>
                    </label>
                ) : f.type === 'photo' ? (
                    <AdminPhotoField value={content[f.key]} onChange={(v) => handleChange(f.key, v)} shape={f.shape} />
                ) : f.markdown ? (
                    <RichTextArea value={content[f.key] || ''} onChange={(val) => handleChange(f.key, val)} placeholder={f.placeholder || ''} rows={f.rows || 4} />
                ) : f.type === 'textarea' ? (
                    <textarea className="mom-input" value={content[f.key] || ''} onChange={(e) => handleChange(f.key, e.target.value)} placeholder={f.placeholder || ''} rows={f.rows || 3} style={{ resize: 'vertical', lineHeight: 1.6 }} />
                ) : f.type === 'row' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <input type="text" className="mom-input" value={content[f.key] || ''} onChange={(e) => handleChange(f.key, e.target.value)} placeholder={f.placeholder || ''} />
                        {f.pairKey && <input type="text" className="mom-input" value={content[f.pairKey] || ''} onChange={(e) => handleChange(f.pairKey, e.target.value)} placeholder={f.pairPlaceholder || ''} />}
                    </div>
                ) : (
                    <input type="text" className="mom-input" value={content[f.key] || ''} onChange={(e) => handleChange(f.key, e.target.value)} placeholder={f.placeholder || ''} />
                )}
            </div>
        </>
    );
}

function CollapsibleSection({ section, desc, items, content, handleChange, defaultOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);
    const [flash, setFlash] = useState(null);
    const deepLink = useContext(AdminDeepLinkContext);
    const target = deepLink && deepLink.target;
    // ⌘K deep-link: if this section owns the target field, open + scroll + flash it.
    useEffect(() => {
        if (!target || !items.some((it) => it.key === target || it.pairKey === target)) return;
        setOpen(true);
        const t = setTimeout(() => {
            const el = typeof document !== 'undefined' ? document.querySelector('[data-field="' + target + '"]') : null;
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setFlash(target);
            if (deepLink && deepLink.clear) deepLink.clear();
            setTimeout(() => setFlash(null), 1800);
        }, 80);
        return () => clearTimeout(t);
    }, [target]);
    const filledCount = items.filter((f) => content[f.key] && String(content[f.key]).trim()).length + items.filter((f) => f.pairKey && content[f.pairKey] && String(content[f.pairKey]).trim()).length;

    return (
        <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, marginBottom: 12, boxShadow: theme.shadows.sm, overflow: 'hidden' }}>
            <button onClick={() => setOpen(!open)} style={{ width: '100%', padding: '20px 28px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, textAlign: 'left' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: theme.colors.charcoal }}>{section}</h3>
                        {!children && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, background: theme.colors.cream, padding: '2px 10px', borderRadius: 20 }}>{filledCount}/{items.length + items.filter((f) => f.pairKey).length}</span>}
                    </div>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginTop: 4 }}>{desc}</p>
                </div>
                <span style={{ fontSize: 18, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>▾</span>
            </button>
            {open && (
                <div style={{ padding: '0 28px 24px', borderTop: `1px solid ${theme.colors.creamDark}` }}>
                    <div style={{ paddingTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', columnGap: 24, alignItems: 'start' }}>
                        {items.map((f) => (
                            <AdminField key={f.key} f={f} content={content} handleChange={handleChange} highlight={flash === f.key || (!!f.pairKey && flash === f.pairKey)} />
                        ))}
                        {children && <div style={{ gridColumn: '1 / -1' }}>{children}</div>}
                    </div>
                </div>
            )}
        </div>
    );
}

function TestimonialsManager({ testimonials, onChange, programs = [] }) {
    const [editIdx, setEditIdx] = useState(null);
    const [editForm, setEditForm] = useState({ text: '', name: '', detail: '', programIds: [], membership: false });

    const openNew = () => { setEditForm({ text: '', name: '', detail: '', programIds: [], membership: false }); setEditIdx(-1); };
    const openEdit = (i) => { setEditForm({ ...testimonials[i], programIds: testimonials[i].programIds || [], membership: testimonials[i].membership === true }); setEditIdx(i); };
    const close = () => { setEditIdx(null); };

    const save = () => {
        if (!editForm.text.trim()) return;
        const updated = [...testimonials];
        if (editIdx === -1) { updated.push({ ...editForm }); }
        else { updated[editIdx] = { ...editForm }; }
        onChange(updated);
        close();
    };

    const remove = (i) => { onChange(testimonials.filter((_, idx) => idx !== i)); };

    const move = (from, to) => {
        const updated = [...testimonials];
        const [item] = updated.splice(from, 1);
        updated.splice(to, 0, item);
        onChange(updated);
    };

    const toggleProgram = (progId) => {
        setEditForm((prev) => {
            const ids = prev.programIds || [];
            return { ...prev, programIds: ids.includes(progId) ? ids.filter((id) => id !== progId) : [...ids, progId] };
        });
    };

    const toggleMembership = () => { setEditForm((prev) => ({ ...prev, membership: !prev.membership })); };

    const progName = (id) => { const p = programs.find((pr) => pr.id === id); return p ? p.name : id; };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>{testimonials.length} testimonial{testimonials.length !== 1 ? 's' : ''} — first 5 shown on homepage</p>
                <button className="mom-btn" onClick={openNew} style={{ padding: '8px 20px', borderRadius: 10, fontSize: 13, background: theme.colors.charcoal, color: theme.colors.white, border: 'none', fontWeight: 600 }}>+ Add Testimonial</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {testimonials.map((t, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: i < 5 ? hexToRgba(theme.colors.terracotta, 0.04) : theme.colors.cream, borderRadius: 12, border: i < 5 ? `1px solid ${hexToRgba(theme.colors.terracotta, 0.15)}` : `1px solid ${theme.colors.creamDark}` }}>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, color: i < 5 ? theme.colors.terracotta : theme.colors.warmGray, width: 24, textAlign: 'center', flexShrink: 0 }}>{i + 1}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name || 'Anonymous'}{t.detail ? ` · ${t.detail}` : ''}</p>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.text}</p>
                            {(t.membership === true || (t.programIds && t.programIds.length > 0)) && (
                                <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                                    {t.membership === true && (
                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 8, background: hexToRgba(theme.colors.charcoal, 0.07), color: theme.colors.charcoal }}>Membership</span>
                                    )}
                                    {(t.programIds || []).map((pid) => (
                                        <span key={pid} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 8, background: hexToRgba(theme.colors.terracotta, 0.1), color: theme.colors.terracotta }}>{progName(pid)}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button className="mom-btn" disabled={i === 0} onClick={() => move(i, i - 1)} style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11, border: `1px solid ${theme.colors.creamDark}`, background: theme.colors.white, color: theme.colors.charcoal, opacity: i === 0 ? 0.3 : 1, cursor: i === 0 ? 'default' : 'pointer' }}>↑</button>
                            <button className="mom-btn" disabled={i === testimonials.length - 1} onClick={() => move(i, i + 1)} style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11, border: `1px solid ${theme.colors.creamDark}`, background: theme.colors.white, color: theme.colors.charcoal, opacity: i === testimonials.length - 1 ? 0.3 : 1, cursor: i === testimonials.length - 1 ? 'default' : 'pointer' }}>↓</button>
                            <button className="mom-btn" onClick={() => openEdit(i)} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, border: `1px solid ${theme.colors.creamDark}`, background: theme.colors.white, color: theme.colors.charcoal }}>Edit</button>
                            <button className="mom-btn" onClick={() => remove(i)} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, border: `1px solid ${theme.colors.creamDark}`, background: theme.colors.white, color: theme.colors.danger }}>Remove</button>
                        </div>
                    </div>
                ))}
            </div>

            {testimonials.length === 0 && (
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, textAlign: 'center', padding: '24px 0' }}>No testimonials yet. Click "+ Add Testimonial" to get started.</p>
            )}

            {/* Edit/Add Modal */}
            {editIdx !== null && (
                <div style={{ position: 'fixed', inset: 0, background: theme.colors.overlay, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={close}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: theme.colors.white, borderRadius: theme.radius.xl, padding: '32px 28px', maxWidth: 520, width: '100%', boxShadow: theme.shadows.xl, maxHeight: '85vh', overflowY: 'auto' }}>
                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 20 }}>{editIdx === -1 ? 'Add Testimonial' : 'Edit Testimonial'}</h3>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Quote</label>
                            <textarea className="mom-input" value={editForm.text} onChange={(e) => setEditForm({ ...editForm, text: e.target.value })} rows={4} style={{ resize: 'vertical', lineHeight: 1.6 }} placeholder="What they said about the program..." />
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Name</label>
                                <input type="text" className="mom-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Sarah M." />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Detail</label>
                                <input type="text" className="mom-input" value={editForm.detail} onChange={(e) => setEditForm({ ...editForm, detail: e.target.value })} placeholder="Mom of 3, 5 to 20 member" />
                            </div>
                        </div>

                        {/* Membership tag — drives which testimonials appear on the /join page */}
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 8, display: 'block' }}>Show on the membership page</label>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 10, opacity: 0.7 }}>Tag this if it's about the monthly membership. Once at least one testimonial is tagged, the /join page shows only tagged ones. (The homepage always shows all.)</p>
                            <button className="mom-btn" onClick={toggleMembership}
                                style={{
                                    padding: '7px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                                    border: editForm.membership ? `2px solid ${theme.colors.terracotta}` : `1.5px solid ${theme.colors.creamDark}`,
                                    background: editForm.membership ? hexToRgba(theme.colors.terracotta, 0.08) : theme.colors.white,
                                    color: editForm.membership ? theme.colors.terracotta : theme.colors.charcoal,
                                    cursor: 'pointer', transition: 'all 0.15s ease',
                                }}>
                                {editForm.membership ? '✓ ' : ''}Membership
                            </button>
                        </div>

                        {/* Program tags */}
                        {programs.length > 0 && (
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 8, display: 'block' }}>Show on program page(s)</label>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 10, opacity: 0.7 }}>Select which programs this testimonial relates to. It will appear on those program pages.</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {programs.map((prog) => {
                                        const selected = (editForm.programIds || []).includes(prog.id);
                                        return (
                                            <button key={prog.id} className="mom-btn" onClick={() => toggleProgram(prog.id)}
                                                style={{
                                                    padding: '7px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                                                    border: selected ? `2px solid ${theme.colors.terracotta}` : `1.5px solid ${theme.colors.creamDark}`,
                                                    background: selected ? hexToRgba(theme.colors.terracotta, 0.08) : theme.colors.white,
                                                    color: selected ? theme.colors.terracotta : theme.colors.charcoal,
                                                    cursor: 'pointer', transition: 'all 0.15s ease',
                                                }}>
                                                {selected ? '✓ ' : ''}{prog.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                            <button className="mom-btn mom-btn-ghost" onClick={close} style={{ padding: '10px 24px' }}>Cancel</button>
                            <button className="mom-btn mom-btn-primary" onClick={save} disabled={!editForm.text.trim()} style={{ padding: '10px 24px' }}>{editIdx === -1 ? 'Add' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StickyBar({ saving, saved, onSave, label, hasChanges }) {
    return (
        <div style={{ position: 'sticky', bottom: 0, zIndex: 50, padding: '16px 0 0' }}>
            <div style={{ background: theme.colors.white, borderRadius: 14, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, boxShadow: '0 -2px 20px rgba(0,0,0,0.08)', border: `1px solid ${hasChanges && !saved ? theme.colors.terracotta : theme.colors.creamDark}`, transition: 'border-color 0.2s' }}>
                {hasChanges && !saved && !saving && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.terracotta, fontWeight: 500 }}>Unsaved changes</span>}
                {saved && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.success, fontWeight: 600 }}>Saved</span>}
                <button className="mom-btn mom-btn-primary" onClick={onSave} disabled={saving} style={{ padding: '10px 28px', fontSize: 14 }}>{saving ? 'Saving...' : (label || 'Save Changes')}</button>
            </div>
        </div>
    );
}

// ── Drag-and-drop reorder list ──────────────────────────────
function DragReorderList({ items, idKey, onReorder, renderItem, compact }) {
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);

    const handleDragStart = (e, idx) => {
        setDragIdx(idx);
        e.dataTransfer.effectAllowed = 'move';
        // Firefox requires data to be set
        e.dataTransfer.setData('text/plain', idx.toString());
        // Make drag image slightly transparent
        if (e.target) e.target.style.opacity = '0.6';
    };

    const handleDragEnd = (e) => {
        if (e.target) e.target.style.opacity = '1';
        if (dragIdx !== null && overIdx !== null && dragIdx !== overIdx) {
            onReorder(dragIdx, overIdx);
        }
        setDragIdx(null);
        setOverIdx(null);
    };

    const handleDragOver = (e, idx) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (idx !== overIdx) setOverIdx(idx);
    };

    const handleDragLeave = () => { };

    const handleDrop = (e, idx) => {
        e.preventDefault();
        // Drop is handled in dragEnd for cleaner state
    };

    const pad = compact ? '8px 12px' : '10px 14px';
    const gap = compact ? 4 : 6;
    const radius = compact ? 8 : 10;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
            {items.map((item, i) => (
                <div
                    key={item[idKey]}
                    draggable
                    onDragStart={(e) => handleDragStart(e, i)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, i)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: pad,
                        background: dragIdx === i ? theme.colors.cream : theme.colors.white,
                        borderRadius: radius,
                        border: overIdx === i && dragIdx !== i ? `2px solid ${theme.colors.terracotta}` : `1px solid ${theme.colors.creamDark}`,
                        cursor: 'grab',
                        transition: 'border-color 0.15s, background 0.15s',
                        userSelect: 'none',
                    }}
                >
                    {/* Drag handle */}
                    <span style={{ color: theme.colors.warmGray, fontSize: 16, cursor: 'grab', flexShrink: 0, lineHeight: 1 }}>⠿</span>
                    {/* Up/down controls — a deterministic reorder path that works on
                        every input (touch, trackpad, mouse), since native HTML5 drag
                        is unreliable on touch + some trackpads. Additive: native drag
                        above is untouched. onMouseDown stopPropagation so a click on a
                        button never initiates a row drag. */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                        {[
                            { dir: 'up', disabled: i === 0, to: i - 1, points: '18 15 12 9 6 15', label: 'Move up' },
                            { dir: 'down', disabled: i === items.length - 1, to: i + 1, points: '6 9 12 15 18 9', label: 'Move down' },
                        ].map(({ dir, disabled, to, points, label }) => (
                            <button
                                key={dir}
                                type="button"
                                aria-label={label}
                                disabled={disabled}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); if (!disabled) onReorder(i, to); }}
                                style={{
                                    width: 24, height: compact ? 15 : 17, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    padding: 0, lineHeight: 1, borderRadius: 5,
                                    border: `1px solid ${theme.colors.creamDark}`,
                                    background: disabled ? theme.colors.cream : theme.colors.white,
                                    color: disabled ? theme.colors.creamDark : theme.colors.charcoal,
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                }}
                            >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points={points} /></svg>
                            </button>
                        ))}
                    </div>
                    {renderItem(item, i)}
                </div>
            ))}
        </div>
    );
}

// ── Shared Pod Editor ────────────────────────────────────────
// Used by both ProgramsTab (inline) and PodsTab (full page)
function PodEditor({ podForm, setPodForm, editingPod, siblingPods, podUsers, podUsersLoading, addUserSearch, setAddUserSearch, allUsers, onSearchUsers, searchLoading, addUserToPod, removeUserFromPod, moveUserToPod, addMeeting, updateMeeting, removeMeeting, meetingIdPrefix, programWeeks, onAutoGenMeetings, programId = null, onGrantAccess = null, grantingIds = [] }) {
    const [expandedMeeting, setExpandedMeeting] = useState(null);
    // Debounced server-side user search
    const searchTimerRef = useRef(null);
    const handleSearchChange = (e) => {
        const val = e.target.value;
        setAddUserSearch(val);
        clearTimeout(searchTimerRef.current);
        if (val.length >= 2 && onSearchUsers) {
            searchTimerRef.current = setTimeout(() => onSearchUsers(val), 300);
        }
    };
    useEffect(() => () => clearTimeout(searchTimerRef.current), []);
    return (
        <>
            {/* Basic fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Pod Name</label>
                    <input type="text" className="mom-input" value={podForm.podName || ''} onChange={(e) => setPodForm({ ...podForm, podName: e.target.value })} placeholder="e.g. March 2026 Cohort" />
                </div>
                <div>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Max Participants</label>
                    <input type="number" className="mom-input" value={podForm.maxParticipants || ''} onChange={(e) => setPodForm({ ...podForm, maxParticipants: e.target.value })} />
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Start Date</label>
                    <input type="date" className="mom-input" value={podForm.startDate ? podForm.startDate.slice(0, 10) : ''} onChange={(e) => {
                        const newStart = e.target.value ? new Date(e.target.value + 'T06:00:00').toISOString() : '';
                        const updates = { ...podForm, startDate: newStart };
                        // Auto-set lock date to day before start
                        if (newStart && !podForm.lockDate) {
                            const lockD = new Date(e.target.value + 'T00:00:00');
                            lockD.setDate(lockD.getDate() - 1);
                            updates.lockDate = new Date(lockD.getFullYear(), lockD.getMonth(), lockD.getDate(), 23, 59, 59).toISOString();
                        }
                        // Auto-name based on month
                        if (newStart && !podForm.podName) {
                            const d = new Date(e.target.value);
                            updates.podName = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) + ' Cohort';
                        }
                        // Auto-fill meeting dates for duplicated pods (meetings exist but dates are blank)
                        if (newStart && (podForm.meetings || []).length > 0 && (podForm.meetings || []).every((m) => !m.date)) {
                            const startD = new Date(e.target.value + 'T00:00:00');
                            // Use 7-day spacing by default, or detect from weekNumber gaps
                            updates.meetings = podForm.meetings.map((m, i) => {
                                const meetingDate = new Date(startD);
                                meetingDate.setDate(meetingDate.getDate() + (i * 7));
                                return { ...m, date: meetingDate.toISOString().slice(0, 10) };
                            });
                        }
                        setPodForm(updates);
                    }} />
                </div>
                <div>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Lock Date (no new signups after)</label>
                    <input type="date" className="mom-input" value={podForm.lockDate ? podForm.lockDate.slice(0, 10) : ''} onChange={(e) => setPodForm({ ...podForm, lockDate: e.target.value ? new Date(e.target.value + 'T23:59:59').toISOString() : '' })} />
                </div>
            </div>

            {/* Sales Model */}
            <div style={{ marginBottom: 14 }}>
                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Sales Model</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    {[{ key: 'immediate', label: 'Immediate Purchase' }, { key: 'waitlist', label: 'Waitlist → Sale Window' }].map((s) => (
                        <button key={s.key} className="mom-btn" onClick={() => setPodForm({ ...podForm, salesModel: s.key })}
                            style={{ padding: '6px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: (podForm.salesModel || 'immediate') === s.key ? theme.colors.charcoal : theme.colors.white, color: (podForm.salesModel || 'immediate') === s.key ? theme.colors.white : theme.colors.charcoal, border: `1px solid ${theme.colors.creamDark}` }}>
                            {s.label}
                        </button>
                    ))}
                </div>
                {(podForm.salesModel === 'waitlist') && (
                    <div style={{ background: theme.colors.cream, borderRadius: 10, padding: '14px', marginTop: 8 }}>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Sale Start Date</label>
                            <input type="date" className="mom-input" value={podForm.saleStartDate ? podForm.saleStartDate.slice(0, 10) : ''} onChange={(e) => setPodForm({ ...podForm, saleStartDate: e.target.value ? new Date(e.target.value + 'T00:00:00').toISOString() : '' })} />
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 4 }}>Users can join the waitlist before this date. Purchasing opens on this date.</p>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Waitlist → Sale Email Subject</label>
                            <input type="text" className="mom-input" value={podForm.waitlistEmailSubject || ''} onChange={(e) => setPodForm({ ...podForm, waitlistEmailSubject: e.target.value })} placeholder="Enrollment is OPEN!" style={{ fontSize: 13 }} />
                        </div>
                        <div>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Waitlist → Sale Email Body</label>
                            <RichTextArea value={podForm.waitlistEmailBody || ''} onChange={(val) => setPodForm({ ...podForm, waitlistEmailBody: val })} placeholder="Hey! The doors are officially open for..." rows={4} />
                        </div>
                        {editingPod !== 'new' && (podForm.waitlist || []).length > 0 && (
                            <div style={{ marginTop: 12, padding: '12px 14px', background: theme.colors.white, borderRadius: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal }}>{(podForm.waitlist || []).length} on waitlist</span>
                                    <a href={(() => { const emails = (podForm.waitlist || []).map((w) => w.email).filter(Boolean); const params = []; params.push('bcc=' + encodeURIComponent(emails.join(','))); if (podForm.waitlistEmailSubject) params.push('subject=' + encodeURIComponent(podForm.waitlistEmailSubject)); if (podForm.waitlistEmailBody) params.push('body=' + encodeURIComponent(podForm.waitlistEmailBody.replace(/\*\*/g, '').replace(/\*/g, ''))); return 'mailto:hello@daniellenbarr.com?' + params.join('&'); })()}
                                        target="_blank" rel="noopener noreferrer"
                                        style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.terracotta, textDecoration: 'none' }}>
                                        📧 Email Waitlist
                                    </a>
                                </div>
                                <div style={{ marginTop: 8, maxHeight: 100, overflowY: 'auto' }}>
                                    {(podForm.waitlist || []).map((w, wi) => (
                                        <p key={wi} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, padding: '2px 0' }}>{w.email} — joined {new Date(w.joinedAt).toLocaleDateString()}</p>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Status */}
            <div style={{ marginBottom: 14 }}>
                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Status</label>
                <div style={{ display: 'flex', gap: 8 }}>
                    {['upcoming', 'active', 'completed'].map((s) => (
                        <button key={s} className="mom-btn" onClick={() => setPodForm({ ...podForm, status: s })}
                            style={{ padding: '6px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: podForm.status === s ? theme.colors.charcoal : theme.colors.white, color: podForm.status === s ? theme.colors.white : theme.colors.charcoal, border: `1px solid ${theme.colors.creamDark}` }}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Meetings */}
            <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal }}>Weekly Meetings</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {onAutoGenMeetings && (!podForm.meetings || podForm.meetings.length === 0) && podForm.startDate && (
                            <button className="mom-btn mom-btn-ghost" onClick={onAutoGenMeetings} style={{ fontSize: 12, color: theme.colors.terracotta, fontWeight: 600 }}>⚡ Auto-generate from weeks</button>
                        )}
                        <button className="mom-btn mom-btn-ghost" onClick={addMeeting} style={{ fontSize: 12, color: theme.colors.terracotta }}>+ Add Meeting</button>
                    </div>
                </div>
                {(podForm.meetings || []).map((m, mi) => {
                    // Meeting time is stored as trainer-local wall clock (Central).
                    // Convert to a true UTC moment so ended/today logic is correct
                    // and display lands in the viewer's local tz.
                    const meetStart = m.date ? trainerWallClockToUTC(m.date, m.time) : null;
                    const meetDayAnchor = m.date && !m.time ? trainerWallClockToUTC(m.date, '23:59') : null;
                    const meetMoment = meetStart || meetDayAnchor;
                    const now = new Date();
                    let meetingEnded = false;
                    if (meetStart) {
                        meetingEnded = now.getTime() > meetStart.getTime() + 60 * 60 * 1000;
                    } else if (meetDayAnchor) {
                        meetingEnded = now.getTime() > meetDayAnchor.getTime();
                    }
                    const isExpMeeting = expandedMeeting === mi;
                    // Format time/date in viewer-local tz
                    const timeLabel = meetStart ? formatViewerLocalTime(meetStart) : '';
                    const dateLabel = meetMoment ? meetMoment.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                    return (
                        <div key={mi} id={`${meetingIdPrefix || 'meeting'}-${mi + 1}`} style={{ background: theme.colors.cream, borderRadius: 10, marginBottom: 6, overflow: 'hidden' }}>
                            {/* Summary row — always visible */}
                            <div onClick={() => setExpandedMeeting(isExpMeeting ? null : mi)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' }}>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: theme.colors.charcoal, width: 20, textAlign: 'center', flexShrink: 0 }}>{mi + 1}</span>
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal }}>{m.title || `Meeting ${mi + 1}`}</span>
                                    {dateLabel && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>{dateLabel}{timeLabel ? ` · ${timeLabel}` : ''}</span>}
                                    {meetingEnded && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '1px 7px', borderRadius: 8, background: m.recordingUrl ? theme.colors.success : theme.colors.warmGray, color: theme.colors.white, fontWeight: 600 }}>{m.recordingUrl ? 'REC ✓' : 'ENDED'}</span>}
                                </div>
                                <span style={{ fontSize: 14, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: isExpMeeting ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>▾</span>
                            </div>
                            {/* Expanded edit form */}
                            {isExpMeeting && (
                                <div style={{ padding: '0 14px 14px', borderTop: `1px solid ${theme.colors.creamDark}` }}>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8, marginBottom: 8 }}>
                                        <button className="mom-btn mom-btn-ghost" onClick={() => removeMeeting(mi)} style={{ fontSize: 11, color: theme.colors.danger }}>Remove</button>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                        <input type="text" className="mom-input" value={m.title || ''} onChange={(e) => updateMeeting(mi, 'title', e.target.value)} placeholder="Meeting title" style={{ fontSize: 13 }} />
                                        <input type="text" className="mom-input" value={m.zoomLink || ''} onChange={(e) => updateMeeting(mi, 'zoomLink', e.target.value)} placeholder="Zoom link" style={{ fontSize: 13 }} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                        <input type="date" className="mom-input" value={m.date || ''} onChange={(e) => updateMeeting(mi, 'date', e.target.value)} style={{ fontSize: 13 }} />
                                        <div>
                                            <input type="time" className="mom-input" value={m.time || ''} onChange={(e) => updateMeeting(mi, 'time', e.target.value)} style={{ fontSize: 13 }} />
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, color: theme.colors.warmGray, marginTop: 2 }}>Central Time (CT)</p>
                                        </div>
                                    </div>
                                    {meetingEnded && (
                                        <div style={{ marginTop: 10, padding: '10px 12px', background: m.recordingUrl ? hexToRgba(theme.colors.success, 0.06) : hexToRgba(theme.colors.terracotta, 0.04), borderRadius: 8, border: `1px solid ${m.recordingUrl ? hexToRgba(theme.colors.success, 0.15) : hexToRgba(theme.colors.terracotta, 0.1)}` }}>
                                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: m.recordingUrl ? theme.colors.success : theme.colors.terracotta, marginBottom: 6, display: 'block' }}>
                                                {m.recordingUrl ? 'Recording Link' : 'Add Recording Link'}
                                            </label>
                                            <input type="text" className="mom-input" value={m.recordingUrl || ''} onChange={(e) => updateMeeting(mi, 'recordingUrl', e.target.value)} placeholder="Paste Bunny video URL or embed link..." style={{ fontSize: 13 }} />
                                        </div>
                                    )}
                                    {mi === (podForm.meetings || []).length - 1 && (
                                        <button className="mom-btn mom-btn-ghost" onClick={addMeeting} style={{ fontSize: 12, color: theme.colors.terracotta, marginTop: 10, fontWeight: 600 }}>+ Add Another Meeting</button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {(!podForm.meetings || podForm.meetings.length === 0) && (
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>No meetings scheduled. {onAutoGenMeetings && podForm.startDate ? 'Click "Auto-generate from weeks" or "' : 'Click "'}+ Add Meeting" to set up weekly calls.</p>
                )}
            </div>

            {/* Supporting Materials */}
            <div style={{ marginBottom: 20 }}>
                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Supporting Materials URL</label>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginBottom: 6 }}>Link to presentation materials (e.g. Canva). Shown to enrolled users in the schedule section.</p>
                <input type="text" className="mom-input" value={podForm.supportingMaterialUrl || ''} onChange={(e) => setPodForm({ ...podForm, supportingMaterialUrl: e.target.value })} placeholder="https://canva.com/..." style={{ fontSize: 13 }} />
            </div>

            {/* Participants */}
            {editingPod !== 'new' && (
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal }}>
                            Participants ({(podForm.participants || []).length}/{podForm.maxParticipants || '∞'})
                        </label>
                        {podUsers.length > 0 && (
                            <a href={'mailto:hello@daniellenbarr.com?bcc=' + encodeURIComponent(podUsers.map((u) => u.email || u.emailAddresses?.[0]?.emailAddress).filter(Boolean).join(',')) + '&subject=' + encodeURIComponent(podForm.podName + ' Update')}
                                target="_blank" rel="noopener noreferrer"
                                style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.terracotta, textDecoration: 'none' }}>
                                📧 Email Group
                            </a>
                        )}
                    </div>
                    {podUsersLoading ? (
                        <div style={{ padding: '12px 0' }}><FaSpinner style={{ fontSize: 16, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} /></div>
                    ) : podUsers.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                            {/* Anyone in this pod who still can't open the program — fixed in one tap.
                                Saving the pod also grants access automatically. */}
                            {onGrantAccess && (() => {
                                const missing = podUsers.filter((u) => lacksProgramAccess(u, programId));
                                if (missing.length === 0) return null;
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, background: hexToRgba('#dc2626', 0.07), color: '#991b1b', fontFamily: 'var(--mom-font-sans)', fontSize: 12.5 }}>
                                        <span style={{ flex: 1 }}>{missing.length} {missing.length === 1 ? 'person in this pod can’t' : 'people in this pod can’t'} open the program yet.</span>
                                        <button className="mom-btn mom-btn-primary" disabled={grantingIds.length > 0} onClick={() => onGrantAccess(missing.map((u) => u.id))} style={{ padding: '6px 14px', fontSize: 12 }}>
                                            {grantingIds.length > 0 ? 'Granting…' : `Grant access${missing.length > 1 ? ' to all' : ''}`}
                                        </button>
                                    </div>
                                );
                            })()}
                            {podUsers.map((u) => (
                                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: theme.colors.cream, borderRadius: 10 }}>
                                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: theme.colors.terracotta, color: theme.colors.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                        {(u.firstName || u.email || '?')[0].toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal }}>{u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email}</p>
                                        {u.firstName && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>{u.email}</p>}
                                    </div>
                                    {onGrantAccess && lacksProgramAccess(u, programId) && (
                                        <button onClick={() => onGrantAccess([u.id])} disabled={grantingIds.includes(u.id)}
                                            title="This person is in the pod but can't open the program"
                                            style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, color: '#991b1b', background: hexToRgba('#dc2626', 0.08), border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            {grantingIds.includes(u.id) ? 'Granting…' : 'No access · Grant'}
                                        </button>
                                    )}
                                    {siblingPods && siblingPods.length > 0 && (
                                        <select onChange={(e) => { if (e.target.value) { moveUserToPod(u.id, e.target.value); e.target.value = ''; } }} defaultValue=""
                                            style={{ padding: '4px 8px', borderRadius: 8, border: `1px solid ${theme.colors.creamDark}`, fontSize: 11, fontFamily: 'var(--mom-font-sans)', color: theme.colors.charcoal, cursor: 'pointer', background: theme.colors.white }}>
                                            <option value="" disabled>Move to...</option>
                                            {siblingPods.map((p) => (<option key={p.id} value={p.id}>{p.podName} ({p.participants?.length || 0}/{p.maxParticipants})</option>))}
                                        </select>
                                    )}
                                    <button className="mom-btn mom-btn-ghost" onClick={() => removeUserFromPod(u.id)} style={{ fontSize: 11, color: theme.colors.danger, padding: '4px 8px' }}>Remove</button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 14 }}>No participants yet.</p>
                    )}
                    <input type="text" className="mom-input" value={addUserSearch} onChange={handleSearchChange} placeholder="Search by name or email to add..." style={{ fontSize: 13, marginBottom: 4 }} />
                    {addUserSearch.length >= 2 && (
                        <div style={{ maxHeight: 180, overflowY: 'auto', borderRadius: 10, border: `1px solid ${theme.colors.creamDark}`, background: theme.colors.white }}>
                            {searchLoading ? (
                                <p style={{ padding: '12px 14px', fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>Searching...</p>
                            ) : (<>
                            {allUsers.filter((u) => !(podForm.participants || []).includes(u.id)).slice(0, 8).map((u) => (
                                <div key={u.id} onClick={() => addUserToPod(u.id)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${theme.colors.cream}`, display: 'flex', alignItems: 'center', gap: 10 }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.cream} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: theme.colors.charcoal, color: theme.colors.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700 }}>{(u.firstName || u.email || '?')[0].toUpperCase()}</div>
                                    <div>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal }}>{u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email}</p>
                                        {u.firstName && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>{u.email}</p>}
                                    </div>
                                </div>
                            ))}
                            {allUsers.filter((u) => !(podForm.participants || []).includes(u.id)).length === 0 && (
                                <p style={{ padding: '12px 14px', fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>No matching users found.</p>
                            )}
                            </>)}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════
//  HOMEPAGE TAB
// ═══════════════════════════════════════════════════════════════
// Renders a tab's sections as collapsed-by-default cards, with an optional
// uppercase group sub-heading inserted whenever `section.group` changes. One
// place controls the copy-tab layout → consistency + the "calm on entry" rule.
function SectionList({ sections, content, handleChange, renderChildren, tabKey }) {
    useEffect(() => {
        if (!tabKey) return;
        ADMIN_FIELD_INDEX[tabKey] = sections.flatMap((s) => s.items.map((it) => ({ section: s.section, label: it.label, key: it.key })));
    }, [tabKey, sections]);
    return (
        <>
            {sections.map((s, i) => {
                const showHeader = s.group && (i === 0 || sections[i - 1].group !== s.group);
                return (
                    <React.Fragment key={s.section}>
                        {showHeader && (
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.colors.warmGray, margin: i === 0 ? '0 0 10px 4px' : '28px 0 10px 4px' }}>{s.group}</p>
                        )}
                        <CollapsibleSection section={s.section} desc={s.desc} items={s.items} content={content} handleChange={handleChange} defaultOpen={false}>
                            {renderChildren ? renderChildren(s) : null}
                        </CollapsibleSection>
                    </React.Fragment>
                );
            })}
        </>
    );
}

// Scaled, NON-INTERACTIVE frame for live page previews. pointerEvents:'none'
// means clicks inside the preview can never navigate or trigger modals.
function LivePreviewFrame({ children, height = 560 }) {
    const SCALE = 0.5;
    return (
        <div style={{ border: `1px solid ${theme.colors.creamDark}`, borderRadius: 16, overflow: 'auto', height, background: theme.colors.cream, boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.05)' }}>
            <div style={{ width: `${100 / SCALE}%`, transform: `scale(${SCALE})`, transformOrigin: 'top left', pointerEvents: 'none' }}>
                {children}
            </div>
        </div>
    );
}

// Reusable "Show live preview" disclosure — owns its own toggle, renders the
// passed page read-only in a LivePreviewFrame. Children only MOUNT when shown,
// so the previewed page has no side effects while hidden.
function LivePreviewSection({ children }) {
    const [show, setShow] = useState(false);
    return (
        <div style={{ marginBottom: 16 }}>
            <button onClick={() => setShow((v) => !v)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 10, border: `1.5px solid ${show ? theme.colors.charcoal : theme.colors.creamDark}`, background: show ? theme.colors.charcoal : theme.colors.white, color: show ? theme.colors.white : theme.colors.charcoal, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>
                {show ? 'Hide live preview' : 'Show live preview'}
            </button>
            {show && (
                <div style={{ marginTop: 12 }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginBottom: 8 }}>Your real page, updating as you edit. Scroll inside to see more — the preview is read-only.</p>
                    <LivePreviewFrame>{children}</LivePreviewFrame>
                </div>
            )}
        </div>
    );
}

function HomepageTab({ unsavedRef }) {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [programs, setPrograms] = useState([]);

    useEffect(() => {
        Promise.all([fetchSiteContent(), fetchPrograms()]).then(([c, p]) => {
            setContent(c);
            setPrograms((p || []).filter((pr) => pr.active !== false));
            setLoading(false);
        });
    }, []);
    const handleChange = (key, value) => { setContent((prev) => ({ ...prev, [key]: value })); setSaved(false); if (unsavedRef) unsavedRef.current = true; };
    const handleSave = async () => { setSaving(true); const ok = await updateSiteContent(content); setSaving(false); if (ok) { setSaved(true); if (unsavedRef) unsavedRef.current = false; setTimeout(() => setSaved(false), 3000); } };

    if (loading) return <Spinner />;

    const fields = [
        {
            group: 'Top of page', section: 'Hero Section', desc: 'The first thing visitors see — your main headline and description.', items: [
                { key: 'heroTagline', label: 'Tagline (small text above title)', type: 'text' },
                { key: 'heroTitle', label: 'Main Title', type: 'text' },
                { key: 'heroSubtitle', label: 'Subtitle (below title)', type: 'text' },
                { key: 'heroDescription', label: 'Description (visitors / signed out)', type: 'textarea' },
                { key: 'heroDescriptionSignedIn', label: 'Description (signed-in members)', type: 'textarea', hint: 'If blank, falls back to the visitor description above.' },
            ]
        },
        {
            section: 'Peek Inside (app preview)', desc: 'The section directly under the hero that shows off real free Shorts and a locked class library. The videos themselves come from your live content automatically — you edit the labels here.', items: [
                { key: 'landingPreviewEnabled', label: 'Show the "Peek Inside" section', type: 'toggle', toggleLabel: 'Visible on the homepage' },
                { key: 'landingPreviewShortsHeading', label: 'Free Shorts — Heading', type: 'text', groupLabel: 'Free workouts rail' },
                { key: 'landingPreviewShortsSubcopy', label: 'Free Shorts — Subtext', type: 'textarea' },
                { key: 'landingPreviewLibraryHeading', label: 'Class Library — Heading', type: 'text', groupLabel: 'Locked class library' },
                { key: 'landingPreviewLibrarySubcopy', label: 'Class Library — Subtext', type: 'textarea' },
                { key: 'landingPreviewLockedLabel', label: 'Locked badge text (on members-only classes)', type: 'text' },
            ]
        },
        {
            group: 'Body content', section: 'Tiles', desc: 'Three cards below the hero. Each can have a link (like your TikTok URL).', items: [
                { key: 'tile1Title', label: 'Tile 1 Title', type: 'text', groupLabel: 'Tile 1' },
                { key: 'tile1Desc', label: 'Tile 1 Description', type: 'textarea' },
                { key: 'tile1Link', label: 'Tile 1 Link URL (optional)', type: 'text' },
                { key: 'tile2Title', label: 'Tile 2 Title', type: 'text', groupLabel: 'Tile 2' },
                { key: 'tile2Desc', label: 'Tile 2 Description', type: 'textarea' },
                { key: 'tile2Link', label: 'Tile 2 Link URL (optional)', type: 'text' },
                { key: 'tile3Title', label: 'Tile 3 Title', type: 'text', groupLabel: 'Tile 3' },
                { key: 'tile3Desc', label: 'Tile 3 Description', type: 'textarea' },
                { key: 'tile3Link', label: 'Tile 3 Link URL (optional)', type: 'text' },
            ]
        },
        {
            section: 'Featured Program', desc: 'The highlighted section with the dark background showcasing your signature program.', items: [
                { key: 'featuresHeading', label: 'Features Heading (section above tiles)', type: 'text' },
                { key: 'featuresSubheading', label: 'Features Subheading', type: 'textarea' },
                { key: 'methodLabel', label: 'Featured Program Label (small text above title)', type: 'text' },
                { key: 'methodTitle', label: 'Featured Program Title', type: 'text' },
                { key: 'methodDescription', label: 'Featured Program Description', type: 'textarea' },
                { key: 'methodStat1Num', label: 'Stat 1 — Number + Label', type: 'row', pairKey: 'methodStat1Label', placeholder: 'Number', pairPlaceholder: 'Label', groupLabel: 'Stats' },
                { key: 'methodStat2Num', label: 'Stat 2 — Number + Label', type: 'row', pairKey: 'methodStat2Label', placeholder: 'Number', pairPlaceholder: 'Label' },
                { key: 'methodStat3Num', label: 'Stat 3 — Number + Label', type: 'row', pairKey: 'methodStat3Label', placeholder: 'Number', pairPlaceholder: 'Label' },
                { key: 'methodStat4Num', label: 'Stat 4 — Number + Label (leave empty to hide)', type: 'row', pairKey: 'methodStat4Label', placeholder: 'Number', pairPlaceholder: 'Label' },
            ]
        },
        {
            group: 'Social proof', section: 'Testimonials', desc: 'Manage testimonials shown on the homepage. First 5 are displayed, the rest are in the "See all" modal.', customRender: 'testimonials', items: [
                { key: 'testimonialsHeading', label: 'Section Heading', type: 'text' },
                { key: 'testimonialsSubheading', label: 'Section Subheading', type: 'text' },
            ]
        },
        {
            group: 'Conversion & nav', section: 'Call to Action', desc: 'The "Join the Movement" section near the bottom of the page.', items: [
                { key: 'ctaHeading', label: 'CTA Heading', type: 'text' },
                { key: 'ctaSubheading', label: 'CTA Subheading', type: 'text' },
            ]
        },
        {
            section: 'Navigation & Buttons', desc: 'Customize the nav bar label and the "Go to Dashboard" button text shown to signed-in users.', items: [
                { key: 'dashboardNavLabel', label: 'Nav Bar Label (replaces "Dashboard" in menu)', type: 'text' },
                { key: 'goToDashboardLabel', label: '"Go to Dashboard" Button Text (shown on homepage when signed in)', type: 'text' },
            ]
        },
    ];

    // Migrate old numbered testimonials to array format on first load
    const getTestimonials = () => {
        if (content.testimonials && Array.isArray(content.testimonials)) return content.testimonials;
        // Migrate from old format
        const migrated = [];
        for (let i = 1; i <= 5; i++) {
            const text = content[`testimonial${i}Text`];
            if (text && text.trim()) {
                migrated.push({ text, name: content[`testimonial${i}Name`] || '', detail: content[`testimonial${i}Detail`] || '' });
            }
        }
        if (migrated.length > 0) handleChange('testimonials', migrated);
        return migrated;
    };

    return (
        <div style={{ paddingBottom: 72 }}>
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Homepage Content</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>Edit the landing page that visitors see before signing in.</p>
            </div>

            <LivePreviewSection>
                <LandingShell previewContent={content || {}} isSignedIn={false} onGoToApp={() => {}}>
                    <button className="mom-btn mom-btn-primary" style={{ padding: '16px 44px', fontSize: 16 }}>Sign Up</button>
                    <button className="mom-btn mom-btn-outline" style={{ padding: '14px 36px', fontSize: 15 }}>Sign In</button>
                </LandingShell>
            </LivePreviewSection>
            <SectionList tabKey="homepage" sections={fields} content={content} handleChange={handleChange}
                renderChildren={(s) => s.customRender === 'testimonials'
                    ? <TestimonialsManager testimonials={getTestimonials()} onChange={(t) => { handleChange('testimonials', t); }} programs={programs} />
                    : null} />
            <StickyBar saving={saving} saved={saved} onSave={handleSave} hasChanges={!saved} />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  DASHBOARD TAB
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
//  ABOUT ME TAB
// ═══════════════════════════════════════════════════════════════
const ABOUT_FIELDS = [
    {
        section: 'Profile', desc: 'Your photo, headline, and tagline at the top of the page.', items: [
            { key: 'aboutPhoto', label: 'Profile Photo', hint: 'Use the image picker or paste a direct URL.', type: 'photo', shape: 'circle' },
            { key: 'aboutHeadline', label: 'Headline', hint: 'The big text at the top (e.g., "Hey, I\'m Danielle!")', placeholder: "Hey, I'm Danielle!" },
            { key: 'aboutTagline', label: 'Tagline', hint: 'Short line below the headline in your brand color (optional).', placeholder: 'Mom, fitness lover, and coach' },
        ]
    },
    {
        section: 'Your story', desc: 'The heart of the page — your intro and main story.', items: [
            { key: 'aboutIntro', label: 'Introduction', hint: 'A short, heartfelt opening — displayed in italics and centered.', markdown: true, placeholder: 'I believe that any movement counts...' },
            { key: 'aboutBody', label: 'Main Story', hint: 'The longer section about your journey, philosophy, and what drives you.', markdown: true, rows: 10, placeholder: 'My fitness journey started when...' },
        ]
    },
    {
        section: 'Closing', desc: 'The warm wrap-up shown before the button.', items: [
            { key: 'aboutClosingHeadline', label: 'Closing Headline', hint: 'A headline before the closing section (optional).', placeholder: "Let's do this together" },
            { key: 'aboutClosing', label: 'Closing Message', hint: 'Final words — warm, personal, inviting.', markdown: true, placeholder: "Whether you have 5 minutes or 20, I'm here to move with you..." },
        ]
    },
    {
        section: 'Call to action', desc: 'The button at the bottom of the page.', items: [
            { key: 'aboutCtaText', label: 'Button Text', placeholder: 'Browse Programs' },
            { key: 'aboutCtaLink', label: 'Button Link', hint: 'Use /programs, /free-workouts, or a full URL.', placeholder: '/programs' },
        ]
    },
];
ADMIN_FIELD_INDEX.about = indexFields(ABOUT_FIELDS);

function AboutMeTab({ unsavedRef }) {
    const [content, setContent] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => { fetchSiteContent().then((c) => { setContent(c || {}); setLoading(false); }); }, []);

    const handleChange = (key, val) => { setContent((prev) => ({ ...prev, [key]: val })); setSaved(false); if (unsavedRef) unsavedRef.current = true; };
    const handleSave = async () => { setSaving(true); await updateSiteContent(content); setSaving(false); setSaved(true); if (unsavedRef) unsavedRef.current = false; };

    if (loading) return <Spinner />;

    const fields = ABOUT_FIELDS;

    return (
        <div style={{ paddingBottom: 72 }}>
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>About Me Page</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>
                    Tell your story. This page is linked from the landing page tiles — set a tile link to <strong>/about</strong> to connect it.
                </p>
            </div>
            <LivePreviewSection>
                <AboutMe previewContent={content} />
            </LivePreviewSection>
            <SectionList tabKey="about" sections={fields} content={content} handleChange={handleChange} />
            <StickyBar saving={saving} saved={saved} onSave={handleSave} hasChanges={!saved} />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════
//  JOIN PAGE TAB — /join subscription marketing surface
//  See `docs/ux/join-marketing-page.md` + `docs/decisions/adr-join-marketing-page.md`.
//
//  ── IMPORTANT: helpers MUST be at module scope ──────────────────────────
//  Helpers (JoinSubCard, JoinTextField, JoinTextareaField, JoinPhotoField)
//  used to be defined inside JoinPageTab. That caused every keystroke in any
//  input to:
//    1. trigger setContent → JoinPageTab re-render
//    2. create new helper function references (new component types)
//    3. React unmounted the old children + remounted as "new" components
//    4. JoinSubCard's `open` useState reset to defaultOpen (sections collapsed)
//    5. input lost focus, browser scrolled to where input HAD been
//
//  Hoisting them out gives them stable component identity across re-renders.
//  Internal state survives, inputs keep focus, sections stay expanded.
//  Classic React gotcha — never define components inside other components.
// ═══════════════════════════════════════════════════════════════════════

function JoinSubCard({ title, desc, children, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, marginBottom: 12, boxShadow: theme.shadows.sm, overflow: 'hidden' }}>
            <button onClick={() => setOpen(!open)} style={{ width: '100%', padding: '20px 28px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, textAlign: 'left' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: theme.colors.charcoal }}>{title}</h3>
                    {desc && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginTop: 4 }}>{desc}</p>}
                </div>
                <span style={{ fontSize: 18, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>▾</span>
            </button>
            {open && (
                <div style={{ padding: '0 28px 24px', borderTop: `1px solid ${theme.colors.creamDark}` }}>
                    <div style={{ paddingTop: 20 }}>{children}</div>
                </div>
            )}
        </div>
    );
}

// Join field helpers now delegate to the shared AdminField so /join renders
// identically to every other tab (same labels, hints, modern editor, photo picker).
function JoinTextField({ fieldKey, label, hint, placeholder, content, handleChange }) {
    return <AdminField f={{ key: fieldKey, label, hint, placeholder }} content={content} handleChange={handleChange} />;
}

function JoinTextareaField({ fieldKey, label, hint, placeholder, rows = 4, supportsMarkdown, content, handleChange }) {
    return <AdminField f={{ key: fieldKey, label, hint, placeholder, rows, markdown: supportsMarkdown || undefined, type: supportsMarkdown ? undefined : 'textarea' }} content={content} handleChange={handleChange} />;
}

function JoinPhotoField({ fieldKey, label, hint, content, handleChange }) {
    return <AdminField f={{ key: fieldKey, label, hint, type: 'photo' }} content={content} handleChange={handleChange} />;
}

function JoinPageTab({ unsavedRef }) {
    const [content, setContent] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [driveImages, setDriveImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    // Tracks which photo field's picker is currently open (key string or null).
    // One picker open at a time — opening another closes the first.
    const [openPickerKey, setOpenPickerKey] = useState(null);

    useEffect(() => { fetchSiteContent().then((c) => { setContent(c || {}); setLoading(false); }); }, []);

    const handleChange = (key, val) => {
        setContent((prev) => ({ ...prev, [key]: val }));
        setSaved(false);
        if (unsavedRef) unsavedRef.current = true;
    };
    const handleSave = async () => {
        setSaving(true);
        const ok = await updateSiteContent(content);
        setSaving(false);
        if (ok) {
            setSaved(true);
            if (unsavedRef) unsavedRef.current = false;
            setTimeout(() => setSaved(false), 3000);
        }
    };

    const loadDriveImages = async (fieldKey) => {
        if (driveImages.length > 0) {
            setOpenPickerKey(fieldKey);
            return;
        }
        setImagesLoading(true);
        try {
            const res = await fetch('/api/bunny-images');
            if (res.ok) {
                const data = await res.json();
                setDriveImages(data.images || []);
            }
        } catch (e) { /* network failure surfaces as "no images" — admin can paste URL manually */ }
        setImagesLoading(false);
        setOpenPickerKey(fieldKey);
    };

    if (loading) return <Spinner />;

    // Shared prop bundles — minimizes per-field boilerplate. Identity of these
    // objects changes every render (new literals), but that's fine: the hoisted
    // helpers are pure-prop components, so a different prop ref triggers re-render
    // (cheap) but NEVER an unmount-remount (which is what was killing focus).
    const textProps = { content, handleChange };
    const photoProps = { content, handleChange, openPickerKey, setOpenPickerKey, loadDriveImages, imagesLoading, driveImages };

    return (
        <div style={{ paddingBottom: 72 }}>
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Join Page</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, lineHeight: 1.6 }}>
                    The shareable subscription marketing page at <strong>/join</strong>. Edit copy, photos, and FAQ below.
                    {' '}
                    <a href="/join" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.terracotta, fontWeight: 600, textDecoration: 'none' }}>View live page →</a>
                </p>
            </div>

            <JoinSubCard title="Hero" desc="The first thing visitors see — headline, sub-copy, primary CTA, and hero photo.">
                <JoinTextField fieldKey="joinHeroEyebrow" label="Eyebrow (small caps brand kicker)" hint='Tiny uppercase text above the headline. Default: "UNLIMITED · WITH DANIELLE".' {...textProps} />
                <JoinTextareaField fieldKey="joinHeroHeadline" label="Headline" hint='The big italic-serif headline. Use a single Enter to break onto a new line (poetic line break).' placeholder="Show up for yourself,&#10;on your schedule." rows={2} {...textProps} />
                <JoinTextareaField fieldKey="joinHeroSubcopy" label="Sub-copy" hint="One or two warm sentences explaining the offer." rows={3} {...textProps} />
                <JoinTextField fieldKey="joinPrimaryCta" label="Primary Button Label" hint='Text on the main CTA button. Default: "Start Moving".' {...textProps} />
                <JoinTextField fieldKey="joinReassurance" label="Reassurance Line" hint='Small text below the button. Default: "Cancel anytime · No commitment".' {...textProps} />
                <JoinTextareaField fieldKey="joinEnrollmentClosedMessage" label="Enrollment-Closed Message" hint='Shown in place of BOTH Subscribe buttons (hero + bottom) when the "Subscription Option" toggle on the Settings tab is OFF. Subscribers continue to see "You’re already in" — this message is for new visitors. Default: "Enrollment opens May 17th @ 11am CT. See you then!".' rows={2} {...textProps} />
                <JoinPhotoField fieldKey="joinHeroImage" label="Hero Photo" hint="Portrait-orientation (4:5) works best. Leave empty to show a text-only hero." {...photoProps} />
                <JoinTextField fieldKey="joinSocialProof" label="Social Proof Line" hint='Italic line below the photo. Default: "Join women already moving with Danielle". Leave empty to hide.' {...textProps} />
            </JoinSubCard>

            <JoinSubCard title="When a Member Visits" desc="Shown to existing subscribers in place of BOTH Subscribe CTAs (hero + bottom). Editorial composition: eyebrow + italic-serif welcome + optional sub-copy + single button back to workouts. Leave a field blank to hide that element (headline + button always show with defaults).">
                <JoinTextField fieldKey="joinMemberEyebrow" label="Eyebrow (small caps)" hint='Tiny uppercase kicker above the welcome. Default: "MEMBER ACCESS". Leave blank to hide.' {...textProps} />
                <JoinTextField fieldKey="joinMemberHeadline" label="Welcome Headline" hint='The italic-serif welcome line. Default: "You’re already moving with us."' {...textProps} />
                <JoinTextField fieldKey="joinMemberSubcopy" label="Optional Sub-copy" hint='Warm line below the headline. Default: empty (hidden). Add a sentence for extra warmth — e.g., "Your classes are ready when you are."' {...textProps} />
                <JoinTextField fieldKey="joinMemberCta" label="Button Label" hint='Default: "Take me to my workouts →". Routes to /workouts.' {...textProps} />
            </JoinSubCard>

            <JoinSubCard title="What's Included" desc="Six feature tiles. Each tile is auto-numbered (01–06) in the brand serif. Leave any title blank to hide that tile — the remaining tiles renumber so the sequence always reads as a complete set.">
                <JoinTextField fieldKey="joinIncludedHeading" label="Section Heading" {...textProps} />
                <JoinTextField fieldKey="joinIncludedSubheading" label="Section Subheading" {...textProps} />
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} style={{ borderTop: `1px solid ${theme.colors.creamDark}`, paddingTop: 16, marginTop: i === 1 ? 8 : 0, marginBottom: i === 6 ? 0 : 4 }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: theme.colors.charcoal, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Tile {i}</p>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Title</label>
                            <input type="text" className="mom-input" value={content[`joinIncluded${i}Title`] || ''} onChange={(e) => handleChange(`joinIncluded${i}Title`, e.target.value)} placeholder={i === 1 ? "Live classes every week" : "Title (leave blank to hide tile)"} style={{ fontSize: 13 }} />
                        </div>
                        <div>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Description</label>
                            <textarea className="mom-input" value={content[`joinIncluded${i}Desc`] || ''} onChange={(e) => handleChange(`joinIncluded${i}Desc`, e.target.value)} rows={2} style={{ resize: 'vertical', lineHeight: 1.5, fontSize: 13 }} />
                        </div>
                    </div>
                ))}
            </JoinSubCard>

            <JoinSubCard title="Lifestyle Photos" desc="Two photo strips placed between the main sections — each one is a three-tile magazine-style triptych. 4:5 portrait photos work best (Instagram-standard aspect). Leave any tile blank to hide just that tile; the grid adapts. Leave all three blank in a section to hide the whole strip.">
                <div style={{ marginBottom: 8 }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: theme.colors.charcoal, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Strip 1 — between What's Included and Meet Danielle</p>
                </div>
                <JoinPhotoField fieldKey="joinLifestyle1Image1" label="Photo A" hint="Leftmost tile (or topmost on mobile). 4:5 portrait recommended." {...photoProps} />
                <JoinPhotoField fieldKey="joinLifestyle1Image2" label="Photo B" hint="Middle tile." {...photoProps} />
                <JoinPhotoField fieldKey="joinLifestyle1Image3" label="Photo C" hint="Rightmost tile (or bottom on mobile)." {...photoProps} />
                <div style={{ borderTop: `1px solid ${theme.colors.creamDark}`, marginTop: 16, paddingTop: 16, marginBottom: 8 }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: theme.colors.charcoal, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Strip 2 — between Testimonials and Stats</p>
                </div>
                <JoinPhotoField fieldKey="joinLifestyle2Image1" label="Photo A" hint="Leftmost tile (or topmost on mobile)." {...photoProps} />
                <JoinPhotoField fieldKey="joinLifestyle2Image2" label="Photo B" hint="Middle tile." {...photoProps} />
                <JoinPhotoField fieldKey="joinLifestyle2Image3" label="Photo C" hint="Rightmost tile (or bottom on mobile)." {...photoProps} />
                {(content.joinLifestyleImage1 || content.joinLifestyleImage2) && (
                    <div style={{ marginTop: 16, padding: '12px 14px', background: theme.colors.cream, borderRadius: 10 }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, lineHeight: 1.6 }}>
                            <strong>Migration note:</strong> Your previous single Lifestyle photo(s) automatically appear as <strong>Photo A</strong> of each strip until you replace them above. To clean up the old data, just set Photo A above for each strip and re-save.
                        </p>
                    </div>
                )}
            </JoinSubCard>

            <JoinSubCard title="Meet Danielle" desc="A short personal note with your photo. Supports **bold**, *italic*, and [links](url).">
                <JoinTextField fieldKey="joinMeetHeading" label="Heading" placeholder="Hey, I'm Danielle." {...textProps} />
                <JoinTextareaField fieldKey="joinMeetBody" label="Body" hint="A warm 1-2 paragraph intro. Supports markdown." rows={5} supportsMarkdown {...textProps} />
                <JoinPhotoField fieldKey="joinMeetImage" label="Photo (circular crop)" hint="Square portrait works best. Optional." {...photoProps} />
            </JoinSubCard>

            <JoinSubCard title="Testimonials" desc="Heading and subheading shown on the /join page. The testimonial quotes themselves are managed on the Homepage tab — they're shared between the homepage and this page.">
                <JoinTextField fieldKey="joinTestimonialsHeading" label="Section Heading" {...textProps} />
                <JoinTextField fieldKey="joinTestimonialsSubheading" label="Section Subheading" {...textProps} />
                <div style={{ background: theme.colors.cream, borderRadius: 10, padding: '14px 16px', marginTop: 4 }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, lineHeight: 1.6 }}>
                        Testimonial quotes are managed on the <strong>Homepage</strong> tab. Tag a quote <strong>Membership</strong> there to show it here — once at least one is tagged, this page shows only tagged quotes (top 5, with a "See all" modal beyond that). Until then, the whole pool shows here.
                    </p>
                </div>
            </JoinSubCard>

            <JoinSubCard title="Stats" desc="Three big numbers for credibility. Leave a number blank to hide that stat.">
                <JoinTextField fieldKey="joinStatsHeading" label="Section Heading" {...textProps} />
                {[1, 2, 3].map((i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Stat {i} — Number</label>
                            <input type="text" className="mom-input" value={content[`joinStat${i}Num`] || ''} onChange={(e) => handleChange(`joinStat${i}Num`, e.target.value)} placeholder="5–60" style={{ fontSize: 13 }} />
                        </div>
                        <div>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Stat {i} — Label</label>
                            <input type="text" className="mom-input" value={content[`joinStat${i}Label`] || ''} onChange={(e) => handleChange(`joinStat${i}Label`, e.target.value)} placeholder="minute options" style={{ fontSize: 13 }} />
                        </div>
                    </div>
                ))}
            </JoinSubCard>

            <JoinSubCard title="FAQ" desc="Up to ten questions. The first five ship with default copy; rows 6–10 are blank and optional. Leave either Q or A blank to hide a row.">
                <JoinTextField fieldKey="joinFaqHeading" label="Section Heading" placeholder="Questions?" {...textProps} />
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                    <div key={i} style={{ borderTop: `1px solid ${theme.colors.creamDark}`, paddingTop: 16, marginTop: i === 1 ? 8 : 0 }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: theme.colors.charcoal, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>FAQ {i}</p>
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Question</label>
                            <input type="text" className="mom-input" value={content[`joinFaq${i}Q`] || ''} onChange={(e) => handleChange(`joinFaq${i}Q`, e.target.value)} placeholder="Can I cancel anytime?" style={{ fontSize: 13 }} />
                        </div>
                        <div>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Answer</label>
                            <textarea className="mom-input" value={content[`joinFaq${i}A`] || ''} onChange={(e) => handleChange(`joinFaq${i}A`, e.target.value)} rows={3} style={{ resize: 'vertical', lineHeight: 1.5, fontSize: 13 }} />
                        </div>
                    </div>
                ))}
            </JoinSubCard>

            <JoinSubCard title="Top Bar (back to app)" desc="The slim sticky bar at the top of /join that lets visitors get back into the app instead of being stranded. The right-hand link adapts: signed-in visitors see the first label (→ Workouts); signed-out visitors see the second (opens the sign-in modal).">
                <JoinTextField fieldKey="joinNavBrowseLabel" label="Signed-in link" hint='Shown to signed-in visitors; goes to /workouts. Default: "Browse classes →".' {...textProps} />
                <JoinTextField fieldKey="joinNavSignInLabel" label="Signed-out link" hint='Shown to signed-out visitors; opens the sign-in modal. Default: "Sign in".' {...textProps} />
            </JoinSubCard>

            <JoinSubCard title="Bottom CTA" desc="The final call-to-action at the bottom of the page (full-bleed brand gradient).">
                <JoinTextField fieldKey="joinBottomHeadline" label="Headline" placeholder="Ready when you are." {...textProps} />
                <JoinTextField fieldKey="joinBottomSubcopy" label="Sub-copy" hint="One sentence reinforcing the offer." {...textProps} />
            </JoinSubCard>

            <StickyBar saving={saving} saved={saved} onSave={handleSave} hasChanges={!saved} />
        </div>
    );
}

const DASHBOARD_FIELDS = [
    {
        section: 'Welcome Area', desc: 'The greeting users see at the top when they sign in.', items: [
            { key: 'dashboardTitle', label: 'Welcome Title (user\'s name is appended automatically)', type: 'text' },
            { key: 'dashboardSubtitle', label: 'Subtitle (non-members)', type: 'text', hint: 'Shown to signed-in users without an active membership.' },
            { key: 'dashboardMemberSubtitle', label: 'Subtitle (members)', type: 'text', hint: 'Shown to active subscribers and admin/friend. Default: "Your membership is active — here\'s how your week is shaping up." Clear this field to use the non-member subtitle for everyone.' },
        ]
    },
    {
        section: 'Program purchase', desc: 'Signed-out visitors create their account (or sign in) before paying, so every purchase reaches the right member.', items: [
            { key: 'programSignInFirstNote', label: 'Note under Purchase / Continue (signed-out only)', type: 'textarea', hint: "Default: “You’ll create your free account (or sign in) first — then pay. It keeps your purchase safely tied to you.”" },
            { key: 'purchaseNudge', label: 'Bell text when a purchase needs attention (admin)', type: 'text', hint: 'Use {count}. Default: "{count} purchase needs your attention"' },
        ]
    },
    {
        section: 'Comments & member replies', desc: 'Wording in class comment threads once Member Replies is on (Settings → Member Replies), plus the moderation nudge.', items: [
            { key: 'commentReplyLabel', label: 'Reply button', type: 'text', hint: 'Default: "Reply"' },
            { key: 'commentReplyingTo', label: 'Reply context line', type: 'text', hint: 'Use {name}. Default: "Replying to {name}"' },
            { key: 'commentReplyPlaceholder', label: 'Reply box placeholder', type: 'text', hint: 'Default: "Write a reply…"' },
            { key: 'commentViewMoreReplies', label: 'Collapsed replies link', type: 'text', hint: 'Use {count}. Default: "View {count} more replies"' },
            { key: 'commentHideReplies', label: 'Re-collapse link', type: 'text', hint: 'Default: "Hide replies"' },
            { key: 'commentLinkBlocked', label: 'No-links message', type: 'text', hint: "Shown as a member types a link. Default: “Links can’t be shared in comments. Remove it to post.”" },
            { key: 'commentDeletedUndo', label: 'Deleted (with Undo)', type: 'text', hint: 'Default: "Comment deleted"' },
            { key: 'commentDeleteWithReplies', label: 'Delete warning with replies', type: 'text', hint: 'Use {count}. Default: "This will also remove {count} replies."' },
            { key: 'commentReportTitle', label: 'Report sheet title', type: 'text', hint: 'Default: "Report this comment?"' },
            { key: 'commentReportSubtext', label: 'Report sheet subtext', type: 'text', hint: "Default: “Danielle’s team will take a look.”" },
            { key: 'commentReportReason1', label: 'Report reason 1', type: 'text', hint: 'Default: "Unkind or hurtful"' },
            { key: 'commentReportReason2', label: 'Report reason 2', type: 'text', hint: 'Default: "Spam or selling"' },
            { key: 'commentReportReason3', label: 'Report reason 3', type: 'text', hint: 'Default: "Something else"' },
            { key: 'commentReportThanks', label: 'After reporting', type: 'text', hint: 'Default: "Thanks for letting us know."' },
            { key: 'commentReportedPlaceholder', label: 'Reported comment placeholder', type: 'text', hint: 'Default: "You reported this comment"' },
            { key: 'commentHeldNote', label: 'Held comment note (author only)', type: 'text', hint: "Default: “Only visible to you while it’s reviewed.”" },
            { key: 'moderationNudge', label: 'Moderation bell text (admin)', type: 'text', hint: 'Use {count}. Default: "{count} comment needs your eye"' },
        ]
    },
    {
        section: 'Class Milestones', desc: 'The badge shelf on Home, the full Milestones page, and the community counter.', items: [
            { key: 'milestonesHeading', label: 'Home shelf heading', type: 'text', hint: 'Default: "Your Class Milestones"' },
            { key: 'milestoneCelebrationEyebrow', label: 'Celebration eyebrow', type: 'text', hint: 'Small caps line above the badge name. Default: "Milestone reached"' },
            { key: 'milestoneCelebrationCloseLabel', label: 'Celebration button', type: 'text', hint: 'Closes the celebration. Default: "Keep moving"' },
            { key: 'milestonesViewAllLabel', label: 'Home shelf link', type: 'text', hint: 'Default: "View all"' },
            { key: 'milestonesPageHeading', label: 'Milestones page title', type: 'text', hint: 'Default: "Class Milestones"' },
            { key: 'milestonesPageSubtext', label: 'Milestones page subtitle', type: 'text', hint: "Default: “Every milestone you’ve reached — and the ones still ahead.”" },
            { key: 'milestonesBackLabel', label: 'Back link', type: 'text', hint: 'Default: "Back to home"' },
            { key: 'milestonesBestRunTemplate', label: 'Weekly badge "best run" note', type: 'text', hint: 'Shown beside a weekly badge’s progress when an earlier run was longer than the current one. Use {count} for the number of weeks. Default: "Your best: {count} weeks"' },
            { key: 'milestoneWallHeading', label: 'Wall heading', type: 'text', hint: 'Default: "The Milestone Wall"' },
            { key: 'milestoneWallSubtext', label: 'Wall subtitle', type: 'text', hint: 'Default: "Every milestone this community has reached. Cheer someone on."' },
            { key: 'milestoneWallEmpty', label: 'Wall empty state', type: 'text', hint: 'Default: "The first milestones will appear here soon."' },
            { key: 'communityCounterLabel', label: 'Community counter label', type: 'text', hint: "Sits above the number. Default: “Together we’ve completed”" },
            { key: 'communityCounterCaption', label: 'Community counter caption', type: 'text', hint: 'Sits below the number. Default: "classes and counting"' },
        ]
    },
    {
        section: 'Milestone badge details', desc: 'The card that opens when a member taps a badge: how it is earned, her progress, and the fine print. The fine print describes exactly how the app counts — change the wording freely, but keep the meaning.', items: [
            { key: 'milestoneTapHint', label: "Tap hint under the badges", type: 'text', hint: "Default: “Tap a badge to see how it's earned”" },
            { key: 'milestoneHowLabel', label: "\"How to earn it\" label", type: 'text', hint: "Default: “How to earn it”" },
            { key: 'milestoneNotEarnedLabel', label: "Not-earned label", type: 'text', hint: "Default: “Not yet earned”" },
            { key: 'milestoneEarnedLabel', label: "Earned label (date follows)", type: 'text', hint: "Default: “Earned”" },
            { key: 'milestoneEarnedByTemplate', label: "Someone else's badge (Wall)", type: 'text', hint: "Default: “{name} earned this” Keep the {placeholders}." },
            { key: 'milestoneMetaFirst', label: "First Step subtitle", type: 'text', hint: "Default: “Your very first class”" },
            { key: 'milestoneMetaClassesTemplate', label: "Class-badge subtitle", type: 'text', hint: "Default: “{count} classes” Keep the {placeholders}." },
            { key: 'milestoneMetaWeeksTemplate', label: "Weekly-badge subtitle", type: 'text', hint: "Default: “{count} weeks in a row” Keep the {placeholders}." },
            { key: 'milestoneProgressClassesTemplate', label: "Progress — class badges", type: 'text', hint: "Default: “{current} of {target} classes” Keep the {placeholders}." },
            { key: 'milestoneProgressWeeksTemplate', label: "Progress — weekly badges", type: 'text', hint: "Default: “{current} of {target} weeks in your current run” Keep the {placeholders}." },
            { key: 'milestoneToGoTemplate', label: "\"To go\" line", type: 'text', hint: "Default: “{count} to go” Keep the {placeholders}." },
            { key: 'milestoneYouLabel', label: "\"You\" label (Wall)", type: 'text', hint: "Default: “You”" },
            { key: 'milestoneYouEarnedLabel', label: "Viewer already has it (Wall)", type: 'text', hint: "Default: “You've earned this one too”" },
            { key: 'milestoneHow_first', label: "How to earn — First Step", type: 'text', hint: "Default: “Complete your first class.”" },
            { key: 'milestoneHow_ten', label: "How to earn — 10 Classes Strong", type: 'text', hint: "Default: “Complete 10 classes.”" },
            { key: 'milestoneHow_tfive', label: "How to earn — 25 Class Club", type: 'text', hint: "Default: “Complete 25 classes.”" },
            { key: 'milestoneHow_fifty', label: "How to earn — 50 Class Club", type: 'text', hint: "Default: “Complete 50 classes.”" },
            { key: 'milestoneHow_hundred', label: "How to earn — 100 Class Club", type: 'text', hint: "Default: “Complete 100 classes.”" },
            { key: 'milestoneHow_quarter', label: "How to earn — 250 Class Club", type: 'text', hint: "Default: “Complete 250 classes.”" },
            { key: 'milestoneHow_mo1', label: "How to earn — 1 Month Strong", type: 'text', hint: "Default: “Take at least one class every week for 4 weeks in a row.”" },
            { key: 'milestoneHow_mo3', label: "How to earn — 3 Months Strong", type: 'text', hint: "Default: “Take at least one class every week for 12 weeks in a row.”" },
            { key: 'milestoneHow_mo6', label: "How to earn — 6 Months Strong", type: 'text', hint: "Default: “Take at least one class every week for 26 weeks in a row.”" },
            { key: 'milestoneRuleClass1', label: "Class badges — fine print 1", type: 'textarea', hint: "Default: “Live classes and On Demand classes both count — and so does repeating a class you love.”" },
            { key: 'milestoneRuleClass2', label: "Class badges — fine print 2", type: 'textarea', hint: "Default: “A class counts once it's complete: automatically when you watch most of it, or when you tap Log this workout.”" },
            { key: 'milestoneRuleClass3', label: "Class badges — fine print 3", type: 'textarea', hint: "Default: “Shorts, program workouts and challenge days don't count toward class milestones.”" },
            { key: 'milestoneRuleWeek1', label: "Weekly badges — fine print 1", type: 'textarea', hint: "Default: “Weeks run Sunday through Saturday.”" },
            { key: 'milestoneRuleWeek2', label: "Weekly badges — fine print 2", type: 'textarea', hint: "Default: “A week counts once it ends — so your badge arrives the Sunday after your last week.”" },
            { key: 'milestoneRuleWeek3', label: "Weekly badges — fine print 3", type: 'textarea', hint: "Default: “Miss a week and your run starts again. Badges you've earned are always yours to keep.”" },
            { key: 'milestoneHow_gift26', label: "How to earn — The Gift of Showing Up", type: 'textarea', hint: "Default: “Complete 18 membership workouts between November 1 and December 31, 2026.”" },
            { key: 'milestoneMetaSeasonalTemplate', label: "Gift of Showing Up — subtitle", type: 'text', hint: "Default: “{count} workouts · Holiday Season 2026” Keep the {placeholders}." },
            { key: 'milestoneProgressSeasonalTemplate', label: "Gift of Showing Up — progress", type: 'text', hint: "Default: “{current} of {target} workouts this season” Keep the {placeholders}." },
            { key: 'milestoneRuleSeason1', label: "Gift of Showing Up — fine print 1", type: 'textarea', hint: "Default: “Live and On Demand membership classes both count, including repeats of your favorites.”" },
            { key: 'milestoneRuleSeason2', label: "Gift of Showing Up — fine print 2", type: 'textarea', hint: "Default: “Workouts count once completed, either automatically or when you log them in the app.”" },
            { key: 'milestoneRuleSeason3', label: "Gift of Showing Up — closing line", type: 'textarea', hint: "Default: “You made time for yourself during one of the busiest seasons of the year. That's worth celebrating!”" },
            { key: 'milestoneCelebrationLine_gift26', label: "Gift of Showing Up — celebration line", type: 'textarea', hint: "Shown in the pop-up when she earns it. Default: “Eighteen classes this holiday season. The best gift you gave this year was to yourself.”" },
        ]
    },
    {
        section: 'Purchased Programs Section', desc: 'Shown when users have purchased programs.', items: [
            { key: 'dashboardPurchasedHeading', label: 'Section Heading', type: 'text' },
        ]
    },
    {
        section: 'Weekly Motivation Messages', desc: 'These appear on the dashboard based on how many workouts a user has completed this week. Goal is 3/week.', items: [
            { key: 'motivation0', label: '0 workouts (no streak)', type: 'text', hint: 'Default: "Ready to move? Even a few minutes makes a difference."' },
            { key: 'motivation0streak', label: '0 workouts (has streak)', type: 'text', hint: 'Default: "Let\'s keep that streak alive — you\'ve got this!"' },
            { key: 'motivation1', label: '1 workout', type: 'text', hint: 'Default: "You showed up — that\'s what matters. Two more this week?"' },
            { key: 'motivation2', label: '2 workouts', type: 'text', hint: 'Default: "Almost there! One more and you\'ve hit your goal."' },
            { key: 'motivation3', label: '3 workouts (goal hit! 🎉)', type: 'text', hint: 'Default: "You hit your goal! Three this week — so proud of you."' },
            { key: 'motivation4', label: '4 workouts', type: 'text', hint: 'Default: "Going above and beyond — four this week!"' },
            { key: 'motivation5plus', label: '5+ workouts', type: 'text', hint: 'Default: "Incredible week! You\'re really showing up for yourself."' },
        ]
    },
    {
        section: 'Workouts CTA Card', desc: 'The pink card under the Welcome area. Shown ONLY to non-members (signed-in users without an active membership) — routes them to the Workouts page where Subscribe CTAs handle conversion. Members see the "Your next class" card here instead.', items: [
            { key: 'dashboardQuickLinkTitle', label: 'Card Title', type: 'text', hint: 'Default: "Browse upcoming classes"' },
            { key: 'dashboardQuickLinkSubtitle', label: 'Card Subtitle', type: 'text', hint: 'Default: "See what\'s coming up — live, on-demand, and more"' },
            { key: 'dashboardBrowseLabel', label: 'Browse Programs Link (only shown to users without programs)', type: 'text', hint: 'Default: "Or browse structured programs →"' },
        ]
    },
];
ADMIN_FIELD_INDEX.dashboard = indexFields(DASHBOARD_FIELDS);

function DashboardTab({ unsavedRef }) {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => { fetchSiteContent().then((c) => { setContent(c); setLoading(false); }); }, []);
    const handleChange = (key, value) => { setContent((prev) => ({ ...prev, [key]: value })); setSaved(false); if (unsavedRef) unsavedRef.current = true; };
    const handleSave = async () => { setSaving(true); const ok = await updateSiteContent(content); setSaving(false); if (ok) { setSaved(true); if (unsavedRef) unsavedRef.current = false; setTimeout(() => setSaved(false), 3000); } };

    if (loading) return <Spinner />;

    const fields = DASHBOARD_FIELDS;

    return (
        <div style={{ paddingBottom: 72 }}>
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Dashboard Page</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>Edit the text users see after signing in.</p>
            </div>
            <SectionList tabKey="dashboard" sections={fields} content={content} handleChange={handleChange} />
            <StickyBar saving={saving} saved={saved} onSave={handleSave} hasChanges={!saved} />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  PROGRAMS TAB
// ═══════════════════════════════════════════════════════════════
function ProgramsTab({ unsavedRef }) {
    const getToken = useAdminAuth();
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [previewVideos, setPreviewVideos] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [driveImages, setDriveImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [showImagePicker, setShowImagePicker] = useState(false);
    // Pod management state
    const [pods, setPods] = useState([]);
    const [podsLoading, setPodsLoading] = useState(false);
    const [editingPod, setEditingPod] = useState(null);
    const [podForm, setPodForm] = useState({});
    const [podSaving, setPodSaving] = useState(false);
    const [podUsers, setPodUsers] = useState([]);
    const [podUsersLoading, setPodUsersLoading] = useState(false);
    const [addUserSearch, setAddUserSearch] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    // Weeks management
    const [editingWeek, setEditingWeek] = useState(null);
    const [weekForm, setWeekForm] = useState({});
    const [expandedWeek, setExpandedWeek] = useState(null);
    // Pod delete confirmation
    const [podDeleteConfirm, setPodDeleteConfirm] = useState(null);
    const [podDeleteUsersLoading, setPodDeleteUsersLoading] = useState(false);
    // In-app recorded program videos (Phase 2 — see adr-program-recorded-videos.md)
    const [recordedVideos, setRecordedVideos] = useState([]);
    const [recordedLoading, setRecordedLoading] = useState(false);
    const [recorderOpen, setRecorderOpen] = useState(false);
    const [editingRecordedId, setEditingRecordedId] = useState(null);
    const [recordedEdit, setRecordedEdit] = useState({ title: '', description: '' });

    const loadRecordedVideos = async (programId) => {
        if (!programId) { setRecordedVideos([]); return; }
        setRecordedLoading(true);
        const list = await fetchProgramVideos(programId);
        setRecordedVideos(list || []);
        setRecordedLoading(false);
    };
    const handleDeleteRecordedVideo = async (pvId) => {
        if (!window.confirm('Delete this recorded video? This cannot be undone.')) return;
        // Also strip it from videoOrder so the slot doesn't linger.
        const nextOrder = (form.videoOrder || []).filter((id) => id !== pvId);
        if (nextOrder.length !== (form.videoOrder || []).length) setForm({ ...form, videoOrder: nextOrder });
        await deleteProgramVideo(pvId);
        if (editing && editing !== 'new') loadRecordedVideos(editing);
    };
    const startEditRecorded = (pv) => { setEditingRecordedId(pv.id); setRecordedEdit({ title: pv.title || '', description: pv.description || '' }); };
    const saveRecordedEdit = async () => {
        if (!editingRecordedId) return;
        await updateProgramVideo(editingRecordedId, { title: recordedEdit.title.trim(), description: recordedEdit.description });
        setEditingRecordedId(null);
        if (editing && editing !== 'new') loadRecordedVideos(editing);
    };

    // ── Thumbnail picker for recorded program videos (Mux) ──
    // Mirrors the class-recording picker in Workouts.js: sample evenly-spaced Mux
    // frames and write the chosen frame URL to the programVideos doc. shapeProgramVideo
    // mirrors thumbnailUrl → driveThumbnail, so the pick shows on the program card.
    const [pvThumbnailPicker, setPvThumbnailPicker] = useState(null);
    const openPvThumbnailPicker = (pv) => {
        if (!pv.muxPlaybackId || pv.status !== 'ready') return; // no frames until the asset is ready
        const durationSeconds = pv.durationSeconds || (pv.length || 0) * 60;
        setPvThumbnailPicker({ id: pv.id, playbackId: pv.muxPlaybackId, durationSeconds });
    };
    const getPvThumbnailTimestamps = (durationSeconds) => {
        if (!durationSeconds || durationSeconds < 10) return [0, 2, 5];
        const count = 8;
        const start = Math.floor(durationSeconds * 0.05); // skip black intro
        const end = Math.floor(durationSeconds * 0.90);    // skip outro
        const step = (end - start) / (count - 1);
        return Array.from({ length: count }, (_, i) => Math.round(start + i * step));
    };
    const handlePvThumbnailSelect = async (thumbnailUrl) => {
        if (!pvThumbnailPicker) return;
        const { id } = pvThumbnailPicker;
        // Optimistic local update so the Recorded Videos list reflects it immediately.
        setRecordedVideos((prev) => prev.map((pv) => pv.id === id ? { ...pv, thumbnailUrl } : pv));
        setPvThumbnailPicker(null);
        await updateProgramVideo(id, { thumbnailUrl });
    };

    useEffect(() => { loadPrograms(); }, []);

    const loadPrograms = async () => { setLoading(true); const p = await fetchPrograms(); setPrograms(p || []); setLoading(false); };

    const loadDriveImages = async () => {
        if (driveImages.length > 0) { setShowImagePicker(true); return; }
        setImagesLoading(true);
        const imgs = await fetchProgramImages();
        setDriveImages(imgs || []);
        setImagesLoading(false);
        setShowImagePicker(true);
    };

    const startNew = () => {
        setForm({ name: '', description: '', price: '', bunnyCollectionId: '', facebookGroupUrl: '', imageEmoji: '', imageUrl: '', highlights: '', details: '', order: programs.length + 1, active: true, programType: 'self-paced', weeks: [], videoOrder: [], faqs: [], stripeProductId: '', flodeskWelcomeSegmentId: '', videoDescriptions: {} });
        setEditing('new'); setSaved(false); if (unsavedRef) unsavedRef.current = true; setPreviewVideos([]); setShowImagePicker(false); setPods([]); setEditingPod(null);
        setRecordedVideos([]); setRecorderOpen(false);
    };

    const startEdit = (prog) => {
        setForm({ ...prog, highlights: Array.isArray(prog.highlights) ? prog.highlights.join('\n') : '', programType: prog.programType || 'self-paced', weeks: prog.weeks || [], videoOrder: prog.videoOrder || [], faqs: prog.faqs || [], videoDescriptions: prog.videoDescriptions || {} });
        setEditing(prog.id); setSaved(false); if (unsavedRef) unsavedRef.current = true; setPreviewVideos([]); setShowImagePicker(false); setEditingPod(null);
        if (prog.bunnyCollectionId) loadPreviewVideos(prog.bunnyCollectionId);
        if (prog.programType === 'cohort') loadPods(prog.id);
        setRecorderOpen(false); loadRecordedVideos(prog.id);
    };

    const selectImage = (img) => { setForm({ ...form, imageUrl: img.imageUrl }); setShowImagePicker(false); };
    const removeImage = () => { setForm({ ...form, imageUrl: '' }); };

    const loadPreviewVideos = async (collectionId) => {
        if (!collectionId) return;
        setPreviewLoading(true);
        const vids = await fetchVideosFromCollection(collectionId);
        setPreviewVideos(vids || []);
        setPreviewLoading(false);
    };

    const loadPods = async (programId) => {
        setPodsLoading(true);
        const p = await fetchPods(programId);
        setPods(p || []);
        setPodsLoading(false);
    };

    const handleSave = async () => {
        setSaving(true);
        const data = { ...form, price: parseFloat(form.price) || 0, order: parseInt(form.order) || 0, highlights: form.highlights ? form.highlights.split('\n').map((h) => h.trim()).filter(Boolean) : [] };
        delete data.id; delete data.createdAt; delete data._faqsOpen;
        if (editing === 'new') { const id = await createProgram(data); if (id) await loadPrograms(); }
        else { await updateProgram(editing, data); await loadPrograms(); }
        setSaving(false); setSaved(true); if (unsavedRef) unsavedRef.current = false;
        setTimeout(() => { setSaved(false); setEditing(null); }, 1000);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this program and all its pods? This cannot be undone.\n\nThis will also remove program access from any users who purchased it.')) return;
        // Clean up purchasedPrograms in Clerk for any users who have this program
        try {
            const usersRes = await adminFetch('/api/users', getToken);
            if (usersRes.ok) {
                const usersData = await usersRes.json();
                const affectedUsers = (usersData.users || []).filter((u) => Array.isArray(u.purchasedPrograms) && u.purchasedPrograms.includes(id));
                for (const u of affectedUsers) {
                    await adminFetch('/api/grant-access', getToken, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId: u.id, programId: id, action: 'revoke' }),
                    });
                }
                if (affectedUsers.length > 0) console.log(`Revoked program ${id} from ${affectedUsers.length} user(s)`);
            }
        } catch (e) { console.warn('Non-critical: user cleanup on program delete failed:', e); }
        await deleteProgram(id); await loadPrograms();
    };

    // ── Weeks helpers ──
    const addWeek = () => {
        const weeks = [...(form.weeks || [])];
        const nextNum = weeks.length + 1;
        weeks.push({ weekNumber: nextNum, title: `Week ${nextNum}`, description: '', videoIds: [], releaseDayOffset: (nextNum - 1) * 7 });
        setForm({ ...form, weeks });
        setExpandedWeek(nextNum - 1);
        setTimeout(() => {
            const el = document.getElementById(`week-${nextNum}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };
    const updateWeek = (idx, field, value) => {
        const weeks = [...form.weeks];
        weeks[idx] = { ...weeks[idx], [field]: value };
        setForm({ ...form, weeks });
    };
    const removeWeek = (idx) => {
        if (!window.confirm('Remove this week?')) return;
        const weeks = form.weeks.filter((_, i) => i !== idx);
        setForm({ ...form, weeks });
    };
    const toggleVideoInWeek = (weekIdx, videoId) => {
        const weeks = [...form.weeks];
        const week = { ...weeks[weekIdx] };
        const ids = [...(week.videoIds || [])];
        if (ids.includes(videoId)) { week.videoIds = ids.filter((id) => id !== videoId); }
        else { week.videoIds = [...ids, videoId]; }
        weeks[weekIdx] = week;
        setForm({ ...form, weeks });
    };

    // Per-video description override (keyed by Bunny videoId, stored on the
    // program doc). Empty/whitespace ⇒ remove the key ⇒ revert to the auto-
    // generated description. Saved with the program's existing Save button.
    const updateVideoDescription = (videoId, text) => {
        const next = { ...(form.videoDescriptions || {}) };
        if (text && text.trim()) next[videoId] = text;
        else delete next[videoId];
        setForm({ ...form, videoDescriptions: next });
    };

    const moveWeekVideo = (weekIdx, fromIdx, toIdx) => {
        const weeks = [...form.weeks];
        const week = { ...weeks[weekIdx] };
        const ids = [...(week.videoIds || [])];
        const [item] = ids.splice(fromIdx, 1);
        ids.splice(toIdx, 0, item);
        week.videoIds = ids;
        weeks[weekIdx] = week;
        setForm({ ...form, weeks });
    };

    // ── Video order helpers (self-paced) ──
    // Combined orderable set = Bunny preview videos + in-app recorded videos, so
    // the admin can MIX their order (Phase 3). When there are no recorded videos
    // the set is exactly previewVideos ⇒ Bunny-only behavior is byte-identical.
    const getOrderedVideos = () => {
        const base = [
            ...previewVideos,
            // muxPlaybackId + durationSeconds carried so the in-list Thumbnail button
            // can open the frame picker without re-looking-up the full programVideos doc.
            ...recordedVideos.map((pv) => ({ id: pv.id, title: pv.title || 'Recorded video', length: pv.length || 0, _recorded: true, status: pv.status, muxPlaybackId: pv.muxPlaybackId, durationSeconds: pv.durationSeconds })),
        ];
        if (!form.videoOrder || form.videoOrder.length === 0) return base;
        const ordered = [];
        form.videoOrder.forEach((id) => { const v = base.find((bv) => bv.id === id); if (v) ordered.push(v); });
        base.forEach((v) => { if (!form.videoOrder.includes(v.id)) ordered.push(v); });
        return ordered;
    };
    const moveVideo = (fromIdx, toIdx) => {
        // Operate on the EXACT rendered order so indices always align — even when
        // form.videoOrder is empty or a legacy Bunny-only subset. After any drag,
        // videoOrder becomes the full combined id list (Bunny + recorded).
        const order = getOrderedVideos().map((v) => v.id);
        const [item] = order.splice(fromIdx, 1);
        order.splice(toIdx, 0, item);
        setForm({ ...form, videoOrder: order });
    };

    // ── Pod helpers ──
    const startNewPod = () => {
        const progId = editing !== 'new' ? editing : null;
        setPodForm({ programId: progId, podName: '', startDate: '', lockDate: '', saleStartDate: '', maxParticipants: 15, meetings: [], status: 'upcoming', salesModel: 'immediate', waitlistEmailSubject: '', waitlistEmailBody: '', supportingMaterialUrl: '' });
        setEditingPod('new'); setPodUsers([]); setAddUserSearch('');
    };
    const duplicatePod = (pod) => {
        const newForm = { ...pod, podName: '', startDate: '', lockDate: '', saleStartDate: '', meetings: (pod.meetings || []).map((m) => ({ ...m, date: '', zoomLink: '' })), participants: [], waitlist: [], status: 'upcoming' };
        delete newForm.id; delete newForm.createdAt;
        setPodForm(newForm);
        setEditingPod('new'); setPodUsers([]); setAddUserSearch('');
    };
    const autoGenerateMeetings = () => {
        if (!podForm.startDate || !form.weeks || form.weeks.length === 0) return;
        const startD = new Date(podForm.startDate);
        const meetings = form.weeks.map((week, i) => {
            const meetingDate = new Date(startD);
            meetingDate.setDate(meetingDate.getDate() + (i * 7));
            return { weekNumber: i + 1, title: week.title || `Week ${i + 1} Meeting`, date: meetingDate.toISOString().slice(0, 10), time: '', zoomLink: '' };
        });
        setPodForm({ ...podForm, meetings });
    };
    const startEditPod = (pod) => { setPodForm({ ...pod }); setEditingPod(pod.id); setPodLoadedParticipants(pod.participants || []); loadPodUsers(pod.participants || []); };
    const loadPodUsers = async (participantIds) => {
        if (!participantIds || participantIds.length === 0) { setPodUsers([]); return; }
        setPodUsersLoading(true);
        try {
            const res = await adminFetch('/api/users', getToken);
            if (res.ok) {
                const raw = await res.json();
                const all = Array.isArray(raw) ? raw : (raw.users || []);
                setAllUsers(all);
                setPodUsers(all.filter((u) => participantIds.includes(u.id)));
            }
        } catch (e) { console.error('Error loading pod users:', e); }
        setPodUsersLoading(false);
    };
    const [searchLoading, setSearchLoading] = useState(false);
    const searchUsers = async (term) => {
        if (!term || term.length < 2) return;
        setSearchLoading(true);
        try {
            const res = await adminFetch(`/api/users?search=${encodeURIComponent(term)}`, getToken);
            if (res.ok) { const raw = await res.json(); setAllUsers(Array.isArray(raw) ? raw : (raw.users || [])); }
        } catch (e) { }
        setSearchLoading(false);
    };
    const addUserToPod = (userId) => {
        const participants = [...(podForm.participants || [])];
        if (!participants.includes(userId)) {
            participants.push(userId);
            setPodForm({ ...podForm, participants });
            const user = allUsers.find((u) => u.id === userId);
            if (user) setPodUsers((prev) => [...prev, user]);
        }
        setAddUserSearch('');
    };
    const removeUserFromPod = (userId) => {
        const participants = (podForm.participants || []).filter((id) => id !== userId);
        setPodForm({ ...podForm, participants });
        setPodUsers((prev) => prev.filter((u) => u.id !== userId));
    };
    // Pod membership ⇒ program access (see grantProgramToUsers). Updates the
    // local user lists so the "No access" flags clear without a reload.
    const [grantingIds, setGrantingIds] = useState([]);
    const [podAccessNotice, setPodAccessNotice] = useState(null);
    const grantPodAccess = async (ids, programId, label = '') => {
        if (!ids.length || !programId) return;
        setGrantingIds(ids);
        const { granted, failed } = await grantProgramToUsers(ids, programId, getToken);
        const mark = (list) => list.map((u) => (granted.includes(u.id) ? { ...u, purchasedPrograms: Array.from(new Set([...(u.purchasedPrograms || []), programId])) } : u));
        setAllUsers((prev) => mark(prev));
        setPodUsers((prev) => mark(prev));
        setGrantingIds([]);
        const nameOf = (id) => { const u = allUsers.find((x) => x.id === id) || podUsers.find((x) => x.id === id); return u ? (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email) : id; };
        if (failed.length) {
            setPodAccessNotice({ text: `Couldn't grant program access to ${failed.map(nameOf).join(', ')}. They're in the pod but can't open the program yet.`, failed, programId });
        } else if (granted.length) {
            setPodAccessNotice({ text: `${label}Program access granted to ${granted.length === 1 ? nameOf(granted[0]) : `${granted.length} people`}.`, failed: [], programId });
        }
    };
    // Participants the editor loaded — Save applies only the admin's changes
    // against these to the pod's CURRENT list (see applyPodMembershipDelta).
    const [podLoadedParticipants, setPodLoadedParticipants] = useState([]);
    const savePodChanges = async (podId, data) => {
        const { participants = [], ...rest } = data;
        // The waitlist is display-only here. Saving the copy loaded when the
        // editor opened would erase anyone who joined while it was open.
        delete rest.waitlist;
        const ok = await updatePod(podId, rest);
        if (!ok) return null;
        const add = participants.filter((id) => !podLoadedParticipants.includes(id));
        const remove = podLoadedParticipants.filter((id) => !participants.includes(id));
        if (!add.length && !remove.length) return participants;
        return applyPodMembershipDelta(podId, add, remove);
    };
    const moveUserToPod = async (userId, targetPodId) => {
        const targetPod = pods.find((p) => p.id === targetPodId);
        if (!targetPod) return;
        // One transaction: out of this pod and into the target together.
        const ok = await movePodMember(userId, editingPod !== 'new' ? editingPod : null, targetPodId);
        if (!ok) { setPodAccessNotice({ text: "Couldn't move that person — nothing was changed. Please try again.", failed: [], programId: null }); return; }
        removeUserFromPod(userId);
        setPodLoadedParticipants((prev) => prev.filter((id) => id !== userId));
        await grantPodAccess([userId], targetPod.programId || editing);
    };
    const addMeeting = () => {
        const meetings = [...(podForm.meetings || [])];
        const nextNum = meetings.length + 1;
        meetings.push({ weekNumber: nextNum, date: '', time: '', zoomLink: '', title: `Week ${nextNum} Meeting` });
        setPodForm({ ...podForm, meetings });
        setTimeout(() => {
            const el = document.getElementById(`meeting-${nextNum}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };
    const updateMeeting = (idx, field, value) => {
        const meetings = [...podForm.meetings];
        meetings[idx] = { ...meetings[idx], [field]: value };
        setPodForm({ ...podForm, meetings });
    };
    const removeMeeting = (idx) => {
        const meetings = podForm.meetings.filter((_, i) => i !== idx);
        setPodForm({ ...podForm, meetings });
    };
    const handleSavePod = async () => {
        setPodSaving(true);
        const data = { ...podForm, maxParticipants: parseInt(podForm.maxParticipants) || 15 };
        delete data.id; delete data.createdAt;
        if (!data.programId && editing !== 'new') data.programId = editing;
        let finalParticipants;
        if (editingPod === 'new') { finalParticipants = (await createPod(data)) ? (data.participants || []) : null; }
        else { finalParticipants = await savePodChanges(editingPod, data); }
        // Everyone in the pod gets the program (idempotent; covers newly added
        // people and anyone already in the pod without access).
        if (finalParticipants) await grantPodAccess(participantsNeedingAccess(finalParticipants, [...allUsers, ...podUsers], data.programId), data.programId);
        else setPodAccessNotice({ text: "Couldn't save the pod — please try again.", failed: [], programId: null });
        if (editing !== 'new') await loadPods(editing);
        setPodSaving(false); setEditingPod(null);
    };
    const handleDeletePod = async (pod) => {
        setPodDeleteConfirm({ podId: pod.id, pod, users: [] });
        if (pod.participants && pod.participants.length > 0) {
            setPodDeleteUsersLoading(true);
            try {
                const res = await adminFetch('/api/users', getToken);
                if (res.ok) { const raw = await res.json(); const all = Array.isArray(raw) ? raw : (raw.users || []); setPodDeleteConfirm((prev) => prev ? { ...prev, users: all.filter((u) => pod.participants.includes(u.id)) } : null); }
            } catch (e) { }
            setPodDeleteUsersLoading(false);
        }
    };
    const confirmPodDelete = async () => {
        if (!podDeleteConfirm) return;
        await deletePod(podDeleteConfirm.podId);
        setPodDeleteConfirm(null);
        if (editing !== 'new') await loadPods(editing);
    };

    if (loading) return <Spinner />;

    // ── EDITING A PROGRAM ──────────────────────────────────────
    if (editing !== null) {
        const isCohort = form.programType === 'cohort';
        const orderedVids = getOrderedVideos();
        // Video list for the description editor, ordered the way users encounter
        // the videos: self-paced uses the drag-ordered list; cohort flattens the
        // weeks (in week order, then video order within each), then appends any
        // videos not yet assigned to a week. Keyed by id, so order never affects
        // what's saved — purely a scannability/parity improvement.
        const descEditorVideos = isCohort
            ? [
                ...(form.weeks || []).flatMap((w) => (w.videoIds || [])
                    .map((vid) => previewVideos.find((pv) => pv.id === vid))
                    .filter(Boolean)),
                ...previewVideos.filter((v) => !(form.weeks || []).some((w) => (w.videoIds || []).includes(v.id))),
            ]
            : orderedVids;

        return (
            <div style={{ paddingBottom: 72 }}>
                <button className="mom-btn mom-btn-ghost" onClick={() => {
                    if (!saved && editing !== null && !window.confirm('You have unsaved changes. Leave anyway?')) return;
                    setEditing(null); setEditingPod(null);
                }} style={{ marginBottom: 20, color: theme.colors.warmGray }}>← Back to programs</button>
                <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, padding: '32px', boxShadow: theme.shadows.sm }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal }}>
                            {editing === 'new' ? 'Create New Program' : 'Edit Program'}
                        </h3>
                        <div onClick={() => setForm({ ...form, active: form.active === false ? true : false })} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 10, cursor: 'pointer', background: form.active !== false ? hexToRgba(theme.colors.success, 0.08) : hexToRgba(theme.colors.warmGray, 0.1), border: `1.5px solid ${form.active !== false ? hexToRgba(theme.colors.success, 0.3) : theme.colors.creamDark}`, transition: 'all 0.2s' }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: form.active !== false ? theme.colors.success : theme.colors.warmGray }} />
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: form.active !== false ? theme.colors.success : theme.colors.warmGray }}>{form.active !== false ? 'Active' : 'Inactive'}</span>
                        </div>
                    </div>

                    {/* ── Program Type Toggle ──────────────── */}
                    <div style={{ marginBottom: 24 }}>
                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 10, display: 'block' }}>Program Type</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {['self-paced', 'cohort'].map((t) => (
                                <button key={t} className="mom-btn" onClick={() => setForm({ ...form, programType: t })}
                                    style={{ padding: '10px 24px', borderRadius: 12, fontSize: 14, fontWeight: 600, border: form.programType === t ? 'none' : `1.5px solid ${theme.colors.creamDark}`, background: form.programType === t ? theme.colors.charcoal : theme.colors.white, color: form.programType === t ? theme.colors.white : theme.colors.charcoal }}>
                                    {t === 'self-paced' ? 'Self-Paced' : 'Cohort (Pods)'}
                                </button>
                            ))}
                        </div>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 8 }}>
                            {isCohort ? 'Cohort programs have pods with start dates, limited spots, and weekly content unlocks.' : 'Self-paced programs give immediate access to all content in a set order.'}
                        </p>
                    </div>

                    {/* ── Basic fields (short fields two-up; long copy full-width) ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', columnGap: 20, alignItems: 'start' }}>
                    {[
                        { key: 'name', label: 'Program Name', type: 'text', placeholder: 'e.g. The 5 to 20 Method' },
                        { key: 'price', label: 'Price ($)', type: 'text', placeholder: '49.99' },
                        { key: 'description', label: 'Short Description (shown on cards)', type: 'textarea', placeholder: 'One or two sentences', full: true },
                        { key: 'details', label: 'Full Details (shown on program page)', type: 'textarea', placeholder: 'Detailed description', rows: 5, full: true },
                    ].map((f) => (
                        <div key={f.key} style={{ gridColumn: f.full ? '1 / -1' : 'auto', marginBottom: 20 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>{f.label}</label>
                            {(f.key === 'description' || f.key === 'details') ? (
                                <RichTextArea value={form[f.key] || ''} onChange={(val) => setForm({ ...form, [f.key]: val })} placeholder={f.placeholder} rows={f.rows || 3} />
                            ) : (
                                <input type="text" className="mom-input" value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} />
                            )}
                        </div>
                    ))}
                    </div>

                    {/* ── Stripe Product ID (right after price) ──────────────── */}
                    <div style={{ marginBottom: 20, marginTop: -8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray }}>Stripe Product ID</label>
                            {form.stripeProductId && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: hexToRgba(theme.colors.success, 0.1), color: theme.colors.success }}>Connected</span>}
                        </div>
                        <input type="text" className="mom-input" value={form.stripeProductId || ''} onChange={(e) => setForm({ ...form, stripeProductId: e.target.value })} placeholder="prod_abc123" style={{ fontSize: 13 }} />
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 6, lineHeight: 1.5 }}>
                            Optional — needed for program-specific coupons. Create a product in <strong>Stripe Dashboard → Products</strong>, then paste the ID here (starts with <code style={{ background: theme.colors.creamDark, padding: '1px 5px', borderRadius: 4 }}>prod_</code>).
                        </p>
                    </div>

                    {/* ── Welcome Email Segment ID (per-program override) ─────── */}
                    {/* Overrides emailAutomations.programWelcomeGeneric for THIS program only. */}
                    {/* Leave blank to fall back to the generic Program Welcome segment. */}
                    {/* See docs/decisions/adr-email-automations.md §2.8. */}
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray }}>Welcome Email Segment ID</label>
                            {form.flodeskWelcomeSegmentId && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: hexToRgba(theme.colors.terracotta, 0.1), color: theme.colors.terracotta }}>Override</span>}
                        </div>
                        <input type="text" className="mom-input" value={form.flodeskWelcomeSegmentId || ''} onChange={(e) => setForm({ ...form, flodeskWelcomeSegmentId: e.target.value })} placeholder="Leave blank to use the generic Program Welcome" style={{ fontSize: 13 }} />
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 6, lineHeight: 1.5 }}>
                            Optional. Overrides the generic Program Welcome for THIS program only. Manage segments in <strong>Admin → Emails</strong>. If both are blank, no email is sent when users buy this program.
                        </p>
                    </div>

                    {/* ── Remaining fields (short fields two-up; long copy full-width) ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', columnGap: 20, alignItems: 'start' }}>
                    {[
                        { key: 'highlights', label: "Highlights / What's Included (one per line)", type: 'textarea', placeholder: '4-week program\nVideo guides\nPrivate community', rows: 4, full: true },
                        { key: 'bunnyCollectionId', label: "Bunny Stream Collection ID", type: 'text', placeholder: 'Paste collection ID from Bunny dashboard' },
                        { key: 'facebookGroupUrl', label: 'Facebook Group URL', type: 'text', placeholder: 'https://facebook.com/groups/...' },
                        { key: 'order', label: 'Display Order (lower = first)', type: 'text', placeholder: '1' },
                    ].map((f) => (
                        <div key={f.key} style={{ gridColumn: f.full ? '1 / -1' : 'auto', marginBottom: 20 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>{f.label}</label>
                            {f.type === 'textarea' ? (
                                <textarea className="mom-input" value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} rows={f.rows || 3} style={{ resize: 'vertical', lineHeight: 1.6 }} />
                            ) : (
                                <input type="text" className="mom-input" value={form[f.key] || ''} onChange={(e) => {
                                    setForm({ ...form, [f.key]: e.target.value });
                                    if (f.key === 'bunnyCollectionId' && e.target.value.length > 10) loadPreviewVideos(e.target.value);
                                }} placeholder={f.placeholder} />
                            )}
                        </div>
                    ))}
                    </div>

                    {/* ── FAQs ──────────────── */}
                    <ProgramEditorSection title={`FAQs${(form.faqs || []).length > 0 ? ` (${(form.faqs || []).length})` : ''}`} defaultOpen={false}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 12 }}>Add frequently asked questions for this program. Shown as a collapsible section on the program page.</p>
                        {(form.faqs || []).map((faq, idx) => (
                            <div key={idx} style={{ padding: '16px', background: theme.colors.cream, borderRadius: 12, marginBottom: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray }}>FAQ {idx + 1}</span>
                                    <button className="mom-btn mom-btn-ghost" onClick={() => { const faqs = form.faqs.filter((_, i) => i !== idx); setForm({ ...form, faqs }); }} style={{ fontSize: 11, color: theme.colors.danger, padding: '2px 8px' }}>Remove</button>
                                </div>
                                <input type="text" className="mom-input" value={faq.question || ''} onChange={(e) => { const faqs = [...form.faqs]; faqs[idx] = { ...faqs[idx], question: e.target.value }; setForm({ ...form, faqs }); }} placeholder="Question" style={{ marginBottom: 8 }} />
                                <RichTextArea value={faq.answer || ''} onChange={(val) => { const faqs = [...form.faqs]; faqs[idx] = { ...faqs[idx], answer: val }; setForm({ ...form, faqs }); }} placeholder="Answer (supports **bold**, *italic*, [links](url))" rows={2} />
                            </div>
                        ))}
                        <button className="mom-btn mom-btn-outline" onClick={() => setForm({ ...form, faqs: [...(form.faqs || []), { question: '', answer: '' }] })} style={{ fontSize: 13, padding: '8px 20px' }}>+ Add FAQ</button>
                    </ProgramEditorSection>

                    {/* ── Content & Media ──────────────── */}
                    <ProgramEditorSection title={`Cover Image & Videos${form.imageUrl ? ' · Image ✓' : ''}${previewVideos.length > 0 ? ` · ${previewVideos.length} videos` : ''}`} defaultOpen={false}>
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Program Cover Image</label>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 12 }}>Select an image from your "Program Images" folder in Bunny Storage. Upload images there first, then pick one here.</p>
                            {form.imageUrl ? (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ width: '100%', maxWidth: 400, borderRadius: 12, overflow: 'hidden', border: `1.5px solid ${theme.colors.creamDark}`, marginBottom: 8 }}>
                                        <img src={form.imageUrl} alt="Program cover" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button className="mom-btn mom-btn-outline" onClick={loadDriveImages} style={{ padding: '8px 20px', fontSize: 13 }}>Change Image</button>
                                        <button className="mom-btn mom-btn-ghost" onClick={removeImage} style={{ color: theme.colors.danger, fontSize: 13 }}>Remove Image</button>
                                    </div>
                                </div>
                            ) : (
                                <button className="mom-btn mom-btn-outline" onClick={loadDriveImages} style={{ padding: '12px 24px', fontSize: 14 }}>
                                    {imagesLoading ? 'Loading images...' : 'Choose from Image Library'}
                                </button>
                            )}
                            {showImagePicker && (
                                <div style={{ marginTop: 16, background: theme.colors.cream, borderRadius: 12, padding: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal }}>Select an image ({driveImages.length} available)</p>
                                        <button className="mom-btn mom-btn-ghost" onClick={() => setShowImagePicker(false)} style={{ fontSize: 13, color: theme.colors.warmGray }}>Close</button>
                                    </div>
                                    {imagesLoading ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><FaSpinner style={{ fontSize: 24, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} /></div>
                                    ) : driveImages.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '24px' }}>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 8 }}>No images found.</p>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>Upload images to the "Program Images" folder in Bunny Storage, then click "Choose from Image Library" again.</p>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                                            {driveImages.map((img) => (
                                                <div key={img.id} onClick={() => selectImage(img)}
                                                    style={{ borderRadius: 10, overflow: 'hidden', cursor: 'pointer', border: form.imageUrl === img.imageUrl ? `3px solid ${theme.colors.terracotta}` : `2px solid ${theme.colors.creamDark}`, transition: 'all 0.2s', position: 'relative' }}>
                                                    <img src={img.thumbnailUrl} alt={img.title} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.charcoal, padding: '6px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{img.title}</p>
                                                    {form.imageUrl === img.imageUrl && (
                                                        <div style={{ position: 'absolute', top: 4, right: 4, background: theme.colors.terracotta, color: theme.colors.white, width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>✓</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <button className="mom-btn mom-btn-ghost" onClick={() => { setDriveImages([]); loadDriveImages(); }} style={{ marginTop: 12, fontSize: 12, color: theme.colors.terracotta }}>Refresh images</button>
                                </div>
                            )}
                        </div>

                        {/* ── Video Preview / Ordering ──────────────── */}
                        {/* Gate relaxed (Phase 3): show the order UI when there are
                            Bunny videos OR in-app recorded videos, so recorded-only
                            and mixed programs can be ordered. No recorded videos ⇒
                            identical to the prior Bunny-only behavior. */}
                        {(form.bunnyCollectionId || recordedVideos.length > 0) && (
                            <div style={{ marginBottom: 20, background: theme.colors.cream, borderRadius: 12, padding: '20px' }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 12 }}>
                                    {previewLoading ? 'Loading…' : `${previewVideos.length} Bunny video${previewVideos.length !== 1 ? 's' : ''}${recordedVideos.length > 0 ? ` + ${recordedVideos.length} recorded` : ''}`}
                                </p>

                                {/* Self-paced: drag to reorder (Bunny + recorded, mixed) */}
                                {!isCohort && (previewVideos.length > 0 || recordedVideos.length > 0) && (
                                    <div>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 12 }}>Drag to reorder — this is the order users see them. Recorded and Bunny videos can be mixed freely.</p>
                                        <DragReorderList
                                            items={orderedVids}
                                            idKey="id"
                                            onReorder={(fromIdx, toIdx) => moveVideo(fromIdx, toIdx)}
                                            renderItem={(v, i) => (
                                                <>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700, color: theme.colors.terracotta, width: 28, textAlign: 'center', flexShrink: 0 }}>{i + 1}</span>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, flex: 1 }}>{v.title}{v.length ? ` — ${v.length} min` : ''}</span>
                                                    {/* Thumbnail picker for recorded videos only — Bunny thumbnails come
                                                        from Bunny, not Mux. stopPropagation on mousedown so the click can't
                                                        start a row drag (mirrors the up/down controls' guard). */}
                                                    {v._recorded && v.status === 'ready' && v.muxPlaybackId && (
                                                        <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); openPvThumbnailPicker(v); }}
                                                            style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.charcoal, background: theme.colors.white, border: `1px solid ${theme.colors.creamDark}`, borderRadius: 6, padding: '3px 10px', cursor: 'pointer', flexShrink: 0 }}>Thumbnail</button>
                                                    )}
                                                    {v._recorded && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: hexToRgba(theme.colors.terracotta, 0.12), color: theme.colors.terracotta, flexShrink: 0 }}>RECORDED</span>}
                                                </>
                                            )}
                                        />
                                    </div>
                                )}

                                {/* Cohort: just list videos */}
                                {isCohort && previewVideos.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {previewVideos.map((v) => (
                                            <p key={v.id} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>{v.title} — {v.length} min</p>
                                        ))}
                                    </div>
                                )}

                                {/* ── Per-video descriptions ──────────────────────
                                    Custom description per video, overriding the text
                                    auto-generated from the title (length + muscle
                                    groups). Placeholder shows that auto default, so the
                                    admin sees exactly what users get by default and what
                                    they're replacing. Blank ⇒ reverts to auto. Stored on
                                    the program doc (form.videoDescriptions) and saved with
                                    the program's Save button. */}
                                {previewVideos.length > 0 && (
                                    <div style={{ marginTop: 20, paddingTop: 18, borderTop: `1px solid ${theme.colors.creamDark}` }}>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 4 }}>Video Descriptions</p>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 14, lineHeight: 1.5 }}>
                                            Write a custom description for any video. Leave blank to use the auto-generated text shown in grey.
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {descEditorVideos.map((v) => {
                                                const custom = (form.videoDescriptions || {})[v.id] || '';
                                                const hasCustom = !!custom.trim();
                                                return (
                                                    <div key={v.id} style={{ background: theme.colors.white, borderRadius: 10, padding: '12px 14px', border: `1px solid ${theme.colors.creamDark}` }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {v.title} <span style={{ color: theme.colors.warmGray, fontWeight: 400 }}>— {v.length} min</span>
                                                            </span>
                                                            <span style={{ flexShrink: 0, fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 999, background: hasCustom ? hexToRgba(theme.colors.terracotta, 0.12) : theme.colors.cream, color: hasCustom ? theme.colors.terracotta : theme.colors.warmGray }}>
                                                                {hasCustom ? 'Custom' : 'Auto'}
                                                            </span>
                                                            {hasCustom && (
                                                                <button onClick={() => updateVideoDescription(v.id, '')} style={{ flexShrink: 0, fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                                                    Reset to auto
                                                                </button>
                                                            )}
                                                        </div>
                                                        <textarea
                                                            className="mom-input"
                                                            value={custom}
                                                            onChange={(e) => updateVideoDescription(v.id, e.target.value)}
                                                            placeholder={v.description || 'Auto-generated from the video title'}
                                                            rows={2}
                                                            style={{ resize: 'vertical', width: '100%', fontFamily: 'var(--mom-font-sans)', fontSize: 13 }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </ProgramEditorSection>

                    {/* ═══ IN-APP RECORDED VIDEOS (Phase 2) ═══
                        Record program videos with the in-app broadcaster (Mux),
                        mixable with Bunny. Only for SAVED programs (needs an id +
                        the programVideos doc is keyed to it). See adr-program-recorded-videos.md. */}
                    <ProgramEditorSection title={`Recorded Videos (in-app)${recordedVideos.length > 0 ? ` · ${recordedVideos.length}` : ''}`} subtitle="Record workouts with the in-app camera — saved to Mux and mixed into this program alongside any Bunny videos." defaultOpen={false}>
                        {editing === 'new' ? (
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>Save the program first, then reopen it to record videos.</p>
                        ) : (
                            <div>
                                <button className="mom-btn mom-btn-primary" onClick={() => setRecorderOpen(true)} style={{ padding: '10px 22px', fontSize: 14, marginBottom: 16 }}>+ Record a video</button>
                                {recordedLoading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><FaSpinner style={{ fontSize: 20, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} /></div>
                                ) : recordedVideos.length === 0 ? (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>No recorded videos yet.</p>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {recordedVideos.map((pv) => (
                                            editingRecordedId === pv.id ? (
                                                <div key={pv.id} style={{ padding: '14px 16px', background: theme.colors.cream, borderRadius: 12 }}>
                                                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Title</label>
                                                    <input type="text" className="mom-input" value={recordedEdit.title} onChange={(e) => setRecordedEdit({ ...recordedEdit, title: e.target.value })} style={{ marginBottom: 10 }} />
                                                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Description</label>
                                                    <RichTextArea value={recordedEdit.description} onChange={(val) => setRecordedEdit({ ...recordedEdit, description: val })} placeholder="Shown on the video in the program" rows={3} />
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                                        <button className="mom-btn mom-btn-primary" onClick={saveRecordedEdit} style={{ padding: '8px 20px', fontSize: 13 }}>Save</button>
                                                        <button className="mom-btn mom-btn-ghost" onClick={() => setEditingRecordedId(null)} style={{ fontSize: 13, color: theme.colors.warmGray }}>Cancel</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div key={pv.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: theme.colors.cream, borderRadius: 12 }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pv.title || 'Recorded video'}</p>
                                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 2 }}>{pv.length ? `${pv.length} min · ` : ''}{pv.status === 'ready' ? 'Ready' : 'Processing…'}</p>
                                                    </div>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 8, flexShrink: 0, background: pv.status === 'ready' ? hexToRgba(theme.colors.success, 0.12) : theme.colors.creamDark, color: pv.status === 'ready' ? theme.colors.success : theme.colors.warmGray }}>
                                                        {pv.status === 'ready' ? 'READY' : 'PROCESSING'}
                                                    </span>
                                                    {pv.status === 'ready' && pv.muxPlaybackId && (
                                                        <button className="mom-btn mom-btn-ghost" onClick={() => openPvThumbnailPicker(pv)} style={{ fontSize: 12, color: theme.colors.charcoal, padding: '4px 8px', flexShrink: 0 }}>Thumbnail</button>
                                                    )}
                                                    <button className="mom-btn mom-btn-ghost" onClick={() => startEditRecorded(pv)} style={{ fontSize: 12, color: theme.colors.charcoal, padding: '4px 8px', flexShrink: 0 }}>Edit</button>
                                                    <button className="mom-btn mom-btn-ghost" onClick={() => handleDeleteRecordedVideo(pv.id)} style={{ fontSize: 12, color: theme.colors.danger, padding: '4px 8px', flexShrink: 0 }}>Delete</button>
                                                </div>
                                            )
                                        ))}
                                        <button className="mom-btn mom-btn-ghost" onClick={() => loadRecordedVideos(editing)} style={{ fontSize: 12, color: theme.colors.terracotta, alignSelf: 'flex-start', marginTop: 4 }}>Refresh</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </ProgramEditorSection>

                    {/* ═══ COHORT: WEEKS MANAGEMENT ═══ */}
                    {isCohort && (
                        <ProgramEditorSection title={`Content Weeks${(form.weeks || []).length > 0 ? ` (${(form.weeks || []).length})` : ''}`} subtitle="Organize videos into weeks. Each week unlocks based on days after pod start date." defaultOpen={true}>
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                                    <button className="mom-btn mom-btn-outline" onClick={addWeek} style={{ padding: '8px 20px', fontSize: 13 }}>+ Add Week</button>
                                </div>
                                {(form.weeks || []).map((week, wi) => {
                                    // Videos assignable to a week = Bunny collection videos + in-app
                                    // recorded videos (programVideos). Recorded ids share videoIds[]
                                    // with Bunny ids (distinct namespaces) and ProgramView already
                                    // resolves week.videoIds against the combined list — so weeks can
                                    // drip recorded videos too. Recorded-only cohort programs (no Bunny
                                    // collection) rely on this to have any assignable videos at all.
                                    const weekAssignVideos = [
                                        ...previewVideos,
                                        ...recordedVideos.map((pv) => ({
                                            id: pv.id,
                                            title: pv.title || 'Recorded video',
                                            length: pv.length || (pv.durationSeconds ? Math.round(pv.durationSeconds / 60) : 0),
                                            _recorded: true,
                                            status: pv.status,
                                        })),
                                    ];
                                    const videoCount = weekAssignVideos.length > 0 ? (week.videoIds || []).filter((id) => weekAssignVideos.some((pv) => pv.id === id)).length : (week.videoIds || []).length;
                                    const isOpen = expandedWeek === wi;
                                    return (
                                        <div key={wi} id={`week-${week.weekNumber}`} style={{ background: theme.colors.cream, borderRadius: 12, marginBottom: 8, overflow: 'hidden' }}>
                                            {/* Collapsed header — always visible */}
                                            <button onClick={() => setExpandedWeek(isOpen ? null : wi)} style={{ width: '100%', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: 'left' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                                                    <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 15, fontStyle: 'italic', color: theme.colors.charcoal, fontWeight: 600, flexShrink: 0 }}>Week {week.weekNumber}</span>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{week.title || ''}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, background: theme.colors.white, padding: '2px 10px', borderRadius: 20 }}>{videoCount} video{videoCount !== 1 ? 's' : ''}</span>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>Day {week.releaseDayOffset ?? 0}</span>
                                                    <span style={{ fontSize: 16, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                                                </div>
                                            </button>
                                            {/* Expanded content */}
                                            {isOpen && (
                                                <div style={{ padding: '0 20px 20px', borderTop: `1px solid ${theme.colors.creamDark}` }}>
                                                    <div style={{ paddingTop: 16 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                                                            <button className="mom-btn mom-btn-ghost" onClick={() => removeWeek(wi)} style={{ fontSize: 12, color: theme.colors.danger }}>Remove Week</button>
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                                                            <div>
                                                                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Title</label>
                                                                <input type="text" className="mom-input" value={week.title || ''} onChange={(e) => updateWeek(wi, 'title', e.target.value)} placeholder={`Week ${week.weekNumber}`} />
                                                            </div>
                                                            <div>
                                                                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Unlocks (days after start)</label>
                                                                <input type="number" className="mom-input" value={week.releaseDayOffset ?? 0} onChange={(e) => updateWeek(wi, 'releaseDayOffset', parseInt(e.target.value) || 0)} />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 4, display: 'block' }}>Description (optional)</label>
                                                            <input type="text" className="mom-input" value={week.description || ''} onChange={(e) => updateWeek(wi, 'description', e.target.value)} placeholder="Brief description of this week" />
                                                        </div>
                                                        {weekAssignVideos.length > 0 && (
                                                            <div style={{ marginTop: 14 }}>
                                                                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 8, display: 'block' }}>Assign Videos</label>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                    {weekAssignVideos.map((v) => {
                                                                        const inWeek = (week.videoIds || []).includes(v.id);
                                                                        const inOtherWeek = (form.weeks || []).some((w, owi) => owi !== wi && (w.videoIds || []).includes(v.id));
                                                                        const isProcessing = v._recorded && v.status && v.status !== 'ready';
                                                                        return (
                                                                            <div key={v.id} onClick={() => !inOtherWeek && toggleVideoInWeek(wi, v.id)}
                                                                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, cursor: inOtherWeek ? 'default' : 'pointer', background: inWeek ? hexToRgba(theme.colors.terracotta, 0.08) : theme.colors.white, border: inWeek ? `1.5px solid ${theme.colors.terracotta}` : `1px solid ${theme.colors.creamDark}`, opacity: inOtherWeek && !inWeek ? 0.4 : 1, transition: 'all 0.15s' }}>
                                                                                <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${inWeek ? theme.colors.terracotta : theme.colors.creamDark}`, background: inWeek ? theme.colors.terracotta : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                                    {inWeek && <span style={{ color: theme.colors.white, fontSize: 12 }}>✓</span>}
                                                                                </div>
                                                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal }}>{v.title} — {v.length} min</span>
                                                                                {v._recorded && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: theme.colors.terracotta, background: hexToRgba(theme.colors.terracotta, 0.1), padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>RECORDED</span>}
                                                                                {isProcessing && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, flexShrink: 0 }}>· processing</span>}
                                                                                {inOtherWeek && !inWeek && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginLeft: 'auto' }}>In another week</span>}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                                {/* Reorder assigned videos */}
                                                                {(week.videoIds || []).length > 1 && (
                                                                    <div style={{ marginTop: 12 }}>
                                                                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Video Order</label>
                                                                        <DragReorderList
                                                                            items={(week.videoIds || []).map((vid) => weekAssignVideos.find((pv) => pv.id === vid)).filter(Boolean)}
                                                                            idKey="id"
                                                                            onReorder={(fromIdx, toIdx) => moveWeekVideo(wi, fromIdx, toIdx)}
                                                                            renderItem={(v, vi) => (
                                                                                <>
                                                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, color: theme.colors.terracotta, width: 24, textAlign: 'center', flexShrink: 0 }}>{vi + 1}</span>
                                                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, flex: 1 }}>{v.title} — {v.length} min</span>
                                                                                </>
                                                                            )}
                                                                            compact
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {/* Inline add next week */}
                                                        {wi === (form.weeks || []).length - 1 && (
                                                            <button className="mom-btn mom-btn-ghost" onClick={addWeek} style={{ fontSize: 12, color: theme.colors.terracotta, marginTop: 10, fontWeight: 600 }}>+ Add Another Week</button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {(!form.weeks || form.weeks.length === 0) && (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, padding: '16px 0' }}>No weeks created yet. Add weeks to organize your content schedule.</p>
                                )}
                            </div>
                        </ProgramEditorSection>
                    )}

                    {/* ═══ COHORT: POD MANAGEMENT ═══ */}
                    {isCohort && editing !== 'new' && (
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <div>
                                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, display: 'block' }}>Pods</label>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 4 }}>Each pod is a group of participants with a shared start date and meeting schedule.</p>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.terracotta, marginTop: 4 }}>Tip: The Pods tab has a dedicated view for managing pods across all programs.</p>
                                </div>
                                <button className="mom-btn mom-btn-outline" onClick={startNewPod} style={{ padding: '8px 20px', fontSize: 13 }}>+ New Pod</button>
                            </div>

                            <PodAccessNotice notice={podAccessNotice} onDismiss={() => setPodAccessNotice(null)}
                                onRetry={() => podAccessNotice && grantPodAccess(podAccessNotice.failed, podAccessNotice.programId)} />
                            {/* Pod editor */}
                            {editingPod !== null && (
                                <div style={{ background: theme.colors.cream, borderRadius: 12, padding: '24px', marginBottom: 16, border: `2px solid ${theme.colors.terracotta}` }}>
                                    <h4 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 16 }}>{editingPod === 'new' ? 'Create New Pod' : 'Edit Pod'}</h4>
                                    <PodEditor
                                        podForm={podForm} setPodForm={setPodForm} editingPod={editingPod}
                                        siblingPods={pods.filter((p) => p.id !== editingPod)}
                                        podUsers={podUsers} podUsersLoading={podUsersLoading}
                                        addUserSearch={addUserSearch} setAddUserSearch={setAddUserSearch}
                                        allUsers={allUsers} onSearchUsers={searchUsers} searchLoading={searchLoading}
                                        addUserToPod={addUserToPod} removeUserFromPod={removeUserFromPod} moveUserToPod={moveUserToPod}
                                        addMeeting={addMeeting} updateMeeting={updateMeeting} removeMeeting={removeMeeting}
                                        meetingIdPrefix="meeting"
                                        programWeeks={form.weeks}
                                        onAutoGenMeetings={form.weeks && form.weeks.length > 0 ? autoGenerateMeetings : null}
                                        programId={podForm.programId || (editing !== 'new' ? editing : null)}
                                        onGrantAccess={(ids) => grantPodAccess(ids, podForm.programId || editing)}
                                        grantingIds={grantingIds}
                                    />
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        <button className="mom-btn mom-btn-primary" onClick={handleSavePod} disabled={podSaving || !podForm.podName} style={{ padding: '10px 28px', fontSize: 13 }}>
                                            {podSaving ? 'Saving...' : editingPod === 'new' ? 'Create Pod' : 'Save Pod'}
                                        </button>
                                        <button className="mom-btn mom-btn-ghost" onClick={() => setEditingPod(null)} style={{ fontSize: 13 }}>Cancel</button>
                                    </div>
                                </div>
                            )}

                            {/* Pod list */}
                            {podsLoading ? <Spinner /> : pods.map((pod) => {
                                const spotsLeft = (pod.maxParticipants || 0) - (pod.participants?.length || 0);
                                const isLocked = pod.lockDate && new Date() >= new Date(pod.lockDate);
                                const isFull = spotsLeft <= 0;
                                const isWaitlist = pod.salesModel === 'waitlist' && pod.saleStartDate && new Date() < new Date(pod.saleStartDate);
                                return (
                                    <div key={pod.id} style={{ background: theme.colors.white, borderRadius: 12, padding: '16px 20px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 14, border: `1px solid ${theme.colors.creamDark}` }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal }}>{pod.podName}</span>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: pod.status === 'active' ? theme.colors.success : pod.status === 'completed' ? theme.colors.warmGray : theme.colors.terracotta, color: theme.colors.white, fontWeight: 600, textTransform: 'uppercase' }}>{pod.status}</span>
                                                {isWaitlist && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: theme.colors.terracotta, color: theme.colors.white, fontWeight: 600 }}>WAITLIST</span>}
                                                {isFull && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: theme.colors.danger, color: theme.colors.white, fontWeight: 600 }}>FULL</span>}
                                                {isLocked && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: theme.colors.warmGray, color: theme.colors.white, fontWeight: 600 }}>LOCKED</span>}
                                            </div>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>
                                                Starts {pod.startDate ? new Date(pod.startDate).toLocaleDateString() : '—'} · {pod.participants?.length || 0}/{pod.maxParticipants} participants · {pod.meetings?.length || 0} meetings{(pod.waitlist?.length || 0) > 0 ? ` · ${pod.waitlist.length} on waitlist` : ''}
                                            </p>
                                        </div>
                                        <button className="mom-btn mom-btn-ghost" onClick={() => duplicatePod(pod)} style={{ fontSize: 12, color: theme.colors.charcoal }}>Duplicate</button>
                                        <button className="mom-btn mom-btn-ghost" onClick={() => startEditPod(pod)} style={{ fontSize: 12, color: theme.colors.terracotta, fontWeight: 600 }}>Edit</button>
                                        <button className="mom-btn mom-btn-ghost" onClick={() => handleDeletePod(pod)} style={{ fontSize: 12, color: theme.colors.danger }}>Delete</button>
                                    </div>
                                );
                            })}
                            {!podsLoading && pods.length === 0 && editingPod === null && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, padding: '8px 0' }}>No pods created yet. Save the program first, then create pods.</p>
                            )}
                        </div>
                    )}

                </div>
                <StickyBar saving={saving} saved={saved} onSave={handleSave} label={editing === 'new' ? 'Create Program' : 'Save Changes'} hasChanges={!saved} />
                {recorderOpen && editing !== 'new' && (
                    <ProgramRecorder
                        programId={editing}
                        programTitle={form.name}
                        nextOrder={recordedVideos.length}
                        getToken={getToken}
                        onClose={() => { setRecorderOpen(false); loadRecordedVideos(editing); }}
                        onSaved={() => loadRecordedVideos(editing)}
                    />
                )}

                {/* Thumbnail picker for recorded program videos — samples Mux frames */}
                {pvThumbnailPicker && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                        onClick={() => setPvThumbnailPicker(null)}>
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
                        <div style={{ position: 'relative', background: theme.colors.white, borderRadius: 20, padding: 24, maxWidth: 600, width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: theme.shadows.xl }}
                            onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <h3 style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, fontWeight: 700, color: theme.colors.charcoal, margin: 0 }}>Choose Thumbnail</h3>
                                <button onClick={() => setPvThumbnailPicker(null)} style={{ background: 'none', border: 'none', fontSize: 20, color: theme.colors.warmGray, cursor: 'pointer', padding: '4px 8px' }}>×</button>
                            </div>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 16 }}>Click a frame to set it as the thumbnail</p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
                                {getPvThumbnailTimestamps(pvThumbnailPicker.durationSeconds).map((time) => {
                                    const url = `https://image.mux.com/${pvThumbnailPicker.playbackId}/thumbnail.jpg?time=${time}&width=640&height=360`;
                                    const mins = Math.floor(time / 60);
                                    const secs = time % 60;
                                    return (
                                        <button key={time} onClick={() => handlePvThumbnailSelect(url)}
                                            style={{ padding: 0, border: `2px solid ${theme.colors.creamDark}`, borderRadius: 10, overflow: 'hidden', cursor: 'pointer', background: theme.colors.cream, transition: 'all 0.15s ease' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = theme.colors.terracotta; e.currentTarget.style.transform = 'scale(1.03)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.colors.creamDark; e.currentTarget.style.transform = 'scale(1)'; }}>
                                            <img src={url} alt={`Frame at ${mins}:${secs.toString().padStart(2, '0')}`} style={{ width: '100%', height: 'auto', display: 'block' }} loading="lazy" />
                                            <span style={{ display: 'block', padding: '4px 0', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 600, color: theme.colors.warmGray, textAlign: 'center' }}>{mins}:{secs.toString().padStart(2, '0')}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── PROGRAM LIST ──────────────────────────────────────
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Programs</h3>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>{programs.length} program{programs.length !== 1 ? 's' : ''} — create, edit, or remove programs here.</p>
                </div>
                <button className="mom-btn mom-btn-primary" onClick={startNew} style={{ padding: '12px 28px' }}>+ New Program</button>
            </div>
            {programs.map((prog) => (
                <div key={prog.id} style={{ background: theme.colors.white, borderRadius: theme.radius.lg, padding: '20px 24px', marginBottom: 12, boxShadow: theme.shadows.sm, display: 'flex', alignItems: 'center', gap: 16 }}>
                    <span style={{ fontSize: 36, width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {prog.imageUrl ? <img src={prog.imageUrl} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} referrerPolicy="no-referrer" /> : null}
                    </span>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, color: theme.colors.charcoal }}>{prog.name}</h3>
                            {prog.active === false && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: theme.colors.warmGray, color: theme.colors.white }}>INACTIVE</span>}
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: prog.programType === 'cohort' ? theme.colors.terracotta : theme.colors.charcoal, color: theme.colors.white, fontWeight: 600, textTransform: 'uppercase' }}>{prog.programType === 'cohort' ? 'Cohort' : 'Self-Paced'}</span>
                        </div>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>
                            {`$${prog.price} USD`} · Order: {prog.order}{prog.bunnyCollectionId ? ' · Videos ✓' : ''}{prog.facebookGroupUrl ? ' · FB Group ✓' : ''}{prog.stripeProductId ? ' · Stripe ✓' : ''}{prog.flodeskWelcomeSegmentId ? ' · Email ✓' : ''}
                        </p>
                    </div>
                    <button className="mom-btn mom-btn-ghost" onClick={() => startEdit(prog)} style={{ color: theme.colors.terracotta, fontWeight: 600, fontSize: 13 }}>Edit</button>
                    <button className="mom-btn mom-btn-ghost" onClick={() => handleDelete(prog.id)} style={{ color: theme.colors.danger, fontSize: 13 }}>Delete</button>
                </div>
            ))}
            {programs.length === 0 && <p style={{ fontFamily: 'var(--mom-font-sans)', color: theme.colors.warmGray, textAlign: 'center', padding: 40 }}>No programs yet. Click "+ New Program" to create one.</p>}

            {/* Pod Delete Confirmation Modal (shared by ProgramsTab) */}
            {podDeleteConfirm && (
                <div style={{ position: 'fixed', inset: 0, background: theme.colors.overlay, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setPodDeleteConfirm(null)}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: theme.colors.white, borderRadius: theme.radius.xl, padding: '32px 28px', maxWidth: 480, width: '100%', boxShadow: theme.shadows.xl }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: hexToRgba(theme.colors.danger, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>⚠️</div>
                            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: theme.colors.charcoal }}>Delete Pod</h3>
                        </div>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, marginBottom: 8, lineHeight: 1.6 }}>
                            You are about to delete <strong>{podDeleteConfirm.pod.podName}</strong>. This action cannot be undone.
                        </p>
                        {podDeleteConfirm.pod.participants && podDeleteConfirm.pod.participants.length > 0 ? (
                            <div style={{ background: hexToRgba(theme.colors.danger, 0.05), border: `1px solid ${hexToRgba(theme.colors.danger, 0.15)}`, borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.danger, marginBottom: 10 }}>
                                    {podDeleteConfirm.pod.participants.length} member{podDeleteConfirm.pod.participants.length !== 1 ? 's' : ''} will lose access to all content in this program:
                                </p>
                                {podDeleteUsersLoading ? (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>Loading members...</p>
                                ) : podDeleteConfirm.users.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                                        {podDeleteConfirm.users.map((u) => (
                                            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                                                <div style={{ width: 28, height: 28, borderRadius: '50%', background: theme.colors.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, color: theme.colors.charcoal, flexShrink: 0 }}>
                                                    {(u.firstName || u.email || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal }}>{u.firstName || ''} {u.lastName || ''}{!u.firstName && !u.lastName ? 'Unknown User' : ''}</p>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>{u.email || u.id}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>{podDeleteConfirm.pod.participants.length} user{podDeleteConfirm.pod.participants.length !== 1 ? 's' : ''} assigned</p>
                                )}
                            </div>
                        ) : (
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginBottom: 20 }}>This pod has no members.</p>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button className="mom-btn mom-btn-ghost" onClick={() => setPodDeleteConfirm(null)} style={{ padding: '10px 24px' }}>Cancel</button>
                            <button className="mom-btn" onClick={confirmPodDelete} style={{ padding: '10px 24px', borderRadius: 12, background: theme.colors.danger, color: theme.colors.white, border: 'none', fontWeight: 600 }}>Delete Pod</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  PODS TAB (standalone pod management across all cohort programs)
// ═══════════════════════════════════════════════════════════════
function PodListItem({ pod, onEdit, onDuplicate, onDelete }) {
    const spotsLeft = (pod.maxParticipants || 0) - (pod.participants?.length || 0);
    const isFull = spotsLeft <= 0;
    const isLocked = pod.lockDate && new Date() >= new Date(pod.lockDate);
    const isWaitlist = pod.salesModel === 'waitlist' && pod.saleStartDate && new Date() < new Date(pod.saleStartDate);
    return (
        <div style={{ background: theme.colors.white, borderRadius: 12, padding: '16px 24px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 14, marginLeft: 16, border: `1px solid ${theme.colors.creamDark}` }}>
            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal }}>{pod.podName}</span>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: pod.status === 'active' ? theme.colors.success : pod.status === 'completed' ? theme.colors.warmGray : theme.colors.terracotta, color: theme.colors.white, fontWeight: 600, textTransform: 'uppercase' }}>{pod.status}</span>
                    {isWaitlist && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: theme.colors.terracotta, color: theme.colors.white, fontWeight: 600 }}>WAITLIST</span>}
                    {isFull && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: theme.colors.danger, color: theme.colors.white, fontWeight: 600 }}>FULL</span>}
                    {isLocked && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 10, background: theme.colors.warmGray, color: theme.colors.white, fontWeight: 600 }}>LOCKED</span>}
                </div>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>
                    Starts {pod.startDate ? new Date(pod.startDate).toLocaleDateString() : '—'} · {pod.participants?.length || 0}/{pod.maxParticipants} participants · {pod.meetings?.length || 0} meetings{(pod.waitlist?.length || 0) > 0 ? ` · ${pod.waitlist.length} on waitlist` : ''}
                </p>
            </div>
            <button className="mom-btn mom-btn-ghost" onClick={onDuplicate} style={{ fontSize: 12, color: theme.colors.charcoal }}>Duplicate</button>
            <button className="mom-btn mom-btn-ghost" onClick={onEdit} style={{ fontSize: 12, color: theme.colors.terracotta, fontWeight: 600 }}>Edit</button>
            <button className="mom-btn mom-btn-ghost" onClick={onDelete} style={{ fontSize: 12, color: theme.colors.danger }}>Delete</button>
        </div>
    );
}

function PodsTab({ unsavedRef }) {
    const getToken = useAdminAuth();
    const [programs, setPrograms] = useState([]);
    const [allPods, setAllPods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPod, setEditingPod] = useState(null);
    const [podForm, setPodForm] = useState({});
    const [podSaving, setPodSaving] = useState(false);
    const [podUsers, setPodUsers] = useState([]);
    const [podUsersLoading, setPodUsersLoading] = useState(false);
    const [addUserSearch, setAddUserSearch] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [expandedProgram, setExpandedProgram] = useState(null);
    const [completedExpanded, setCompletedExpanded] = useState({});

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        setLoading(true);
        const [progs, pods] = await Promise.all([fetchPrograms(), fetchPods()]);
        setPrograms((progs || []).filter((p) => p.programType === 'cohort'));
        setAllPods(pods || []);
        setLoading(false);
    };

    const podsForProgram = (progId) => allPods.filter((p) => p.programId === progId);

    const loadPodUsers = async (participantIds) => {
        if (!participantIds || participantIds.length === 0) { setPodUsers([]); return; }
        setPodUsersLoading(true);
        try {
            const res = await adminFetch('/api/users', getToken);
            if (res.ok) { const raw = await res.json(); const all = Array.isArray(raw) ? raw : (raw.users || []); setAllUsers(all); setPodUsers(all.filter((u) => participantIds.includes(u.id))); }
        } catch (e) { }
        setPodUsersLoading(false);
    };
    const [searchLoading, setSearchLoading] = useState(false);
    const searchUsers = async (term) => {
        if (!term || term.length < 2) return;
        setSearchLoading(true);
        try {
            const res = await adminFetch(`/api/users?search=${encodeURIComponent(term)}`, getToken);
            if (res.ok) { const raw = await res.json(); setAllUsers(Array.isArray(raw) ? raw : (raw.users || [])); }
        } catch (e) { }
        setSearchLoading(false);
    };

    const startNewPod = (programId) => {
        setPodForm({ programId, podName: '', startDate: '', lockDate: '', saleStartDate: '', maxParticipants: 15, meetings: [], participants: [], status: 'upcoming', salesModel: 'immediate', waitlistEmailSubject: '', waitlistEmailBody: '' });
        setEditingPod('new');
    };
    const startEditPod = (pod) => { setPodForm({ ...pod }); setEditingPod(pod.id); setPodLoadedParticipants(pod.participants || []); loadPodUsers(pod.participants || []); };

    const addMeeting = () => {
        const meetings = [...(podForm.meetings || [])];
        const n = meetings.length + 1;
        meetings.push({ weekNumber: n, date: '', time: '', zoomLink: '', title: `Week ${n} Meeting` });
        setPodForm({ ...podForm, meetings });
        setTimeout(() => { const el = document.getElementById(`ptab-meeting-${n}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
    };
    const updateMeeting = (idx, field, value) => { const m = [...podForm.meetings]; m[idx] = { ...m[idx], [field]: value }; setPodForm({ ...podForm, meetings: m }); };
    const removeMeeting = (idx) => { setPodForm({ ...podForm, meetings: podForm.meetings.filter((_, i) => i !== idx) }); };

    const addUserToPod = (userId) => {
        const participants = [...(podForm.participants || [])];
        if (!participants.includes(userId)) { participants.push(userId); setPodForm({ ...podForm, participants }); const u = allUsers.find((x) => x.id === userId); if (u) setPodUsers((prev) => [...prev, u]); }
        setAddUserSearch('');
    };
    const removeUserFromPod = (userId) => {
        setPodForm({ ...podForm, participants: (podForm.participants || []).filter((id) => id !== userId) });
        setPodUsers((prev) => prev.filter((u) => u.id !== userId));
    };
    // Pod membership ⇒ program access (see grantProgramToUsers). Updates the
    // local user lists so the "No access" flags clear without a reload.
    const [grantingIds, setGrantingIds] = useState([]);
    const [podAccessNotice, setPodAccessNotice] = useState(null);
    const grantPodAccess = async (ids, programId, label = '') => {
        if (!ids.length || !programId) return;
        setGrantingIds(ids);
        const { granted, failed } = await grantProgramToUsers(ids, programId, getToken);
        const mark = (list) => list.map((u) => (granted.includes(u.id) ? { ...u, purchasedPrograms: Array.from(new Set([...(u.purchasedPrograms || []), programId])) } : u));
        setAllUsers((prev) => mark(prev));
        setPodUsers((prev) => mark(prev));
        setGrantingIds([]);
        const nameOf = (id) => { const u = allUsers.find((x) => x.id === id) || podUsers.find((x) => x.id === id); return u ? (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email) : id; };
        if (failed.length) {
            setPodAccessNotice({ text: `Couldn't grant program access to ${failed.map(nameOf).join(', ')}. They're in the pod but can't open the program yet.`, failed, programId });
        } else if (granted.length) {
            setPodAccessNotice({ text: `${label}Program access granted to ${granted.length === 1 ? nameOf(granted[0]) : `${granted.length} people`}.`, failed: [], programId });
        }
    };
    // Participants the editor loaded — Save applies only the admin's changes
    // against these to the pod's CURRENT list (see applyPodMembershipDelta).
    const [podLoadedParticipants, setPodLoadedParticipants] = useState([]);
    const savePodChanges = async (podId, data) => {
        const { participants = [], ...rest } = data;
        // The waitlist is display-only here. Saving the copy loaded when the
        // editor opened would erase anyone who joined while it was open.
        delete rest.waitlist;
        const ok = await updatePod(podId, rest);
        if (!ok) return null;
        const add = participants.filter((id) => !podLoadedParticipants.includes(id));
        const remove = podLoadedParticipants.filter((id) => !participants.includes(id));
        if (!add.length && !remove.length) return participants;
        return applyPodMembershipDelta(podId, add, remove);
    };
    const moveUserToPod = async (userId, targetPodId) => {
        const targetPod = allPods.find((p) => p.id === targetPodId);
        if (!targetPod) return;
        const ok = await movePodMember(userId, editingPod !== 'new' ? editingPod : null, targetPodId);
        if (!ok) { setPodAccessNotice({ text: "Couldn't move that person — nothing was changed. Please try again.", failed: [], programId: null }); return; }
        removeUserFromPod(userId);
        setPodLoadedParticipants((prev) => prev.filter((id) => id !== userId));
        await grantPodAccess([userId], targetPod.programId);
    };

    const handleSavePod = async () => {
        setPodSaving(true);
        const data = { ...podForm, maxParticipants: parseInt(podForm.maxParticipants) || 15 };
        delete data.id; delete data.createdAt;
        const finalParticipants = editingPod === 'new'
            ? ((await createPod(data)) ? (data.participants || []) : null)
            : await savePodChanges(editingPod, data);
        // Everyone in the pod gets the program (idempotent).
        if (finalParticipants) await grantPodAccess(participantsNeedingAccess(finalParticipants, [...allUsers, ...podUsers], data.programId), data.programId);
        else setPodAccessNotice({ text: "Couldn't save the pod — please try again.", failed: [], programId: null });
        await loadAll();
        setPodSaving(false); setEditingPod(null);
    };
    const [deleteConfirm, setDeleteConfirm] = useState(null); // { podId, pod, users: [] }
    const [deleteUsersLoading, setDeleteUsersLoading] = useState(false);

    const initiateDelete = async (pod) => {
        setDeleteConfirm({ podId: pod.id, pod, users: [] });
        if (pod.participants && pod.participants.length > 0) {
            setDeleteUsersLoading(true);
            try {
                const res = await adminFetch('/api/users', getToken);
                if (res.ok) {
                    const raw = await res.json();
                    const all = Array.isArray(raw) ? raw : (raw.users || []);
                    const podMembers = all.filter((u) => pod.participants.includes(u.id));
                    setDeleteConfirm((prev) => prev ? { ...prev, users: podMembers } : null);
                }
            } catch (e) { }
            setDeleteUsersLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        await deletePod(deleteConfirm.podId);
        setDeleteConfirm(null);
        await loadAll();
    };

    const handleDeletePod = async (pod) => {
        initiateDelete(pod);
    };

    const duplicatePod = (pod) => {
        const newForm = { ...pod, podName: '', startDate: '', lockDate: '', saleStartDate: '', meetings: (pod.meetings || []).map((m) => ({ ...m, date: '', zoomLink: '' })), participants: [], waitlist: [], status: 'upcoming' };
        delete newForm.id; delete newForm.createdAt;
        setPodForm(newForm);
        setEditingPod('new'); setPodUsers([]); setAddUserSearch('');
    };
    const autoGenerateMeetings = () => {
        const prog = programs.find((p) => p.id === podForm.programId);
        if (!podForm.startDate || !prog?.weeks || prog.weeks.length === 0) return;
        const startD = new Date(podForm.startDate);
        const meetings = prog.weeks.map((week, i) => {
            const meetingDate = new Date(startD);
            meetingDate.setDate(meetingDate.getDate() + (i * 7));
            return { weekNumber: i + 1, title: week.title || `Week ${i + 1} Meeting`, date: meetingDate.toISOString().slice(0, 10), time: '', zoomLink: '' };
        });
        setPodForm({ ...podForm, meetings });
    };

    if (loading) return <Spinner />;

    if (programs.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 18, color: theme.colors.warmGray, marginBottom: 8 }}>No cohort programs yet.</p>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>Create a program with type "Cohort" in the Programs tab first, then manage pods here.</p>
            </div>
        );
    }

    // Pod editor (shared for new + edit)
    if (editingPod !== null) {
        const programName = programs.find((p) => p.id === podForm.programId)?.name || 'Unknown';
        const prog = programs.find((p) => p.id === podForm.programId);
        const siblingPods = allPods.filter((p) => p.programId === podForm.programId && p.id !== editingPod);

        return (
            <div style={{ paddingBottom: 72 }}>
                <button className="mom-btn mom-btn-ghost" onClick={() => setEditingPod(null)} style={{ marginBottom: 20, color: theme.colors.warmGray }}>← Back to pods</button>
                <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, padding: '32px', boxShadow: theme.shadows.sm }}>
                    <PodAccessNotice notice={podAccessNotice} onDismiss={() => setPodAccessNotice(null)}
                        onRetry={() => podAccessNotice && grantPodAccess(podAccessNotice.failed, podAccessNotice.programId)} />
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.terracotta, fontWeight: 600, marginBottom: 4 }}>{programName}</p>
                    <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 24 }}>
                        {editingPod === 'new' ? 'Create New Pod' : 'Edit Pod'}
                    </h3>
                    <PodEditor
                        podForm={podForm} setPodForm={setPodForm} editingPod={editingPod}
                        siblingPods={siblingPods}
                        podUsers={podUsers} podUsersLoading={podUsersLoading}
                        addUserSearch={addUserSearch} setAddUserSearch={setAddUserSearch}
                        allUsers={allUsers} onSearchUsers={searchUsers} searchLoading={searchLoading}
                        addUserToPod={addUserToPod} removeUserFromPod={removeUserFromPod} moveUserToPod={moveUserToPod}
                        addMeeting={addMeeting} updateMeeting={updateMeeting} removeMeeting={removeMeeting}
                        meetingIdPrefix="ptab-meeting"
                        programWeeks={prog?.weeks}
                        onAutoGenMeetings={prog?.weeks && prog.weeks.length > 0 ? autoGenerateMeetings : null}
                        programId={podForm.programId}
                        onGrantAccess={(ids) => grantPodAccess(ids, podForm.programId)}
                        grantingIds={grantingIds}
                    />
                </div>
                <StickyBar saving={podSaving} saved={false} onSave={handleSavePod} label={editingPod === 'new' ? 'Create Pod' : 'Save Pod'} hasChanges={true} />
            </div>
        );
    }

    // Pod list grouped by program
    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Pod Management</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>Manage all cohort pods across your programs — participants, meetings, and schedules.</p>
            </div>
            <PodAccessNotice notice={podAccessNotice} onDismiss={() => setPodAccessNotice(null)}
                onRetry={() => podAccessNotice && grantPodAccess(podAccessNotice.failed, podAccessNotice.programId)} />

            {programs.map((prog) => {
                const progPods = podsForProgram(prog.id);
                const activePods = progPods.filter((p) => p.status !== 'completed');
                const completedPods = progPods.filter((p) => p.status === 'completed');
                const isExpanded = expandedProgram === prog.id;
                const showCompleted = completedExpanded[prog.id] || false;
                const toggleCompleted = () => setCompletedExpanded((prev) => ({ ...prev, [prog.id]: !prev[prog.id] }));
                return (
                    <div key={prog.id} style={{ marginBottom: 16 }}>
                        <div onClick={() => setExpandedProgram(isExpanded ? null : prog.id)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setExpandedProgram(isExpanded ? null : prog.id); }}
                            style={{ width: '100%', background: theme.colors.white, borderRadius: theme.radius.lg, padding: '20px 24px', border: 'none', cursor: 'pointer', boxShadow: theme.shadows.sm, display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div>
                                    <h4 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, color: theme.colors.charcoal }}>{prog.name}</h4>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginTop: 2 }}>
                                        {activePods.length} active pod{activePods.length !== 1 ? 's' : ''}{completedPods.length > 0 ? ` · ${completedPods.length} completed` : ''} · {progPods.reduce((sum, p) => sum + (p.participants?.length || 0), 0)} total participants
                                    </p>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <button className="mom-btn mom-btn-outline" onClick={(e) => { e.stopPropagation(); startNewPod(prog.id); }} style={{ padding: '8px 18px', fontSize: 12 }}>+ New Pod</button>
                                <span style={{ fontSize: 18, color: theme.colors.warmGray, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▾</span>
                            </div>
                        </div>

                        {isExpanded && (
                            <div style={{ padding: '12px 0 0 0' }}>
                                {progPods.length === 0 ? (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, padding: '12px 24px' }}>No pods yet. Click "+ New Pod" to create one.</p>
                                ) : (
                                    <>
                                        {/* Active / Upcoming pods */}
                                        {activePods.length > 0 && activePods.map((pod) => (
                                            <PodListItem key={pod.id} pod={pod} onEdit={() => startEditPod(pod)} onDuplicate={() => duplicatePod(pod)} onDelete={() => handleDeletePod(pod)} />
                                        ))}

                                        {activePods.length === 0 && completedPods.length > 0 && (
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, padding: '12px 24px' }}>No active pods. {completedPods.length} completed pod{completedPods.length !== 1 ? 's' : ''} below.</p>
                                        )}

                                        {/* Completed pods — collapsed by default */}
                                        {completedPods.length > 0 && (
                                            <div style={{ marginTop: activePods.length > 0 ? 12 : 0 }}>
                                                <button onClick={toggleCompleted}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                        Completed Pods ({completedPods.length})
                                                    </span>
                                                    <span style={{ fontSize: 12, color: theme.colors.warmGray, transform: showCompleted ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▾</span>
                                                </button>
                                                {showCompleted && completedPods.map((pod) => (
                                                    <PodListItem key={pod.id} pod={pod} onEdit={() => startEditPod(pod)} onDuplicate={() => duplicatePod(pod)} onDelete={() => handleDeletePod(pod)} />
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div style={{ position: 'fixed', inset: 0, background: theme.colors.overlay, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setDeleteConfirm(null)}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: theme.colors.white, borderRadius: theme.radius.xl, padding: '32px 28px', maxWidth: 480, width: '100%', boxShadow: theme.shadows.xl }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: hexToRgba(theme.colors.danger, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>⚠️</div>
                            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: theme.colors.charcoal }}>Delete Pod</h3>
                        </div>

                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, marginBottom: 8, lineHeight: 1.6 }}>
                            You are about to delete <strong>{deleteConfirm.pod.podName}</strong>. This action cannot be undone.
                        </p>

                        {deleteConfirm.pod.participants && deleteConfirm.pod.participants.length > 0 ? (
                            <div style={{ background: hexToRgba(theme.colors.danger, 0.05), border: `1px solid ${hexToRgba(theme.colors.danger, 0.15)}`, borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.danger, marginBottom: 10 }}>
                                    {deleteConfirm.pod.participants.length} member{deleteConfirm.pod.participants.length !== 1 ? 's' : ''} will lose access to all content in this program:
                                </p>
                                {deleteUsersLoading ? (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>Loading members...</p>
                                ) : deleteConfirm.users.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                                        {deleteConfirm.users.map((u) => (
                                            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                                                <div style={{ width: 28, height: 28, borderRadius: '50%', background: theme.colors.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, color: theme.colors.charcoal, flexShrink: 0 }}>
                                                    {(u.firstName || u.email || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal }}>
                                                        {u.firstName || ''} {u.lastName || ''}{!u.firstName && !u.lastName ? 'Unknown User' : ''}
                                                    </p>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>{u.email || u.id}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>
                                        {deleteConfirm.pod.participants.length} user{deleteConfirm.pod.participants.length !== 1 ? 's' : ''} assigned
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginBottom: 20 }}>
                                This pod has no members.
                            </p>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button className="mom-btn mom-btn-ghost" onClick={() => setDeleteConfirm(null)} style={{ padding: '10px 24px' }}>Cancel</button>
                            <button className="mom-btn" onClick={confirmDelete} style={{ padding: '10px 24px', borderRadius: 12, background: theme.colors.danger, color: theme.colors.white, border: 'none', fontWeight: 600 }}>
                                Delete Pod
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  WORKOUTS TAB — configurable copy for the Workouts page
// ═══════════════════════════════════════════════════════════════
function WorkoutsTab({ unsavedRef }) {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => { fetchSiteContent().then((c) => { setContent(c); setLoading(false); }); }, []);
    const handleChange = (key, value) => { setContent((prev) => ({ ...prev, [key]: value })); setSaved(false); if (unsavedRef) unsavedRef.current = true; };
    const handleSave = async () => { setSaving(true); const ok = await updateSiteContent(content); setSaving(false); if (ok) { setSaved(true); if (unsavedRef) unsavedRef.current = false; setTimeout(() => setSaved(false), 3000); } };

    if (loading) return <Spinner />;

    const fields = [
        {
            group: 'Subscription & upsell', section: 'Subscription CTA', desc: 'The display price and button label used across all subscribe buttons and banners. Changing these updates text everywhere — they do NOT change the actual Stripe price.', items: [
                { key: 'subscriptionPriceLabel', label: 'Display Price', type: 'text', hint: 'Default: "$15/mo"' },
                { key: 'subscribeCtaLabel', label: 'Subscribe Button Label', type: 'text', hint: 'Default: "Subscribe"' },
                { key: 'tellMeMoreCtaLabel', label: '"Tell me more" Button Label', type: 'text', hint: 'Default: "Tell me more". The white outline button next to Subscribe on the Schedule + On Demand banners; links non-members to the /join page to learn more.' },
            ]
        },
        {
            section: 'Yearly plan', desc: 'Offer a yearly membership alongside monthly. Prices and the "Save" amount are read LIVE from Stripe — nothing to keep in sync here. If the prices can\'t be loaded, the site quietly shows monthly only. $19 members see "Switch to yearly · Save"; founding members ($15 for life) see "Lock in a year" and can go back to their founding rate themselves when their year ends.', items: [
                { key: 'enableAnnualPlan', label: 'Offer the yearly plan', type: 'toggle', toggleLabel: 'Show a Monthly / Yearly choice everywhere members can subscribe', hint: 'Off = today\'s monthly-only checkout everywhere. Before turning on: update the /join FAQ that describes pricing.' },
                { key: 'planYearlyLabel', label: 'Yearly option name', type: 'text', hint: 'Default: "Yearly"' },
                { key: 'planMonthlyLabel', label: 'Monthly option name', type: 'text', hint: 'Default: "Monthly"' },
                { key: 'planSavingsTemplate', label: 'Savings badge', type: 'text', hint: 'Default: "Save {amount}" — {amount} is calculated from Stripe (12 × monthly − yearly).' },
                { key: 'planYearlyNote', label: 'Yearly fine print', type: 'text', hint: 'Default: "Billed {price} once a year"' },
                { key: 'planMonthlyNote', label: 'Monthly fine print', type: 'text', hint: 'Default: "Billed monthly"' },
                { key: 'planPickerHeading', label: 'In-app plan sheet heading', type: 'text', hint: 'Default: "Choose your membership" (shown when a subscribe button is tapped inside the app)' },
                { key: 'planPickerContinueLabel', label: 'In-app plan sheet button', type: 'text', hint: 'Default: "Continue"' },
                { key: 'switchYearlyLabel', label: 'Account: switch button', type: 'text', hint: 'Default: "Switch to yearly" (the savings badge is added after it)' },
                { key: 'switchYearlyTodayTemplate', label: 'Account: amount due line', type: 'text', hint: 'Default: "You\'ll pay {today} today" — {today} comes from Stripe\'s own preview' },
                { key: 'switchYearlyCreditTemplate', label: 'Account: credit line', type: 'text', hint: 'Default: "That\'s {price} less a {credit} credit for the unused part of this month."' },
                { key: 'switchYearlyBody', label: 'Account: renewal line', type: 'textarea', hint: 'Default: "Then {price} every year, next on {date}. Cancel anytime — you keep access to the end of your year."' },
                { key: 'switchYearlySuccess', label: 'Account: success message', type: 'text', hint: 'Default: "You\'re on the yearly plan. Thank you for being here!"' },
                { key: 'switchYearlyIneligible', label: 'Account: can\'t-switch message', type: 'text', hint: 'Default: "This membership can\'t be switched online. Reach out and Danielle will help."' },
                { key: 'switchYearlyCancelLabel', label: 'Account: dismiss button', type: 'text', hint: 'Default: "Not now"' },
                { key: 'foundingLockLabel', label: 'Founders: switch button', type: 'text', groupLabel: 'Founding members ($15/mo for life)', hint: 'Default: "Lock in a year" (the yearly price is added after it). Founders see this instead of the Save badge.' },
                { key: 'foundingKeepRateNote', label: 'Founders: reassurance in the switch window', type: 'textarea', hint: 'Default: "Your {rate}/month founding rate stays yours. When your year ends you can go back to it right here on this page."' },
                { key: 'foundingRateNote', label: 'Founders on yearly: rate line', type: 'text', hint: 'Default: "Your founding rate of {rate}/month is yours to go back to."' },
                { key: 'foundingBackLabel', label: 'Founders on yearly: go-back button', type: 'text', hint: 'Default: "Go back to {rate}/month when my year ends"' },
                { key: 'foundingBackConfirm', label: 'Founders on yearly: confirm text', type: 'textarea', hint: 'Default: "From {date} you\'ll pay {rate}/month instead of {yearly}. Nothing changes before then, and you can undo this any time before {date}."' },
                { key: 'foundingBackConfirmButton', label: 'Founders on yearly: confirm button', type: 'text', hint: 'Default: "Yes, switch me back then"' },
                { key: 'foundingKeepYearlyLabel', label: 'Founders on yearly: dismiss', type: 'text', hint: 'Default: "Keep yearly"' },
                { key: 'foundingScheduledTemplate', label: 'Founders on yearly: scheduled line', type: 'text', hint: 'Default: "Switching to your founding rate of {rate}/month on {date}."' },
                { key: 'foundingUndoLabel', label: 'Founders on yearly: undo button', type: 'text', hint: 'Default: "Keep yearly instead"' },
            ]
        },
        {
            section: 'Upcoming Tab — Subscribe Banner', desc: 'The gradient banner shown to non-subscribers on the Upcoming tab when classes are scheduled.', items: [
                { key: 'upcomingBannerHeadline', label: 'Headline', type: 'text', hint: 'Default: "Unlimited classes for $15/mo"' },
                { key: 'upcomingBannerSubtitle', label: 'Subtitle', type: 'text', hint: 'Default: "Every live class + full replay library, all included."' },
            ]
        },
        {
            section: 'On Demand Tab — Subscribe Banner', desc: 'The gradient banner shown to non-subscribers on the On Demand tab.', items: [
                { key: 'onDemandBannerHeadline', label: 'Headline', type: 'text', hint: 'Default: "Unlock the full library"' },
                { key: 'onDemandBannerSubtitle', label: 'Subtitle', type: 'text', hint: 'Default: "Every class recording + all scheduled classes for $15/mo"' },
            ]
        },
        {
            section: 'Locked Video Modal', desc: 'Shown when a non-subscriber taps a locked On Demand video.', items: [
                { key: 'lockedVideoDescription', label: 'Description', type: 'textarea', hint: 'Default: "Unlock unlimited access to all classes—follow the weekly schedule or work out On Demand, anytime."' },
                { key: 'lockedVideoButtonLabel', label: 'Subscribe Button Label', type: 'text', hint: 'Default: "Subscribe · $15/mo"' },
            ]
        },
        {
            group: 'Tabs & schedule', section: 'Tab Labels', desc: 'The three tab names at the top of the Workouts page.', items: [
                { key: 'workoutsTabUpcoming', label: 'First Tab (Schedule)', type: 'text', hint: 'Default: "Schedule". The tab where upcoming + this-week\'s aired classes live.' },
                { key: 'workoutsTabOnDemand', label: 'Second Tab (On Demand)', type: 'text', hint: 'Default: "On Demand"' },
                { key: 'workoutsTabShorts', label: 'Third Tab (Shorts)', type: 'text', hint: 'Default: "Shorts"' },
                { key: 'workoutsTierMembershipLabel', label: 'Tier label — Membership group', type: 'text', hint: 'Default: "Membership". The small label over the paid tabs (Schedule + On Demand) in the Workouts tab bar.' },
                { key: 'workoutsTierFreeLabel', label: 'Tier label — Free group', type: 'text', hint: 'Default: "Free". The small label over the free tabs (Shorts + Series) in the Workouts tab bar.' },
            ]
        },
        {
            group: 'Tabs & schedule', section: 'Class Guide', desc: 'A quiet "Your Class Guide" line that sits under the tab bar on Schedule and On Demand. Members tap it to see what each tab and each difficulty tag is for. It is closed by default, never interrupts, and there is nothing to dismiss — so a newer member can still find it weeks later. The tab NAMES come from the Tab Labels section above, so renaming a tab renames it here too. "Classic", "Ease In" and "Strong+" are fixed names and cannot be renamed.', items: [
                { key: 'showClassGuide', label: 'Show the Class Guide', type: 'toggle', toggleLabel: 'Visible on Schedule + On Demand', hint: 'On by default. Turn off to hide it from both tabs without a deploy. It already hides itself automatically while a class is playing.' },
                { key: 'classGuideTitle', label: 'Guide title', type: 'text', hint: 'Default: "Your Class Guide". Used for both the closed line and the heading inside.' },
                { key: 'classGuideScheduleHead', label: 'Schedule — headline', type: 'text', hint: 'Default: "This is your weekly plan."' },
                { key: 'classGuideScheduleBody', label: 'Schedule — description', type: 'textarea', hint: 'Default: "Three 15-minute workouts each week, so you always know what to do next. Just follow along!"' },
                { key: 'classGuideOnDemandHead', label: 'On Demand — headline', type: 'text', hint: 'Default: "More classes, whenever you want them."' },
                { key: 'classGuideOnDemandBody', label: 'On Demand — description', type: 'textarea', hint: 'Default: "Explore the full workout library anytime. Perfect for off days, extra movement, or simply choosing what feels good that day."' },
                { key: 'classGuideClassicHead', label: 'Classic — headline', type: 'text', hint: 'Default: "The signature class, just as you know it."' },
                { key: 'classGuideClassicBody', label: 'Classic — description', type: 'textarea', hint: 'Default: "Danielle’s classic 15-minute workout at a steady pace. On days with two classes, it’s the one on the left."' },
                { key: 'classGuideEaseInHead', label: 'Ease In — headline', type: 'text', hint: 'Default: "Gentler classes for when you’re easing back in."' },
                { key: 'classGuideEaseInBody', label: 'Ease In — description', type: 'textarea', hint: 'Default: "A slower pace with more time to learn each movement. A great place to start or come back to anytime."' },
                { key: 'classGuideStrongPlusHead', label: 'Strong+ — headline', type: 'text', hint: 'Default: "Heavier weights for when you’re ready."' },
                { key: 'classGuideStrongPlusBody', label: 'Strong+ — description', type: 'textarea', hint: 'Default: "More focused strength work using heavier weights, with options to scale back whenever you need."' },
            ]
        },
        {
            group: 'Tabs & schedule', section: 'Schedule — Days with two classes', desc: 'When a day on the Schedule has exactly two classes, they sit together in one card, side by side (also on phones), with a small "or" between them. Classic always sits on the left and Strong+ on the right. These are the two lines at the top of that card.', items: [
                { key: 'classChoiceHeading', label: 'Card heading', type: 'text', hint: 'Default: "Two ways to move today. Pick one or double up."' },
                { key: 'classChoiceKicker', label: 'Small label above it', type: 'text', hint: 'Default: "Two options". When the pair is the next class up, this shows "Next up · {day}" instead.' },
            ]
        },
        {
            section: 'Schedule — Aired Classes (This Week)', desc: 'Classes that already happened this week stay on the Schedule tab through Sunday so users can follow along all week — miss Monday, catch up Tuesday. They drop off at the Sunday→Monday rollover.', items: [
                { key: 'airedTodayBadgeLabel', label: 'Aired badge label', type: 'text', hint: 'Default: "AIRED" (renders as "AIRED 6:00 AM" with the class time)' },
                { key: 'catchUpCtaLabel', label: 'Aired-class button label', type: 'text', hint: 'Default: "Let\'s move". The button shown on this-week\'s aired class tiles for users who can access the recording. Reads as an invitation regardless of whether they attended live (which avoids "Catch up" sounding like remediation for users who already showed up).' },
                { key: 'recordingFinalizingLabel', label: 'Recording finalizing message', type: 'text', hint: 'Default: "Class Recording Finalizing". Shown for ~30 seconds to 2 minutes after a class ends, while Mux processes the recording.' },
                { key: 'liveJoinClassLabel', label: 'Join button (concurrent classes)', type: 'text', hint: 'Default: "Join this class". Only appears when TWO OR MORE classes are airing at the same time — in that case members choose which one to enter instead of being placed in one automatically. With a single class airing, nothing changes and this button never shows.' },
            ]
        },
        {
            section: 'Schedule — Release-day classes', desc: 'Scheduled classes set to a RELEASE DAY (no time). Each member gets the class at 12:00 AM on that day in her own time zone, plays it from the Schedule, and it joins On Demand around 7 AM Central the same morning. Leave a field blank to use its default.', items: [
                { key: 'releaseDayLabelTemplate', label: 'Day label', type: 'text', hint: 'Default: "{day}\'s class" — reads "WEDNESDAY\'S CLASS" on the tile (coming up, or earlier this week) and "Wednesday\'s class · October 21" in the class details. {day} becomes the weekday.' },
                { key: 'releaseNewTodayLabel', label: 'Released-today badge', type: 'text', hint: 'Default: "New today". Shown on the tile once the class is out for her, on its release day.' },
                { key: 'releaseTodayEyebrow', label: 'Spotlight label for today\'s class', type: 'text', hint: 'Default: "Today\'s class". Used in the highlighted (Next up) tile slot and on the Dashboard card.' },
                { key: 'releaseReadyLine', label: 'Before-release line', type: 'text', hint: 'Default: "Ready at 12:00 AM, your time". Shown under an upcoming release-day class and in its details.' },
                { key: 'releaseComingSoonLabel', label: 'Video-not-ready label', type: 'text', hint: 'Default: "Coming soon". Only if the release day arrives before the video finished uploading.' },
                { key: 'releaseDashOutNow', label: 'Dashboard — out now line', type: 'text', hint: 'Default: "Out now — tap to start".' },
                { key: 'releaseDashReadyFirstThing', label: 'Dashboard — upcoming line', type: 'text', hint: 'Default: "ready first thing" — reads "Tomorrow · ready first thing".' },
                { key: 'classTogetherTileLabel', label: 'Tile — who has taken it', type: 'text', hint: 'Default: "have taken it" — reads "38 have taken it" next to a few member photos.' },
                { key: 'classTogetherNowLabel', label: 'Player — in class right now', type: 'text', hint: 'Default: "in class with you" — reads "Megan, Sarah + 2 others are in class with you". Only shows when someone else is in the class at the same moment.' },
                { key: 'classTogetherTakenLabel', label: 'Player — who has taken it', type: 'text', hint: 'Default: "taken this class" — reads "Sarah, Megan + 36 others have taken this class".' },
                { key: 'classFinishTemplate', label: 'Finish moment', type: 'text', hint: 'Default: "You\'re one of {count} who took {day} class." — {count} is the number of members, {day} becomes "Monday\'s" or "today\'s".' },
                { key: 'classFinishFirstTemplate', label: 'Finish moment — first one', type: 'text', hint: 'Default: "You\'re the first to finish {day} class."' },
                { key: 'classFinishTodayWord', label: 'Finish moment — "today\'s"', type: 'text', hint: 'Default: "today\'s". Used for {day} on the release day itself.' },
                { key: 'classFinishCta', label: 'Finish moment — button', type: 'text', hint: 'Default: "How did it feel?" — opens the comments with the cursor ready.' },
                { key: 'classDayReminderOffLabel', label: 'Class-day reminder — invite', type: 'text', hint: 'Default: "Want a nudge on class days?" Shown in a release-day class\'s details and on the Account page. Members choose a time; one notification on mornings a new class is out, never if they\'ve already done it.' },
                { key: 'classDayReminderOffSub', label: 'Class-day reminder — invite line', type: 'text', hint: 'Default: "We’ll send one note at the time you pick."' },
                { key: 'classDayReminderSetCta', label: 'Class-day reminder — button', type: 'text', hint: 'Default: "Set a time".' },
                { key: 'classDayReminderOnLabel', label: 'Class-day reminder — when on', type: 'text', hint: 'Default: "Class-day reminder · {time}" — {time} becomes e.g. 6:30 AM.' },
                { key: 'classDayReminderHint', label: 'Class-day reminder — picker note', type: 'text', hint: 'Default: "One note on mornings a new class is out — never if you’ve already done it."' },
                { key: 'classDayReminderTitle', label: 'Class-day reminder — notification title', type: 'text', hint: 'Default: "{day}\'s class is ready" — {day} becomes Monday, Wednesday… The body is the class title and length.' },
                { key: 'classDayReminderMulti', label: 'Class-day reminder — two or more classes', type: 'text', hint: 'Default: "{count} new classes are ready".' },
            ]
        },
        {
            section: 'Challenges (Series tab)', desc: "Every label on the Challenges tab and on an individual challenge page. Headings for a SINGLE challenge (Highlights, FAQ, day list) are set per challenge in Admin → Series — these are the defaults used when a challenge sets none of its own. Leave a field blank to use its default.", items: [
                { key: 'workoutsTabSeries', label: "Tab name", type: 'text', hint: "Default: 'Series'. The fourth tab in the Workouts tab bar." },
                { key: 'seriesTabHeading', label: "Challenges list heading", type: 'text', hint: "Default: 'Challenges'" },
                { key: 'seriesTabSubtext', label: "Challenges list subtext", type: 'textarea', rows: 2, hint: "Default: 'Multi-day movement journeys you can start anytime and do at your own pace.'" },
                { key: 'seriesEmptyComingSoon', label: "No challenges published yet", type: 'text', hint: "Default: 'New challenges are coming soon — check back shortly.'" },
                { key: 'seriesDayLabelTemplate', label: "Day label template", type: 'text', hint: "Default: 'Day {n}' — {n} is replaced with the day number." },
                { key: 'seriesUpNextLabel', label: "Up-next chip", type: 'text', hint: "Default: 'Up next'. Marks the first day a member has not done yet." },
                { key: 'seriesProgressTemplate', label: "Progress text", type: 'text', hint: "Default: '{done} of {total} days'" },
                { key: 'seriesStartCue', label: "Not-started cue", type: 'text', hint: "Default: 'Start the challenge'. Shown on a challenge card before the first day is done." },
                { key: 'seriesCompletedLabel', label: "Finished label", type: 'text', hint: "Default: 'Challenge complete'" },
                { key: 'seriesHighlightsHeading', label: "Highlights heading (default)", type: 'text', hint: "Default: 'What’s Included'. Used when a challenge does not set its own heading." },
                { key: 'seriesFaqHeading', label: "FAQ heading (default)", type: 'text', hint: "Default: 'Questions'. Used when a challenge does not set its own heading." },
                { key: 'seriesShareLabel', label: "Share button", type: 'text', hint: "Default: 'Share'. On the challenge page — opens the phone share sheet, or copies the link on a computer." },
                { key: 'seriesShareCopiedLabel', label: "Share button after copying", type: 'text', hint: "Default: 'Link copied'" },
                { key: 'seriesLockedLabel', label: "Locked day prefix", type: 'text', hint: "Default: 'Unlocks'. Shown on a day that has a release date, as 'Unlocks Mon, Sep 15'. Days with no date are open right away." },
                { key: 'seriesNotFoundText', label: "Broken challenge link message", type: 'text', hint: "Default: 'That challenge is not available right now.' Shown on the Challenges list when someone opens a link to a challenge that is unpublished, deleted, or mistyped — instead of an error." },
            ]
        },
        {
            // Split out of 'Challenges (Series tab)' on 2026-09-19 — these were
            // buried two thirds of the way down a 22-field list, so the copy for
            // the one surface strangers see was the hardest copy to find.
            section: 'Challenge — public page (no account needed)', desc: "Seen ONLY by visitors with no account, on a challenge whose Sharing setting is set to 'Anyone with the link'. Members never see any of this. Leave a field blank to use its default.", items: [
                { key: 'seriesPublicInviteHeading', label: "Invitation heading", type: 'text', hint: "Default: 'Want to keep this going?' The card shown after the last day — the only place the page asks a visitor to sign up." },
                { key: 'seriesPublicInviteClosingLine', label: "Invitation line", type: 'textarea', rows: 2, hint: "Default: 'Create a free account and your days here start counting toward your streak.' The reason to sign up. They can already watch everything, so the honest pitch is keeping their progress — not access." },
                { key: 'seriesPublicCreateAccountLabel', label: "Sign-up button", type: 'text', hint: "Default: 'Create free account'" },
                { key: 'seriesPublicSignInPrefix', label: "Sign-in lead-in", type: 'text', hint: "Default: 'Already have one?' Sits under the button, for someone who already has an account." },
                { key: 'seriesPublicSignInLabel', label: "Sign-in link", type: 'text', hint: "Default: 'Sign in'" },
                { key: 'seriesPublicStartHereLabel', label: "First day marker", type: 'text', hint: "Default: 'Start here'. Marks the first day a visitor can do — the public stand-in for 'Up next', which needs an account." },
            ]
        },
        {
            section: 'Empty States', desc: 'Messages shown when there are no videos in a tab.', items: [
                { key: 'noRecordingsMessage', label: 'No On Demand recordings', type: 'text', hint: 'Default: "No class recordings yet"' },
                { key: 'noShortsMessage', label: 'No Shorts videos', type: 'text', hint: 'Default: "No shorts yet"' },
                { key: 'emptyWorkoutsSubtitle', label: 'Subtitle below empty message', type: 'text', hint: 'Default: "Check back soon — new workouts are on the way!"' },
            ]
        },
        {
            group: 'Sharing & completion', section: 'Shared Class Page (/class links)', desc: 'The public page anyone lands on when you share a class link (the Share button on an On Demand class copies it). Strangers watch free classes here and can join; members are sent into the app automatically. Leave a field blank to use its default.', items: [
                { key: 'classPageEyebrow', label: 'Eyebrow (small label above the title)', type: 'text', hint: 'Default: "A CLASS WITH DANIELLE"' },
                { key: 'classPageFreeBadge', label: 'Free badge label', type: 'text', hint: 'Default: "FREE CLASS"' },
                { key: 'classPageJoinHeading', label: 'Join heading — after a FREE class', type: 'text', hint: 'Default: "Loved that?"' },
                { key: 'classPageJoinBody', label: 'Join body — after a FREE class', type: 'textarea', hint: 'Default: "This is one of many. Members get the full on-demand library plus live classes every week."' },
                { key: 'classPagePaidHeading', label: 'Join heading — for a MEMBERS-ONLY class', type: 'text', hint: 'Default: "This class is part of the membership"' },
                { key: 'classPagePaidBody', label: 'Join body — for a MEMBERS-ONLY class', type: 'textarea', hint: 'Default: "Join to unlock this class, the full on-demand library, and live classes every week."' },
                { key: 'classPageJoinCtaLabel', label: 'Join button label', type: 'text', hint: 'Default: "Join the membership"' },
                { key: 'classPageSignInLabel', label: 'Returning-member sign-in link', type: 'text', hint: 'Default: "Already a member? Sign in"' },
                { key: 'classPageExploreLabel', label: 'Fallback button (unavailable class)', type: 'text', hint: 'Default: "Explore the membership"' },
                { key: 'classPageUnavailableHeading', label: 'Unavailable — heading', type: 'text', hint: 'Default: "This class isn’t available"' },
                { key: 'classPageUnavailableBody', label: 'Unavailable — body', type: 'textarea', hint: 'Default: "It may have been moved or is still being prepared. Explore everything else inside."' },
            ]
        },
        {
            section: 'Workout Completion & Streak', desc: 'The "log this workout" controls on video cards + the live post-class screen, and the Dashboard streak / challenge copy. Completions are a session log — re-doing a workout credits it again. Leave a field blank to use its default.', items: [
                { key: 'completionLogLabel', label: 'Log button — first time', type: 'text', hint: 'Default: "Log this workout". Shown on a video the member hasn\'t completed yet.' },
                { key: 'completionLogAgainLabel', label: 'Log button — repeat', type: 'text', hint: 'Default: "Log again". Shown after at least one completion — re-logging credits a repeat toward streaks/challenges.' },
                { key: 'completionLoggedLabel', label: 'Confirmation label', type: 'text', hint: 'Default: "Logged". Shown briefly after logging, alongside Undo.' },
                { key: 'completionUndoLabel', label: 'Undo label', type: 'text', hint: 'Default: "Undo".' },
                { key: 'completionLiveManualHint', label: 'Live post-class manual hint', type: 'text', hint: 'Default: "Didn\'t get auto-credited? Log it." Shown under a just-finished live class for anyone whose ≥75% auto-credit didn\'t land.' },
                { key: 'completionHistoryDisclosureLabel', label: 'History disclosure label', type: 'text', hint: 'Default: "Your history". The expandable link (on video cards + inside the player) that shows the member\'s completion dates so they can remove an accidental one.' },
                { key: 'completionHistoryRemoveLabel', label: 'History — remove row', type: 'text', hint: 'Default: "Remove". The per-date remove control in the "Your history" list.' },
                { key: 'completionRemoveConfirmLabel', label: 'Remove — confirm button', type: 'text', hint: 'Default: "Remove". The confirm step after tapping a row\'s Remove.' },
                { key: 'completionRemoveKeepLabel', label: 'Remove — keep button', type: 'text', hint: 'Default: "Keep". Dismisses the remove confirm without deleting.' },
                { key: 'streakHeldHint', label: 'Streak "held" nudge', type: 'text', hint: 'Default: "Move today to keep your streak alive!" Shown when the streak is alive on yesterday\'s workout but the member hasn\'t moved yet today.' },
                { key: 'showMonthlyChallengeStats', label: 'Show monthly challenge stats on the Dashboard', type: 'toggle', toggleLabel: 'Show "active days" + "workouts this month"', hint: 'Turn on during a challenge month to surface monthly progress under the streak.' },
                { key: 'challengeActiveDaysLabel', label: 'Active-days stat template', type: 'text', hint: 'Default: "{count} active days this month". {count} is replaced with the number.' },
                { key: 'challengeSessionsLabel', label: 'Workouts stat template', type: 'text', hint: 'Default: "{count} workouts this month". {count} is replaced with the number.' },
            ]
        },
        {
            section: 'Program — load error', desc: 'Shown to a member inside a purchased program ONLY when their workout list fails to load (a temporary video-host / network hiccup). The app retries automatically first; this is the fallback card with a Retry button, so a load failure never looks like an empty program. Leave blank to use defaults.', items: [
                { key: 'programVideosErrorText', label: 'Load-error message', type: 'text', hint: 'Default: "We couldn\'t load your workouts just now. Please try again."' },
                { key: 'programVideosRetryLabel', label: 'Retry button', type: 'text', hint: 'Default: "Retry".' },
            ]
        },
        {
            section: 'Program — "Already enrolled?" login', desc: 'Shown beside the Join Waitlist button (as an outlined button) and under the Upcoming Groups list (as a line) on a program page, to visitors who are NOT signed in — so a member who reopens the original program link doesn’t think she’s lost access. Tapping the link opens Log in and brings her back to the program. Leave blank to use defaults.', items: [
                { key: 'programAlreadyEnrolledButtonLabel', label: 'Button text (beside Join Waitlist)', type: 'text', hint: 'Default: "Already enrolled? Log in". Keep it short — it sits next to Join Waitlist.' },
                { key: 'programAlreadyEnrolledText', label: 'Line prompt (Upcoming Groups)', type: 'text', hint: 'Default: "Already enrolled?"' },
                { key: 'programAlreadyEnrolledLinkLabel', label: 'Line link text (Upcoming Groups)', type: 'text', hint: 'Default: "Log in to see your program".' },
            ]
        },
    ];

    return (
        <div style={{ paddingBottom: 72 }}>
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Workouts Page</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>Edit the text users see on the Workouts page — subscription banners, tab labels, and empty states.</p>
            </div>
            <SectionList tabKey="workouts" sections={fields} content={content} handleChange={handleChange} />
            <StickyBar saving={saving} saved={saved} onSave={handleSave} hasChanges={!saved} />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  DELETE MEMBER MODAL  (used by UsersTab)
// ═══════════════════════════════════════════════════════════════
// Three phases in one modal: PREVIEW (what will be erased, from a server dry
// run that writes nothing) → CONFIRM (type the email) → RESULT (per-step
// report). Module-scope on purpose — a component defined inside another
// component remounts on every keystroke and would drop focus in the confirm
// input.
// ── Member community profile moderation (PHOTO-002) ──────────────────────
// Shows what other members see (photo + tagline) and lets Danielle take it
// down in two taps. Writes only the memberProfiles display index — the image
// stays in the member's Clerk account but is no longer shown anywhere.
// Module scope (never nested) so it keeps state across Members-tab renders.
function MemberCommunityProfile({ userId }) {
    const photoFor = useMemberPhotos([userId]);
    const url = photoFor(userId);
    const tagline = getCachedMemberTagline(userId);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const soft = `color-mix(in srgb, ${theme.colors.charcoal} 62%, ${theme.colors.cream})`;

    const clear = async () => {
        setBusy(true); setError('');
        try { await clearMemberProfile(userId); setDone(true); setConfirming(false); }
        catch (e) { setError('Could not clear it — try again.'); }
        setBusy(false);
    };

    return (
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: `1px solid ${hexToRgba(theme.colors.charcoal, 0.08)}` }}>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 4 }}>Community profile</p>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, lineHeight: 1.5, marginBottom: 12, color: soft }}>
                The photo and line other members see next to this person's comments, Wall posts and in live classes.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', background: hexToRgba(theme.colors.charcoal, 0.08) }}>
                    {url && <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 160, fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, color: theme.colors.charcoal }}>
                    {tagline ? <span style={{ fontStyle: 'italic' }}>“{tagline}”</span> : <span style={{ color: soft }}>{url ? 'Photo only — no line' : (done ? 'Cleared' : 'No photo or line')}</span>}
                </div>
                {(url || tagline) && !confirming && (
                    <button className="mom-btn" onClick={() => setConfirming(true)} style={{
                        padding: '9px 18px', borderRadius: 10, fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                        color: theme.colors.charcoal, background: 'transparent', border: `1px solid ${hexToRgba(theme.colors.charcoal, 0.25)}`,
                    }}>Remove photo & line</button>
                )}
                {confirming && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: soft }}>Hide both from everyone?</span>
                        <button className="mom-btn" disabled={busy} onClick={clear} style={{
                            padding: '9px 18px', borderRadius: 10, fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                            color: '#fff', background: theme.colors.charcoal, border: 'none', opacity: busy ? 0.6 : 1,
                        }}>{busy ? 'Removing…' : 'Remove'}</button>
                        <button className="mom-btn" disabled={busy} onClick={() => setConfirming(false)} style={{
                            padding: '9px 14px', borderRadius: 10, fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                            color: theme.colors.charcoal, background: 'transparent', border: 'none',
                        }}>Keep</button>
                    </div>
                )}
            </div>
            {error && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: '#dc2626', marginTop: 8 }}>{error}</p>}
        </div>
    );
}

function DeleteMemberModal({ user, getToken, onClose, onDeleted }) {
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [confirmText, setConfirmText] = useState('');
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [hoverCancel, setHoverCancel] = useState(false);
    const [hoverDelete, setHoverDelete] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await adminFetch('/api/delete-user', getToken, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: user.id, dryRun: true }),
                });
                const data = await res.json();
                if (cancelled) return;
                if (!res.ok) throw new Error(data.error || 'Preview failed');
                setPreview(data);
            } catch (e) {
                if (!cancelled) setError(e.message);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [user.id, getToken]);

    const targetEmail = (preview && preview.user && preview.user.email) || user.email || '';
    const canDelete = !!targetEmail && confirmText.trim().toLowerCase() === targetEmail.trim().toLowerCase();

    const runDelete = async () => {
        setRunning(true);
        setError(null);
        try {
            const res = await adminFetch('/api/delete-user', getToken, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user.id, confirmEmail: confirmText.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Delete failed');
            setResult(data);
        } catch (e) {
            setError(e.message);
        }
        setRunning(false);
    };

    const counts = (preview && preview.counts) || {};
    const countKeys = Object.keys(counts);
    const blockers = (preview && preview.blockers) || [];
    const hardBlocked = blockers.some((b) => b.includes('ACTIVE paid subscription'));

    const label = { fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal };
    const muted = {
        fontFamily: 'var(--mom-font-sans)',
        fontSize: 13,
        color: `color-mix(in srgb, ${theme.colors.charcoal} 62%, ${theme.colors.cream})`,
        lineHeight: 1.55,
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 1200, display: 'flex',
                alignItems: 'center', justifyContent: 'center', padding: 20,
                background: 'rgba(62,47,47,0.45)', backdropFilter: 'blur(4px)',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: theme.colors.white, borderRadius: 20, padding: 28,
                    width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto',
                    boxShadow: 'var(--shadow-xl)', animation: 'modalIn 0.25s ease',
                }}
            >
                {/* ── RESULT ─────────────────────────────────────────────── */}
                {result ? (
                    <>
                        <h3 style={{ fontFamily: 'var(--mom-font-heading)', fontSize: 22, color: theme.colors.charcoal, marginBottom: 6 }}>
                            {result.clerkDeleted ? 'Member deleted' : 'Partly done'}
                        </h3>
                        <p style={{ ...muted, marginBottom: 18 }}>
                            {result.clerkDeleted
                                ? 'Their account and personal data have been removed.'
                                : 'Some steps did not finish. Nothing is stranded — the account still exists, so you can run this again.'}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
                            {result.steps.filter((s) => s.count > 0 || s.status === 'failed').map((s, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '7px 12px', background: theme.colors.cream, borderRadius: 10 }}>
                                    <span style={label}>{s.label}</span>
                                    <span style={{
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, flexShrink: 0,
                                        color: s.status === 'ok' ? theme.colors.success : '#dc2626',
                                    }}>
                                        {s.status === 'ok' ? `${s.count} removed` : (s.error || 'failed')}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {Array.isArray(result.notes) && result.notes.length > 0 && (
                            <div style={{ borderTop: `1px solid ${hexToRgba(theme.colors.charcoal, 0.08)}`, paddingTop: 14, marginBottom: 20 }}>
                                <p style={{ ...muted, fontWeight: 600, marginBottom: 6 }}>Worth checking by hand</p>
                                {result.notes.map((n, i) => (
                                    <p key={i} style={{ ...muted, marginBottom: 4 }}>— {n}</p>
                                ))}
                            </div>
                        )}
                        <button
                            className="mom-btn"
                            onClick={() => { onDeleted(); onClose(); }}
                            style={{ width: '100%', padding: '12px 20px', borderRadius: 12, border: 'none', background: theme.colors.charcoal, color: theme.colors.white, fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600 }}
                        >
                            Done
                        </button>
                    </>
                ) : (
                    <>
                        {/* ── PREVIEW + CONFIRM ──────────────────────────── */}
                        <h3 style={{ fontFamily: 'var(--mom-font-heading)', fontSize: 22, color: theme.colors.charcoal, marginBottom: 6 }}>
                            Delete this member and their data
                        </h3>
                        <p style={{ ...muted, marginBottom: 18 }}>
                            {[user.firstName, user.lastName].filter(Boolean).join(' ') || targetEmail}
                            {targetEmail ? ` · ${targetEmail}` : ''}
                        </p>

                        {loading && (
                            <p style={{ ...muted, marginBottom: 18 }}>Checking what this member has…</p>
                        )}

                        {!loading && blockers.map((b, i) => (
                            <div key={i} style={{
                                padding: '12px 14px', borderRadius: 12, marginBottom: 12,
                                background: hexToRgba('#dc2626', 0.06),
                                borderLeft: '3px solid #dc2626',
                            }}>
                                <p style={{ ...label, lineHeight: 1.5 }}>{b}</p>
                            </div>
                        ))}

                        {!loading && !error && (
                            <>
                                {countKeys.length > 0 ? (
                                    <>
                                        <p style={{ ...muted, marginBottom: 10 }}>This will permanently remove:</p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
                                            {countKeys.map((k) => (
                                                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '7px 12px', background: theme.colors.cream, borderRadius: 10 }}>
                                                    <span style={label}>{k}</span>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, flexShrink: 0 }}>{counts[k]}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <p style={{ ...muted, marginBottom: 18 }}>
                                        This member has no stored activity. Only their login account will be removed.
                                    </p>
                                )}

                                <p style={{ ...muted, marginBottom: 18 }}>
                                    Their consent record is kept but stripped of name, email and IP — it stays as
                                    anonymous proof that someone agreed to the Terms, which is worth having if a
                                    question ever comes up about the period they were using the app. Everything else
                                    goes, including their name on the Milestone Wall and in class comments.
                                    <strong style={{ color: theme.colors.charcoal }}> This cannot be undone.</strong>
                                </p>

                                <label style={{ ...muted, display: 'block', marginBottom: 6 }}>
                                    Type <strong style={{ color: theme.colors.charcoal }}>{targetEmail}</strong> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder={targetEmail}
                                    autoComplete="off"
                                    style={{
                                        width: '100%', padding: '11px 14px', borderRadius: 12, marginBottom: 18,
                                        border: `1px solid ${hexToRgba(theme.colors.charcoal, 0.15)}`,
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal,
                                        background: theme.colors.cream, boxSizing: 'border-box',
                                    }}
                                />
                            </>
                        )}

                        {error && (
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: '#dc2626', marginBottom: 18 }}>{error}</p>
                        )}

                        <div style={{ display: 'flex', gap: 10 }}>
                            <button
                                className="mom-btn"
                                onClick={onClose}
                                onMouseEnter={() => setHoverCancel(true)}
                                onMouseLeave={() => setHoverCancel(false)}
                                style={{
                                    flex: 1, padding: '12px 20px', borderRadius: 12, fontFamily: 'var(--mom-font-sans)',
                                    fontSize: 14, fontWeight: 600, color: theme.colors.charcoal,
                                    border: `1px solid ${hexToRgba(theme.colors.charcoal, hoverCancel ? 0.28 : 0.15)}`,
                                    background: hoverCancel ? theme.colors.cream : 'transparent',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="mom-btn"
                                onClick={runDelete}
                                disabled={!canDelete || running || hardBlocked || loading}
                                onMouseEnter={() => setHoverDelete(true)}
                                onMouseLeave={() => setHoverDelete(false)}
                                style={{
                                    flex: 1, padding: '12px 20px', borderRadius: 12, border: 'none',
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
                                    color: theme.colors.white,
                                    background: hoverDelete && canDelete && !running && !hardBlocked ? '#b91c1c' : '#dc2626',
                                    opacity: (!canDelete || running || hardBlocked || loading) ? 0.45 : 1,
                                    cursor: (!canDelete || running || hardBlocked || loading) ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {running ? 'Deleting…' : 'Delete permanently'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  USERS TAB
// ═══════════════════════════════════════════════════════════════
// Last successful Members load, kept for the page lifetime so coming back to the
// tab paints instantly (then refreshes quietly in the background). Module scope
// so it survives the tab unmounting. Admin-only data, admin-only page.
let usersTabCache = null; // { users, programs, completionCounts }

// Rows drawn at once. Search / filters / CSV still operate on EVERY member —
// this only limits how many rows are on screen (drawing ~5.7k rows was the
// main reason typing in search felt slow).
const USERS_PAGE_SIZE = 100;

function UsersTab() {
    const getToken = useAdminAuth();
    const [users, setUsers] = useState(() => usersTabCache?.users || []);
    const [programs, setPrograms] = useState(() => usersTabCache?.programs || []);
    const [completionCounts, setCompletionCounts] = useState(() => usersTabCache?.completionCounts || {});
    const [loading, setLoading] = useState(() => !usersTabCache);
    const [refreshing, setRefreshing] = useState(false);
    const [visibleCount, setVisibleCount] = useState(USERS_PAGE_SIZE);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [programFilter, setProgramFilter] = useState('all'); // 'all', 'any', 'none', or a program ID
    const [activityFilter, setActivityFilter] = useState('all'); // 'all', 'active', 'inactive'
    const [membershipFilter, setMembershipFilter] = useState('all'); // 'all', 'member', 'canceling', 'none'
    const [expandedUser, setExpandedUser] = useState(null);
    const [granting, setGranting] = useState(null);
    const [changingRole, setChangingRole] = useState(null);
    const [deletingUser, setDeletingUser] = useState(null);

    // `silent`: keep the current list on screen and refresh behind it (used when
    // we already have a cached list). A silent failure keeps the list rather
    // than replacing it with an error screen.
    const loadUsers = async ({ silent = false } = {}) => {
        if (silent) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const [usersRes, progs, counts] = await Promise.all([
                adminFetch('/api/users', getToken),
                fetchPrograms(),
                fetchAllUserStats(),
            ]);
            if (!usersRes.ok) {
                const err = await usersRes.json();
                throw new Error(err.error || 'Failed to fetch users');
            }
            const data = await usersRes.json();
            setUsers(data.users || []);
            setPrograms(progs || []);
            setCompletionCounts(counts || {});
        } catch (err) {
            if (!silent) setError(err.message);
            else console.error('Members background refresh failed:', err);
        }
        setLoading(false);
        setRefreshing(false);
    };

    useEffect(() => { loadUsers({ silent: !!usersTabCache }); }, []);

    // Keep the cache in step with every change (loads AND in-place edits such as
    // grant/revoke or a role change), so returning to the tab shows current data.
    useEffect(() => {
        if (loading) return;
        usersTabCache = { users, programs, completionCounts };
    }, [users, programs, completionCounts, loading]);

    const handleAccess = async (userId, programId, action) => {
        setGranting(`${userId}-${programId}`);
        try {
            const res = await adminFetch('/api/grant-access', getToken, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, programId, action }),
            });
            if (!res.ok) {
                const err = await res.json();
                alert(err.error || 'Failed to update access');
            } else {
                const data = await res.json();
                // Update local state
                setUsers((prev) => prev.map((u) =>
                    u.id === userId ? { ...u, purchasedPrograms: data.purchasedPrograms } : u
                ));
            }
        } catch (err) {
            alert('Failed to update access');
        }
        setGranting(null);
    };

    const handleRoleChange = async (userId, newRole) => {
        setChangingRole(userId);
        try {
            const res = await adminFetch('/api/grant-access', getToken, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, setRole: newRole }),
            });
            if (!res.ok) {
                const err = await res.json();
                alert(err.error || 'Failed to update role');
            } else {
                const data = await res.json();
                setUsers((prev) => prev.map((u) =>
                    u.id === userId ? { ...u, role: data.role } : u
                ));
            }
        } catch (err) {
            alert('Failed to update role');
        }
        setChangingRole(null);
    };

    const exportCSV = () => {
        const programMap = {};
        programs.forEach((p) => { programMap[p.id] = p.name; });
        const header = 'Email,First Name,Last Name,Signed Up,Last Active,Role,Membership,Programs';
        const rows = filtered.map((u) => {
            const progs = (Array.isArray(u.purchasedPrograms) ? u.purchasedPrograms : []).map((id) => programMap[id] || id).join('; ');
            const signUp = u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '';
            const lastActiveTs = u.lastActiveAt || u.lastSignInAt;
            const lastSign = lastActiveTs ? new Date(lastActiveTs).toLocaleDateString() : 'Never';
            const membership = u.classSubscription ? (u.classSubscriptionCancelAt ? 'Member (canceling)' : 'Member') : '';
            return `"${u.email}","${u.firstName}","${u.lastName}","${signUp}","${lastSign}","${u.role}","${membership}","${progs}"`;
        });
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Typing updates the box immediately; the (5k-member) filter catches up a
    // beat later instead of blocking each keystroke.
    const deferredSearch = useDeferredValue(search);
    const filtered = useMemo(() => users.filter((u) => {
        // Text search
        if (deferredSearch.trim()) {
            const s = deferredSearch.toLowerCase();
            if (!u.email.toLowerCase().includes(s) && !`${u.firstName} ${u.lastName}`.toLowerCase().includes(s)) return false;
        }
        // Program filter
        const hasPurchases = Array.isArray(u.purchasedPrograms) && u.purchasedPrograms.length > 0;
        if (programFilter === 'any' && !hasPurchases) return false;
        if (programFilter === 'none' && hasPurchases) return false;
        if (programFilter !== 'all' && programFilter !== 'any' && programFilter !== 'none') {
            if (!u.purchasedPrograms?.includes(programFilter)) return false;
        }
        // Activity filter — "active" = active within last 14 days, "inactive" = not active within 30 days.
        // Uses Clerk last_active_at (falls back to last_sign_in_at) so a member who keeps the app
        // open without re-authenticating still counts as active.
        const lastActiveTs = u.lastActiveAt || u.lastSignInAt;
        if (activityFilter === 'active') {
            if (!lastActiveTs) return false;
            const daysSince = (Date.now() - lastActiveTs) / (1000 * 60 * 60 * 24);
            if (daysSince > 14) return false;
        } else if (activityFilter === 'inactive') {
            if (lastActiveTs) {
                const daysSince = (Date.now() - lastActiveTs) / (1000 * 60 * 60 * 24);
                if (daysSince <= 30) return false;
            }
        }
        // Membership filter — "member" = active subscriber, "canceling" = active
        // but cancel-at-period-end set, "none" = no active subscription.
        const isMember = !!u.classSubscription;
        const isCanceling = isMember && !!u.classSubscriptionCancelAt;
        if (membershipFilter === 'member' && !isMember) return false;
        if (membershipFilter === 'canceling' && !isCanceling) return false;
        if (membershipFilter === 'none' && isMember) return false;
        return true;
    }), [users, deferredSearch, programFilter, activityFilter, membershipFilter]);

    // New search / filter ⇒ start from the top of the results again.
    useEffect(() => { setVisibleCount(USERS_PAGE_SIZE); }, [deferredSearch, programFilter, activityFilter, membershipFilter]);

    const programMap = {};
    programs.forEach((p) => { programMap[p.id] = p.name; });

    if (loading) return <Spinner />;

    if (error) {
        return (
            <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, padding: '40px 32px', textAlign: 'center', boxShadow: theme.shadows.sm }}>

                <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 8 }}>Could not load users</p>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 20 }}>{error}</p>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, lineHeight: 1.6 }}>Make sure <strong>CLERK_SECRET_KEY</strong> is set in your Vercel environment variables.<br />Find it in Clerk Dashboard → API Keys → Secret keys.</p>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Users</h3>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>
                        {users.length} registered user{users.length !== 1 ? 's' : ''}
                        {refreshing && <span style={{ marginLeft: 8, fontSize: 12, fontStyle: 'italic' }}>· updating…</span>}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <button className="mom-btn" onClick={() => loadUsers()} style={{ padding: '10px 20px', borderRadius: 12, fontSize: 13, background: theme.colors.cream, border: 'none', color: theme.colors.charcoal }}>Refresh</button>
                    <button className="mom-btn" onClick={exportCSV} style={{ padding: '10px 20px', borderRadius: 12, fontSize: 13, background: theme.colors.charcoal, border: 'none', color: theme.colors.white }}>Export CSV</button>
                </div>
            </div>

            {/* Search & Filters */}
            <div style={{ marginBottom: 20 }}>
                <input type="text" className="mom-input" placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 400, marginBottom: 12 }} />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginRight: 4 }}>Program:</span>
                    {[{ key: 'all', label: 'All' }, { key: 'any', label: 'Has Program' }, { key: 'none', label: 'No Program' }].map((f) => (
                        <button key={f.key} className="mom-btn" onClick={() => setProgramFilter(f.key)}
                            style={{ padding: '5px 14px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: 'none', background: programFilter === f.key ? theme.colors.charcoal : theme.colors.cream, color: programFilter === f.key ? theme.colors.white : theme.colors.charcoal, transition: 'all 0.15s' }}>
                            {f.label}
                        </button>
                    ))}
                    {programs.filter((p) => p.active !== false).map((p) => (
                        <button key={p.id} className="mom-btn" onClick={() => setProgramFilter(programFilter === p.id ? 'all' : p.id)}
                            style={{ padding: '5px 14px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: 'none', background: programFilter === p.id ? theme.colors.terracotta : theme.colors.cream, color: programFilter === p.id ? theme.colors.white : theme.colors.charcoal, transition: 'all 0.15s' }}>
                            {p.name}
                        </button>
                    ))}
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginLeft: 12, marginRight: 4 }}>Activity:</span>
                    {[{ key: 'all', label: 'All' }, { key: 'active', label: 'Active (14d)' }, { key: 'inactive', label: 'Inactive (30d+)' }].map((f) => (
                        <button key={f.key} className="mom-btn" onClick={() => setActivityFilter(f.key)}
                            style={{ padding: '5px 14px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: 'none', background: activityFilter === f.key ? theme.colors.charcoal : theme.colors.cream, color: activityFilter === f.key ? theme.colors.white : theme.colors.charcoal, transition: 'all 0.15s' }}>
                            {f.label}
                        </button>
                    ))}
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, marginLeft: 12, marginRight: 4 }}>Membership:</span>
                    {[{ key: 'all', label: 'All' }, { key: 'member', label: 'Members' }, { key: 'canceling', label: 'Canceling' }, { key: 'none', label: 'Non-members' }].map((f) => (
                        <button key={f.key} className="mom-btn" onClick={() => setMembershipFilter(f.key)}
                            style={{ padding: '5px 14px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: 'none', background: membershipFilter === f.key ? theme.colors.charcoal : theme.colors.cream, color: membershipFilter === f.key ? theme.colors.white : theme.colors.charcoal, transition: 'all 0.15s' }}>
                            {f.label}
                        </button>
                    ))}
                </div>
                {(programFilter !== 'all' || activityFilter !== 'all' || membershipFilter !== 'all') && (
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 8 }}>
                        Showing {filtered.length} of {users.length} users
                        <button className="mom-btn mom-btn-ghost" onClick={() => { setProgramFilter('all'); setActivityFilter('all'); setMembershipFilter('all'); }} style={{ fontSize: 12, color: theme.colors.terracotta, marginLeft: 8, padding: 0 }}>Clear filters</button>
                    </p>
                )}
            </div>

            {/* Users list — first USERS_PAGE_SIZE matches drawn, "Show more" for the rest. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filtered.slice(0, visibleCount).map((u) => {
                    const isExpanded = expandedUser === u.id;
                    const hasPurchases = Array.isArray(u.purchasedPrograms) && u.purchasedPrograms.length > 0;
                    const userCompletions = completionCounts[u.id] || 0;
                    // Membership ($15/mo subscription). "Canceling" = still active but
                    // cancel-at-period-end is set; shown as MEMBER + an "ENDS" cue.
                    const isMember = !!u.classSubscription;
                    const isCanceling = isMember && !!u.classSubscriptionCancelAt;
                    let cancelLabel = '';
                    if (isCanceling) {
                        const cd = new Date(u.classSubscriptionCancelAt);
                        cancelLabel = isNaN(cd.getTime()) ? 'ENDING' : `ENDS ${cd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                    }
                    // Activity: green dot = last 14 days, yellow = 14-30 days, gray = 30+ or never.
                    // Based on Clerk last_active_at (fallback last_sign_in_at) — reflects real activity,
                    // not just the last fresh sign-in, so a long-lived open session still reads as active.
                    const lastActiveTs = u.lastActiveAt || u.lastSignInAt;
                    const daysSinceActive = lastActiveTs ? (Date.now() - lastActiveTs) / (1000 * 60 * 60 * 24) : 999;
                    const activityColor = daysSinceActive <= 14 ? theme.colors.success : daysSinceActive <= 30 ? '#e8b84a' : theme.colors.warmGray;
                    const lastActiveLabel = lastActiveTs ? (daysSinceActive < 1 ? 'Today' : daysSinceActive < 2 ? 'Yesterday' : daysSinceActive < 7 ? `${Math.floor(daysSinceActive)}d ago` : new Date(lastActiveTs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })) : 'Never';
                    return (
                        <div key={u.id} style={{ background: theme.colors.white, borderRadius: theme.radius.lg, boxShadow: theme.shadows.sm, overflow: 'hidden' }}>
                            <button onClick={() => setExpandedUser(isExpanded ? null : u.id)} style={{ width: '100%', padding: '16px 24px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left' }}>
                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: u.role === 'admin' ? theme.colors.charcoal : u.role === 'friend' ? `linear-gradient(135deg, ${theme.colors.success}, #4a8a5a)` : `linear-gradient(135deg, ${theme.colors.terracotta}, ${theme.colors.terracottaLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.white, fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 700 }}>
                                        {u.firstName ? u.firstName.charAt(0).toUpperCase() : u.email.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ position: 'absolute', bottom: -1, right: -1, width: 12, height: 12, borderRadius: '50%', background: activityColor, border: `2px solid ${theme.colors.white}` }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: theme.colors.charcoal }}>{u.firstName || u.lastName ? `${u.firstName} ${u.lastName}`.trim() : u.email}</span>
                                        {u.role === 'admin' && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: theme.colors.charcoal, color: theme.colors.white }}>ADMIN</span>}
                                        {u.role === 'friend' && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: theme.colors.success, color: theme.colors.white }}>FRIEND</span>}
                                        {isMember && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: theme.colors.terracotta, color: theme.colors.white }}>MEMBER</span>}
                                        {isCanceling && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: hexToRgba('#e8b84a', 0.22), color: theme.colors.charcoal }}>{cancelLabel}</span>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>{u.email}</span>
                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>·</span>
                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: activityColor, fontWeight: 500 }}>{lastActiveLabel}</span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    {hasPurchases && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, padding: '4px 10px', borderRadius: 10, background: hexToRgba(theme.colors.success, 0.1), color: theme.colors.success, fontWeight: 600 }}>{u.purchasedPrograms.length} program{u.purchasedPrograms.length !== 1 ? 's' : ''}</span>}
                                    {userCompletions > 0 && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, padding: '4px 10px', borderRadius: 10, background: hexToRgba(theme.colors.terracotta, 0.1), color: theme.colors.terracotta, fontWeight: 600 }}>{userCompletions} workout{userCompletions !== 1 ? 's' : ''}</span>}
                                    <span style={{ fontSize: 14, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                                </div>
                            </button>
                            {isExpanded && (
                                <div style={{ padding: '0 24px 20px', borderTop: `1px solid ${theme.colors.creamDark}` }}>
                                    <div style={{ display: 'flex', gap: 24, padding: '16px 0 12px', flexWrap: 'wrap' }}>
                                        <div><span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Signed up</span><p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, marginTop: 4 }}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</p></div>
                                        <div><span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last active</span><p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, marginTop: 4 }}>{(u.lastActiveAt || u.lastSignInAt) ? new Date(u.lastActiveAt || u.lastSignInAt).toLocaleDateString() : 'Never'}</p></div>
                                    </div>

                                    {/* Role management */}
                                    {u.role !== 'admin' && (
                                        <div style={{ marginBottom: 16, padding: '14px 16px', background: theme.colors.cream, borderRadius: 12 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <div>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 2 }}>Friend Access</p>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, lineHeight: 1.4 }}>
                                                        Friends get full access to all programs and content without needing to be in a pod.
                                                    </p>
                                                </div>
                                                <button className="mom-btn" onClick={() => handleRoleChange(u.id, u.role === 'friend' ? '' : 'friend')} disabled={changingRole === u.id}
                                                    style={{
                                                        padding: '6px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, border: 'none', flexShrink: 0, marginLeft: 12,
                                                        background: u.role === 'friend' ? hexToRgba(theme.colors.terracotta, 0.1) : theme.colors.success,
                                                        color: u.role === 'friend' ? theme.colors.terracotta : theme.colors.white,
                                                        opacity: changingRole === u.id ? 0.5 : 1,
                                                    }}>
                                                    {changingRole === u.id ? '...' : u.role === 'friend' ? 'Remove' : 'Make Friend'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 12 }}>Program Access</p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {programs.map((p) => {
                                            const hasAccess = (Array.isArray(u.purchasedPrograms) && u.purchasedPrograms.includes(p.id)) || u.role === 'admin' || u.role === 'friend';
                                            const isGranting = granting === `${u.id}-${p.id}`;
                                            return (
                                                <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: theme.colors.cream, borderRadius: 12, gap: 12 }}>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal }}>{p.name}</span>
                                                    {(u.role === 'admin' || u.role === 'friend') ? (
                                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>{u.role === 'admin' ? 'Admin' : 'Friend'} access</span>
                                                    ) : (
                                                        <button className="mom-btn" onClick={() => handleAccess(u.id, p.id, hasAccess ? 'revoke' : 'grant')} disabled={isGranting}
                                                            style={{ padding: '6px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, border: 'none', background: hasAccess ? hexToRgba(theme.colors.terracotta, 0.1) : theme.colors.success, color: hasAccess ? theme.colors.terracotta : theme.colors.white, opacity: isGranting ? 0.5 : 1 }}>
                                                            {isGranting ? '...' : hasAccess ? 'Revoke' : 'Grant Access'}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {programs.length === 0 && (
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>No programs created yet.</p>
                                        )}
                                    </div>

                                    <MemberCommunityProfile userId={u.id} />

                                    {/* Danger zone — deliberately at the very bottom of the expanded
                                        row and visually quiet, so it is never adjacent to the
                                        everyday access controls above it. */}
                                    <div style={{ marginTop: 22, paddingTop: 16, borderTop: `1px solid ${hexToRgba(theme.colors.charcoal, 0.08)}` }}>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 4 }}>Delete member</p>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, lineHeight: 1.5, marginBottom: 12, color: `color-mix(in srgb, ${theme.colors.charcoal} 62%, ${theme.colors.cream})` }}>
                                            For "please delete my account and all my data" requests. Removes their
                                            activity, their name from the Milestone Wall and class comments, their
                                            notifications, and their login. You'll see exactly what goes before confirming.
                                        </p>
                                        <button
                                            className="mom-btn"
                                            onClick={() => setDeletingUser(u)}
                                            style={{
                                                padding: '9px 18px', borderRadius: 10, fontFamily: 'var(--mom-font-sans)',
                                                fontSize: 13, fontWeight: 600, color: '#dc2626', background: 'transparent',
                                                border: `1px solid ${hexToRgba('#dc2626', 0.35)}`,
                                            }}
                                        >
                                            Delete member and all data
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                {filtered.length > visibleCount && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '16px 0 8px' }}>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray }}>
                            Showing {visibleCount} of {filtered.length}
                        </span>
                        <button className="mom-btn" onClick={() => setVisibleCount((n) => n + USERS_PAGE_SIZE * 2)} style={{
                            padding: '10px 22px', borderRadius: 12, fontSize: 13, fontWeight: 600,
                            background: theme.colors.cream, border: 'none', color: theme.colors.charcoal,
                        }}>
                            Show {Math.min(USERS_PAGE_SIZE * 2, filtered.length - visibleCount)} more
                        </button>
                    </div>
                )}
                {filtered.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: theme.colors.warmGray }}>{search ? 'No users match your search.' : 'No users yet.'}</p>
                    </div>
                )}
            </div>

            {deletingUser && (
                <DeleteMemberModal
                    user={deletingUser}
                    getToken={getToken}
                    onClose={() => setDeletingUser(null)}
                    onDeleted={() => { setExpandedUser(null); loadUsers(); }}
                />
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  NOTIFICATIONS TAB
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
//  IN-APP NOTIFICATIONS SECTION (inside NotificationsTab)
// ═══════════════════════════════════════════════════════════════
const ANNOUNCEMENT_SIZE_HINT = {
    small: 'Small — a little picture above your title.',
    medium: 'Medium — a good fit for most screenshots.',
    large: 'Large — fills the pop-up width; best for artwork or a banner.',
};

// Shows the REAL member pop-up card on a phone- or computer-sized screen with
// the same frosted backdrop, at a realistic height — so a tall image shows the
// pinned button and the "more below" fade exactly as a member would see them.
// Buttons are inert here; tapping the image still opens the full-screen view.
function AnnouncementPreviewFrame({ device, notification }) {
    const phone = device === 'phone';
    const height = phone ? 600 : 520;
    return (
        <div style={{
            position: 'relative', width: phone ? 340 : 680, maxWidth: '100%', height, margin: '0 auto',
            borderRadius: phone ? 30 : 16, overflow: 'hidden', transition: 'width 0.25s ease',
            border: '1px solid color-mix(in srgb, var(--mom-charcoal) 12%, var(--mom-cream))',
            background: 'linear-gradient(160deg, color-mix(in srgb, var(--mom-primary) 14%, var(--mom-cream)), var(--mom-cream) 50%, color-mix(in srgb, var(--mom-charcoal) 6%, var(--mom-cream)))',
        }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(250, 245, 241, 0.65)' }} />
            <div style={{ position: 'absolute', inset: 0, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <InAppNotificationCard notification={notification} preview
                    maxHeight={Math.round((height - 32) * (phone ? 1 : 0.9))}
                    onClose={() => {}} onCTA={() => {}} />
            </div>
        </div>
    );
}

function InAppNotificationsSection() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ title: '', body: '', ctaLabel: '', ctaLink: '', durationDays: 7, imageUrl: '', imageSize: 'medium', audience: 'all' });
    const [imageBusy, setImageBusy] = useState(false); // an upload is in flight
    const getToken = useAdminAuth();

    useEffect(() => {
        getAllNotifications().then((n) => { setNotifications(n); setLoading(false); });
    }, []);

    const resetForm = () => {
        setForm({ title: '', body: '', ctaLabel: '', ctaLink: '', durationDays: 7, imageUrl: '', imageSize: 'medium', audience: 'all' });
        setShowForm(false);
        setEditingId(null);
    };

    const handleSave = async () => {
        if (!form.title.trim() || !form.body.trim() || imageBusy) return;
        setSaving(true);
        try {
            if (editingId) {
                await updateNotification(editingId, form);
            } else {
                await createNotification(form);
            }
            const updated = await getAllNotifications();
            setNotifications(updated);
            resetForm();
        } catch (e) {
            console.error('Error saving notification:', e);
        }
        setSaving(false);
    };

    const handleEdit = (n) => {
        setForm({ title: n.title, body: n.body, ctaLabel: n.ctaLabel || '', ctaLink: n.ctaLink || '', durationDays: n.durationDays || 7, imageUrl: n.imageUrl || '', imageSize: normalizePostImageSize(n.imageSize), audience: cleanAudience(n.audience) });
        setEditingId(n.id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this announcement? Users who haven\'t seen it yet won\'t receive it.')) return;
        await deleteNotification(id);
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    };

    const handleToggleActive = async (n) => {
        await updateNotification(n.id, { active: !n.active });
        setNotifications((prev) => prev.map((item) => item.id === n.id ? { ...item, active: !item.active } : item));
    };

    const activeCount = notifications.filter((n) => n.active && !isExpired(n)).length;

    return (
        <div style={{ marginBottom: 40 }}>
            <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>In-App Announcements</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>
                    Create pop-up announcements that appear when users open their dashboard.
                    {!loading && <span style={{ fontWeight: 600, marginLeft: 4 }}>{activeCount} active</span>}
                </p>
            </div>

            {/* Notification list */}
            {loading ? (
                <div style={{ padding: 20, textAlign: 'center' }}>
                    <FaSpinner style={{ fontSize: 18, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} />
                </div>
            ) : (
                <>
                    {notifications.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                            {notifications.map((n) => {
                                const expired = isExpired(n);
                                const remaining = daysRemaining(n);
                                const statusColor = !n.active ? theme.colors.warmGray : expired ? theme.colors.warmGray : theme.colors.success;
                                const statusLabel = !n.active ? 'Inactive' : expired ? 'Expired' : `${remaining}d remaining`;

                                return (
                                    <div key={n.id} style={{
                                        background: theme.colors.white,
                                        borderRadius: 16,
                                        padding: '18px 22px',
                                        boxShadow: theme.shadows.sm,
                                        opacity: (expired || !n.active) ? 0.6 : 1,
                                        transition: 'opacity 0.2s ease',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                    <h4 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', color: theme.colors.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</h4>
                                                    <span style={{
                                                        fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700,
                                                        padding: '2px 10px', borderRadius: 20,
                                                        background: hexToRgba(statusColor, 0.1),
                                                        color: statusColor,
                                                        whiteSpace: 'nowrap', flexShrink: 0,
                                                    }}>{statusLabel}</span>
                                                </div>
                                                <p style={{
                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray,
                                                    lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis',
                                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                                }}>{n.body.replace(/\*+/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')}</p>
                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 6 }}>
                                                    Created {new Date(n.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    {isForMembersOnly(n) ? <span> · Members only</span> : <span> · All users</span>}
                                                    {n.imageUrl && <span> · With image</span>}
                                                    {n.ctaLink && <span> · Links to <code style={{ background: theme.colors.cream, padding: '1px 5px', borderRadius: 4 }}>{n.ctaLink}</code></span>}
                                                </p>
                                            </div>
                                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                                <button onClick={() => handleToggleActive(n)}
                                                    style={{
                                                        padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                                                        fontFamily: 'var(--mom-font-sans)', cursor: 'pointer',
                                                        background: n.active ? hexToRgba(theme.colors.success, 0.08) : theme.colors.cream,
                                                        color: n.active ? theme.colors.success : theme.colors.warmGray,
                                                        border: `1px solid ${n.active ? hexToRgba(theme.colors.success, 0.2) : theme.colors.creamDark}`,
                                                        transition: 'all 0.15s ease',
                                                    }}
                                                    title={n.active ? 'Deactivate' : 'Activate'}
                                                >{n.active ? 'On' : 'Off'}</button>
                                                <button onClick={() => handleEdit(n)}
                                                    style={{
                                                        padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                                                        fontFamily: 'var(--mom-font-sans)', cursor: 'pointer',
                                                        background: theme.colors.white, color: theme.colors.charcoal,
                                                        border: `1px solid ${theme.colors.creamDark}`,
                                                        transition: 'all 0.15s ease',
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.cream}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = theme.colors.white}
                                                >Edit</button>
                                                <button onClick={() => handleDelete(n.id)}
                                                    style={{
                                                        padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                                                        fontFamily: 'var(--mom-font-sans)', cursor: 'pointer',
                                                        background: theme.colors.white, color: theme.colors.danger,
                                                        border: `1px solid ${hexToRgba(theme.colors.danger, 0.2)}`,
                                                        transition: 'all 0.15s ease',
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = hexToRgba(theme.colors.danger, 0.04)}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = theme.colors.white}
                                                >Delete</button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {notifications.length === 0 && !showForm && (
                        <div style={{ background: theme.colors.cream, borderRadius: 16, padding: '32px 24px', textAlign: 'center', marginBottom: 20 }}>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 4 }}>No announcements yet</p>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>Create one to let your users know about new features, programs, or events.</p>
                        </div>
                    )}
                </>
            )}

            {/* Create / Edit form */}
            {showForm ? (
                <div style={{ background: theme.colors.white, borderRadius: 16, padding: 28, boxShadow: theme.shadows.sm, marginBottom: 20 }}>
                    <h4 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 17, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 20 }}>
                        {editingId ? 'Edit Announcement' : 'New Announcement'}
                    </h4>

                    {/* Title */}
                    <div style={{ marginBottom: 18 }}>
                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 6, display: 'block' }}>Title *</label>
                        <input type="text" className="mom-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="New Program Dropping!" />
                    </div>

                    {/* Body with rich text */}
                    <div style={{ marginBottom: 18 }}>
                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 6, display: 'block' }}>Message *</label>
                        <RichTextArea value={form.body} onChange={(val) => setForm({ ...form, body: val })} placeholder="Write your announcement here... supports **bold**, *italic*, [links](url), and bullet lists." rows={4} />
                    </div>

                    {/* CTA Label + Link on same row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                        <div>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Button Label</label>
                            <input type="text" className="mom-input" value={form.ctaLabel} onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })} placeholder='Got it!' />
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 4 }}>Leave blank for "Got it!"</p>
                        </div>
                        <div>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Button Link</label>
                            <input type="text" className="mom-input" value={form.ctaLink} onChange={(e) => setForm({ ...form, ctaLink: e.target.value })} placeholder='/programs' />
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 4 }}>Optional — where to go when tapped</p>
                        </div>
                    </div>

                    {/* Audience — 'all' (default) or 'members' (active subscription). */}
                    <div style={{ marginBottom: 20 }}>
                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Who sees this</label>
                        <div role="radiogroup" aria-label="Who sees this" style={{ display: 'inline-flex', gap: 4, padding: 4, borderRadius: 12, background: theme.colors.cream, border: `1px solid ${hexToRgba(theme.colors.charcoal, 0.1)}` }}>
                            {[{ key: 'all', label: 'All users' }, { key: 'members', label: 'Members only' }].map((opt) => {
                                const on = form.audience === opt.key;
                                return (
                                    <button key={opt.key} type="button" role="radio" aria-checked={on}
                                        onClick={() => setForm((f) => ({ ...f, audience: opt.key }))}
                                        style={{
                                            padding: '8px 16px', borderRadius: 9, border: 'none', cursor: 'pointer',
                                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                                            background: on ? '#fff' : 'transparent',
                                            color: on ? theme.colors.charcoal : hexToRgba(theme.colors.charcoal, 0.6),
                                            boxShadow: on ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                                            transition: 'background 0.15s, color 0.15s',
                                        }}>{opt.label}</button>
                                );
                            })}
                        </div>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 4 }}>
                            {form.audience === 'members'
                                ? 'Only members with an active monthly subscription will see this. Program-only buyers and free accounts won’t.'
                                : 'Everyone who opens the app will see this — free accounts and members alike.'}
                        </p>
                    </div>

                    {/* Duration */}
                    <div style={{ marginBottom: 24 }}>
                        <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Active Duration (days)</label>
                        <input type="number" className="mom-input" value={form.durationDays} onChange={(e) => setForm({ ...form, durationDays: e.target.value })} min="1" max="365" style={{ maxWidth: 120 }} />
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 4 }}>Auto-expires after this many days. Users who haven't logged in by then won't see it.</p>
                    </div>

                    {/* Image + true-to-life preview. The preview IS the member's
                        pop-up card (InAppNotificationCard), so it can't drift. */}
                    <div style={{ marginBottom: 24 }}>
                        <CoachPostImageField
                            imageUrl={form.imageUrl}
                            imageSize={form.imageSize}
                            onChange={({ imageUrl, imageSize }) => setForm((f) => ({ ...f, imageUrl, imageSize }))}
                            onBusyChange={setImageBusy}
                            getToken={getToken}
                            previewVisible={!!(form.title.trim() || form.body.trim() || form.imageUrl)}
                            sizeHints={ANNOUNCEMENT_SIZE_HINT}
                            renderPreview={({ device }) => (
                                <AnnouncementPreviewFrame device={device} notification={{
                                    title: form.title.trim() || 'Your title',
                                    body: form.body.trim() || 'Your message',
                                    ctaLabel: form.ctaLabel.trim() || 'Got it!',
                                    imageUrl: form.imageUrl,
                                    imageSize: form.imageSize,
                                }} />
                            )}
                        />
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <button className="mom-btn mom-btn-primary" onClick={handleSave} disabled={saving || imageBusy || !form.title.trim() || !form.body.trim()}
                            style={{ padding: '12px 28px', fontSize: 14, opacity: (saving || imageBusy || !form.title.trim() || !form.body.trim()) ? 0.5 : 1 }}>
                            {saving ? 'Saving...' : imageBusy ? 'Uploading image…' : (editingId ? 'Update Announcement' : 'Create Announcement')}
                        </button>
                        <button className="mom-btn mom-btn-ghost" onClick={resetForm} style={{ fontSize: 14, color: theme.colors.warmGray }}>Cancel</button>
                    </div>
                </div>
            ) : (
                <button className="mom-btn mom-btn-primary" onClick={() => setShowForm(true)}
                    style={{ padding: '12px 24px', fontSize: 14 }}>
                    + New Announcement
                </button>
            )}

            {/* Info cards */}
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <InfoCard title="How it works" desc="Announcements appear as a premium pop-up when users open their dashboard. Each user sees it once — after they dismiss it, it won't show again." />
                <InfoCard title="Pair with push" desc="For maximum reach, create an in-app announcement AND send a push notification. Push gets them to open the app, the popup delivers the details." />
                <InfoCard title="Duration" desc="Set how many days the announcement stays active. Users who don't log in during that window won't see it — keep durations generous for important news." />
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  COACH POSTS SECTION (inside NotificationsTab)
// ═══════════════════════════════════════════════════════════════
function CoachPostsSection() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ body: '', imageUrl: '', imageSize: 'medium' });
    const [imageBusy, setImageBusy] = useState(false); // an upload is in flight
    const getToken = useAdminAuth();
    // Avatar picker state — single coach avatar applied to ALL coach posts.
    // Stored in siteConfig.coachAvatarUrl, falls back to ★ gradient when empty.
    const [siteContent, setSiteContent] = useState(null);
    const [avatarDriveImages, setAvatarDriveImages] = useState([]);
    const [avatarImagesLoading, setAvatarImagesLoading] = useState(false);
    const [showAvatarPicker, setShowAvatarPicker] = useState(false);
    const [avatarSaving, setAvatarSaving] = useState(false);

    useEffect(() => {
        getAllCoachPosts().then((p) => { setPosts(p); setLoading(false); });
        fetchSiteContent().then((c) => setSiteContent(c || {}));
    }, []);

    // Load Bunny-hosted images for the avatar picker (lazy — only when admin
    // clicks the picker button). Uses the same `fetchProgramImages()` helper
    // Workouts uses for class thumbnails, which filters directories + image
    // extensions and constructs CDN URLs. Yields objects with `imageUrl`,
    // `thumbnailUrl`, `id`, `name`, `title`.
    const loadAvatarImages = async () => {
        if (avatarDriveImages.length > 0) { setShowAvatarPicker(true); return; }
        setAvatarImagesLoading(true);
        try {
            const imgs = await fetchProgramImages();
            setAvatarDriveImages(imgs || []);
        } catch (e) { /* non-critical — picker just stays empty */ }
        setAvatarImagesLoading(false);
        setShowAvatarPicker(true);
    };

    // Auto-save on pick — single decision, no Save button needed. Merges the
    // single field into the existing siteContent blob so other admin-edited
    // fields aren't disturbed.
    const setCoachAvatar = async (url) => {
        if (!siteContent) return;
        const next = { ...siteContent, coachAvatarUrl: url };
        setSiteContent(next);
        setAvatarSaving(true);
        try {
            await updateSiteContent(next);
        } catch (e) {
            console.error('Error saving coach avatar:', e);
        }
        setAvatarSaving(false);
        setShowAvatarPicker(false);
    };

    const coachAvatarUrl = siteContent?.coachAvatarUrl || '';

    const resetForm = () => {
        setForm({ body: '', imageUrl: '', imageSize: 'medium' });
        setShowForm(false);
        setEditingId(null);
    };

    const handleSave = async () => {
        if (!form.body.trim() || imageBusy) return;
        setSaving(true);
        try {
            if (editingId) {
                await updateCoachPost(editingId, { body: form.body, imageUrl: form.imageUrl, imageSize: form.imageSize });
            } else {
                await createCoachPost({ body: form.body, imageUrl: form.imageUrl, imageSize: form.imageSize, createdBy: 'admin' });
            }
            const updated = await getAllCoachPosts();
            setPosts(updated);
            resetForm();
        } catch (e) {
            console.error('Error saving coach post:', e);
        }
        setSaving(false);
    };

    const handleEdit = (post) => {
        setForm({ body: post.body, imageUrl: post.imageUrl || '', imageSize: normalizePostImageSize(post.imageSize) });
        setEditingId(post.id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this post? It will be removed from the Dashboard.')) return;
        await deleteCoachPost(id);
        setPosts((prev) => prev.filter((p) => p.id !== id));
    };

    const activePosts = posts.filter((p) => p.status === 'active');

    return (
        <div style={{ marginBottom: 40 }}>
            <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Coach Posts</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>
                    Post messages that appear on your subscribers' Dashboard. Tips, motivation, weekly updates.
                    {!loading && <span style={{ fontWeight: 600, marginLeft: 4 }}>{activePosts.length} active</span>}
                </p>
            </div>

            {/* Avatar picker — one image applied to every coach post. Auto-saves
                on selection. Empty falls back to the ★ gradient mark. */}
            <div style={{ background: theme.colors.white, borderRadius: 14, padding: '16px 20px', border: `1px solid ${theme.colors.creamDark}`, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {/* Live preview of what subscribers see */}
                    <div style={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: coachAvatarUrl ? 'transparent' : `linear-gradient(135deg, ${theme.colors.terracotta}, ${theme.colors.charcoal})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, overflow: 'hidden',
                        border: `1px solid ${theme.colors.creamDark}`,
                    }}>
                        {coachAvatarUrl ? (
                            <img src={coachAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                        ) : (
                            <span style={{ fontSize: 16, color: '#fff', fontWeight: 700 }}>★</span>
                        )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 2 }}>
                            Your avatar for coach posts
                        </p>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, lineHeight: 1.4 }}>
                            {coachAvatarUrl ? 'Shown next to every coach post on the Dashboard.' : 'No image set — subscribers see a brand star. Pick an uploaded image to personalize.'}
                            {avatarSaving && <span style={{ marginLeft: 6, color: theme.colors.terracotta, fontWeight: 600 }}>Saving…</span>}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button onClick={loadAvatarImages}
                            style={{ background: 'none', border: `1px solid ${theme.colors.creamDark}`, borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 600, color: theme.colors.terracotta, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                            {avatarImagesLoading ? 'Loading…' : (coachAvatarUrl ? 'Change' : 'Choose image')}
                        </button>
                        {coachAvatarUrl && (
                            <button onClick={() => setCoachAvatar('')}
                                style={{ background: 'none', border: `1px solid ${theme.colors.creamDark}`, borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                Remove
                            </button>
                        )}
                    </div>
                </div>
                {showAvatarPicker && avatarDriveImages.length > 0 && (
                    <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 8 }}>
                        {avatarDriveImages.map((img) => (
                            <div key={img.id} onClick={() => setCoachAvatar(img.imageUrl)}
                                style={{
                                    cursor: 'pointer', borderRadius: 10, overflow: 'hidden',
                                    border: coachAvatarUrl === img.imageUrl ? `2px solid ${theme.colors.terracotta}` : `1px solid ${theme.colors.creamDark}`,
                                    aspectRatio: '1', transition: 'all 0.15s',
                                }}>
                                <img src={img.thumbnailUrl} alt={img.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                            </div>
                        ))}
                    </div>
                )}
                {showAvatarPicker && avatarDriveImages.length === 0 && !avatarImagesLoading && (
                    <p style={{ marginTop: 12, fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, fontStyle: 'italic' }}>
                        No uploaded images yet. Upload images from the Bunny image picker on any class or program first, then come back here.
                    </p>
                )}
            </div>

            {/* Post list */}
            {loading ? (
                <div style={{ padding: 20, textAlign: 'center' }}>
                    <FaSpinner style={{ fontSize: 18, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} />
                </div>
            ) : (
                <>
                    {posts.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                            {posts.filter((p) => p.status === 'active').map((post) => (
                                <div key={post.id} style={{
                                    background: theme.colors.white, borderRadius: 14, padding: '16px 20px',
                                    border: `1px solid ${theme.colors.creamDark}`,
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 6 }}>
                                                {post.body}
                                            </p>
                                            {post.imageUrl && (
                                                <div style={{ marginBottom: 6 }}>
                                                    <img src={post.imageUrl} alt="" style={{ maxHeight: 80, borderRadius: 8, objectFit: 'cover' }} referrerPolicy="no-referrer" />
                                                </div>
                                            )}
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>
                                                {new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                            <button onClick={() => handleEdit(post)}
                                                style={{ background: 'none', border: `1px solid ${theme.colors.creamDark}`, borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                Edit
                                            </button>
                                            <button onClick={() => handleDelete(post.id)}
                                                style={{ background: 'none', border: `1px solid ${theme.colors.creamDark}`, borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Create/Edit form */}
                    {!showForm ? (
                        <button onClick={() => setShowForm(true)}
                            style={{ width: '100%', padding: '14px 24px', borderRadius: 14, fontSize: 14, fontWeight: 700, background: theme.colors.charcoal, color: theme.colors.white, border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                            + New Post
                        </button>
                    ) : (
                        <div style={{ background: theme.colors.white, borderRadius: 16, padding: 24, border: `1px solid ${theme.colors.creamDark}` }}>
                            <h4 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 14 }}>
                                {editingId ? 'Edit Post' : 'New Post'}
                            </h4>
                            <textarea
                                className="mom-input"
                                value={form.body}
                                onChange={(e) => setForm({ ...form, body: e.target.value.slice(0, 500) })}
                                placeholder="Share something with your community..."
                                rows={4}
                                style={{ resize: 'vertical', lineHeight: 1.6, marginBottom: 4 }}
                            />
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginBottom: 12 }}>
                                {form.body.length}/500 characters
                            </p>
                            {/* Image — upload / library / link, size, and a true-to-life
                                preview (CoachPostImageField; shares CoachPostImage with the
                                Dashboard so the preview can't drift). */}
                            <div style={{ marginBottom: 20 }}>
                                <CoachPostImageField
                                    imageUrl={form.imageUrl}
                                    imageSize={form.imageSize}
                                    onChange={({ imageUrl, imageSize }) => setForm((f) => ({ ...f, imageUrl, imageSize }))}
                                    onBusyChange={setImageBusy}
                                    getToken={getToken}
                                    body={form.body}
                                    coachAvatarUrl={coachAvatarUrl}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: 10 }}>
                                <button onClick={handleSave} disabled={saving || imageBusy || !form.body.trim()}
                                    style={{ padding: '10px 24px', borderRadius: 12, fontSize: 14, fontWeight: 700, background: theme.colors.charcoal, color: theme.colors.white, border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', opacity: (saving || imageBusy || !form.body.trim()) ? 0.5 : 1 }}>
                                    {saving ? 'Saving...' : imageBusy ? 'Uploading image…' : editingId ? 'Update Post' : 'Publish Post'}
                                </button>
                                <button onClick={resetForm}
                                    style={{ padding: '10px 20px', borderRadius: 12, fontSize: 13, background: 'none', color: theme.colors.warmGray, border: `1px solid ${theme.colors.creamDark}`, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)' }}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <div style={{ marginTop: 16 }}>
                <InfoCard title="Coach Posts" desc="These posts appear on your subscribers' Dashboard in the 'from Danielle' section. They see the latest 3 posts. Keep them warm, personal, and motivational — this is your direct line to your community." />
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Reply Assistant — Admin → Replies queue + Settings section.
// UX: docs/ux/comment-reply-assistant.md · ADR: docs/decisions/adr-comment-reply-assistant.md
// All components here are module-scope (no inner-component pitfall). Hover
// state is React state, never DOM mutation.
// ═══════════════════════════════════════════════════════════════════════════

const REPLY_ATTENTION_LABELS = { question: 'asked a question', pain: 'mentions pain', personal: 'personal' };
const REPLY_MAX_CHARS = 280;
const REPLY_UNDO_MS = 6000;

function replyFill(tpl, count) { return String(tpl || '').replace(/\{count\}/g, String(count)); }

function replyRelTime(iso) {
    const t = Date.parse(iso || '');
    if (!t) return '';
    const s = Math.max(0, (Date.now() - t) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    const d = Math.floor(s / 86400);
    return d === 1 ? 'yesterday' : `${d}d ago`;
}

// Secondary text tone. Production sets --mom-warm-gray equal to charcoal (see
// reference_live_theme_values), so hierarchy is derived from charcoal instead.
const REPLY_MUTED = 'color-mix(in srgb, var(--mom-charcoal) 62%, var(--mom-cream))';
const REPLY_FAINT = 'color-mix(in srgb, var(--mom-charcoal) 45%, var(--mom-cream))';

// One card: the member's comment with Danielle's draft beneath it as a live
// text field. `ghost` renders the post-send "Sent · Undo" strip in the same slot.
function ReplyDraftCard({ draft, text, onChange, onReply, onSkip, onRedraft, sending, redrafting, error, onFocus, onBlur, ghost, onUndo, viewHref }) {
    const [hover, setHover] = useState(false);
    const [focused, setFocused] = useState(false);
    const taRef = useRef(null);
    // Auto-grow: the field is the reply bubble, so it should never scroll inside itself.
    useEffect(() => {
        const el = taRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.max(44, el.scrollHeight)}px`;
    }, [text]);

    if (ghost) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                padding: '12px 18px', borderRadius: 14, marginBottom: 10,
                background: 'color-mix(in srgb, var(--mom-success) 10%, var(--mom-white))',
                border: '1px solid color-mix(in srgb, var(--mom-success) 30%, transparent)',
                animation: 'fadeIn 0.2s ease',
            }}>
                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)' }}>
                    <strong style={{ fontWeight: 700 }}>Sent</strong>
                    <span style={{ color: REPLY_MUTED }}> · to {String(draft.memberName || 'member').split(' ')[0]}</span>
                </span>
                <button className="mom-btn mom-btn-ghost" onClick={onUndo}
                    style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)' }}>
                    Undo
                </button>
            </div>
        );
    }

    const firstName = String(draft.memberName || 'Member').trim();
    const ctx = draft.context || {};
    const chip = draft.needsAttention
        ? (REPLY_ATTENTION_LABELS[draft.attentionReason] || 'needs you')
        : (ctx.firstComment ? 'First comment' : (ctx.attendedLive ? 'Was there live' : null));
    const isDrafting = draft.status === 'drafting';
    const noDraft = !isDrafting && !(draft.draftText || '').trim() && !(text || '').trim();
    const over = (text || '').length > 240;
    const canSend = !sending && !isDrafting && (text || '').trim().length > 0 && (text || '').length <= REPLY_MAX_CHARS;

    return (
        <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                background: 'var(--mom-white)', borderRadius: 18, padding: '18px 20px 14px', marginBottom: 12,
                border: `1px solid ${draft.needsAttention ? 'color-mix(in srgb, var(--mom-primary) 28%, var(--mom-cream-dark))' : 'var(--mom-cream-dark)'}`,
                boxShadow: hover || focused ? 'var(--shadow-md, 0 6px 20px rgba(0,0,0,0.06))' : 'var(--shadow-sm, 0 2px 8px rgba(0,0,0,0.04))',
                transition: 'box-shadow 0.18s ease',
                animation: 'fadeUp 0.25s ease',
            }}>
            {/* Meta line */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: REPLY_MUTED, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <strong style={{ color: 'var(--mom-charcoal)', fontWeight: 700 }}>{firstName}</strong>
                    {draft.classTitle && <span> · {draft.classTitle}</span>}
                    <span> · {replyRelTime(draft.commentCreatedAt)}</span>
                </div>
                {chip && (
                    <span style={{
                        flexShrink: 0, fontFamily: 'var(--mom-font-sans)', fontSize: 10.5, fontWeight: 700,
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        color: draft.needsAttention ? 'var(--mom-primary)' : REPLY_MUTED,
                    }}>{chip}</span>
                )}
            </div>

            {/* Member's comment */}
            <p style={{
                fontFamily: 'var(--mom-font-sans)', fontSize: 15, lineHeight: 1.5, color: 'var(--mom-charcoal)',
                margin: '0 0 12px', wordBreak: 'break-word',
            }}>“{draft.commentText}”</p>

            {/* Draft — the reply bubble IS the field */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0, marginTop: 6,
                    background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: '#fff' }}>D</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {isDrafting ? (
                        <div style={{ padding: '11px 14px', borderRadius: 14, background: 'var(--mom-cream)', fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: REPLY_MUTED, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> Drafting…
                        </div>
                    ) : (
                        <>
                            <textarea
                                ref={taRef}
                                value={text}
                                onChange={(e) => onChange(e.target.value)}
                                onFocus={() => { setFocused(true); onFocus?.(); }}
                                onBlur={() => { setFocused(false); onBlur?.(); }}
                                placeholder={noDraft ? 'Write a reply…' : ''}
                                rows={1}
                                disabled={sending}
                                style={{
                                    width: '100%', resize: 'none', boxSizing: 'border-box',
                                    padding: '10px 14px', borderRadius: 14,
                                    border: `1.5px solid ${focused ? 'var(--mom-primary)' : 'transparent'}`,
                                    background: focused ? 'var(--mom-white)' : 'var(--mom-cream)',
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 15, lineHeight: 1.5, color: 'var(--mom-charcoal)',
                                    outline: 'none', transition: 'border-color 0.15s, background 0.15s',
                                }}
                            />
                            {(noDraft && draft.status === 'failed') && (
                                <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: REPLY_FAINT, marginTop: 4, paddingLeft: 4 }}>
                                    Couldn’t write a draft — you can still reply.
                                </div>
                            )}
                            {over && (
                                <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11.5, color: (text || '').length > REPLY_MAX_CHARS ? 'var(--mom-danger, #c0392b)' : REPLY_FAINT, marginTop: 4, textAlign: 'right' }}>
                                    {(text || '').length}/{REPLY_MAX_CHARS}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {error && (
                <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: 'var(--mom-danger, #c0392b)', marginTop: 8, paddingLeft: 40 }}>{error}</div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 10, paddingLeft: 40 }}>
                <a href={viewHref} target="_blank" rel="noreferrer"
                    style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: REPLY_FAINT, textDecoration: 'none' }}>
                    View thread
                </a>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button className="mom-btn mom-btn-ghost" onClick={onSkip} disabled={sending}
                        style={{ padding: '8px 12px', fontSize: 13, color: REPLY_MUTED }}>
                        Skip
                    </button>
                    <button className="mom-btn mom-btn-ghost" onClick={onRedraft} disabled={sending || redrafting || isDrafting}
                        style={{ padding: '8px 12px', fontSize: 13, color: REPLY_MUTED }}>
                        {redrafting ? 'Redrafting…' : 'Redraft'}
                    </button>
                    <button className="mom-btn mom-btn-primary" onClick={onReply} disabled={!canSend}
                        style={{ padding: '10px 22px', fontSize: 14, marginLeft: 6 }}>
                        {sending ? 'Sending…' : (error ? 'Retry' : 'Reply')}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ReplySectionEyebrow({ label, count, accent }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '22px 0 10px' }}>
            <span style={{ width: 20, height: 2, borderRadius: 1, background: accent }} />
            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: `color-mix(in srgb, ${accent} 58%, var(--mom-charcoal))` }}>
                {label} ({count})
            </span>
        </div>
    );
}

function RepliesTab() {
    const getToken = useAdminAuth();
    const { user } = useUser();
    const queueCtx = useContext(ReplyQueueContext);
    const queue = (queueCtx && queueCtx.queue) || [];
    const loading = !!(queueCtx && queueCtx.loading);
    const [copy, setCopy] = useState(() => getCachedSiteContent() || {});
    const [texts, setTexts] = useState({});       // id → edited text (only while dirty)
    const [ghosts, setGhosts] = useState({});     // id → { draft, sentCommentId, sentText }
    const [order, setOrder] = useState([]);       // stable display order (ghosts hold their slot)
    const [sending, setSending] = useState({});
    const [errors, setErrors] = useState({});
    const [redrafting, setRedrafting] = useState({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [batch, setBatch] = useState(null);     // { done, total, failed } while Reply all runs
    const [batchSummary, setBatchSummary] = useState(null);
    const [skipped, setSkipped] = useState(null); // null = not loaded yet
    const [skippedOpen, setSkippedOpen] = useState(false);
    const [assistantConfig, setAssistantConfig] = useState(null);
    const timersRef = useRef({});
    const cancelledRef = useRef(false);

    useEffect(() => {
        cancelledRef.current = false;
        fetchSiteContent().then((c) => { if (!cancelledRef.current && c) setCopy(c); });
        fetchReplyAssistantConfig().then((c) => { if (!cancelledRef.current) setAssistantConfig(c); });
        const timers = timersRef.current;
        return () => { cancelledRef.current = true; Object.values(timers).forEach(clearTimeout); };
    }, []);

    const queueById = useMemo(() => Object.fromEntries(queue.map((q) => [q.id, q])), [queue]);

    useEffect(() => {
        setOrder((prev) => {
            const live = new Set(queue.map((q) => q.id));
            const keep = prev.filter((id) => live.has(id) || ghosts[id]);
            const seen = new Set(keep);
            const add = queue.map((q) => q.id).filter((id) => !seen.has(id));
            return [...add, ...keep];
        });
    }, [queue, ghosts]);

    const items = useMemo(() => order.map((id) => {
        if (queueById[id]) return { kind: 'draft', draft: queueById[id] };
        if (ghosts[id]) return { kind: 'ghost', ...ghosts[id] };
        return null;
    }).filter(Boolean), [order, queueById, ghosts]);
    const needsYou = items.filter((it) => !!it.draft.needsAttention);
    const ready = items.filter((it) => !it.draft.needsAttention);
    const readyDrafts = ready.filter((it) => it.kind === 'draft').map((it) => it.draft);
    const textFor = (d) => (texts[d.id] !== undefined ? texts[d.id] : (d.draftText || ''));

    const adminName = user ? `${user.firstName || ''} ${(user.lastName || '').charAt(0)}${user.lastName ? '.' : ''}`.trim() : '';

    const sendOne = async (draft) => {
        const text = textFor(draft).trim();
        if (!text) { setErrors((e) => ({ ...e, [draft.id]: 'Write a reply first.' })); return false; }
        if (!user) { setErrors((e) => ({ ...e, [draft.id]: 'Not signed in.' })); return false; }
        setSending((s) => ({ ...s, [draft.id]: true }));
        setErrors((e) => { const n = { ...e }; delete n[draft.id]; return n; });
        const result = await sendDraftReply({ draft, text, userId: user.id, userName: adminName || 'Danielle', getToken });
        setSending((s) => { const n = { ...s }; delete n[draft.id]; return n; });
        if (result && result.alreadyAnswered) {
            // She already replied in the thread — the card leaves via the snapshot.
            setTexts((t) => { const n = { ...t }; delete n[draft.id]; return n; });
            return true;
        }
        if (!result) {
            setErrors((e) => ({ ...e, [draft.id]: 'Didn’t send. Check your connection and try again.' }));
            return false;
        }
        setGhosts((g) => ({ ...g, [draft.id]: { draft, sentCommentId: result.id, sentText: text } }));
        setTexts((t) => { const n = { ...t }; delete n[draft.id]; return n; });
        timersRef.current[draft.id] = setTimeout(() => {
            setGhosts((g) => { const n = { ...g }; delete n[draft.id]; return n; });
            delete timersRef.current[draft.id];
        }, REPLY_UNDO_MS);
        return true;
    };

    const handleUndo = async (ghost) => {
        const id = ghost.draft.id;
        clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];
        const ok = await undoSentReply({ draft: ghost.draft, sentCommentId: ghost.sentCommentId, sentText: ghost.sentText, getToken });
        setGhosts((g) => { const n = { ...g }; delete n[id]; return n; });
        if (!ok) setErrors((e) => ({ ...e, [id]: 'Couldn’t undo — the reply is still posted.' }));
    };

    const handleSkip = async (draft) => {
        await markDraftSkipped(draft.id);
        setSkipped(null);
    };

    const handleRedraft = async (draft) => {
        setRedrafting((r) => ({ ...r, [draft.id]: true }));
        setErrors((e) => { const n = { ...e }; delete n[draft.id]; return n; });
        try {
            await requestRedraft(draft.id, getToken);
            // Drop any local edit so the fresh draft shows when the snapshot lands.
            setTexts((t) => { const n = { ...t }; delete n[draft.id]; return n; });
        } catch (err) {
            setErrors((e) => ({ ...e, [draft.id]: err.message || 'Couldn’t redraft.' }));
        } finally {
            setRedrafting((r) => { const n = { ...r }; delete n[draft.id]; return n; });
        }
    };

    const runReplyAll = async () => {
        setConfirmOpen(false);
        const targets = readyDrafts.filter((d) => textFor(d).trim() && !sending[d.id]);
        if (targets.length === 0) return;
        setBatchSummary(null);
        setBatch({ done: 0, total: targets.length, failed: 0 });
        let failed = 0;
        for (let i = 0; i < targets.length; i++) {
            if (cancelledRef.current) break;
            const ok = await sendOne(targets[i]);
            if (!ok) failed++;
            setBatch({ done: i + 1, total: targets.length, failed });
        }
        setBatch(null);
        if (failed > 0) {
            setBatchSummary(`${targets.length - failed} sent · ${failed} didn’t send`);
            setTimeout(() => setBatchSummary(null), 6000);
        }
    };

    const toggleSkipped = async () => {
        const next = !skippedOpen;
        setSkippedOpen(next);
        if (next && skipped === null) setSkipped(await fetchSkippedDrafts());
    };

    const handleRestore = async (d) => {
        await restoreDraft(d.id);
        setSkipped((s) => (s || []).filter((x) => x.id !== d.id));
    };

    const readyCount = readyDrafts.filter((d) => textFor(d).trim()).length;
    const openCount = queue.length;
    const viewHrefFor = (d) => `/workouts?class=${encodeURIComponent(d.classId || '')}&comment=${encodeURIComponent(d.commentId || d.id)}`;
    const draftingOff = assistantConfig && !assistantConfig.enabled;
    const noVoice = assistantConfig && !(assistantConfig.voiceProfile || '').trim();

    const renderItem = (it) => it.kind === 'ghost'
        ? <ReplyDraftCard key={`ghost-${it.draft.id}`} draft={it.draft} ghost onUndo={() => handleUndo(it)} />
        : (
            <ReplyDraftCard
                key={it.draft.id}
                draft={it.draft}
                text={textFor(it.draft)}
                onChange={(v) => setTexts((t) => ({ ...t, [it.draft.id]: v }))}
                onReply={() => sendOne(it.draft)}
                onSkip={() => handleSkip(it.draft)}
                onRedraft={() => handleRedraft(it.draft)}
                sending={!!sending[it.draft.id]}
                redrafting={!!redrafting[it.draft.id]}
                error={errors[it.draft.id]}
                viewHref={viewHrefFor(it.draft)}
            />
        );

    return (
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
            {/* Sticky header */}
            <div style={{
                position: 'sticky', top: 0, zIndex: 5, padding: '6px 0 12px', marginBottom: 4,
                background: 'color-mix(in srgb, var(--mom-cream) 88%, transparent)',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
                    <div style={{ minWidth: 0 }}>
                        <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: 'var(--mom-charcoal)', margin: 0 }}>
                            Replies
                            {openCount > 0 && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontStyle: 'normal', fontWeight: 600, color: REPLY_MUTED, marginLeft: 10 }}>{openCount} ready</span>}
                        </h2>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, color: REPLY_MUTED, margin: '4px 0 0' }}>
                            {batch
                                ? `Sending ${batch.done} of ${batch.total}…`
                                : (batchSummary || copy.replyQueueSubline || 'Drafted in your voice. Edit anything, then reply.')}
                        </p>
                    </div>
                    {readyCount > 0 && (
                        <button className="mom-btn mom-btn-primary" onClick={() => setConfirmOpen(true)} disabled={!!batch}
                            style={{ padding: '11px 22px', fontSize: 14, flexShrink: 0, whiteSpace: 'nowrap' }}>
                            Reply all ({readyCount})
                        </button>
                    )}
                </div>
            </div>

            {noVoice && (
                <div style={{ padding: '12px 16px', borderRadius: 14, background: 'color-mix(in srgb, var(--mom-primary) 8%, var(--mom-white))', border: '1px solid color-mix(in srgb, var(--mom-primary) 25%, transparent)', fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)', marginBottom: 12, lineHeight: 1.5 }}>
                    Drafts start once Danielle’s voice profile exists. Generate it in <strong>Settings → Reply Assistant</strong>.
                </div>
            )}
            {!noVoice && draftingOff && (
                <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: REPLY_FAINT, marginBottom: 12 }}>
                    Drafting is off — turn it on in Settings → Reply Assistant. You can still reply from here.
                </div>
            )}

            {loading && items.length === 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '30px 0', fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: REPLY_MUTED }}>
                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> Loading…
                </div>
            )}

            {!loading && items.length === 0 && (
                <div style={{ textAlign: 'center', padding: '56px 0 40px' }}>
                    <div style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)' }}>
                        {copy.replyQueueEmptyTitle || 'All caught up.'}
                    </div>
                </div>
            )}

            {needsYou.length > 0 && (
                <>
                    <ReplySectionEyebrow label="Needs you" count={needsYou.filter((it) => it.kind === 'draft').length} accent="var(--mom-primary)" />
                    {needsYou.map(renderItem)}
                </>
            )}
            {ready.length > 0 && (
                <>
                    <ReplySectionEyebrow label="Ready" count={readyDrafts.length} accent="var(--mom-success)" />
                    {ready.map(renderItem)}
                </>
            )}

            {/* Skipped — collapsed, loaded on demand */}
            <div style={{ marginTop: 28, paddingTop: 14, borderTop: '1px solid var(--mom-cream-dark)' }}>
                <button onClick={toggleSkipped} className="mom-btn mom-btn-ghost" style={{ padding: '6px 0', fontSize: 12.5, color: REPLY_FAINT }}>
                    {skippedOpen ? 'Hide skipped' : 'Show skipped'}
                </button>
                {skippedOpen && (
                    <div style={{ marginTop: 8 }}>
                        {skipped === null && <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: REPLY_FAINT }}>Loading…</div>}
                        {skipped && skipped.length === 0 && <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: REPLY_FAINT }}>Nothing skipped.</div>}
                        {(skipped || []).map((d) => (
                            <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderRadius: 12, background: 'var(--mom-white)', border: '1px solid var(--mom-cream-dark)', marginBottom: 8 }}>
                                <div style={{ minWidth: 0, fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)' }}>
                                    <strong style={{ fontWeight: 700 }}>{String(d.memberName || 'Member').split(' ')[0]}</strong>
                                    <span style={{ color: REPLY_MUTED }}> · “{String(d.commentText || '').slice(0, 90)}{String(d.commentText || '').length > 90 ? '…' : ''}”</span>
                                </div>
                                <button className="mom-btn mom-btn-ghost" onClick={() => handleRestore(d)} style={{ padding: '6px 10px', fontSize: 12.5, fontWeight: 600, color: 'var(--mom-primary)', flexShrink: 0 }}>Restore</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Reply-all confirmation */}
            {confirmOpen && (
                <div onClick={() => setConfirmOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(45,41,38,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, animation: 'fadeIn 0.15s ease' }}>
                    <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
                        style={{ width: '100%', maxWidth: 420, background: 'var(--mom-white)', borderRadius: 22, padding: '28px 26px 22px', boxShadow: 'var(--shadow-xl, 0 24px 60px rgba(0,0,0,0.18))', animation: 'modalIn 0.22s ease' }}>
                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', margin: '0 0 10px' }}>
                            {replyFill(copy.replyAllHeading || 'Send {count} replies?', readyCount)}
                        </h3>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, lineHeight: 1.55, color: REPLY_MUTED, margin: '0 0 22px' }}>
                            {copy.replyAllBody || 'They’ll post as you, and each member will get the usual notification. Anything you’ve edited sends exactly as you wrote it.'}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button className="mom-btn mom-btn-ghost" onClick={() => setConfirmOpen(false)} style={{ padding: '10px 16px', fontSize: 14 }}>
                                {copy.replyAllBackLabel || 'Back'}
                            </button>
                            <button className="mom-btn mom-btn-primary" onClick={runReplyAll} style={{ padding: '11px 24px', fontSize: 14 }}>
                                {replyFill(copy.replyAllConfirmLabel || 'Send all {count}', readyCount)}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Settings → Reply Assistant. Its own doc (replyAssistant/config), its own save —
// deliberately NOT siteConfig, which is fetched by every visitor.
const REPLY_HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'am' : 'pm'}` }));

function ReplyAssistantSettings({ getToken }) {
    const [cfg, setCfg] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [genResult, setGenResult] = useState(null);
    const [sweeping, setSweeping] = useState(false);
    const [sweepResult, setSweepResult] = useState(null);

    useEffect(() => { fetchReplyAssistantConfig().then(setCfg); }, []);

    const set = (key, value) => { setCfg((c) => ({ ...c, [key]: value })); setDirty(true); setSaved(false); };

    const save = async () => {
        setSaving(true);
        const ok = await updateReplyAssistantConfig({
            enabled: !!cfg.enabled,
            model: (cfg.model || '').trim(),
            // Comment screening (member replies) — its OWN model setting (owner decision).
            screenEnabled: cfg.screenEnabled !== false,
            screenModel: (cfg.screenModel || '').trim(),
            voiceProfile: cfg.voiceProfile || '',
            windowDays: Math.min(30, Math.max(1, parseInt(cfg.windowDays, 10) || 7)),
            nudgeEnabled: !!cfg.nudgeEnabled,
            nudgeHoursCT: (cfg.nudgeHoursCT || [17, 21]).map((h) => parseInt(h, 10)).filter((h) => h >= 0 && h <= 23),
        });
        setSaving(false);
        if (ok) { setDirty(false); setSaved(true); setTimeout(() => setSaved(false), 2500); }
    };

    const generate = async () => {
        if ((cfg.voiceProfile || '').trim() && !window.confirm('Replace the current voice profile with a fresh one learned from all of Danielle’s replies? Any hand edits to the profile will be lost.')) return;
        setGenerating(true);
        setGenResult(null);
        try {
            const r = await generateVoiceProfile(getToken);
            setCfg((c) => ({ ...c, voiceProfile: r.profile, voiceProfileGeneratedAt: new Date().toISOString(), voiceProfileSampleSize: r.sampleSize }));
            setGenResult({ ok: true, text: `Learned from ${r.sampleSize} of ${r.totalPairs} replies.` });
        } catch (err) {
            setGenResult({ ok: false, text: err.message || 'Couldn’t generate the profile.' });
        } finally {
            setGenerating(false);
        }
    };

    const sweepNow = async () => {
        setSweeping(true);
        setSweepResult(null);
        try {
            const r = await runReplySweepNow(getToken);
            const bits = [`Drafted ${r.drafted}`];
            if (r.shells) bits.push(`listed ${r.shells} without a draft`);
            if (r.reconciled) bits.push(`closed ${r.reconciled}`);
            if (r.failed) bits.push(`${r.failed} failed`);
            let text = bits.join(', ') + '.';
            if (r.hasVoice === false) text += ' Generate the voice profile first.';
            else if (r.remaining > 0) text += ` ${r.remaining} still waiting — click again.`;
            setSweepResult({ ok: true, text });
        } catch (err) {
            setSweepResult({ ok: false, text: err.message || 'Sweep failed.' });
        } finally {
            setSweeping(false);
        }
    };

    if (!cfg) return null;

    const toggle = (key) => (
        <button onClick={() => set(key, !cfg[key])} style={{ width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer', background: cfg[key] ? 'var(--mom-primary)' : theme.colors.creamDark, position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: cfg[key] ? 27 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
        </button>
    );
    const label = { fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, display: 'block', marginBottom: 6 };
    const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: `1px solid ${theme.colors.creamDark}`, fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, background: theme.colors.white };
    const hint = { fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 6, lineHeight: 1.5 };
    const hasProfile = !!(cfg.voiceProfile || '').trim();
    const hours = Array.isArray(cfg.nudgeHoursCT) ? cfg.nudgeHoursCT : [17, 21];

    return (
        <>
            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: theme.colors.charcoal, marginTop: 40, marginBottom: 8 }}>Reply Assistant</h2>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 16, lineHeight: 1.6 }}>
                Drafts replies to member comments in Danielle’s voice for review in <strong>Replies</strong>. Nothing posts until she taps Reply.
                Generate the voice profile first, then turn drafting on.
            </p>
            <div style={{ background: theme.colors.white, borderRadius: 16, padding: '20px 24px', border: `1px solid ${cfg.enabled ? 'var(--mom-primary)' : theme.colors.creamDark}`, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                        <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, fontWeight: 700, color: theme.colors.charcoal, marginBottom: 4 }}>Drafting</div>
                        <div style={hint}>When off, the Replies queue still lists unanswered comments with empty reply fields{!hasProfile ? ' — and it stays off until a voice profile exists' : ''}.</div>
                    </div>
                    {toggle('enabled')}
                </div>

                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ ...label, marginBottom: 0 }}>Voice profile</span>
                        <button className="mom-btn mom-btn-primary" onClick={generate} disabled={generating} style={{ padding: '9px 18px', fontSize: 13 }}>
                            {generating ? 'Reading her replies…' : (hasProfile ? 'Regenerate from my replies' : 'Generate from my replies')}
                        </button>
                    </div>
                    <textarea value={cfg.voiceProfile || ''} onChange={(e) => set('voiceProfile', e.target.value)} rows={10}
                        placeholder="Generate to learn Danielle’s voice from her real replies — or write it by hand."
                        style={{ ...input, resize: 'vertical', lineHeight: 1.5, fontSize: 13 }} />
                    <div style={hint}>
                        {cfg.voiceProfileGeneratedAt
                            ? `Last generated ${new Date(cfg.voiceProfileGeneratedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} from ${cfg.voiceProfileSampleSize || '?'} replies. `
                            : 'Not generated yet. '}
                        This is the one place to say “sound more like this” or “never say that” — edit it freely, then save.
                    </div>
                    {genResult && <div style={{ ...hint, color: genResult.ok ? 'var(--mom-success)' : 'var(--mom-danger, #c0392b)' }}>{genResult.text}</div>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                    <div>
                        <span style={label}>Model</span>
                        <input value={cfg.model || ''} onChange={(e) => set('model', e.target.value)} placeholder="claude-haiku-5-5" style={input} />
                        <div style={hint}>Anthropic model alias for drafts. Blank uses the default. Change here — no deploy needed.</div>
                    </div>
                    <div>
                        <span style={label}>Comment screening model</span>
                        <input value={cfg.screenModel || ''} onChange={(e) => set('screenModel', e.target.value)} placeholder="claude-haiku-5-5" style={input} />
                        <div style={hint}>Screens member comments and replies for abuse and spam. Separate from the drafting model, so changing one never changes the other. Blank uses the default.</div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, cursor: 'pointer' }}>
                            <input type="checkbox" checked={cfg.screenEnabled !== false} onChange={(e) => set('screenEnabled', e.target.checked)} />
                            Screen member comments with AI
                        </label>
                        <div style={hint}>Off ⇒ comments publish unscreened; members' reports still work.</div>
                    </div>
                    <div>
                        <span style={label}>Draft window (days)</span>
                        <input type="number" min={1} max={30} value={cfg.windowDays ?? 7} onChange={(e) => set('windowDays', e.target.value)} style={input} />
                        <div style={hint}>Only comments this recent get drafts. Older ones are left alone.</div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700, color: theme.colors.charcoal, marginBottom: 4 }}>Nudge</div>
                        <div style={hint}>A bell notification (in-app only) when replies are waiting, at these times (Central).</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            {[0, 1].map((i) => (
                                <select key={i} value={hours[i] ?? ''} onChange={(e) => { const next = [...hours]; next[i] = parseInt(e.target.value, 10); set('nudgeHoursCT', next.filter((h) => Number.isFinite(h))); }} style={{ ...input, width: 'auto', padding: '8px 10px' }}>
                                    {REPLY_HOUR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            ))}
                        </div>
                    </div>
                    {toggle('nudgeEnabled')}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 4, borderTop: `1px solid ${theme.colors.creamDark}` }}>
                    <button className="mom-btn mom-btn-primary" onClick={save} disabled={saving || !dirty} style={{ padding: '10px 24px', fontSize: 14 }}>
                        {saving ? 'Saving…' : (saved ? 'Saved' : 'Save assistant settings')}
                    </button>
                    <button className="mom-btn" onClick={sweepNow} disabled={sweeping} style={{ padding: '10px 20px', fontSize: 14, border: `1.5px solid ${theme.colors.primary}`, color: theme.colors.primary, background: 'none' }}>
                        {sweeping ? 'Running…' : 'Draft now'}
                    </button>
                    {sweepResult && <span style={{ ...hint, marginTop: 0, color: sweepResult.ok ? theme.colors.warmGray : 'var(--mom-danger, #c0392b)' }}>{sweepResult.text}</span>}
                </div>
            </div>
        </>
    );
}

function NotificationsTab() {
    const getToken = useAdminAuth();
    const [title, setTitle] = useState('Moments of Movement');
    const [body, setBody] = useState('');
    const [url, setUrl] = useState('/');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [subscriberCount, setSubscriberCount] = useState(null);
    const [subscribers, setSubscribers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [showSubscribers, setShowSubscribers] = useState(false);
    const [subscribersLoading, setSubscribersLoading] = useState(false);

    // Load subscriber count and data on mount
    useEffect(() => {
        async function loadSubscribers() {
            try {
                if (!db) return;
                const { getDocs, collection } = await import('firebase/firestore');
                const snap = await getDocs(collection(db, 'pushSubscriptions'));
                setSubscriberCount(snap.size);
                setSubscribers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (e) { setSubscriberCount(0); }
        }
        loadSubscribers();
    }, []);

    const loadSubscriberDetails = async () => {
        if (allUsers.length > 0) { setShowSubscribers(!showSubscribers); return; }
        setSubscribersLoading(true);
        try {
            const res = await adminFetch('/api/users', getToken);
            if (res.ok) {
                const raw = await res.json();
                setAllUsers(Array.isArray(raw) ? raw : (raw.users || []));
            }
        } catch (e) { }
        setSubscribersLoading(false);
        setShowSubscribers(true);
    };

    const handleSend = async () => {
        if (!body.trim()) return;
        if (!window.confirm(`Send this notification to ${subscriberCount ?? 'all'} subscriber${subscriberCount !== 1 ? 's' : ''}?\n\n"${title}"\n${body.trim()}`)) return;
        setSending(true);
        setResult(null);
        try {
            const res = await adminFetch('/api/send-notification', getToken, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: title.trim(), body: body.trim(), url: url.trim() }),
            });
            const data = await res.json();
            if (data.success) {
                setResult({ type: 'success', message: `Sent to ${data.sent} subscriber${data.sent !== 1 ? 's' : ''}${data.failed > 0 ? ` (${data.failed} failed)` : ''}` });
                setBody('');
            } else {
                setResult({ type: 'error', message: data.error || 'Failed to send' });
            }
        } catch (e) {
            setResult({ type: 'error', message: e.message });
        }
        setSending(false);
    };

    return (
        <div>
            {/* Coach Posts section */}
            <CoachPostsSection />

            {/* Divider between sections */}
            <div style={{ height: 1, background: theme.colors.creamDark, margin: '40px 0 32px' }} />

            {/* In-App Announcements section */}
            <InAppNotificationsSection />

            {/* Divider between sections */}
            <div style={{ height: 1, background: theme.colors.creamDark, margin: '40px 0 32px' }} />

            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Push Notifications</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>
                    Send a notification to all users who have opted in.
                    {subscriberCount !== null && <span style={{ fontWeight: 600, marginLeft: 4 }}>{subscriberCount} subscriber{subscriberCount !== 1 ? 's' : ''}</span>}
                </p>
            </div>

            {/* Subscriber list — collapsible */}
            {subscriberCount > 0 && (
                <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, marginBottom: 24, boxShadow: theme.shadows.sm, overflow: 'hidden' }}>
                    <button onClick={loadSubscriberDetails} style={{ width: '100%', padding: '16px 24px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', color: theme.colors.charcoal }}>Subscribers</span>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, background: theme.colors.cream, padding: '2px 10px', borderRadius: 20 }}>{subscriberCount}</span>
                        </div>
                        <span style={{ fontSize: 16, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: showSubscribers ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                    </button>
                    {showSubscribers && (
                        <div style={{ padding: '0 24px 20px', borderTop: `1px solid ${theme.colors.creamDark}` }}>
                            {subscribersLoading ? (
                                <div style={{ padding: '16px 0' }}><FaSpinner style={{ fontSize: 16, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} /></div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 14 }}>
                                    {subscribers.map((sub) => {
                                        const user = allUsers.find((u) => u.id === sub.userId);
                                        const deviceLabel = sub.userAgent ? (
                                            /iPhone|iPad/.test(sub.userAgent) ? 'iOS' :
                                            /Android/.test(sub.userAgent) ? 'Android' :
                                            /Mac/.test(sub.userAgent) ? 'Mac' :
                                            /Windows/.test(sub.userAgent) ? 'Windows' : 'Browser'
                                        ) : 'Unknown';
                                        return (
                                            <div key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: theme.colors.cream, borderRadius: 10 }}>
                                                <div style={{ width: 28, height: 28, borderRadius: '50%', background: theme.colors.terracotta, color: theme.colors.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                                    {(user?.firstName || user?.email || sub.userId || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {user ? (user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.email) : sub.userId}
                                                    </p>
                                                    {user && user.firstName && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>{user.email}</p>}
                                                </div>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '2px 8px', borderRadius: 8, background: theme.colors.white, color: theme.colors.warmGray, fontWeight: 600, flexShrink: 0 }}>{deviceLabel}</span>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, color: theme.colors.warmGray, flexShrink: 0 }}>{sub.createdAt ? new Date(sub.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, padding: '32px', boxShadow: theme.shadows.sm }}>
                {/* Title */}
                <div style={{ marginBottom: 20 }}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Notification Title</label>
                    <input type="text" className="mom-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Moments of Movement" />
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 4 }}>Shown as the bold heading in the notification. Leave as default or customize.</p>
                </div>

                {/* Message */}
                <div style={{ marginBottom: 20 }}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 6, display: 'block' }}>Message *</label>
                    <textarea className="mom-input" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Hey! New workouts just dropped — come check them out!" style={{ resize: 'vertical', lineHeight: 1.6 }} />
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 4 }}>Keep it short and friendly — 1-2 sentences work best on mobile notifications.</p>
                </div>

                {/* Link URL */}
                <div style={{ marginBottom: 24 }}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Link (where to go when tapped)</label>
                    <input type="text" className="mom-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/" />
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 4 }}>
                        Examples: <code style={{ background: theme.colors.cream, padding: '1px 5px', borderRadius: 4 }}>/</code> (dashboard), <code style={{ background: theme.colors.cream, padding: '1px 5px', borderRadius: 4 }}>/programs</code>, <code style={{ background: theme.colors.cream, padding: '1px 5px', borderRadius: 4 }}>/free-workouts</code>, or a full URL.
                    </p>
                </div>

                {/* Preview */}
                {body.trim() && (
                    <div style={{ marginBottom: 24, padding: '16px 20px', background: theme.colors.cream, borderRadius: 14 }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Preview</p>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ fontSize: 16, color: 'white' }}>M</span>
                            </div>
                            <div>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700, color: theme.colors.charcoal, marginBottom: 2 }}>{title.trim() || 'Moments of Movement'}</p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, lineHeight: 1.5 }}>{body.trim()}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Result */}
                {result && (
                    <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 12, background: result.type === 'success' ? hexToRgba(theme.colors.success, 0.08) : hexToRgba(theme.colors.danger, 0.08), border: `1px solid ${result.type === 'success' ? hexToRgba(theme.colors.success, 0.2) : hexToRgba(theme.colors.danger, 0.2)}` }}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: result.type === 'success' ? theme.colors.success : theme.colors.danger, fontWeight: 600 }}>{result.type === 'success' ? '✓' : '✕'} {result.message}</p>
                    </div>
                )}

                {/* Send button */}
                <button className="mom-btn mom-btn-primary" onClick={handleSend} disabled={sending || !body.trim()} style={{ padding: '14px 36px', fontSize: 15, opacity: (sending || !body.trim()) ? 0.5 : 1 }}>
                    {sending ? 'Sending...' : 'Send Notification'}
                </button>
            </div>

            {/* Info cards */}
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <InfoCard title="How it works" desc="Users opt in from their Dashboard or Account page. Notifications are delivered instantly to their device — even when the app isn't open." />
                <InfoCard title="iOS users" desc="Push notifications work on iPhones when users have added the app to their Home Screen (Settings → Safari → Add to Home Screen)." />
                <InfoCard title="Best practices" desc="Keep messages short and personal. Use notifications for new content drops, weekly motivation, cohort reminders, or special announcements. Don't over-send — 1-2 per week is ideal." />
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  EMAILS TAB — Flodesk-powered transactional automations
//
//  Each automation is a card with a toggle, a Flodesk Segment ID input,
//  and a "Test send to me" button. The stripe-webhook reads
//  siteConfig.emailAutomations to decide whether to add the user to the
//  configured Flodesk segment (which triggers the workflow attached to it).
//
//  Safety: all automations ship DISABLED with empty segment IDs. Even after
//  pasting an ID, the toggle stays OFF until the admin flips it. The
//  "Test send to me" button is the verification gate.
//
//  V2 placeholder cards (subscriptionCancelled, subscriptionPaymentFailed)
//  render as "locked" — visible but disabled. The webhook code doesn't wire
//  them up yet; that lands in a follow-on PR.
//
//  See:
//    - docs/decisions/adr-email-automations.md
//    - docs/ux/admin-emails-tab.md
// ═══════════════════════════════════════════════════════════════
function EmailsTab({ unsavedRef }) {
    const getToken = useAdminAuth();
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    // Recipe panel: defaults open on first visit, collapsed after admin
    // engages (flips a toggle or pastes a segment ID). Persisted to
    // localStorage so the second visit doesn't reopen it.
    const [recipeOpen, setRecipeOpen] = useState(false); // collapsed by default — expand on demand
    const markRecipeSeen = () => {
        try { window.localStorage.setItem('mom_emails_recipe_seen', '1'); } catch (e) { /* private mode */ }
    };

    useEffect(() => {
        fetchSiteContent().then((c) => {
            setContent(c || {});
            setLoading(false);
        });
    }, []);

    // Order matters — this is the render order.
    const cardDefs = [
        {
            key: 'subscriptionWelcome',
            title: 'Subscription Welcome',
            description: 'Fires when a new user pays for the monthly subscription. Also fires when a user cancels and later re-subscribes (treated as a fresh welcome).',
            locked: false,
        },
        {
            key: 'programWelcomeGeneric',
            title: 'Program Welcome (generic)',
            description: 'Fires when a user buys any program — UNLESS that program has its own override segment set on the Programs tab.',
            extra: { kind: 'info', text: 'To use a different welcome per program, set an override on each program in the Programs tab.' },
            locked: false,
        },
        {
            key: 'subscriptionCancelled',
            title: 'Subscription Cancelled (winback)',
            description: 'Fires when a user cancels their subscription. Useful for warm "we\'ll miss you" messages or feedback collection.',
            locked: true,
        },
        {
            key: 'subscriptionPaymentFailed',
            title: 'Subscription Payment Failed',
            description: 'Fires when Stripe is unable to charge a recurring subscription payment. Useful for "please update your card" prompts before access is revoked.',
            locked: true,
        },
    ];

    const automations = (content && content.emailAutomations) || {};

    // Update a single automation field. Always works on the WHOLE
    // emailAutomations object so the StickyBar Save persists everything.
    const updateAutomation = (key, fieldPatch) => {
        if (unsavedRef) unsavedRef.current = true;
        setContent((prev) => {
            const prevAuto = (prev && prev.emailAutomations) || {};
            const prevCard = prevAuto[key] || {};
            return {
                ...prev,
                emailAutomations: {
                    ...prevAuto,
                    [key]: { ...prevCard, ...fieldPatch },
                },
            };
        });
        setSaved(false);
    };

    const handleToggle = (key) => {
        const current = automations[key] || {};
        if (current.locked) return; // locked cards can't be toggled
        updateAutomation(key, { enabled: !current.enabled });
        markRecipeSeen();
    };

    const handleSegmentChange = (key, value) => {
        updateAutomation(key, { flodeskSegmentId: value });
        if (value && value.trim()) markRecipeSeen();
    };

    // ── Audience segments (standing lists — people join AND leave) ──
    // Deliberately separate state + save key from the one-shot welcome automations
    // above, because they behave differently and must not be confused for each other.
    const updateAudience = (key, patch) => {
        if (unsavedRef) unsavedRef.current = true;
        setContent((prev) => {
            const prevAll = (prev && prev.audienceSegments) || {};
            return { ...prev, audienceSegments: { ...prevAll, [key]: { ...(prevAll[key] || {}), ...patch } } };
        });
        setSaved(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const ok = await updateSiteContent({
                emailAutomations: content.emailAutomations || {},
                audienceSegments: content.audienceSegments || {},
            });
            if (ok) {
                setSaved(true);
                if (unsavedRef) unsavedRef.current = false;
                setTimeout(() => setSaved(false), 3000);
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading || !content) return <Spinner />;

    return (
        <div style={{ paddingBottom: 72 }}>
            {/* ── Page header ───────────────────────────────────────── */}
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Emails</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 12 }}>
                    Automated emails sent to users at key moments — designed in Flodesk, triggered by our system.
                </p>
                <a href="https://app.flodesk.com" target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.terracotta, textDecoration: 'none', fontWeight: 600 }}>
                    Open Flodesk dashboard <span style={{ fontSize: 14 }}>↗</span>
                </a>
            </div>

            {/* ── Setup recipe panel ────────────────────────────────── */}
            <EmailsRecipePanel open={recipeOpen} onToggle={() => setRecipeOpen(!recipeOpen)} />

            {/* ── Cards ─────────────────────────────────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                {cardDefs.map((def) => (
                    <EmailAutomationCard
                        key={def.key}
                        def={def}
                        value={automations[def.key] || {}}
                        onToggle={() => handleToggle(def.key)}
                        onSegmentChange={(v) => handleSegmentChange(def.key, v)}
                        getToken={getToken}
                    />
                ))}
            </div>

            {/* ── Audience segments ─────────────────────────────────── */}
            <AudienceSegmentsPanel
                values={content.audienceSegments || {}}
                onChange={updateAudience}
                getToken={getToken}
                unsavedRef={unsavedRef}
            />

            <StickyBar saving={saving} saved={saved} onSave={handleSave} hasChanges={!saved} />
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  AUDIENCE SEGMENTS — three STANDING lists, kept in step with reality
//
//  Deliberately rendered as its own block, NOT as more cards above. The cards
//  above send an email once when something happens. These are audiences people
//  join AND LEAVE. Put them in the same list and the removals come as a nasty
//  surprise. See docs/decisions/adr-flodesk-audience-segments.md.
//
//  Module scope, not nested — a nested component would remount on every render
//  and drop focus from the segment-ID inputs on each keystroke.
// ══════════════════════════════════════════════════════════════════════
const AUDIENCE_DEFS = [
    { key: 'activeMembers', title: 'Active Members', desc: 'Everyone currently paying for the membership. Includes members who have cancelled but still have access until their period ends — they are still paying. Comped "friend" accounts are included; your own admin account is not.' },
    { key: 'freeSignups', title: 'Free Signups', desc: 'People with an account who have never paid you anything. They leave this list the moment they buy anything — a membership or a program.' },
    { key: 'programBuyers', title: 'Program Buyers', desc: 'Anyone who has bought at least one program. Program access is for life, so nobody ever leaves this list. Separate from the per-program welcome emails on the Programs tab.' },
];

function AudienceSegmentsPanel({ values, onChange, getToken, unsavedRef }) {
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [progress, setProgress] = useState('');

    const callOnce = async (dry) => {
        const res = await adminFetch(`/api/admin-sync-audiences?dry=${dry ? '1' : '0'}`, getToken, { method: 'POST' });
        const raw = await res.text();
        let data = null;
        try { data = JSON.parse(raw); } catch (_) { /* handled below */ }
        if (!data) throw new Error(`HTTP ${res.status} — ${raw.slice(0, 140) || 'no response body'}`);
        return { ...data, requestOk: res.ok };
    };

    // A real sync runs in short, paced server rounds (Flodesk allows ~100
    // requests/min). We keep calling until the server says `complete`, so one
    // press finishes the job. Every round re-reads the segment and only acts on
    // the difference, so stopping part-way (closing the page) is always safe —
    // the next press carries on. Fix 2026-09-28: first syncs stopped at 52 of 333.
    const run = async (dry) => {
        setRunning(true);
        setResult(null);
        setProgress('');
        try {
            if (dry) { setResult(await callOnce(true)); return; }
            const totals = {};
            let last = null;
            let stalled = 0;
            for (let round = 1; round <= 60; round += 1) {
                const data = await callOnce(false);
                last = data;
                if (!data.requestOk || !Array.isArray(data.segments)) break;
                let progressed = 0;
                let remaining = 0;
                data.segments.forEach((s) => {
                    const t = totals[s.key] || (totals[s.key] = { key: s.key, label: s.label, added: 0, removed: 0 });
                    t.added += s.added || 0;
                    t.removed += s.removed || 0;
                    // Failures are re-attempted every round, so show the latest round's, not a running sum.
                    t.failedCount = s.failedCount || 0;
                    t.failures = s.failures || [];
                    t.remaining = s.remaining || 0;
                    t.error = s.error || null;
                    progressed += (s.added || 0) + (s.removed || 0);
                    remaining += s.remaining || 0;
                });
                if (data.complete || data.ok === false) break;
                const rateLimited = data.segments.some((s) => s.rateLimited);
                if (progressed === 0 && !rateLimited) { stalled += 1; if (stalled >= 2) break; } else stalled = 0;
                const addedSoFar = Object.values(totals).reduce((n, t) => n + t.added + t.removed, 0);
                setProgress(`Syncing… ${addedSoFar} updated so far${remaining ? ` · about ${remaining} to go` : ''}. Keep this page open.`);
                if (rateLimited) await new Promise((r) => setTimeout(r, 30000));
            }
            setResult(last && Array.isArray(last.segments)
                ? { ...last, segments: last.segments.map((s) => ({ ...s, ...(totals[s.key] || {}) })) }
                : last);
        } catch (err) {
            setResult({ requestOk: false, error: err?.message || String(err) });
        } finally {
            setRunning(false);
            setProgress('');
        }
    };

    const anyConfigured = AUDIENCE_DEFS.some((d) => String(values?.[d.key]?.flodeskSegmentId || '').trim());

    return (
        <div style={{ marginTop: 32 }}>
            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Audience segments</h3>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, color: theme.colors.warmGray, lineHeight: 1.6, marginBottom: 14 }}>
                These are <strong>not</strong> emails. They are three lists in Flodesk that we keep matching reality — people are
                <strong> added and removed</strong> as their membership changes, so you can message each group separately.
            </p>

            <div style={{ background: '#fff8e6', border: '1px solid #e8cf94', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, lineHeight: 1.65, fontWeight: 600, marginBottom: 6 }}>
                    Before you turn these on
                </p>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, lineHeight: 1.65 }}>
                    Create a <strong>brand-new, empty segment in Flodesk for each one</strong>, and do <strong>not</strong> attach a
                    workflow to them. Syncing only moves people in and out of a segment — but if that segment has an email workflow
                    attached, everyone added will receive it. Always press <strong>Preview</strong> first; it writes nothing.
                </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {AUDIENCE_DEFS.map((d) => {
                    const v = values?.[d.key] || {};
                    const on = v.enabled === true;
                    return (
                        <div key={d.key} style={{ background: theme.colors.white, border: `1px solid ${theme.colors.creamDark}`, borderRadius: 14, padding: '16px 18px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 8 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 700, color: theme.colors.charcoal }}>{d.title}</p>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, lineHeight: 1.55, marginTop: 3 }}>{d.desc}</p>
                                </div>
                                <button className="mom-btn" onClick={() => onChange(d.key, { enabled: !on })}
                                    style={{
                                        flexShrink: 0, padding: '7px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                                        border: 'none', cursor: 'pointer',
                                        background: on ? theme.colors.success : theme.colors.creamDark,
                                        color: on ? theme.colors.white : theme.colors.charcoal,
                                    }}>{on ? 'ON' : 'OFF'}</button>
                            </div>
                            <input
                                type="text" className="mom-input"
                                value={v.flodeskSegmentId || ''}
                                onChange={(e) => { if (unsavedRef) unsavedRef.current = true; onChange(d.key, { flodeskSegmentId: e.target.value }); }}
                                placeholder="Flodesk segment ID (a new, empty segment)"
                                style={{ fontFamily: 'monospace', fontSize: 13 }}
                            />
                        </div>
                    );
                })}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                <button className="mom-btn" disabled={running || !anyConfigured} onClick={() => run(true)}
                    style={{
                        padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700, border: 'none',
                        background: theme.colors.charcoal, color: theme.colors.white,
                        opacity: (running || !anyConfigured) ? 0.45 : 1, cursor: (running || !anyConfigured) ? 'not-allowed' : 'pointer',
                    }}>{running ? 'Working…' : 'Preview (writes nothing)'}</button>
                <button className="mom-btn" disabled={running || !anyConfigured} onClick={() => {
                    if (!window.confirm('Sync the audience segments now?\n\nThis adds and removes people in Flodesk. If any of these segments has an email workflow attached, everyone added will receive it. Press Cancel if you have not previewed first.')) return;
                    run(false);
                }}
                    style={{
                        padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700, border: 'none',
                        background: theme.colors.terracotta, color: theme.colors.white,
                        opacity: (running || !anyConfigured) ? 0.45 : 1, cursor: (running || !anyConfigured) ? 'not-allowed' : 'pointer',
                    }}>Sync now</button>
            </div>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 8 }}>
                Save your segment IDs before previewing — the sync reads what is saved, not what is typed.
            </p>

            {running && progress && (
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, marginTop: 12, fontWeight: 600 }}>{progress}</p>
            )}
            {result && <AudienceSyncResult result={result} />}
        </div>
    );
}

function AudienceSyncResult({ result }) {
    const failed = result.requestOk === false || result.ok === false;
    return (
        <div style={{
            marginTop: 14, borderRadius: 12, padding: '14px 16px',
            background: failed ? '#fdf0ef' : '#f0f7f2',
            border: `1px solid ${failed ? '#e3b5b0' : '#bcd9c6'}`,
        }}>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, color: theme.colors.charcoal, marginBottom: 8 }}>
                {failed ? 'Did not sync' : (result.dryRun ? 'Preview — nothing was written' : (result.complete === false ? 'Partly synced — press Sync now again to continue' : 'Sync complete'))}
            </p>
            {result.error && (
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, lineHeight: 1.6, marginBottom: 8 }}>{result.error}</p>
            )}
            {Array.isArray(result.collisions) && result.collisions.length > 0 && (
                <ul style={{ margin: '0 0 8px 18px', fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.danger, lineHeight: 1.6 }}>
                    {result.collisions.map((c, i) => (
                        <li key={i}><strong>{c.audience}</strong> uses the same segment as {c.usedBy}.</li>
                    ))}
                </ul>
            )}
            {Array.isArray(result.segments) && result.segments.map((s) => (
                <div key={s.key} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, padding: '6px 0', borderTop: `1px solid ${theme.colors.creamDark}` }}>
                    <strong>{s.label}</strong>{' — '}
                    {s.error
                        ? <span style={{ color: theme.colors.danger }}>{s.error}</span>
                        : (result.dryRun
                            ? `${s.desiredCount} should be in it, ${s.currentCount} are now → would add ${s.toAdd}, remove ${s.toRemove}`
                            : `added ${s.added}, removed ${s.removed}${(s.failedCount ?? s.failures?.length) ? `, ${s.failedCount ?? s.failures.length} could not be updated` : ''}${s.remaining ? ` · ${s.remaining} still to go` : ''}`)}
                    {!result.dryRun && Array.isArray(s.failures) && s.failures.length > 0 && (
                        <ul style={{ margin: '4px 0 0 18px', fontSize: 12, color: theme.colors.warmGray, lineHeight: 1.5 }}>
                            {s.failures.slice(0, 5).map((f, i) => <li key={i}>{f.email} — {f.error}</li>)}
                        </ul>
                    )}
                </div>
            ))}
            {typeof result.totalMembers === 'number' && (
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 8 }}>
                    Read {result.totalMembers} accounts{result.skippedNoEmail ? ` · ${result.skippedNoEmail} skipped (no email address)` : ''}.
                </p>
            )}
        </div>
    );
}

// ── Setup recipe panel (collapsible safety guardrail) ──────────────────
function EmailsRecipePanel({ open, onToggle }) {
    const steps = [
        { num: '01', title: 'Create an empty segment in Flodesk', body: 'Audience → Segments → Create new. Name it something memorable like "Subscription Welcome — Triggered." Do not import anyone into it.' },
        { num: '02', title: 'Build the workflow', body: 'Workflows → Create new. Set the trigger to "Subscriber is added to segment" and pick the segment you just made. Design your email(s).' },
        { num: '03', title: 'Activate — turn BOTH toggles off', body: 'Flodesk\'s Publish dialog shows TWO switches: "Include existing subscribers" and "Include queued subscribers." Leave BOTH off, then Publish now. Either one left on will mail everyone already sitting in that segment. This is the step that prevents an accidental blast.' },
        { num: '04', title: 'Copy the segment ID', body: 'Open the segment in Flodesk → look in the URL or segment detail. Copy the ID.' },
        { num: '05', title: 'Paste it into the card below', body: 'Find the automation card for this event and paste the segment ID into the field.' },
        { num: '06', title: 'Send a test to yourself', body: 'Press "Test send to me." Flodesk will add your admin email to the segment and fire the workflow. Check your inbox.' },
        { num: '07', title: 'Flip the toggle on', body: 'Once you\'re happy with the email, flip the toggle to ON and press Save. From this moment forward, every new user who triggers this event will go through the workflow.' },
    ];
    return (
        <div style={{
            background: hexToRgba(theme.colors.terracotta, 0.04),
            border: `1px dashed ${hexToRgba(theme.colors.terracotta, 0.2)}`,
            borderRadius: 14,
            overflow: 'hidden',
        }}>
            <button onClick={onToggle} style={{
                width: '100%', padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: 'left',
            }}>
                <div>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700, color: theme.colors.charcoal, margin: 0 }}>
                        How to set up a new automation
                    </p>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, fontStyle: 'italic', margin: '4px 0 0' }}>
                        Read once before activating any automation.
                    </p>
                </div>
                <span style={{ fontSize: 16, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>▾</span>
            </button>
            {open && (
                <div style={{ padding: '0 20px 20px' }}>
                    <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {steps.map((s) => (
                            <li key={s.num} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                                <span style={{
                                    fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic',
                                    color: theme.colors.terracotta, fontWeight: 400, flexShrink: 0, lineHeight: 1, minWidth: 32,
                                }}>{s.num}</span>
                                <div>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700, color: theme.colors.charcoal, margin: '0 0 4px' }}>{s.title}</p>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, lineHeight: 1.5, margin: 0 }}>{s.body}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontStyle: 'italic', color: theme.colors.warmGray, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${hexToRgba(theme.colors.terracotta, 0.15)}` }}>
                        ⚠ If you skip step 3, activating the workflow may email everyone already in the segment. Always start empty.
                    </p>
                </div>
            )}
        </div>
    );
}

// ── Individual automation card ──────────────────────────────────────────
function EmailAutomationCard({ def, value, onToggle, onSegmentChange, getToken }) {
    const [testState, setTestState] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
    const [testError, setTestError] = useState('');
    const isLocked = !!def.locked;
    const enabled = !!value.enabled;
    const segmentId = (value.flodeskSegmentId || '').trim();
    const isLive = enabled && segmentId;
    const needsSegment = enabled && !segmentId;

    const handleTest = async () => {
        if (!segmentId) return;
        setTestState('sending');
        setTestError('');
        try {
            const token = await getToken();
            const res = await fetch('/api/admin-email-test', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                // Recipient is fixed to the authenticated admin's email server-side;
                // we only send the segmentId.
                body: JSON.stringify({ segmentId }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.ok) {
                setTestState('error');
                setTestError(data.error || `Request failed (${res.status})`);
                return;
            }
            setTestState('sent');
            setTimeout(() => setTestState('idle'), 2200);
        } catch (e) {
            setTestState('error');
            setTestError(e?.message || 'Network error');
        }
    };

    const dismissError = () => { setTestState('idle'); setTestError(''); };

    return (
        <div style={{
            background: theme.colors.white,
            borderRadius: 16,
            padding: 24,
            border: `1px solid ${isLive ? theme.colors.terracotta : theme.colors.creamDark}`,
            opacity: isLocked ? 0.7 : 1,
            transition: 'border-color 0.2s',
        }}>
            {/* Title row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <h4 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: theme.colors.charcoal, margin: 0 }}>{def.title}</h4>
                        {isLocked && (
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, letterSpacing: '0.04em', background: 'rgba(0,0,0,0.04)', color: theme.colors.warmGray }}>Locked</span>
                        )}
                        {!isLocked && isLive && (
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, letterSpacing: '0.04em', background: hexToRgba(theme.colors.success, 0.12), color: theme.colors.success }}>Live</span>
                        )}
                        {!isLocked && needsSegment && (
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, letterSpacing: '0.04em', background: hexToRgba(theme.colors.terracotta, 0.1), color: theme.colors.terracotta }}>⚠ Needs segment ID</span>
                        )}
                    </div>
                </div>
                <button
                    onClick={onToggle}
                    disabled={isLocked}
                    aria-label={`${enabled ? 'Disable' : 'Enable'} ${def.title}`}
                    style={{
                        width: 52, height: 28, borderRadius: 14, border: 'none',
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        background: enabled ? theme.colors.terracotta : theme.colors.creamDark,
                        position: 'relative', flexShrink: 0, transition: 'background 0.2s', marginTop: 2,
                    }}>
                    <div style={{
                        width: 22, height: 22, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3, left: enabled ? 27 : 3,
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    }} />
                </button>
            </div>

            {/* Description */}
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, lineHeight: 1.6, margin: '0 0 16px' }}>{def.description}</p>

            {/* Locked state — short-circuit the rest */}
            {isLocked ? (
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontStyle: 'italic', color: theme.colors.warmGray, margin: 0 }}>Coming in a future update.</p>
            ) : (
                <>
                    {/* Segment ID input */}
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: theme.colors.warmGray, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6, display: 'block' }}>
                        Flodesk Segment ID
                    </label>
                    <input type="text" className="mom-input" value={value.flodeskSegmentId || ''}
                        onChange={(e) => onSegmentChange(e.target.value)}
                        placeholder="paste segment ID here"
                        style={{ fontSize: 13, fontFamily: 'monospace' }}
                    />
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 6, lineHeight: 1.5 }}>
                        The ID of an empty Flodesk segment whose workflow is triggered by &quot;subscriber added to segment&quot;.
                    </p>

                    {/* Test send + optional info banner */}
                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <button
                            onClick={handleTest}
                            disabled={!segmentId || testState === 'sending'}
                            style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                                padding: '10px 18px', borderRadius: 12,
                                border: `1px solid ${theme.colors.terracotta}`,
                                background: testState === 'sent' ? hexToRgba(theme.colors.success, 0.1) : 'transparent',
                                color: testState === 'sent' ? theme.colors.success : theme.colors.terracotta,
                                cursor: (!segmentId || testState === 'sending') ? 'not-allowed' : 'pointer',
                                opacity: (!segmentId || testState === 'sending') ? 0.5 : 1,
                                transition: 'background 0.15s, color 0.15s',
                            }}>
                            {testState === 'sending' ? 'Sending…' : testState === 'sent' ? '✓ Sent' : 'Test send to me'}
                        </button>
                    </div>

                    {/* Inline error */}
                    {testState === 'error' && testError && (
                        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, background: hexToRgba(theme.colors.danger || '#dc2626', 0.06), border: `1px solid ${hexToRgba(theme.colors.danger || '#dc2626', 0.25)}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.danger || '#dc2626', margin: 0, lineHeight: 1.5 }}>
                                ⚠ Test send failed: {testError}. Double-check the segment ID, or contact support if it persists.
                            </p>
                            <button onClick={dismissError} aria-label="Dismiss error" style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.warmGray, fontSize: 18, padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
                        </div>
                    )}

                    {/* Test send success toast (inline confirmation row) */}
                    {testState === 'sent' && (
                        <p style={{ marginTop: 10, fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.success, fontStyle: 'italic' }}>
                            Test send sent — check your admin inbox. May take a moment to arrive.
                        </p>
                    )}

                    {/* Optional info banner (per-program override reminder for generic card) */}
                    {def.extra && def.extra.kind === 'info' && (
                        <div style={{
                            marginTop: 14, padding: '10px 14px',
                            background: hexToRgba(theme.colors.terracotta, 0.06),
                            border: `1px solid ${hexToRgba(theme.colors.terracotta, 0.18)}`,
                            borderRadius: 12,
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                        }}>
                            <span style={{ fontSize: 14, lineHeight: 1.3, flexShrink: 0 }}>ℹ</span>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, margin: 0, lineHeight: 1.5 }}>
                                {def.extra.text}
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  LEGAL TAB — Editable copy for the public /terms page
//
//  Content here renders the public /terms route (the page Clerk's hosted
//  signup form deep-links to as Terms of Service + Privacy Policy URLs).
//  Each field is a simple textarea; the rendered page (`src/pages/TermsPage.js`)
//  falls through to a hardcoded English default if a field is empty, so the
//  page is always legible. The Participation Waiver sections preserve the
//  exact legal substance of the historical WaiverModal — same protective
//  scope, delivered as a readable page rather than a scroll-jail modal.
//
//  See `docs/decisions/adr-waiver-clerk-express-consent.md`.
// ═══════════════════════════════════════════════════════════════
// Module-scope so the ⌘K deep-link search can index every field without the tab
// being mounted. The three waiver parts are consolidated into one section using
// sub-part group labels (was 3 separate sections).
const LEGAL_FIELDS = [
    {
        section: 'Page Header', desc: 'Title and intro paragraph at the top of /terms.', items: [
            { key: 'termsPageTitle', label: 'Page title', type: 'text', hint: 'Default: "Terms & Participation Waiver"' },
            { key: 'termsPageIntro', label: 'Intro paragraph', type: 'textarea', hint: 'Warm welcome paragraph below the title. Default starts: "Welcome — we\'re so glad you\'re here..."' },
        ]
    },
    {
        section: 'Participation Waiver', desc: 'The three-part participation waiver shown on /terms — participation, risk & responsibility, and the legal release.', items: [
            { key: 'waiverParticipationTitle', label: 'Title', type: 'text', hint: 'Default: "Your Participation"', groupLabel: '1 · Your Participation' },
            { key: 'waiverParticipationContent', label: 'Body', type: 'textarea', hint: 'Voluntary participation, gentle/low-impact framing, not personal training or medical guidance. Blank line between paragraphs.' },
            { key: 'waiverRiskTitle', label: 'Title', type: 'text', hint: 'Default: "Risk and Responsibility"', groupLabel: '2 · Risk and Responsibility' },
            { key: 'waiverRiskContent', label: 'Body', type: 'textarea', hint: 'Inherent risks of physical activity, listening to your body, educational nature. Blank line between paragraphs.' },
            { key: 'waiverReleaseTitle', label: 'Title', type: 'text', hint: 'Default: "Release"', groupLabel: '3 · Release' },
            { key: 'waiverReleaseContent', label: 'Body', type: 'textarea', hint: 'The legal release / hold-harmless clause.' },
        ]
    },
    {
        section: 'Terms of Use', desc: "Brief Terms of Use covering account, personal use, and update notices. Linked from Clerk's signup form as the \"Terms of Service\" URL.", items: [
            { key: 'termsOfUseTitle', label: 'Section title', type: 'text', hint: 'Default: "Terms of Use"' },
            { key: 'termsOfUseContent', label: 'Section body', type: 'textarea', hint: 'Use a blank line between paragraphs.' },
        ]
    },
    {
        section: 'Privacy', desc: "Brief privacy summary. Currently linked from Clerk's signup form as the \"Privacy Policy\" URL too. When a fuller dedicated Privacy Policy is written, the Clerk URL flips to /privacy and this section can stay as a quick summary on /terms.", items: [
            { key: 'privacyTitle', label: 'Section title', type: 'text', hint: 'Default: "Privacy"' },
            { key: 'privacyContent', label: 'Section body', type: 'textarea', hint: 'Use a blank line between paragraphs.' },
        ]
    },
    {
        section: 'Footer Meta', desc: 'Quiet last-updated timestamp at the bottom of the page. Update this manually after any meaningful content change so users have visibility into the document version.', items: [
            { key: 'termsLastUpdated', label: 'Last updated date', type: 'text', hint: 'Default: "2026-05-05". Format however you like — this is purely display.' },
        ]
    },
];
ADMIN_FIELD_INDEX.legal = indexFields(LEGAL_FIELDS);

function LegalTab({ unsavedRef }) {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => { fetchSiteContent().then((c) => { setContent(c || {}); setLoading(false); }); }, []);
    const handleChange = (key, value) => { setContent((prev) => ({ ...prev, [key]: value })); setSaved(false); if (unsavedRef) unsavedRef.current = true; };
    const handleSave = async () => { setSaving(true); const ok = await updateSiteContent(content); setSaving(false); if (ok) { setSaved(true); if (unsavedRef) unsavedRef.current = false; setTimeout(() => setSaved(false), 3000); } };

    if (loading) return <Spinner />;

    const fields = LEGAL_FIELDS;

    return (
        <div style={{ paddingBottom: 72 }}>
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Legal Page</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 12 }}>
                    Edit the content shown on the public <code>/terms</code> page. This is the page Clerk&apos;s signup form links to when new users agree to the terms during account creation.
                </p>
                <a href="/terms" target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.terracotta, textDecoration: 'none', fontWeight: 600 }}>
                    View live page <span style={{ fontSize: 14 }}>↗</span>
                </a>
            </div>
            <LivePreviewSection>
                <TermsPage previewContent={content || {}} />
            </LivePreviewSection>
            <SectionList tabKey="legal" sections={fields} content={content} handleChange={handleChange} />
            <StickyBar saving={saving} saved={saved} onSave={handleSave} hasChanges={!saved} />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  SETTINGS TAB — Feature flags and app configuration
// ═══════════════════════════════════════════════════════════════
// Current calendar month (YYYY-MM) in Central Time — matches the report's
// server-side month boundaries so the default selection is "this month" as the
// coach experiences it, not UTC.
function currentMonthCT() {
    try {
        // en-CA formats as YYYY-MM-DD; slice to YYYY-MM.
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: 'America/Chicago', year: 'numeric', month: '2-digit',
        }).format(new Date()).slice(0, 7);
    } catch (_) {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
}

// ── Series management (free self-paced challenges) ──
// Mirrors the self-paced ProgramsTab shape (Bunny collection → drag-to-order
// days → cover image → save) but writes the isolated `series` collection. No
// pods, pricing, Stripe, or weeks — Series are free and self-paced. See
// docs/decisions/adr-series.md + docs/ux/series-tab.md.
// Module-scope component (NOT nested) per the no-inner-components rule.
function SeriesTab({ unsavedRef }) {
    const [seriesList, setSeriesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | 'new' | seriesId
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [previewVideos, setPreviewVideos] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [driveImages, setDriveImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [showImagePicker, setShowImagePicker] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    // Bulk day-release scheduler inputs (admin-only, not persisted).
    const [bulkStart, setBulkStart] = useState('');
    const [bulkEvery, setBulkEvery] = useState('1');
    // In-app recorded (Mux) day videos. They reuse the `programVideos` collection
    // with the seriesId in its programId field, so the whole recorder + upload +
    // webhook pipeline is the proven program one, unchanged.
    // See docs/decisions/adr-series-recorded-videos.md.
    const [recordedVideos, setRecordedVideos] = useState([]);
    const [recordedLoading, setRecordedLoading] = useState(false);
    const [recorderOpen, setRecorderOpen] = useState(false);
    const [editingRecordedId, setEditingRecordedId] = useState(null);
    const [recordedEdit, setRecordedEdit] = useState({ title: '', description: '' });
    const getToken = useAdminAuth();

    useEffect(() => { loadAll(); }, []);

    const loadRecordedVideos = async (seriesId) => {
        if (!seriesId || seriesId === 'new') return;
        setRecordedLoading(true);
        const list = await fetchProgramVideos(seriesId);
        setRecordedVideos(list || []);
        setRecordedLoading(false);
    };
    const startEditRecorded = (pv) => { setEditingRecordedId(pv.id); setRecordedEdit({ title: pv.title || '', description: pv.description || '' }); };
    const saveRecordedEdit = async () => {
        if (!editingRecordedId) return;
        await updateProgramVideo(editingRecordedId, { title: recordedEdit.title, description: recordedEdit.description });
        setEditingRecordedId(null);
        loadRecordedVideos(editing);
    };
    const handleDeleteRecordedVideo = async (pvId) => {
        if (!window.confirm('Delete this recorded video? This cannot be undone.')) return;
        // Strip it from the day order too so the slot doesn't linger.
        const nextOrder = (form.videoOrder || []).filter((id) => id !== pvId);
        if (nextOrder.length !== (form.videoOrder || []).length) setForm({ ...form, videoOrder: nextOrder });
        await deleteProgramVideo(pvId);
        loadRecordedVideos(editing);
    };
    const loadAll = async () => { setLoading(true); const s = await fetchSeries(); setSeriesList(s || []); setLoading(false); };

    const loadPreviewVideos = async (collectionId) => {
        if (!collectionId) return;
        setPreviewLoading(true);
        const vids = await fetchVideosFromCollection(collectionId);
        setPreviewVideos(vids || []);
        setPreviewLoading(false);
    };

    const loadDriveImages = async () => {
        if (driveImages.length > 0) { setShowImagePicker(true); return; }
        setImagesLoading(true);
        const imgs = await fetchProgramImages();
        setDriveImages(imgs || []);
        setImagesLoading(false);
        setShowImagePicker(true);
    };

    // Rich-content fields (2026-09-10) are all optional. `highlights` / `metaPills`
    // are stored as arrays but edited as one-per-line text (the programs-editor
    // ergonomic), so they're joined on open and split on save.
    // See docs/decisions/adr-series-deep-link-and-sections.md.
    const startNew = () => {
        setForm({
            title: '', description: '', imageUrl: '', bunnyCollectionId: '', videoOrder: [], dayLabels: {}, order: seriesList.length + 1, active: true,
            slug: '', tagline: '', metaPills: '', highlightsHeading: '', highlights: '', sections: [], faqsHeading: '', faqs: [], daysHeading: '', detailsFirst: false, releaseDates: {},
            publicAccess: false, nonDayVideos: {}, hideVideoLengths: false,
        });
        setEditing('new'); setSaved(false); if (unsavedRef) unsavedRef.current = true; setPreviewVideos([]); setShowImagePicker(false);
        setRecordedVideos([]); setEditingRecordedId(null); setRecorderOpen(false);
    };
    const startEdit = (s) => {
        setForm({
            ...s, videoOrder: s.videoOrder || [], dayLabels: s.dayLabels || {}, releaseDates: s.releaseDates || {}, nonDayVideos: s.nonDayVideos || {},
            highlights: Array.isArray(s.highlights) ? s.highlights.join('\n') : (s.highlights || ''),
            metaPills: Array.isArray(s.metaPills) ? s.metaPills.join('\n') : (s.metaPills || ''),
            sections: Array.isArray(s.sections) ? s.sections : [],
            faqs: Array.isArray(s.faqs) ? s.faqs : [],
        });
        setEditing(s.id); setSaved(false); if (unsavedRef) unsavedRef.current = true; setPreviewVideos([]); setShowImagePicker(false);
        setRecordedVideos([]); setEditingRecordedId(null); setRecorderOpen(false);
        loadRecordedVideos(s.id);
        if (s.bunnyCollectionId) loadPreviewVideos(s.bunnyCollectionId);
    };

    const selectImage = (img) => { setForm({ ...form, imageUrl: img.imageUrl }); setShowImagePicker(false); };
    const removeImage = () => { setForm({ ...form, imageUrl: '' }); };

    const handleSave = async () => {
        setSaving(true); setSaveError('');
        const splitLines = (v) => (typeof v === 'string'
            ? v.split('\n').map((x) => x.trim()).filter(Boolean)
            : (Array.isArray(v) ? v : []));
        const data = {
            ...form,
            order: parseInt(form.order) || 0,
            // Normalize the one-per-line editors back to arrays, and keep the
            // shareable slug URL-safe no matter what was typed.
            highlights: splitLines(form.highlights),
            metaPills: splitLines(form.metaPills),
            slug: slugifySeriesTitle(form.slug || ''),
            sections: (form.sections || []).filter((s) => s && ((s.heading || '').trim() || (s.body || '').trim())),
            faqs: (form.faqs || []).filter((f) => f && ((f.question || '').trim() || (f.answer || '').trim())),
        };
        // If the admin never dragged to reorder, persist the natural collection
        // order so the member list view knows the day count (and the order is
        // explicit rather than implicit). orderedVids already applies any
        // existing videoOrder + appends new videos.
        if ((!data.videoOrder || data.videoOrder.length === 0) && orderedVids.length > 0) {
            data.videoOrder = orderedVids.map((v) => v.id);
        }
        delete data.id; delete data.createdAt;
        let ok = false;
        if (editing === 'new') { const id = await createSeries(data); ok = !!id; }
        else { ok = await updateSeries(editing, data); }
        setSaving(false);
        if (!ok) {
            // Don't lie with a "Saved" toast when the write was rejected (the
            // classic cause is a missing `series` Firestore security rule).
            setSaveError('Could not save. Check your connection — and that Firestore rules allow the "series" collection (see the browser console for the exact error).');
            return;
        }
        await loadAll();
        setSaved(true); if (unsavedRef) unsavedRef.current = false;
        setTimeout(() => { setSaved(false); setEditing(null); }, 1000);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this series? This cannot be undone.\n\nMembers’ logged completions for its days will also be removed.')) return;
        await deleteSeries(id); await loadAll();
    };

    // Ordered videos for the day list (videoOrder[] applied, new videos appended).
    // Bunny videos and in-app recordings are ONE list here, so the admin drags a
    // single day order across both. With no recordings this is element-for-element
    // the previous `previewVideos` list, so Bunny-only editing is unchanged.
    const dayCandidates = [
        ...previewVideos,
        ...recordedVideos.map((pv) => ({
            id: pv.id,
            title: pv.title || 'Recorded video',
            length: pv.length || (pv.durationSeconds ? Math.round(pv.durationSeconds / 60) : 0),
            _recorded: true,
            status: pv.status,
        })),
    ];
    const orderedVids = orderSeriesVideos(dayCandidates, form.videoOrder);
    const moveVideo = (fromIdx, toIdx) => {
        // Splice against the EXACT rendered order (orderedVids) so the from/to
        // indices reported by the list always align — even when form.videoOrder
        // is empty, has stale ids, or has newly-appended videos. Mirrors the
        // ProgramsTab moveVideo fix; reordering a misaligned videoOrder directly
        // was the cause of drags landing on the wrong slot / appearing to no-op.
        const order = orderedVids.map((v) => v.id);
        const [item] = order.splice(fromIdx, 1);
        order.splice(toIdx, 0, item);
        setForm({ ...form, videoOrder: order });
    };
    // Per-day scheduled release (keyed by video id — Bunny or recorded alike).
    // Blank ⇒ remove the key ⇒ that day is available immediately.
    const updateReleaseDate = (videoId, date) => {
        const next = { ...(form.releaseDates || {}) };
        if (date && date.trim()) next[videoId] = date.trim();
        else delete next[videoId];
        setForm({ ...form, releaseDates: next });
    };
    // Fill every day from a start date at a fixed cadence — entering 30 dates by
    // hand is not a real option.
    const applyBulkSchedule = () => {
        if (!bulkStart) return;
        // Extras (a welcome note, a bonus) sit outside the drip -- scheduling them
        // would give a video with no day number a "Day 3 opens" date.
        const dayIds = orderedVids.filter((v) => !isSeriesExtra(form.nonDayVideos, v.id)).map((v) => v.id);
        setForm({ ...form, releaseDates: buildSeriesReleaseSchedule(dayIds, bulkStart, bulkEvery) });
    };
    const clearAllReleaseDates = () => setForm({ ...form, releaseDates: {} });

    // Per-day label override (keyed by Bunny videoId). Blank ⇒ remove key ⇒ auto label.
    const updateDayLabel = (videoId, text) => {
        const next = { ...(form.dayLabels || {}) };
        if (text && text.trim()) next[videoId] = text;
        else delete next[videoId];
        setForm({ ...form, dayLabels: next });
    };

    // Mark a video as NOT a day (a welcome note, a bonus). Key PRESENCE is the
    // flag; the value is the optional eyebrow label, so an extra with no label
    // is stored as ''. Turning it back into a day deletes the key -- and also
    // drops any release date, since only days take part in the drip.
    const toggleNonDay = (videoId) => {
        const next = { ...(form.nonDayVideos || {}) };
        const nextDates = { ...(form.releaseDates || {}) };
        if (Object.prototype.hasOwnProperty.call(next, videoId)) delete next[videoId];
        else { next[videoId] = ''; delete nextDates[videoId]; }
        setForm({ ...form, nonDayVideos: next, releaseDates: nextDates });
    };
    const updateNonDayKicker = (videoId, text) => {
        const next = { ...(form.nonDayVideos || {}) };
        if (!Object.prototype.hasOwnProperty.call(next, videoId)) return;
        next[videoId] = text || '';
        setForm({ ...form, nonDayVideos: next });
    };

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><FaSpinner style={{ fontSize: 24, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} /></div>;

    // ── Editor ──
    if (editing) {
        return (
            <div style={{ maxWidth: 720 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal }}>{editing === 'new' ? 'New Series' : 'Edit Series'}</h3>
                    <button className="mom-btn mom-btn-ghost" onClick={() => { setEditing(null); if (unsavedRef) unsavedRef.current = false; }} style={{ fontSize: 13, color: theme.colors.warmGray }}>Cancel</button>
                </div>

                {/* Active toggle */}
                <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button className="mom-btn" onClick={() => setForm({ ...form, active: !form.active })}
                        style={{ padding: '8px 18px', borderRadius: 12, fontSize: 13, fontWeight: 600, border: 'none', background: form.active ? theme.colors.success : theme.colors.creamDark, color: form.active ? theme.colors.white : theme.colors.charcoal }}>
                        {form.active ? 'Published' : 'Hidden (draft)'}
                    </button>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>Drafts are visible to admins only, even when the Series tab is on.</span>
                </div>

                {/* Basic fields (short fields two-up; long copy full-width) */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', columnGap: 20, alignItems: 'start' }}>
                <div style={{ gridColumn: '1 / -1', marginBottom: 20 }}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Series Title</label>
                    <input type="text" className="mom-input" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. 30 Day Desk Challenge" />
                </div>
                <div style={{ gridColumn: '1 / -1', marginBottom: 20 }}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Tagline <span style={{ fontWeight: 400 }}>(optional — one line under the title)</span></label>
                    <input type="text" className="mom-input" value={form.tagline || ''} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="e.g. Ten minutes a day, at your desk, no equipment" />
                </div>
                <div style={{ gridColumn: '1 / -1', marginBottom: 20 }}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Description</label>
                    <RichTextArea value={form.description || ''} onChange={(val) => setForm({ ...form, description: val })} placeholder="One or two sentences shown on the challenge card + detail page" rows={3} />
                </div>
                <div style={{ marginBottom: 20 }}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Bunny Stream Collection ID</label>
                    <input type="text" className="mom-input" value={form.bunnyCollectionId || ''} onChange={(e) => { const v = e.target.value; setForm({ ...form, bunnyCollectionId: v }); if (v.length > 10) loadPreviewVideos(v); }} placeholder="Paste collection ID from Bunny dashboard" />
                </div>
                <div style={{ marginBottom: 20 }}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Display Order (lower = first)</label>
                    <input type="text" className="mom-input" value={form.order ?? ''} onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="1" />
                </div>
                </div>

                {/* ── Sharing: the direct link to THIS challenge ──
                    The link works off the doc id as soon as the series is saved; a
                    slug just makes it readable. Slug wins over id when both could
                    match, so adding one later never breaks an id link already
                    shared. See adr-series-deep-link-and-sections.md. */}
                {(() => {
                    const savedId = editing !== 'new' ? editing : '';
                    const cleanSlug = slugifySeriesTitle(form.slug || '');
                    const taken = isSlugTaken(seriesList, cleanSlug, savedId);
                    // One link, two audiences. The toggle changes WHO it serves —
                    // the link text itself never changes, which is the point of
                    // reusing the address. A draft is never public whatever this
                    // says (see isSeriesPublic / adr-public-series-access.md).
                    const isPublic = form.publicAccess === true && form.active === true;
                    const url = (cleanSlug || savedId)
                        ? seriesShareUrl({ slug: cleanSlug, id: savedId }, typeof window !== 'undefined' ? window.location.origin : '')
                        : '';
                    const copy = async () => {
                        if (!url) return;
                        try {
                            if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(url);
                            else throw new Error('no clipboard');
                            setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2200);
                        } catch (_) {
                            try {
                                const ta = document.createElement('textarea');
                                ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
                                document.body.appendChild(ta); ta.select();
                                document.execCommand('copy'); document.body.removeChild(ta);
                                setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2200);
                            } catch (_e) { /* leave the label alone rather than claim a copy */ }
                        }
                    };
                    return (
                        <ProgramEditorSection title={`Sharing${isPublic ? ' · Public' : (cleanSlug ? ' · ✓' : '')}`} defaultOpen={false}>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 12, lineHeight: 1.5 }}>
                                Share this link and members land straight on this challenge (not the Challenges list).{' '}
                                {isPublic
                                    ? 'Anyone can open it — no account needed.'
                                    : 'Signed-out visitors are asked to sign in first, then sent here automatically.'}
                            </p>
                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Link name <span style={{ fontWeight: 400 }}>(optional — letters, numbers and dashes)</span></label>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                                <input type="text" className="mom-input" value={form.slug || ''} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="30-day-desk-challenge" style={{ flex: '1 1 240px' }} />
                                <button className="mom-btn mom-btn-outline" onClick={() => setForm({ ...form, slug: slugifySeriesTitle(form.title || '') })} style={{ padding: '10px 18px', fontSize: 13 }}>Suggest from title</button>
                            </div>
                            {taken && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.danger, marginBottom: 10 }}>
                                    Another challenge already uses this link name — pick a different one so the link opens the right challenge.
                                </p>
                            )}
                            {url ? (
                                <div style={{ background: theme.colors.cream, borderRadius: 12, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <code style={{ fontFamily: 'monospace', fontSize: 12.5, color: theme.colors.charcoal, wordBreak: 'break-all', flex: '1 1 220px' }}>{url}</code>
                                    <button className="mom-btn mom-btn-primary" onClick={copy} style={{ padding: '9px 18px', fontSize: 13 }}>{linkCopied ? 'Copied ✓' : 'Copy link'}</button>
                                </div>
                            ) : (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>Save this challenge once and its link appears here.</p>
                            )}

                            {/* Who the link serves. Same address either way. */}
                            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${theme.colors.creamDark}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                                    <button className="mom-btn" onClick={() => setForm({ ...form, publicAccess: !form.publicAccess })}
                                        style={{ padding: '8px 18px', borderRadius: 12, fontSize: 13, fontWeight: 600, border: 'none', background: form.publicAccess ? theme.colors.terracotta : theme.colors.creamDark, color: form.publicAccess ? theme.colors.white : theme.colors.charcoal }}>
                                        {form.publicAccess ? 'Anyone with the link' : 'Members only'}
                                    </button>
                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal }}>
                                        Who can open this link
                                    </span>
                                </div>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, lineHeight: 1.55 }}>
                                    {form.publicAccess
                                        ? 'Anyone with the link can watch every day of this challenge without an account. They will not be able to track progress or build a streak until they sign up.'
                                        : 'People need a free account to open this link. They are asked to sign in, then sent straight here.'}
                                </p>
                                {form.publicAccess && !form.active && (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.charcoal, marginTop: 8, background: theme.colors.cream, borderRadius: 10, padding: '10px 12px', lineHeight: 1.5 }}>
                                        This challenge is still a draft, so the link stays private until you publish it.
                                    </p>
                                )}
                            </div>
                        </ProgramEditorSection>
                    );
                })()}

                {/* Cover image */}
                <ProgramEditorSection title={`Cover Image${form.imageUrl ? ' · ✓' : ''}`} defaultOpen={false}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 12 }}>Pick a cover from your "Program Images" folder in Bunny Storage. No cover ⇒ a branded splash is shown.</p>
                    {form.imageUrl ? (
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ width: '100%', maxWidth: 400, borderRadius: 12, overflow: 'hidden', border: `1.5px solid ${theme.colors.creamDark}`, marginBottom: 8 }}>
                                <img src={form.imageUrl} alt="Series cover" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="mom-btn mom-btn-outline" onClick={loadDriveImages} style={{ padding: '8px 20px', fontSize: 13 }}>Change Image</button>
                                <button className="mom-btn mom-btn-ghost" onClick={removeImage} style={{ color: theme.colors.danger, fontSize: 13 }}>Remove Image</button>
                            </div>
                        </div>
                    ) : (
                        <button className="mom-btn mom-btn-outline" onClick={loadDriveImages} style={{ padding: '12px 24px', fontSize: 14 }}>{imagesLoading ? 'Loading images...' : 'Choose from Image Library'}</button>
                    )}
                    {showImagePicker && (
                        <div style={{ marginTop: 16, background: theme.colors.cream, borderRadius: 12, padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal }}>Select an image ({driveImages.length} available)</p>
                                <button className="mom-btn mom-btn-ghost" onClick={() => setShowImagePicker(false)} style={{ fontSize: 13, color: theme.colors.warmGray }}>Close</button>
                            </div>
                            {imagesLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><FaSpinner style={{ fontSize: 24, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} /></div>
                            ) : driveImages.length === 0 ? (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, textAlign: 'center', padding: 16 }}>No images found. Upload to the "Program Images" folder in Bunny Storage, then try again.</p>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                                    {driveImages.map((img) => (
                                        <div key={img.id} onClick={() => selectImage(img)} style={{ borderRadius: 10, overflow: 'hidden', cursor: 'pointer', border: form.imageUrl === img.imageUrl ? `3px solid ${theme.colors.terracotta}` : `2px solid ${theme.colors.creamDark}`, position: 'relative' }}>
                                            <img src={img.thumbnailUrl} alt={img.title} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.charcoal, padding: '6px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{img.title}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </ProgramEditorSection>

                {/* Days: order + per-day labels (Bunny videos AND in-app recordings) */}
                {(form.bunnyCollectionId || recordedVideos.length > 0) && (
                    <ProgramEditorSection title={`Days${orderedVids.length > 0 ? ` · ${orderedVids.length}` : ''}`} defaultOpen={true}>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 8 }}>
                            {previewLoading ? 'Loading...' : `${previewVideos.length} Bunny video${previewVideos.length !== 1 ? 's' : ''}${recordedVideos.length > 0 ? ` + ${recordedVideos.length} recorded` : ''}`}
                        </p>
                        {orderedVids.length > 0 && (
                            <>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 12 }}>Drag to reorder — this is the day order members see (Day 1, Day 2, …).</p>
                                <DragReorderList
                                    items={orderedVids}
                                    idKey="id"
                                    onReorder={(fromIdx, toIdx) => moveVideo(fromIdx, toIdx)}
                                    renderItem={(v, i) => (
                                        <>
                                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, color: theme.colors.terracotta, width: 48, flexShrink: 0 }}>Day {i + 1}</span>
                                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, flex: 1 }}>{v.title}{v.length ? ` — ${v.length} min` : ''}</span>
                                            {v._recorded && (
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 6, flexShrink: 0, background: theme.colors.creamDark, color: theme.colors.warmGray }}>
                                                    RECORDED{v.status !== 'ready' ? ' · processing' : ''}
                                                </span>
                                            )}
                                        </>
                                    )}
                                />

                                {/* Per-day label overrides */}
                                <div style={{ marginTop: 20, borderTop: `1px solid ${theme.colors.creamDark}`, paddingTop: 16 }}>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, color: theme.colors.charcoal, marginBottom: 4 }}>Day Labels &amp; Release Dates</p>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 12, lineHeight: 1.5 }}>
                                        The label is optional — blank uses the video title, and the &quot;Day N&quot; prefix is added automatically.
                                        A release date is optional too: leave it blank and the day is open right away, or set one and it opens at <strong>1:00am Eastern</strong> that morning. Until then members see the day listed with a lock and the date.
                                    </p>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 12, lineHeight: 1.5 }}>
                                        Something that isn&apos;t really a day &mdash; a welcome video, a bonus &mdash; can be set to <strong>Not a day</strong>. It keeps its place in the list but loses its number, stops counting toward &quot;3 of 7 days&quot;, and no longer pushes the first workout to Day 2. Give it a short label like WELCOME, or leave that blank for no label at all.
                                    </p>

                                    {/* Bulk scheduler — typing 30 dates by hand is not a real option */}
                                    <div style={{ background: theme.colors.cream, borderRadius: 12, padding: '12px 14px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                        <div>
                                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Day 1 opens</label>
                                            <input type="date" className="mom-input" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} style={{ fontSize: 13, width: 170 }} />
                                        </div>
                                        <div>
                                            <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Then every</label>
                                            <select className="mom-input" value={bulkEvery} onChange={(e) => setBulkEvery(e.target.value)} style={{ fontSize: 13, width: 130 }}>
                                                <option value="1">1 day</option>
                                                <option value="2">2 days</option>
                                                <option value="3">3 days</option>
                                                <option value="7">7 days</option>
                                            </select>
                                        </div>
                                        <button className="mom-btn mom-btn-outline" onClick={applyBulkSchedule} disabled={!bulkStart} style={{ padding: '9px 18px', fontSize: 13, opacity: bulkStart ? 1 : 0.5 }}>Set all dates</button>
                                        <button className="mom-btn mom-btn-ghost" onClick={clearAllReleaseDates} style={{ fontSize: 12, color: theme.colors.warmGray }}>Clear all dates</button>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {indexSeriesDays(orderedVids, form.nonDayVideos).map(({ video: v, isDay, dayNumber }) => (
                                            <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: isDay ? theme.colors.terracotta : theme.colors.warmGray, width: 48, flexShrink: 0 }}>
                                                    {isDay ? `Day ${dayNumber}` : '—'}
                                                </span>
                                                <input type="text" className="mom-input" value={(form.dayLabels && form.dayLabels[v.id]) || ''} onChange={(e) => updateDayLabel(v.id, e.target.value)} placeholder={v.title} style={{ fontSize: 13, flex: '1 1 200px', minWidth: 150 }} />
                                                {isDay ? (
                                                    <input type="date" className="mom-input" value={(form.releaseDates && form.releaseDates[v.id]) || ''} onChange={(e) => updateReleaseDate(v.id, e.target.value)} title="Opens 1:00am Eastern on this date. Blank = available now." style={{ fontSize: 13, width: 170, flexShrink: 0 }} />
                                                ) : (
                                                    <input type="text" className="mom-input" value={(form.nonDayVideos && form.nonDayVideos[v.id]) || ''} onChange={(e) => updateNonDayKicker(v.id, e.target.value)} placeholder="Label e.g. WELCOME" title="Shown above the title in place of 'Day N'. Blank = no label." style={{ fontSize: 13, width: 170, flexShrink: 0 }} />
                                                )}
                                                <button className="mom-btn" onClick={() => toggleNonDay(v.id)} title={isDay ? 'Remove the day number and stop this counting toward the total' : 'Treat this as a numbered day again'}
                                                    style={{ padding: '7px 12px', borderRadius: 10, fontSize: 12, fontWeight: 600, border: 'none', flexShrink: 0, background: isDay ? theme.colors.creamDark : theme.colors.terracotta, color: isDay ? theme.colors.charcoal : theme.colors.white }}>
                                                    {isDay ? 'Not a day' : 'Not a day ✓'}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </ProgramEditorSection>
                )}

                {/* ── Recorded videos (in-app camera → Mux) ──
                    Reuses the program recorder wholesale: the recording is stored in
                    `programVideos` with this SERIES id as its owner, so the upload
                    endpoint, the Mux webhook and the player all work unchanged. These
                    mix with Bunny videos in the Days list above. */}
                <ProgramEditorSection title={`Recorded Videos (in-app)${recordedVideos.length > 0 ? ` · ${recordedVideos.length}` : ''}`} subtitle="Record days with the in-app camera — saved to Mux and mixed into this challenge alongside any Bunny videos." defaultOpen={false}>
                    {editing === 'new' ? (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>Save the challenge first, then reopen it to record videos.</p>
                    ) : (
                        <div>
                            <button className="mom-btn mom-btn-primary" onClick={() => setRecorderOpen(true)} style={{ padding: '10px 22px', fontSize: 14, marginBottom: 16 }}>+ Record a video</button>
                            {recordedLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><FaSpinner style={{ fontSize: 20, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} /></div>
                            ) : recordedVideos.length === 0 ? (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray }}>No recorded videos yet. You can also point this challenge at a Bunny collection above — or use both.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {recordedVideos.map((pv) => (
                                        editingRecordedId === pv.id ? (
                                            <div key={pv.id} style={{ padding: '14px 16px', background: theme.colors.cream, borderRadius: 12 }}>
                                                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Title</label>
                                                <input type="text" className="mom-input" value={recordedEdit.title} onChange={(e) => setRecordedEdit({ ...recordedEdit, title: e.target.value })} style={{ marginBottom: 10 }} />
                                                <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray, display: 'block', marginBottom: 4 }}>Description</label>
                                                <RichTextArea value={recordedEdit.description} onChange={(val) => setRecordedEdit({ ...recordedEdit, description: val })} placeholder="Shown on the day in the challenge" rows={3} />
                                                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                                    <button className="mom-btn mom-btn-primary" onClick={saveRecordedEdit} style={{ padding: '8px 20px', fontSize: 13 }}>Save</button>
                                                    <button className="mom-btn mom-btn-ghost" onClick={() => setEditingRecordedId(null)} style={{ fontSize: 13, color: theme.colors.warmGray }}>Cancel</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div key={pv.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: theme.colors.cream, borderRadius: 12 }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pv.title || 'Recorded video'}</p>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 2 }}>{pv.length ? `${pv.length} min · ` : ''}{pv.status === 'ready' ? 'Ready' : 'Processing…'}</p>
                                                </div>
                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 8, flexShrink: 0, background: pv.status === 'ready' ? hexToRgba(theme.colors.success, 0.12) : theme.colors.creamDark, color: pv.status === 'ready' ? theme.colors.success : theme.colors.warmGray }}>
                                                    {pv.status === 'ready' ? 'READY' : 'PROCESSING'}
                                                </span>
                                                <button className="mom-btn mom-btn-ghost" onClick={() => startEditRecorded(pv)} style={{ fontSize: 12, color: theme.colors.charcoal, padding: '4px 8px', flexShrink: 0 }}>Edit</button>
                                                <button className="mom-btn mom-btn-ghost" onClick={() => handleDeleteRecordedVideo(pv.id)} style={{ fontSize: 12, color: theme.colors.danger, padding: '4px 8px', flexShrink: 0 }}>Delete</button>
                                            </div>
                                        )
                                    ))}
                                    <button className="mom-btn mom-btn-ghost" onClick={() => loadRecordedVideos(editing)} style={{ fontSize: 12, color: theme.colors.terracotta, alignSelf: 'flex-start', marginTop: 4 }}>Refresh</button>
                                </div>
                            )}
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 12, lineHeight: 1.5 }}>
                                A new recording takes a minute or two to process. Once it says READY it appears in the Days list above, where you can drag it into the order you want.
                            </p>
                        </div>
                    )}
                </ProgramEditorSection>

                {/* ── Details: everything that is NOT the day list ──
                    All optional. Anything left blank simply doesn't render on the
                    member page, so an existing challenge is unchanged. */}
                <ProgramEditorSection title="Details" defaultOpen={false}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                        <button className="mom-btn" onClick={() => setForm({ ...form, detailsFirst: !form.detailsFirst })}
                            style={{ padding: '8px 18px', borderRadius: 12, fontSize: 13, fontWeight: 600, border: 'none', background: form.detailsFirst ? theme.colors.terracotta : theme.colors.creamDark, color: form.detailsFirst ? theme.colors.white : theme.colors.charcoal }}>
                            {form.detailsFirst ? 'Details above the days' : 'Details below the days'}
                        </button>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>Put the details first when members should read something before they start.</span>
                    </div>

                    {/* Video lengths. The file length includes intro/outro, so when the
                        real workout time is written into the title or description it
                        contradicts the automatic number. Off by default — every
                        existing challenge keeps showing lengths. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                        <button className="mom-btn" onClick={() => setForm({ ...form, hideVideoLengths: !form.hideVideoLengths })}
                            style={{ padding: '8px 18px', borderRadius: 12, fontSize: 13, fontWeight: 600, border: 'none', background: form.hideVideoLengths ? theme.colors.terracotta : theme.colors.creamDark, color: form.hideVideoLengths ? theme.colors.white : theme.colors.charcoal }}>
                            {form.hideVideoLengths ? 'Video lengths hidden' : 'Video lengths shown'}
                        </button>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>Hide them when the video runs longer than the workout (intro/outro) and you state the real time yourself. Applies to the day list, the player and the total-length chip — in the app and on the shared link.</span>
                    </div>

                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Day list heading <span style={{ fontWeight: 400 }}>(optional — blank shows no heading)</span></label>
                    <input type="text" className="mom-input" value={form.daysHeading || ''} onChange={(e) => setForm({ ...form, daysHeading: e.target.value })} placeholder="e.g. Your 30 Days" style={{ marginBottom: 20 }} />

                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Quick facts <span style={{ fontWeight: 400 }}>(one per line — shown as small chips; day count and total length are added automatically)</span></label>
                    <textarea className="mom-input" value={form.metaPills || ''} onChange={(e) => setForm({ ...form, metaPills: e.target.value })} rows={3} placeholder={'No equipment\n10 minutes a day\nBeginner friendly'} style={{ marginBottom: 20, fontFamily: 'var(--mom-font-sans)' }} />

                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Highlights heading</label>
                    <input type="text" className="mom-input" value={form.highlightsHeading || ''} onChange={(e) => setForm({ ...form, highlightsHeading: e.target.value })} placeholder="What's Included" style={{ marginBottom: 12 }} />
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>Highlights <span style={{ fontWeight: 400 }}>(one per line)</span></label>
                    <textarea className="mom-input" value={form.highlights || ''} onChange={(e) => setForm({ ...form, highlights: e.target.value })} rows={4} placeholder={'A short movement break for every day\nNothing but a chair\nDo them in any order'} style={{ fontFamily: 'var(--mom-font-sans)' }} />
                </ProgramEditorSection>

                {/* ── Custom sections: the flexible part ──
                    Each challenge can say whatever it needs to ("What You'll Need",
                    "How It Works", "A note from Danielle") with its own heading. */}
                <ProgramEditorSection title={`Custom Sections${(form.sections || []).length > 0 ? ` (${(form.sections || []).length})` : ''}`} defaultOpen={false}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 16, lineHeight: 1.5 }}>
                        Add any section you like, with your own heading. Different challenges can have completely different sections.
                    </p>
                    {(form.sections || []).map((sec, idx) => (
                        <div key={idx} style={{ background: theme.colors.cream, borderRadius: 12, padding: 14, marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray }}>Section {idx + 1}</span>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button className="mom-btn mom-btn-outline" disabled={idx === 0} onClick={() => { const s = [...form.sections]; [s[idx - 1], s[idx]] = [s[idx], s[idx - 1]]; setForm({ ...form, sections: s }); }} style={{ fontSize: 12, padding: '5px 12px', opacity: idx === 0 ? 0.4 : 1 }}>↑</button>
                                    <button className="mom-btn mom-btn-outline" disabled={idx === (form.sections || []).length - 1} onClick={() => { const s = [...form.sections]; [s[idx + 1], s[idx]] = [s[idx], s[idx + 1]]; setForm({ ...form, sections: s }); }} style={{ fontSize: 12, padding: '5px 12px', opacity: idx === (form.sections || []).length - 1 ? 0.4 : 1 }}>↓</button>
                                    <button className="mom-btn mom-btn-outline" onClick={() => setForm({ ...form, sections: form.sections.filter((_, i) => i !== idx) })} style={{ fontSize: 12, padding: '5px 12px', color: theme.colors.danger }}>Remove</button>
                                </div>
                            </div>
                            <input type="text" className="mom-input" value={sec.heading || ''} onChange={(e) => { const s = [...form.sections]; s[idx] = { ...s[idx], heading: e.target.value }; setForm({ ...form, sections: s }); }} placeholder="Section heading (e.g. What You'll Need)" style={{ marginBottom: 8 }} />
                            <RichTextArea value={sec.body || ''} onChange={(val) => { const s = [...form.sections]; s[idx] = { ...s[idx], body: val }; setForm({ ...form, sections: s }); }} placeholder="Section text (supports **bold**, *italic*, [links](url))" rows={3} />
                        </div>
                    ))}
                    <button className="mom-btn mom-btn-outline" onClick={() => setForm({ ...form, sections: [...(form.sections || []), { heading: '', body: '' }] })} style={{ fontSize: 13, padding: '8px 20px' }}>+ Add section</button>
                </ProgramEditorSection>

                {/* ── FAQ (mirrors the Programs FAQ editor) ── */}
                <ProgramEditorSection title={`FAQ${(form.faqs || []).length > 0 ? ` (${(form.faqs || []).length})` : ''}`} defaultOpen={false}>
                    <label style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.warmGray, marginBottom: 6, display: 'block' }}>FAQ heading</label>
                    <input type="text" className="mom-input" value={form.faqsHeading || ''} onChange={(e) => setForm({ ...form, faqsHeading: e.target.value })} placeholder="Questions" style={{ marginBottom: 16 }} />
                    {(form.faqs || []).map((faq, idx) => (
                        <div key={idx} style={{ background: theme.colors.cream, borderRadius: 12, padding: 14, marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: theme.colors.warmGray }}>FAQ {idx + 1}</span>
                                <button className="mom-btn mom-btn-outline" onClick={() => setForm({ ...form, faqs: form.faqs.filter((_, i) => i !== idx) })} style={{ fontSize: 12, padding: '5px 12px', color: theme.colors.danger }}>Remove</button>
                            </div>
                            <input type="text" className="mom-input" value={faq.question || ''} onChange={(e) => { const f = [...form.faqs]; f[idx] = { ...f[idx], question: e.target.value }; setForm({ ...form, faqs: f }); }} placeholder="Question" style={{ marginBottom: 8 }} />
                            <RichTextArea value={faq.answer || ''} onChange={(val) => { const f = [...form.faqs]; f[idx] = { ...f[idx], answer: val }; setForm({ ...form, faqs: f }); }} placeholder="Answer (supports **bold**, *italic*, [links](url))" rows={2} />
                        </div>
                    ))}
                    <button className="mom-btn mom-btn-outline" onClick={() => setForm({ ...form, faqs: [...(form.faqs || []), { question: '', answer: '' }] })} style={{ fontSize: 13, padding: '8px 20px' }}>+ Add FAQ</button>
                </ProgramEditorSection>

                {/* Save */}
                <div style={{ marginTop: 28, display: 'flex', gap: 12, alignItems: 'center' }}>
                    <button className="mom-btn mom-btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '12px 32px', fontSize: 14 }}>
                        {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save Series'}
                    </button>
                    {editing !== 'new' && <button className="mom-btn mom-btn-ghost" onClick={() => handleDelete(editing)} style={{ fontSize: 13, color: theme.colors.danger }}>Delete</button>}
                </div>
                {saveError && (
                    <p style={{ marginTop: 12, fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.danger, lineHeight: 1.5 }}>{saveError}</p>
                )}

                {/* Record-a-day overlay. `programId` is this SERIES id — the recorder
                    treats it as an opaque owner id, which is what lets the proven
                    program pipeline serve challenges with no changes to it. */}
                {recorderOpen && editing !== 'new' && (
                    <ProgramRecorder
                        programId={editing}
                        programTitle={form.title}
                        nextOrder={recordedVideos.length}
                        getToken={getToken}
                        onClose={() => { setRecorderOpen(false); loadRecordedVideos(editing); }}
                        onSaved={() => loadRecordedVideos(editing)}
                    />
                )}
            </div>
        );
    }

    // ── List ──
    return (
        <div style={{ maxWidth: 720 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal }}>Series</h3>
                <button className="mom-btn mom-btn-primary" onClick={startNew} style={{ padding: '10px 22px', fontSize: 14 }}>+ New Series</button>
            </div>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginBottom: 20, lineHeight: 1.5 }}>
                Free, self-paced challenges (e.g. "30 Day Desk Challenge"). They appear as the Series tab in Workouts. Turn the tab on for everyone in <strong>Settings</strong> when you're ready — until then it's visible to admins only.
            </p>
            {seriesList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, background: theme.colors.cream, borderRadius: 16 }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>No series yet. Create your first challenge.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {seriesList.map((s) => (
                        <div key={s.id} onClick={() => startEdit(s)} role="button" tabIndex={0}
                            style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: theme.colors.white, borderRadius: 14, cursor: 'pointer', boxShadow: theme.shadows.sm, border: `1px solid ${theme.colors.creamDark}` }}>
                            <div style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: theme.colors.cream, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {s.imageUrl ? <img src={s.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                                    : <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 20, color: theme.colors.warmGray }}>{(s.videoOrder || []).length || ''}</span>}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: theme.colors.charcoal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.title || 'Untitled series'}</p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 2 }}>
                                    {(s.videoOrder || []).length} day{(s.videoOrder || []).length !== 1 ? 's' : ''} · Order: {s.order}{s.bunnyCollectionId ? ' · Videos ✓' : ''}
                                </p>
                            </div>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 8, flexShrink: 0, background: s.active ? hexToRgba(theme.colors.success, 0.12) : theme.colors.creamDark, color: s.active ? theme.colors.success : theme.colors.warmGray }}>
                                {s.active ? 'PUBLISHED' : 'DRAFT'}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Monthly Completions report ──
// "How many subscribers completed at least N workouts in a given month?"
// Calls GET /api/admin-monthly-completions (admin-gated). Static load: the
// admin picks a month + threshold and clicks Run. See the endpoint for the
// counting + subscriber-filter rules.
// ── Insights: small presentational helpers (module scope — never inner components) ──

const INSIGHTS_PERIODS = [
    { key: 'this-month', label: 'This month' },
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
    { key: 'all', label: 'All time' },
];

// Compact number formatting: 1900 → "1.9k".
function insightsFmt(n) {
    const v = Number(n || 0);
    if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
    return String(v);
}

// A clickable summary card. Whole card is the drill-in target.
function InsightsCard({ eyebrow, cardKey, onClick, hovered, onHover, children }) {
    return (
        <div
            role="button" tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
            onMouseEnter={() => onHover?.(cardKey)} onMouseLeave={() => onHover?.(null)}
            style={{
                background: theme.colors.white, borderRadius: 16, padding: '22px 24px',
                border: `1px solid ${hovered ? hexToRgba(theme.colors.terracotta, 0.4) : theme.colors.creamDark}`,
                boxShadow: hovered ? theme.shadows.lg : theme.shadows.md,
                cursor: 'pointer', transition: 'box-shadow 0.16s, border-color 0.16s, transform 0.16s',
                transform: hovered ? 'translateY(-2px)' : 'none',
                display: 'flex', flexDirection: 'column', minHeight: 190, boxSizing: 'border-box',
            }}
        >
            <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: `color-mix(in srgb, var(--mom-primary) 58%, ${theme.colors.charcoal})`, marginBottom: 12 }}>
                {eyebrow}
            </div>
            <div style={{ flex: 1 }}>{children}</div>
            <div style={{ marginTop: 16, fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.terracotta }}>
                View details →
            </div>
        </div>
    );
}

// Small vertical-bar distribution. data: [{ label, count }].
function InsightsMiniBars({ data }) {
    const max = Math.max(1, ...data.map((d) => d.count));
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 56 }}>
            {data.map((d) => (
                <div key={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, color: theme.colors.charcoal }}>{d.count}</span>
                    <div style={{ width: '100%', height: Math.max(3, Math.round((d.count / max) * 34)), borderRadius: 3, background: hexToRgba(theme.colors.terracotta, 0.55) }} />
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, color: theme.colors.warmGray }}>{d.label}</span>
                </div>
            ))}
        </div>
    );
}

// Horizontal stacked "source mix" bar + legend. segments: [{ label, count }].
function InsightsSourceBar({ segments }) {
    const total = Math.max(1, segments.reduce((s, x) => s + x.count, 0));
    const tints = [1, 0.62, 0.4, 0.26, 0.16];
    return (
        <div>
            <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: theme.colors.cream }}>
                {segments.map((seg, i) => (
                    <div key={seg.label} title={`${seg.label}: ${seg.count}`}
                        style={{ width: `${(seg.count / total) * 100}%`, background: hexToRgba(theme.colors.terracotta, tints[i] ?? 0.12) }} />
                ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 10 }}>
                {segments.map((seg, i) => (
                    <span key={seg.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>
                        <span style={{ width: 9, height: 9, borderRadius: 2, background: hexToRgba(theme.colors.terracotta, tints[i] ?? 0.12) }} />
                        {seg.label} <strong style={{ color: theme.colors.charcoal }}>{seg.count}</strong>
                    </span>
                ))}
            </div>
        </div>
    );
}

// Ranked list with italic-serif numerals (no emoji).
// items: [{ title, count, uniquePeople?, repeats? }]. When `detail` is set the
// row gains a second line reading "N people · N repeats", which is what turns a
// raw completion count into a readable signal (wide reach vs. a few superfans).
function InsightsRankedList({ items, unit, detail }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: detail ? 12 : 8 }}>
            {items.map((it, i) => (
                <div key={it.videoId || it.title || i} style={{ display: 'flex', alignItems: detail ? 'flex-start' : 'center', gap: 12 }}>
                    <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 16, color: hexToRgba(theme.colors.terracotta, 0.9), width: 24, flexShrink: 0 }}>
                        {String(i + 1).padStart(2, '0')}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</span>
                        {detail && (
                            <span style={{ display: 'block', fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 2 }}>
                                {it.uniquePeople} {it.uniquePeople === 1 ? 'person' : 'people'}
                                {it.repeats > 0 ? ` · ${it.repeats} repeat${it.repeats === 1 ? '' : 's'}` : ''}
                            </span>
                        )}
                    </span>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700, color: theme.colors.charcoal, flexShrink: 0 }}>
                        {it.count}{unit ? <span style={{ fontWeight: 400, color: theme.colors.warmGray, fontSize: 12 }}> {unit}</span> : null}
                    </span>
                </div>
            ))}
        </div>
    );
}

// Horizontal breakdown bars — used for cancellation reasons, where the label is
// a full sentence and a stacked bar would be unreadable.
function InsightsBreakdownBars({ items }) {
    const max = Math.max(1, ...items.map((d) => d.count));
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {items.map((d) => (
                <div key={d.key || d.label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 5 }}>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, color: theme.colors.charcoal }}>{d.label}</span>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, color: theme.colors.charcoal, flexShrink: 0 }}>
                            {d.count}<span style={{ fontWeight: 400, color: theme.colors.warmGray }}> · {d.pct}%</span>
                        </span>
                    </div>
                    <div style={{ height: 8, borderRadius: 4, background: theme.colors.cream, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.max(2, (d.count / max) * 100)}%`, height: '100%', borderRadius: 4, background: hexToRgba(theme.colors.terracotta, 0.6) }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

// Name + date roster (new / lapsed / pending-cancel members, recent cancels).
function InsightsRoster({ rows, emptyText, meta }) {
    if (!rows || rows.length === 0) {
        return <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, margin: 0 }}>{emptyText}</p>;
    }
    const fmtDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    };
    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {rows.map((r, i) => (
                <div key={r.userId || i} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderTop: i === 0 ? 'none' : `1px solid ${theme.colors.cream}` }}>
                    <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, color: theme.colors.charcoal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.name}
                        {meta && r[meta] ? <span style={{ color: theme.colors.warmGray }}> — {r[meta]}</span> : null}
                    </span>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, flexShrink: 0 }}>{fmtDate(r.at)}</span>
                </div>
            ))}
        </div>
    );
}

// A quiet "what this number means" footnote. Every figure on this dashboard
// should be able to say exactly what it counted and over what denominator.
function InsightsNote({ children }) {
    return (
        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, lineHeight: 1.55, margin: '14px 0 0', paddingTop: 12, borderTop: `1px solid ${theme.colors.cream}` }}>
            {children}
        </p>
    );
}

function InsightsStatTile({ label, value, sub }) {
    return (
        <div style={{ background: theme.colors.white, borderRadius: 12, padding: '16px 18px', border: `1px solid ${theme.colors.creamDark}`, flex: '1 1 140px', minWidth: 130 }}>
            <div style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 30, color: theme.colors.charcoal, lineHeight: 1 }}>{value}</div>
            <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, marginTop: 8 }}>{label}</div>
            {sub && <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 2, opacity: 0.85 }}>{sub}</div>}
        </div>
    );
}

// Drill-in section wrapper.
function InsightsSection({ title, note, children }) {
    return (
        <div style={{ background: theme.colors.white, borderRadius: 16, padding: '22px 24px', border: `1px solid ${theme.colors.creamDark}`, marginBottom: 20 }}>
            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 19, color: theme.colors.charcoal, margin: '0 0 4px' }}>{title}</h3>
            {note && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, margin: '0 0 16px', lineHeight: 1.5 }}>{note}</p>}
            {children}
        </div>
    );
}

// ── Insights home: 2×2 executive card grid + drill-ins ──
// The Monthly Completions report (REPORT-001, <ReportsTab/>) is preserved and
// reachable as a drill-in inside Member Engagement.
function InsightsTab() {
    const getToken = useAdminAuth();
    const [period, setPeriod] = useState('this-month');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [updatedAt, setUpdatedAt] = useState(null);
    const [drill, setDrill] = useState(null); // null | 'business' | 'engagement' | 'content' | 'community'
    const [hovered, setHovered] = useState(null);
    const [price, setPrice] = useState(15);
    // Business drill-in payload (cancellation reasons + rosters) — loaded lazily
    // so the home view never pays for the extra Clerk pass.
    const [biz, setBiz] = useState(null);
    const [bizLoading, setBizLoading] = useState(false);
    const [bizError, setBizError] = useState('');

    // Pull the subscription price label once, for the approximate-MRR figure.
    useEffect(() => {
        fetchSiteContent().then((c) => {
            const raw = (c?.subscriptionPriceLabel || '').toString();
            const m = raw.match(/(\d+(?:\.\d+)?)/);
            if (m) setPrice(Number(m[1]));
        }).catch(() => { /* keep default */ });
    }, []);

    const load = useCallback(async (force) => {
        setLoading(true);
        setError('');
        try {
            const d = await fetchInsightsSummary(period, getToken, { force: !!force });
            setData(d);
            setUpdatedAt(new Date());
        } catch (err) {
            setData(null);
            setError(err?.message || 'Failed to load insights');
        } finally {
            setLoading(false);
        }
    }, [period, getToken]);

    useEffect(() => { load(false); }, [load]);

    // Lazy-load the Business drill-in the first time it's opened for a period,
    // and whenever the period changes while it's open.
    const loadBusiness = useCallback(async (force) => {
        setBizLoading(true);
        setBizError('');
        try {
            const d = await fetchInsightsBusiness(period, getToken, { force: !!force });
            setBiz(d);
        } catch (err) {
            setBiz(null);
            setBizError(err?.message || 'Failed to load');
        } finally {
            setBizLoading(false);
        }
    }, [period, getToken]);

    useEffect(() => {
        if (drill !== 'business') return;
        loadBusiness(false);
    }, [drill, loadBusiness]);

    const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
    const b = data?.business;
    const eng = data?.engagement;
    const com = data?.community;
    const aud = data?.audience;
    const rhythm = data?.rhythm;
    const mem = eng?.members;
    // Prefer each member's own price (yearly counted ÷ 12, $15-for-life at $15);
    // members with no stored price fall back to the list price. Older cached
    // responses without `revenue` keep the previous estimate.
    const rev = b?.revenue;
    const approxMrr = b
        ? (rev ? Math.round(rev.monthlyCents / 100 + rev.unpricedMembers * price) : Math.round(b.activeSubscribers * price))
        : 0;

    const backBar = (label) => (
        <button onClick={() => setDrill(null)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.terracotta, padding: 0, marginBottom: 14 }}>
            ← Insights
        </button>
    );

    const periodControl = (
        <div style={{ display: 'inline-flex', background: theme.colors.cream, borderRadius: 12, padding: 3, gap: 2, flexWrap: 'wrap' }}>
            {INSIGHTS_PERIODS.map((p) => (
                <button key={p.key} onClick={() => setPeriod(p.key)}
                    style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, fontWeight: period === p.key ? 700 : 500, padding: '7px 13px', borderRadius: 9, border: 'none', cursor: 'pointer', background: period === p.key ? theme.colors.white : 'transparent', color: period === p.key ? theme.colors.charcoal : theme.colors.warmGray, boxShadow: period === p.key ? theme.shadows.sm : 'none', transition: 'background 0.13s' }}>
                    {p.label}
                </button>
            ))}
        </div>
    );

    // ── Header (shared across home + drill-ins) ──
    const header = (
        <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 26, fontStyle: 'italic', color: theme.colors.charcoal, margin: 0 }}>Insights</h2>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, margin: '6px 0 0', lineHeight: 1.5 }}>
                        How your membership is growing, and how members are using it.
                    </p>
                    {aud && (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, margin: '8px 0 0', lineHeight: 1.6 }}>
                            <strong style={{ color: theme.colors.charcoal }}>{aud.members}</strong> paying members
                            {' · '}<strong style={{ color: theme.colors.charcoal }}>{aud.lapsed}</strong> lapsed
                            {' · '}<strong style={{ color: theme.colors.charcoal }}>{aud.free}</strong> free accounts
                            {aud.comped > 0 ? ` · ${aud.comped} comped` : ''}
                            {aud.staff > 0 ? ` · ${aud.staff} staff` : ''}
                            {(aud.comped > 0 || aud.staff > 0) ? ' (excluded from member figures)' : ''}
                        </p>
                    )}
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 6 }}>
                        {updatedAt ? `Last updated ${fmtTime(updatedAt)} · ` : ''}
                        <button onClick={() => { load(true); if (drill === 'business') loadBusiness(true); }} disabled={loading}
                            style={{ background: 'none', border: 'none', cursor: loading ? 'default' : 'pointer', color: theme.colors.terracotta, fontWeight: 600, fontFamily: 'var(--mom-font-sans)', fontSize: 12, padding: 0 }}>
                            {loading ? 'Refreshing…' : 'Refresh ↻'}
                        </button>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: 16 }}>{periodControl}</div>
        </div>
    );

    if (error && !loading) {
        return (
            <div>
                {header}
                <div style={{ background: theme.colors.white, borderRadius: 16, padding: '28px 24px', border: `1px solid ${theme.colors.creamDark}`, textAlign: 'center' }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, margin: '0 0 12px' }}>Couldn’t load insights — {error}</p>
                    <button onClick={() => load(true)} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, padding: '9px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', background: theme.colors.charcoal, color: theme.colors.white }}>Retry</button>
                </div>
            </div>
        );
    }

    if (loading && !data) {
        return (<div>{header}<Spinner /></div>);
    }

    // Shape guard. Every figure below reads the cohort-aware payload; if an
    // older-shaped response is ever served (stale deploy / proxy cache) fail
    // loudly instead of rendering a half-empty dashboard of undefineds.
    if (!mem || !rhythm || !aud) {
        return (
            <div>
                {header}
                <div style={{ background: theme.colors.white, borderRadius: 16, padding: '28px 24px', border: `1px solid ${theme.colors.creamDark}`, textAlign: 'center' }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, margin: '0 0 12px' }}>Insights data is out of date — refresh to reload.</p>
                    <button onClick={() => load(true)} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, padding: '9px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', background: theme.colors.charcoal, color: theme.colors.white }}>Refresh</button>
                </div>
            </div>
        );
    }

    // ── Drill-ins ──
    if (drill === 'business') {
        const cx = biz?.cancellations;
        return (
            <div>{header}{backBar()}
                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, margin: '0 0 18px' }}>Membership &amp; Revenue</h2>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                    <InsightsStatTile value={b.activeSubscribers} label="Paying members" sub="right now" />
                    <InsightsStatTile value={b.newInPeriod} label="Joined" sub={data.periodLabel} />
                    <InsightsStatTile value={b.lapsedInPeriod} label="Left" sub={data.periodLabel} />
                    <InsightsStatTile value={`${b.netChange > 0 ? '+' : ''}${b.netChange}`} label="Net change" sub={data.periodLabel} />
                    <InsightsStatTile value={b.pendingCancel} label="Cancelling" sub="access not ended yet" />
                </div>

                <InsightsSection title="Approximate monthly revenue" note={rev
                    ? `${rev.pricedMembers} members at their own price${rev.yearlyMembers ? ` (${rev.yearlyMembers} yearly, counted ÷ 12)` : ''}${rev.unpricedMembers ? ` + ${rev.unpricedMembers} at the $${price}/mo list price` : ''}.`
                    : `${b.activeSubscribers} paying members × $${price}/mo.`}>
                    <div style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 40, color: theme.colors.charcoal }}>~${approxMrr.toLocaleString()}<span style={{ fontSize: 16, color: theme.colors.warmGray }}> /mo · approx.</span></div>
                    <InsightsNote>
                        Estimated from each current member's own price as recorded at signup (yearly plans spread over 12 months) —
                        it is not read from Stripe billing, so it does not account for coupons, failed payments, or proration.
                        Treat it as a directional figure, not an accounting number.
                    </InsightsNote>
                </InsightsSection>

                <InsightsSection
                    title="Why members leave"
                    note="Reasons chosen by members in the cancellation flow."
                >
                    {bizLoading && !biz ? <Spinner /> : null}
                    {bizError && !bizLoading ? (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, margin: 0 }}>
                            Couldn’t load cancellation reasons — {bizError}{' '}
                            <button onClick={() => loadBusiness(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: theme.colors.terracotta, fontWeight: 600, fontFamily: 'var(--mom-font-sans)', fontSize: 13 }}>Retry</button>
                        </p>
                    ) : null}
                    {cx && !bizLoading ? (
                        cx.reasons.length > 0
                            ? (
                                <>
                                    <InsightsBreakdownBars items={cx.reasons} />
                                    <InsightsNote>
                                        {cx.withReason} of {cx.lapsedInPeriod || cx.withReason} departures in {data.periodLabel} gave a reason.
                                        {cx.withReason < cx.lapsedInPeriod
                                            ? ' The gap is members whose subscription ended without going through the in-app cancel flow (for example a failed payment, or a cancellation made before reason capture existed).'
                                            : ''}
                                        {' '}Percentages are of members who gave a reason, not of all departures.
                                    </InsightsNote>
                                </>
                            )
                            : (
                                <div>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, margin: 0 }}>
                                        {cx.feedbackOk
                                            ? (cx.totalFeedbackDocs > 0
                                                ? `No cancellations in ${data.periodLabel}. ${cx.totalFeedbackDocs} recorded in total — try a wider period.`
                                                : 'No cancellation reasons have been recorded yet.')
                                            : 'Cancellation reasons couldn’t be read from the database.'}
                                    </p>
                                    {!cx.feedbackOk && (
                                        <InsightsNote>
                                            Firestore said: <code style={{ fontFamily: 'monospace', fontSize: 11.5, color: theme.colors.charcoal }}>{cx.feedbackError || 'unknown error'}</code>.
                                            {/permission|insufficient/i.test(cx.feedbackError || '')
                                                ? ' That is a security-rules problem, not a data problem: the cancellationFeedback collection needs a read rule in the Firebase console. Note that if it has no rule at all, the cancel flow has also been failing to WRITE reasons (that write is deliberately swallowed so it can never break a cancellation), so there may be no history to show even once reading works.'
                                                : ' Reasons are recorded best-effort when a member confirms the cancel modal; the rest of this page is unaffected.'}
                                        </InsightsNote>
                                    )}
                                    {cx.feedbackOk && cx.totalFeedbackDocs === 0 && cx.lapsedInPeriod > 0 && (
                                        <InsightsNote>
                                            {cx.lapsedInPeriod} {cx.lapsedInPeriod === 1 ? 'member' : 'members'} left in {data.periodLabel} without a recorded reason.
                                            Reasons are only captured when someone cancels through the in-app modal — a subscription that ended
                                            via a failed payment, or was cancelled directly in Stripe, never produces one.
                                        </InsightsNote>
                                    )}
                                </div>
                            )
                    ) : null}
                </InsightsSection>

                {cx && cx.recent.length > 0 && (
                    <InsightsSection title="Recent cancellations" note="Most recent departures with the reason they chose.">
                        <InsightsRoster rows={cx.recent} meta="reasonLabel" emptyText="None in this period." />
                    </InsightsSection>
                )}

                <InsightsSection title="Who joined and who left" note={`Membership movement in ${data.periodLabel}.`}>
                    {bizLoading && !biz ? <Spinner /> : null}
                    {biz && !bizLoading ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
                            <div>
                                <h4 style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.colors.warmGray, margin: '0 0 8px' }}>Joined ({biz.rosters.newMembers.length})</h4>
                                <InsightsRoster rows={biz.rosters.newMembers} emptyText="Nobody joined in this period." />
                            </div>
                            <div>
                                <h4 style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.colors.warmGray, margin: '0 0 8px' }}>Left ({biz.rosters.lapsedMembers.length})</h4>
                                <InsightsRoster rows={biz.rosters.lapsedMembers} emptyText="Nobody left in this period." />
                            </div>
                            <div>
                                <h4 style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.colors.warmGray, margin: '0 0 8px' }}>Cancelling ({biz.rosters.pendingCancel.length})</h4>
                                <InsightsRoster rows={biz.rosters.pendingCancel} emptyText="No pending cancellations." />
                            </div>
                        </div>
                    ) : null}
                    <InsightsNote>
                        “Cancelling” are members who have cancelled but still have access until their billing period ends —
                        they are still counted in paying members and in revenue until that date.
                    </InsightsNote>
                </InsightsSection>
            </div>
        );
    }

    if (drill === 'engagement') {
        const cohorts = [
            { key: 'members', label: 'Paying members', stats: eng.members, note: 'Active $15/mo subscription' },
            { key: 'lapsed', label: 'Lapsed members', stats: eng.lapsed, note: 'Subscribed before, not now' },
            { key: 'free', label: 'Free accounts', stats: eng.free, note: 'Signed up, never subscribed' },
        ];
        return (
            <div>{header}{backBar()}
                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, margin: '0 0 18px' }}>Member Engagement</h2>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                    <InsightsStatTile value={`${mem.pctActive}%`} label="Paying members who worked out" sub={`${mem.active} of ${mem.total}`} />
                    <InsightsStatTile value={mem.completions} label="Workouts by paying members" sub={data.periodLabel} />
                    <InsightsStatTile value={mem.avgPerActive} label="Avg per active member" sub={`${mem.avgPerPerson} across all members`} />
                    {rhythm.avgPerActiveMemberPerWeek != null && (
                        <InsightsStatTile value={rhythm.avgPerActiveMemberPerWeek} label="Workouts / active member / week" />
                    )}
                </div>

                <InsightsSection
                    title="Paying vs. free"
                    note="Each row is measured against its own audience — a percentage here never mixes paying and free people in one denominator."
                >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {cohorts.map((c, i) => (
                            <div key={c.key} style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', padding: '12px 0', borderTop: i === 0 ? 'none' : `1px solid ${theme.colors.cream}` }}>
                                <span style={{ flex: '1 1 180px', minWidth: 0 }}>
                                    <span style={{ display: 'block', fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal }}>{c.label}</span>
                                    <span style={{ display: 'block', fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 2 }}>{c.note}</span>
                                </span>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, flex: '0 0 auto' }}>
                                    <strong style={{ color: theme.colors.charcoal, fontSize: 15 }}>{c.stats.active}</strong> of {c.stats.total} worked out
                                    {c.stats.total > 0 ? ` · ${c.stats.pctActive}%` : ''}
                                </span>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, flex: '0 0 auto' }}>
                                    <strong style={{ color: theme.colors.charcoal, fontSize: 15 }}>{c.stats.completions}</strong> workouts
                                </span>
                            </div>
                        ))}
                    </div>
                    <InsightsNote>
                        Free accounts can only complete free content (shorts and any class marked free), so their numbers are
                        expected to be lower — read that row as reach into the unconverted audience, not as a shortfall.
                        {eng.comped.total > 0 || eng.staff.total > 0
                            ? ` Comped (${eng.comped.total}) and admin (${eng.staff.total}) accounts are excluded from every row above; they logged ${eng.comped.completions + eng.staff.completions} workouts between them.`
                            : ''}
                    </InsightsNote>
                </InsightsSection>

                <InsightsSection title="How often paying members work out" note={`Number of paying members in each workout-count band for ${data.periodLabel}. The “0” bar is your re-engagement list.`}>
                    <InsightsMiniBars data={eng.distribution} />
                </InsightsSection>

                <InsightsSection title="Repeat behaviour and rhythm" note="Whether members come back to the same workouts, and how steady the overall drumbeat is.">
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <InsightsStatTile value={`${rhythm.repeatRatePct}%`} label="Workouts that were a repeat" sub={`${rhythm.repeatSessions} of ${eng.rawCompletions} sessions`} />
                        <InsightsStatTile value={rhythm.peopleWhoRepeated} label="People who redid a workout" />
                        <InsightsStatTile value={rhythm.avgCompletionsPerDay} label="Workouts completed / day" sub={`over ${rhythm.periodDays} days`} />
                        <InsightsStatTile value={rhythm.distinctWorkoutsDone} label="Different workouts done" />
                    </div>
                    <InsightsNote>
                        A repeat means the same person completed the same workout more than once <em>inside this period</em> —
                        it does not look further back than the selected range, so a longer period will show a higher repeat rate.
                        A healthy repeat rate means your library has favourites worth returning to; a very low one can mean
                        members always need something new.
                    </InsightsNote>
                </InsightsSection>

                <InsightsSection title="How workouts were completed" note="Share of the workouts completed by paying members this period, by the surface they came from.">
                    {eng.memberSourceMix.length > 0
                        ? <InsightsSourceBar segments={eng.memberSourceMix} />
                        : <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, margin: 0 }}>No completions by paying members in this period.</p>}
                    <InsightsNote>
                        Paying members only. The first three are all <strong>membership workout classes</strong> —{' '}
                        <strong>Live class</strong> is credited automatically when someone watches ≥75% as it airs,{' '}
                        <strong>On-demand class</strong> is the recorded library, and <strong>Class (self-logged)</strong> is a
                        class the member ticked off themselves. Together they are the same figure the Monthly Completions report
                        counts. <strong>Short</strong>, <strong>Program video</strong> and <strong>Series day</strong> are the
                        free and self-paced content outside the class schedule.
                    </InsightsNote>
                </InsightsSection>

                <InsightsSection title="Monthly completions report" note="The detailed member roster — pick a month and a threshold. (This is the original report, preserved.)">
                    <ReportsTab embedded />
                </InsightsSection>
            </div>
        );
    }

    if (drill === 'content') {
        const top = data.content.top;
        return (
            <div>{header}{backBar()}
                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, margin: '0 0 18px' }}>Content Performance</h2>
                <InsightsSection title="Most-completed workouts" note={`Every class, program video, series day and short completed in ${data.periodLabel}.`}>
                    {top.length > 0
                        ? <InsightsRankedList items={top} unit="done" detail />
                        : <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, margin: 0 }}>No completions in this period.</p>}
                    <InsightsNote>
                        The bold number is total completions. Underneath, <strong>people</strong> is how many different members
                        completed it and <strong>repeats</strong> is how many of those completions were somebody doing it again —
                        so a workout with high completions but few people is a favourite being replayed, while high completions
                        across many people is broad appeal. Counts include every audience, paying or free.
                    </InsightsNote>
                </InsightsSection>
            </div>
        );
    }

    if (drill === 'community') {
        return (
            <div>{header}{backBar()}
                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, margin: '0 0 18px' }}>Community</h2>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                    <InsightsStatTile value={com.classesOk ? com.classesAired : '—'} label="Classes aired" sub={com.classesUpcoming > 0 ? `${com.classesUpcoming} still upcoming` : data.periodLabel} />
                    <InsightsStatTile value={com.commentsOk ? insightsFmt(com.memberComments) : '—'} label="Comments from members" sub={com.commentsOk ? `${com.distinctCommenters} people` : 'unavailable'} />
                    <InsightsStatTile value={com.commentsOk ? insightsFmt(com.coachReplies) : '—'} label="Replies from Danielle" />
                    <InsightsStatTile value={com.heartsOk ? insightsFmt(com.heartsInPeriod) : '—'} label="Hearts given" sub={com.heartsOk ? `${com.distinctHearters} people` : 'unavailable'} />
                </div>
                <InsightsSection title="People who joined in" note="Members who commented or hearted something during this period.">
                    <div style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 40, color: theme.colors.charcoal }}>
                        {com.engagedSocially}<span style={{ fontSize: 16, color: theme.colors.warmGray }}> people</span>
                    </div>
                    <InsightsNote>
                        Everything on this page counts activity that happened <em>inside the selected period</em> — comments and
                        hearts are dated individually rather than read off a running total on the class, so the figure for a past
                        window stays fixed instead of drifting upward.
                        {' '}“Classes aired” counts scheduled classes that actually went out (live and pre-recorded);
                        drafts, cancelled classes and anything still upcoming are excluded.
                        {' '}For how many people actually worked out, see Member Engagement — that is the stronger signal.
                    </InsightsNote>
                </InsightsSection>
            </div>
        );
    }

    // ── Home: headline strip + 2×2 card grid ──
    return (
        <div>
            {header}

            {/* Activity strip — the "are people actually working out" answer, above the fold. */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <InsightsStatTile value={eng.rawCompletions} label="Workouts completed" sub={data.periodLabel} />
                <InsightsStatTile value={rhythm.avgCompletionsPerDay} label="Per day" sub={`over ${rhythm.periodDays} days`} />
                <InsightsStatTile value={`${rhythm.repeatRatePct}%`} label="Were a repeat" sub={`${rhythm.peopleWhoRepeated} people came back`} />
                <InsightsStatTile value={rhythm.distinctWorkoutsDone} label="Different workouts used" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                <InsightsCard eyebrow="Membership & Revenue" cardKey="business" hovered={hovered === 'business'} onHover={setHovered} onClick={() => setDrill('business')}>
                    <div style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 40, color: theme.colors.charcoal, lineHeight: 1 }}>{b.activeSubscribers}</div>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginTop: 6 }}>paying members</div>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.charcoal, marginTop: 10 }}>
                        {b.netChange > 0 ? `+${b.netChange}` : b.netChange} net in {data.periodLabel.toLowerCase()}
                    </div>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, marginTop: 4 }}>
                        {b.newInPeriod} joined · {b.lapsedInPeriod} left · {b.pendingCancel} cancelling
                    </div>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, marginTop: 6 }}>~${approxMrr.toLocaleString()}/mo · approx.</div>
                </InsightsCard>

                <InsightsCard eyebrow="Member Engagement" cardKey="engagement" hovered={hovered === 'engagement'} onHover={setHovered} onClick={() => setDrill('engagement')}>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, marginBottom: 8 }}>Paying members who worked out</div>
                    <div style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 40, color: theme.colors.charcoal, lineHeight: 1 }}>{mem.pctActive}%</div>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginTop: 6 }}>
                        {mem.active} of {mem.total} · {mem.avgPerActive} workouts each
                    </div>
                    <div style={{ marginTop: 14 }}><InsightsMiniBars data={eng.distribution} /></div>
                </InsightsCard>

                <InsightsCard eyebrow="Content Performance" cardKey="content" hovered={hovered === 'content'} onHover={setHovered} onClick={() => setDrill('content')}>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, marginBottom: 10 }}>Most completed this period</div>
                    {data.content.top.length > 0
                        ? <InsightsRankedList items={data.content.top.slice(0, 3)} />
                        : <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, margin: 0 }}>No completions yet this period.</p>}
                </InsightsCard>

                <InsightsCard eyebrow="Community" cardKey="community" hovered={hovered === 'community'} onHover={setHovered} onClick={() => setDrill('community')}>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, marginBottom: 8 }}>People who joined in</div>
                    <div style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 40, color: theme.colors.charcoal, lineHeight: 1 }}>{com.engagedSocially}</div>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginTop: 6 }}>commented or hearted</div>
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: theme.colors.warmGray, marginTop: 10 }}>
                        {insightsFmt(com.memberComments)} comments · {insightsFmt(com.heartsInPeriod)} hearts · {com.classesAired} classes aired
                    </div>
                </InsightsCard>
            </div>
        </div>
    );
}

function ReportsTab({ embedded }) {
    const getToken = useAdminAuth();
    const [month, setMonth] = useState(() => currentMonthCT());
    const [threshold, setThreshold] = useState(12);
    // Default: membership workout classes only. That is the question this
    // report is actually for — counting shorts/programs/series alongside them
    // inflated the answer. 'all' stays reachable so the old number isn't lost.
    const [countKind, setCountKind] = useState('classes');
    // Default ON: the coach cares about who is currently paying. Unchecking
    // widens to "is or ever was" a subscriber, which is what this report did
    // before the toggle existed.
    const [activeOnly, setActiveOnly] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [report, setReport] = useState(null);
    // The controls that produced the report on screen. Month/threshold/count
    // are server-side, so changing them without re-running would leave the
    // results describing settings the admin can no longer see — hence the nudge.
    const [ranWith, setRanWith] = useState(null);

    const runReport = async () => {
        setLoading(true);
        setError('');
        try {
            const safeThreshold = Number.isInteger(Number(threshold)) && Number(threshold) >= 1
                ? Number(threshold) : 12;
            const data = await fetchMonthlyCompletions(month, safeThreshold, getToken, countKind);
            setReport(data);
            setRanWith({ month, threshold: safeThreshold, countKind });
        } catch (err) {
            setReport(null);
            setRanWith(null);
            setError(err?.message || 'Failed to run report');
        } finally {
            setLoading(false);
        }
    };

    // The subscriber narrowing is a pure CLIENT-side filter: the endpoint
    // already returns `currentlySubscribed` per person, so toggling it is
    // instant and costs no extra query. Unresolved users (Clerk hiccup) are
    // kept either way rather than silently dropped — they're badged UNCONFIRMED.
    const classesOnly = report ? report.countKind !== 'all' : countKind === 'classes';
    const visibleUsers = report
        ? report.users.filter((u) => (activeOnly ? (u.currentlySubscribed || u.unresolved) : true))
        : [];
    const safeThresholdNow = Number.isInteger(Number(threshold)) && Number(threshold) >= 1
        ? Number(threshold) : 12;
    const stale = !!(report && ranWith && (
        ranWith.month !== month
        || ranWith.threshold !== safeThresholdNow
        || ranWith.countKind !== countKind
    ));

    const labelStyle = { fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: theme.colors.charcoal, marginBottom: 6, display: 'block', letterSpacing: '0.02em' };
    const inputStyle = { fontFamily: 'var(--mom-font-sans)', fontSize: 14, padding: '10px 12px', borderRadius: 12, border: `1.5px solid ${theme.colors.creamDark}`, background: theme.colors.white, color: theme.colors.charcoal, boxSizing: 'border-box' };

    return (
        <div>
            {!embedded && <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 8 }}>Reports</h2>}
            {!embedded && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 24, lineHeight: 1.6 }}>
                Monthly completions — how many subscribers completed at least a set number of workouts in a month. Count membership workout classes only, or every completed workout. Narrow to members who are paying right now, or widen to anyone who is or ever was on the $15/mo plan.
            </p>}

            <div style={{ background: theme.colors.white, borderRadius: 16, padding: '20px 24px', border: `1px solid ${theme.colors.creamDark}`, marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                        <label style={labelStyle} htmlFor="report-month">Month</label>
                        <input id="report-month" type="month" value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            style={{ ...inputStyle, width: '100%' }} />
                    </div>
                    <div style={{ flex: '0 1 160px', minWidth: 120 }}>
                        <label style={labelStyle} htmlFor="report-threshold">
                            {countKind === 'classes' ? 'Min. classes' : 'Min. workouts'}
                        </label>
                        <input id="report-threshold" type="number" min={1} step={1} value={threshold}
                            onChange={(e) => setThreshold(e.target.value)}
                            style={{ ...inputStyle, width: '100%' }} />
                    </div>
                    <div style={{ flex: '0 1 auto' }}>
                        <span style={labelStyle}>Count</span>
                        <div style={{ display: 'inline-flex', background: theme.colors.cream, borderRadius: 12, padding: 3, gap: 2 }}>
                            {[
                                { key: 'classes', label: 'Workout classes' },
                                { key: 'all', label: 'All workouts' },
                            ].map((opt) => (
                                <button key={opt.key} type="button" onClick={() => setCountKind(opt.key)}
                                    style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: countKind === opt.key ? 700 : 500, padding: '8px 14px', borderRadius: 9, border: 'none', cursor: 'pointer', background: countKind === opt.key ? theme.colors.white : 'transparent', color: countKind === opt.key ? theme.colors.charcoal : theme.colors.warmGray, boxShadow: countKind === opt.key ? theme.shadows.sm : 'none', transition: 'background 0.13s' }}>
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button onClick={runReport} disabled={loading || !month}
                        style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700, padding: '11px 24px', borderRadius: 12, border: 'none', cursor: loading || !month ? 'default' : 'pointer', background: theme.colors.charcoal, color: theme.colors.white, opacity: loading || !month ? 0.6 : 1, boxShadow: theme.shadows.sm, flexShrink: 0, transition: 'opacity 0.2s' }}>
                        {loading ? 'Running…' : 'Run report'}
                    </button>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 9, marginTop: 16, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, color: theme.colors.charcoal }}>
                    <input type="checkbox" checked={activeOnly}
                        onChange={(e) => setActiveOnly(e.target.checked)}
                        style={{ width: 17, height: 17, accentColor: 'var(--mom-primary)', cursor: 'pointer', margin: 0, flexShrink: 0 }} />
                    <span>
                        Currently subscribed only
                        <span style={{ color: theme.colors.warmGray }}>
                            {' — '}{activeOnly ? 'members paying right now' : 'including members who have since lapsed'}
                        </span>
                    </span>
                </label>
            </div>

            {loading && <Spinner />}

            {error && !loading && (
                <div style={{ padding: '12px 16px', borderRadius: 12, background: 'color-mix(in srgb, var(--mom-terracotta, #c1654f) 10%, transparent)', border: `1px solid ${theme.colors.terracotta}`, fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.terracotta, fontWeight: 600 }}>
                    {error}
                </div>
            )}

            {report && !loading && !error && (
                <div>
                    {stale && (
                        <div style={{ padding: '10px 16px', borderRadius: 12, background: theme.colors.cream, border: `1px solid ${theme.colors.creamDark}`, fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginBottom: 16 }}>
                            You’ve changed the settings — these results are from the previous run. Press <strong style={{ color: theme.colors.charcoal }}>Run report</strong> to update.
                        </div>
                    )}
                    {/* Headline */}
                    <div style={{ background: theme.colors.white, borderRadius: 16, padding: '24px 28px', border: `1px solid ${theme.colors.creamDark}`, marginBottom: 20 }}>
                        <div style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 48, fontStyle: 'italic', color: theme.colors.charcoal, lineHeight: 1 }}>
                            {visibleUsers.length}
                        </div>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: theme.colors.warmGray, margin: '10px 0 0', lineHeight: 1.5 }}>
                            {activeOnly ? 'currently subscribed member' : 'subscriber'}{visibleUsers.length === 1 ? '' : 's'} completed at least{' '}
                            <strong style={{ color: theme.colors.charcoal }}>{report.threshold}</strong>{' '}
                            {report.threshold === 1
                                ? (classesOnly ? 'workout class' : 'workout')
                                : (classesOnly ? 'workout classes' : 'workouts')} in{' '}
                            <strong style={{ color: theme.colors.charcoal }}>{report.monthLabel}</strong>.
                        </p>
                        {/* Every number above needs a stated denominator — otherwise
                            the admin has to guess what was counted. */}
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, margin: '10px 0 0', lineHeight: 1.6 }}>
                            Counting <strong style={{ color: theme.colors.charcoal }}>
                                {classesOnly ? 'membership workout classes only' : 'every completed workout'}
                            </strong>
                            {classesOnly
                                ? ' — live and on-demand classes. Free shorts, program videos and Series days are excluded.'
                                : ' — classes, free shorts, program videos and Series days all count.'}
                        </p>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, margin: '6px 0 0', lineHeight: 1.6 }}>
                            {report.qualifyingAllUsersCount} {report.qualifyingAllUsersCount === 1 ? 'person' : 'people'} hit the threshold before any subscriber filter
                            {' · '}{report.qualifyingSubscriberCount} {report.qualifyingSubscriberCount === 1 ? 'is' : 'are'} a current or former subscriber
                            {activeOnly && report.qualifyingSubscriberCount !== visibleUsers.length
                                ? ` · ${report.qualifyingSubscriberCount - visibleUsers.length} of those have since lapsed (hidden)`
                                : ''}
                        </p>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, margin: '6px 0 0', lineHeight: 1.6 }}>
                            {report.monthLabel} totals: <strong style={{ color: theme.colors.charcoal }}>{report.totalClassCompletionsInRange}</strong> class completions
                            {' of '}<strong style={{ color: theme.colors.charcoal }}>{report.totalCompletionsInRange}</strong> workouts overall · times in {report.timeZone}
                        </p>
                    </div>

                    {/* Roster */}
                    {visibleUsers.length === 0 ? (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, padding: '8px 4px' }}>
                            No {activeOnly ? 'currently subscribed members' : 'subscribers'} completed {report.threshold}+{' '}
                            {classesOnly ? 'workout classes' : 'workouts'} in {report.monthLabel}.
                            {activeOnly && report.qualifyingSubscriberCount > 0
                                ? ' Uncheck “Currently subscribed only” to include members who have since lapsed.'
                                : ''}
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {visibleUsers.map((u) => (
                                <div key={u.userId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: theme.colors.white, borderRadius: 12, padding: '12px 18px', border: `1px solid ${theme.colors.creamDark}` }}>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: theme.colors.charcoal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.fullName}</div>
                                        {u.email && <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                                        {u.unresolved ? (
                                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, letterSpacing: '0.04em', background: theme.colors.cream, color: theme.colors.warmGray }}>UNCONFIRMED</span>
                                        ) : (
                                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, letterSpacing: '0.04em', background: u.currentlySubscribed ? 'color-mix(in srgb, var(--mom-primary) 12%, transparent)' : theme.colors.cream, color: u.currentlySubscribed ? 'var(--mom-primary)' : theme.colors.warmGray }}>
                                                {u.currentlySubscribed ? 'ACTIVE' : 'LAPSED'}
                                            </span>
                                        )}
                                        <span style={{ textAlign: 'right', minWidth: 74 }}>
                                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 700, color: theme.colors.charcoal }}>
                                                {u.completionCount}
                                            </span>
                                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginLeft: 4 }}>
                                                {classesOnly ? 'classes' : 'workouts'}
                                            </span>
                                            {/* The other number, so the gap between "classes"
                                                and "everything" is visible without re-running. */}
                                            <span style={{ display: 'block', fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, marginTop: 2 }}>
                                                {classesOnly
                                                    ? `${u.totalCount} total`
                                                    : `${u.classCount} ${u.classCount === 1 ? 'class' : 'classes'}`}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const SETTINGS_FIELDS = [
    {
        section: 'Account Page', desc: 'Contact info and labels shown on the Account page.', items: [
            { key: 'contactEmail', label: 'Contact Email', hint: 'Default: "hello@daniellenbarr.com"' },
            { key: 'contactHeading', label: 'Contact Section Heading', hint: 'Default: "Contact Danielle"' },
            { key: 'contactDescription', label: 'Contact Description', hint: 'Default: "Questions about a program, need help with your account, or just want to say hi?"' },
            { key: 'contactButtonLabel', label: 'Contact Button Subtitle', hint: 'Default: "Tap to send an email"' },
            { key: 'accountSubscriptionLabel', label: 'Subscription Display Name', hint: 'Default: "Unlimited Classes"' },
            { key: 'accountManageBillingLabel', label: 'Manage Billing Button', hint: 'Default: "Manage billing & payment method"' },
            { key: 'accountMembershipHeading', label: 'Membership Card Heading', hint: 'Default: "The Membership". Card shows above My Programs for anyone without an active membership.' },
            { key: 'accountMembershipDescription', label: 'Membership Card Description', hint: 'Default: "Live classes, the full On Demand library and challenges — all in one membership."' },
            { key: 'accountMembershipButtonLabel', label: 'Membership Join Button', hint: 'Default: "Join Membership" (opens the membership page)' },
            { key: 'accountProgramsHeading', label: 'My Programs Heading', hint: 'Default: "My Programs"' },
            { key: 'accountProgramsEmptyText', label: 'My Programs Empty Text', hint: 'Default: "No programs yet"' },
            { key: 'accountProgramsBrowseLabel', label: 'Browse Programs Button', hint: 'Default: "Browse Programs"' },
        ]
    },
    {
        section: 'Profile Photo & Tagline', desc: 'The add / frame / change flow and the optional one-line tagline on a member’s Account page. Photos then appear next to their comments, on the Milestone Wall and in “Also in class”. Leave a field blank to use its default.', items: [
            { key: 'profilePhotoAddLabel', label: 'Add Photo Link', hint: 'Default: "Add a photo"' },
            { key: 'profilePhotoFrameTitle', label: 'Framing Title', hint: 'Default: "Frame your photo"' },
            { key: 'profilePhotoFrameHint', label: 'Framing Hint', hint: 'Default: "Drag to move · pinch or slide to zoom"' },
            { key: 'profilePhotoPreviewLabel', label: 'Preview Caption', hint: 'Default: "How others will see you"' },
            { key: 'profilePhotoUseLabel', label: 'Save Button', hint: 'Default: "Use photo"' },
            { key: 'profilePhotoChooseAnotherLabel', label: 'Choose Another Button', hint: 'Default: "Choose a different photo"' },
            { key: 'profilePhotoSavedLabel', label: 'Saved Confirmation', hint: 'Default: "Looking good."' },
            { key: 'profilePhotoChangeLabel', label: 'Menu: Change', hint: 'Default: "Choose a new photo"' },
            { key: 'profilePhotoRemoveLabel', label: 'Menu: Remove', hint: 'Default: "Remove photo"' },
            { key: 'profilePhotoRemoveConfirm', label: 'Remove Confirmation', hint: 'Default: "Remove your photo? You’ll show as your initial again."' },
            { key: 'profilePhotoErrorSave', label: 'Save Error', hint: 'Default: "Couldn’t save your photo. Check your connection and try again."' },
            { key: 'profilePhotoErrorFormat', label: 'Unsupported Format Error', hint: 'Default: "That photo format isn’t supported here. Try a JPG or PNG."' },
            { key: 'profileTaglineAddLabel', label: 'Tagline: Add Link', hint: 'Default: "Add a line about you"' },
            { key: 'profileTaglineExamples', label: 'Tagline: Example Hints', hint: 'Rotating placeholder examples, separated by |. Default: "45 · Austin · mom of three | Loves soccer and early classes | 66 · UK · new to lifting"' },
            { key: 'profileTaglinePrivacyNote', label: 'Tagline: Privacy Note', hint: 'Default: "Visible to other members"' },
            { key: 'profileTaglineSavedLabel', label: 'Tagline: Saved Confirmation', hint: 'Default: "Saved."' },
            { key: 'profileTaglineErrorSave', label: 'Tagline: Save Error', hint: 'Default: "Couldn’t save that — try again."' },
        ]
    },
    {
        section: 'Welcome Message (after joining)', desc: 'The celebration a member sees the moment they finish checkout, in place of a plain confirmation. Shown once per checkout and dismissible. Leave a field blank to use its default.', items: [
            { key: 'welcomeModalHeading', label: 'Heading', hint: 'Default: "You’re in!"' },
            { key: 'welcomeModalSubtext', label: 'Sub-line', hint: 'Default: "You now have access to all classes."' },
            { key: 'welcomeModalNote', label: 'Personal Note', type: 'textarea', hint: 'The warm aside in its own panel. Default: "Keep an eye out for a welcome email from me. Thanks so much for being here!"' },
            { key: 'welcomeModalButtonLabel', label: 'Button', hint: 'Default: "Let’s go"' },
        ]
    },
    {
        section: 'Cancellation Flow', desc: 'The on-brand confirmation a member sees when they tap "Cancel Subscription". They must pick one reason before cancelling — make the last one an opt-out. Leave a field blank to use its default.', items: [
            { key: 'cancelModalHeading', label: 'Heading', hint: 'Default: "Before you go…"' },
            { key: 'cancelModalSubtext', label: 'Sub-line', hint: 'Default: "Mind sharing why? It helps Danielle make this better."' },
            { key: 'cancelReason1', label: 'Reason 1', hint: 'Default: "It’s too expensive right now"' },
            { key: 'cancelReason2', label: 'Reason 2', hint: 'Default: "I’m not using it enough"' },
            { key: 'cancelReason3', label: 'Reason 3', hint: 'Default: "The classes weren’t the right fit for me"' },
            { key: 'cancelReason4', label: 'Reason 4', hint: 'Default: "I’m taking a break / life got busy"' },
            { key: 'cancelReason5', label: 'Reason 5 (opt-out)', hint: 'Default: "I’d rather not specify"' },
            { key: 'cancelKeepLabel', label: 'Keep Button', hint: 'Default: "Keep my membership"' },
            { key: 'cancelConfirmLabel', label: 'Confirm-Cancel Button', hint: 'Default: "Cancel membership"' },
        ]
    },
    {
        section: 'Reply Assistant — copy', desc: 'Text in the admin Replies queue and its Reply-all confirmation. {count} is replaced with the number of replies. Leave blank for the default.', items: [
            { key: 'replyQueueSubline', label: 'Queue sub-line', hint: 'Default: "Drafted in your voice. Edit anything, then reply."' },
            { key: 'replyQueueEmptyTitle', label: 'Empty state', hint: 'Default: "All caught up."' },
            { key: 'replyAllHeading', label: 'Reply-all heading', hint: 'Default: "Send {count} replies?"' },
            { key: 'replyAllBody', label: 'Reply-all body', type: 'textarea', hint: 'Default: "They’ll post as you, and each member will get the usual notification. Anything you’ve edited sends exactly as you wrote it."' },
            { key: 'replyAllConfirmLabel', label: 'Reply-all confirm button', hint: 'Default: "Send all {count}"' },
            { key: 'replyAllBackLabel', label: 'Reply-all back button', hint: 'Default: "Back"' },
            { key: 'replyNudgeTemplate', label: 'Nudge notification', hint: 'Default: "{count} replies ready for you"' },
        ]
    },
];
ADMIN_FIELD_INDEX.settings = indexFields(SETTINGS_FIELDS);

function SettingsTab({ unsavedRef }) {
    const getToken = useAdminAuth();
    const [content, setContent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [dirty, setDirty] = useState(false); // tracks unsaved Account/Cancellation copy edits
    // Recording Maintenance state — separate from siteConfig saving above
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [seeding, setSeeding] = useState(false);
    const [seedResult, setSeedResult] = useState(null);
    const [backfilling, setBackfilling] = useState(false);
    const [backfillResult, setBackfillResult] = useState(null);

    useEffect(() => {
        fetchSiteContent().then((c) => setContent(c || {}));
    }, []);

    // Runs the /api/sync-recordings maintenance sweep:
    //   - Deletes Firestore recordings whose Mux asset has been deleted
    //   - Backfills durations for recordings where we only captured the
    //     partial early-webhook duration (< 30s)
    const runRecordingsSync = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const res = await adminFetch('/api/sync-recordings', getToken, { method: 'POST' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Sync failed');
            setSyncResult({ ok: true, ...data });
        } catch (err) {
            setSyncResult({ ok: false, error: err.message });
        } finally {
            setSyncing(false);
        }
    };

    // One-time baseline for the community counter on Home. The counter is a
    // running total incremented per class completion, so it needs a true
    // starting value counted from history. Dry run first — it reports what it
    // WOULD write and changes nothing.
    const runCommunitySeed = async (dry, force = false) => {
        // Force overwrites an existing seed. Guarded behind a confirm because it
        // discards everything counted since the last seed — correct when the
        // baseline was set from test data, wrong at any other time.
        if (force && !window.confirm(
            'Re-seed the community counter? This REPLACES the current total with a fresh count of '
            + 'your history, and discards everything counted since the last seed. '
            + 'Only do this if the current number is wrong.'
        )) return;
        setSeeding(true);
        setSeedResult(null);
        try {
            const res = await adminFetch(
                `/api/admin-seed-community-stats?dry=${dry ? '1' : '0'}${force ? '&force=1' : ''}`,
                getToken,
                { method: 'POST' },
            );
            // A non-JSON body (a 404 page, a gateway timeout) would otherwise throw
            // an opaque parse error. Read as text first so the status is reportable.
            const raw = await res.text();
            let data = null;
            try { data = JSON.parse(raw); } catch (_) { /* handled below */ }
            if (!data) {
                throw new Error(`HTTP ${res.status} — ${raw.slice(0, 120) || 'no response body'}`);
            }
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            // `dry` and the transport result LAST: the payload carries its own `ok`
            // (the already-seeded reply is a 200 with ok:false), and spreading it
            // over these fields made a successful call render as a failure.
            setSeedResult({ ...data, dry, requestOk: true });
        } catch (err) {
            setSeedResult({ requestOk: false, error: err?.message || String(err) || 'Unknown error' });
        } finally {
            setSeeding(false);
        }
    };

    // Publishes every member's already-earned milestones to the Wall. Normally a
    // member's own session does this when they open Home, so without a backfill
    // the Wall only fills in as people sign in.
    const runMilestoneBackfill = async (dry) => {
        setBackfilling(true);
        setBackfillResult(null);
        try {
            const res = await adminFetch(`/api/admin-backfill-milestones?dry=${dry ? '1' : '0'}`, getToken, { method: 'POST' });
            const raw = await res.text();
            let data = null;
            try { data = JSON.parse(raw); } catch (_) { /* handled below */ }
            if (!data) throw new Error(`HTTP ${res.status} — ${raw.slice(0, 120) || 'no response body'}`);
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setBackfillResult({ ...data, requestOk: true });
        } catch (err) {
            setBackfillResult({ requestOk: false, error: err?.message || String(err) });
        } finally {
            setBackfilling(false);
        }
    };

    const handleToggle = async (key) => {
        const updated = { ...content, [key]: !content[key] };
        setContent(updated);
        setSaving(true);
        await updateSiteContent({ [key]: !content[key] });
        setSaving(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    if (!content) return <Spinner />;

    const flags = [
        {
            key: 'workoutsPageEnabled',
            label: 'Workouts Page',
            desc: 'Shows the unified "Workouts" tab for all users — Upcoming classes, On Demand recordings, and Shorts in one place. When off, regular users only see "Free Videos" (admins/friends still see Workouts).',
            status: content.workoutsPageEnabled ? 'Active' : 'Inactive',
        },
        {
            key: 'subscriptionEnabled',
            label: 'Subscription Option',
            desc: 'Enables the $15/mo subscription paywall on the Workouts page. When on, non-subscribers see upsell banners and locked On Demand content. When off, class access controls are hidden.',
            status: content.subscriptionEnabled ? 'Active' : 'Inactive',
        },
        {
            key: 'enableSeriesTab',
            label: 'Series Tab',
            desc: 'Shows the "Series" tab (free, self-paced challenges) in Workouts for everyone. While off, the tab is visible to admins only — so you can build and review challenges before launch. Manage challenges in the Series tab above.',
            status: content.enableSeriesTab ? 'Active' : 'Inactive',
        },
        {
            key: 'memberRepliesEnabled',
            label: 'Member Replies',
            desc: 'Lets members reply to each other on class comments, delete their own comments and report others — with AI screening and the Moderation queue. While off, everyone sees today’s comment thread exactly as before. Takes effect on members’ next visit. Turn on only after the release checklist (docs/plans/member-replies-plan.md).',
            status: content.memberRepliesEnabled ? 'Active' : 'Inactive',
        },
    ];

    return (
        <div>
            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 8 }}>Settings</h2>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 24 }}>Feature flags and app configuration. Toggle features on or off without redeploying.</p>

            {saved && (
                <div style={{ padding: '10px 16px', borderRadius: 12, background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)', border: '1px solid var(--mom-primary)', marginBottom: 16, fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-primary)', fontWeight: 600 }}>
                    Settings saved successfully
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {flags.map((flag) => (
                    <div key={flag.key} style={{
                        background: theme.colors.white, borderRadius: 16, padding: '20px 24px',
                        border: `1px solid ${content[flag.key] ? 'var(--mom-primary)' : theme.colors.creamDark}`,
                        transition: 'border-color 0.2s',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                    <h3 style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, fontWeight: 700, color: theme.colors.charcoal, margin: 0 }}>{flag.label}</h3>
                                    <span style={{
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700,
                                        padding: '3px 10px', borderRadius: 10, letterSpacing: '0.04em',
                                        background: content[flag.key] ? 'color-mix(in srgb, var(--mom-primary) 12%, transparent)' : theme.colors.cream,
                                        color: content[flag.key] ? 'var(--mom-primary)' : theme.colors.warmGray,
                                    }}>{flag.status}</span>
                                </div>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, lineHeight: 1.5, margin: 0 }}>{flag.desc}</p>
                            </div>
                            <button onClick={() => handleToggle(flag.key)} disabled={saving}
                                style={{
                                    width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                                    background: content[flag.key] ? 'var(--mom-primary)' : theme.colors.creamDark,
                                    position: 'relative', flexShrink: 0, transition: 'background 0.2s', marginTop: 2,
                                }}>
                                <div style={{
                                    width: 22, height: 22, borderRadius: '50%', background: '#fff',
                                    position: 'absolute', top: 3,
                                    left: content[flag.key] ? 27 : 3,
                                    transition: 'left 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                }} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ marginTop: 32, padding: '16px 20px', borderRadius: 12, background: theme.colors.cream }}>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, lineHeight: 1.6 }}>
                    Feature flags take effect immediately for all users — no redeploy needed. You can safely enable and test new features, then disable them if anything needs adjustment.
                </p>
            </div>

            {/* ── Account Page + Cancellation copy (shared field system) ── */}
            <div style={{ marginTop: 40 }}>
            <SectionList tabKey="settings" content={content}
                handleChange={(key, value) => { setContent((prev) => ({ ...prev, [key]: value })); setDirty(true); setSaved(false); if (unsavedRef) unsavedRef.current = true; }}
                sections={SETTINGS_FIELDS} />
            </div>

            <StickyBar saving={saving} saved={saved} hasChanges={dirty} label="Save changes" onSave={async () => {
                setSaving(true);
                const ok = await updateSiteContent(content);
                setSaving(false);
                if (ok) {
                    setSaved(true);
                    setDirty(false);
                    if (unsavedRef) unsavedRef.current = false;
                    setTimeout(() => setSaved(false), 3000);
                }
            }} />

            {/* ── Reply Assistant (own doc, own save — not siteConfig) ── */}
            <ReplyAssistantSettings getToken={getToken} />

            {/* ── Community Counter ── */}
            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: theme.colors.charcoal, marginTop: 40, marginBottom: 8 }}>Community Counter</h2>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 16, lineHeight: 1.6 }}>
                Sets the starting value for the "Together we've completed…" number on Home. From then on it counts up
                automatically with every class completed. <strong>Run the preview first</strong> — it counts your history and
                reports the number without writing anything. Only run the real seed once; re-seeding later would discard
                everything counted since.
            </p>
            <div style={{ background: theme.colors.white, borderRadius: 16, padding: '20px 24px', border: `1px solid ${theme.colors.creamDark}` }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="mom-btn mom-btn-primary" onClick={() => runCommunitySeed(true)} disabled={seeding} style={{ padding: '10px 24px', fontSize: 14 }}>
                        {seeding ? 'Counting…' : 'Preview count'}
                    </button>
                    <button className="mom-btn" onClick={() => runCommunitySeed(false)} disabled={seeding}
                        style={{ padding: '10px 24px', fontSize: 14, border: `1.5px solid ${theme.colors.primary}`, color: theme.colors.primary, background: 'none' }}>
                        Seed for real
                    </button>
                    <button className="mom-btn" onClick={() => runCommunitySeed(false, true)} disabled={seeding}
                        style={{ padding: '10px 24px', fontSize: 14, border: `1.5px solid ${theme.colors.warmGray}`, color: theme.colors.warmGray, background: 'none' }}>
                        Re-seed (overwrite)
                    </button>
                </div>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginTop: 10, lineHeight: 1.55 }}>
                    Use <strong>Re-seed</strong> only to correct a wrong baseline — for example after testing. It replaces
                    the total and discards anything counted since.
                </p>
                {seedResult && (() => {
                    // Three outcomes, not two: the request can fail, OR succeed and
                    // report that a baseline already exists (which is information,
                    // not an error).
                    const failed = !seedResult.requestOk;
                    const info = !failed && seedResult.alreadySeeded;
                    const tone = failed ? '#c05050' : info ? '#8a7d3f' : 'var(--mom-primary)';
                    const text = failed ? '#8a3030' : info ? '#6b6131' : 'var(--mom-primary)';
                    return (
                        <div style={{ marginTop: 14, padding: '12px 16px', borderRadius: 12, fontFamily: 'var(--mom-font-sans)', fontSize: 13, lineHeight: 1.6,
                            background: `color-mix(in srgb, ${tone} 11%, transparent)`,
                            border: `1px solid ${tone}`, color: text,
                        }}>
                            {failed ? (
                                <>
                                    <div style={{ fontWeight: 600 }}>Seed failed</div>
                                    <div>{seedResult.error}</div>
                                </>
                            ) : info ? (
                                <>
                                    <div style={{ fontWeight: 600 }}>Already seeded — nothing changed</div>
                                    <div>Current total: {seedResult.currentTotal}. Use Re-seed only if that number is wrong.</div>
                                </>
                            ) : (
                                <>
                                    <div style={{ fontWeight: 600 }}>
                                        {seedResult.dryRun ? 'Preview only — nothing written' : 'Seeded'}
                                    </div>
                                    <div>
                                        {seedResult.classCompletions ?? seedResult.seeded} classes counted
                                        {typeof seedResult.excluded === 'number' && ` · ${seedResult.excluded} excluded (shorts, programs, series)`}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })()}
            </div>

            {/* ── Milestone Wall backfill ── */}
            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: theme.colors.charcoal, marginTop: 40, marginBottom: 8 }}>Milestone Wall</h2>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 16, lineHeight: 1.6 }}>
                Members normally appear on the Wall the first time they open the app after earning a badge, so the Wall
                fills in gradually. This publishes everyone's existing milestones at once, dated to when they were
                actually earned. <strong>Safe to run more than once</strong> — it updates the same entries rather than
                duplicating them, and never affects cheers or comments. Admin accounts and anyone without a name on file are left off, and any existing entries for them are removed.
            </p>
            <div style={{ background: theme.colors.white, borderRadius: 16, padding: '20px 24px', border: `1px solid ${theme.colors.creamDark}` }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="mom-btn mom-btn-primary" onClick={() => runMilestoneBackfill(true)} disabled={backfilling} style={{ padding: '10px 24px', fontSize: 14 }}>
                        {backfilling ? 'Working…' : 'Preview backfill'}
                    </button>
                    <button className="mom-btn" onClick={() => runMilestoneBackfill(false)} disabled={backfilling}
                        style={{ padding: '10px 24px', fontSize: 14, border: `1.5px solid ${theme.colors.primary}`, color: theme.colors.primary, background: 'none' }}>
                        Publish to the Wall
                    </button>
                </div>
                {backfillResult && (
                    <div style={{ marginTop: 14, padding: '12px 16px', borderRadius: 12, fontFamily: 'var(--mom-font-sans)', fontSize: 13, lineHeight: 1.6,
                        background: backfillResult.requestOk ? 'color-mix(in srgb, var(--mom-primary) 10%, transparent)' : 'color-mix(in srgb, #c05050 12%, transparent)',
                        border: `1px solid ${backfillResult.requestOk ? 'var(--mom-primary)' : '#c05050'}`,
                        color: backfillResult.requestOk ? 'var(--mom-primary)' : '#8a3030',
                    }}>
                        {backfillResult.requestOk ? (
                            <>
                                <div style={{ fontWeight: 600 }}>
                                    {backfillResult.dryRun ? 'Preview only — nothing written' : 'Published'}
                                </div>
                                <div>
                                    {backfillResult.milestones} milestones across {backfillResult.members} members
                                    {!backfillResult.dryRun && ` · ${backfillResult.written} written`}
                                    {backfillResult.failed > 0 && ` · ${backfillResult.failed} failed`}
                                </div>
                                {(backfillResult.skippedNoName > 0 || backfillResult.skippedAdmin > 0 || backfillResult.toRemove > 0 || backfillResult.removed > 0) && (
                                    <div style={{ marginTop: 6, fontSize: 12 }}>
                                        {backfillResult.skippedAdmin > 0 && `${backfillResult.skippedAdmin} admin account${backfillResult.skippedAdmin === 1 ? '' : 's'} excluded. `}
                                        {backfillResult.skippedNoName > 0 && `${backfillResult.skippedNoName} member${backfillResult.skippedNoName === 1 ? '' : 's'} skipped (no name on file). `}
                                        {backfillResult.dryRun
                                            ? (backfillResult.toRemove > 0 && `${backfillResult.toRemove} existing entr${backfillResult.toRemove === 1 ? 'y' : 'ies'} would be removed.`)
                                            : (backfillResult.removed > 0 && `${backfillResult.removed} existing entr${backfillResult.removed === 1 ? 'y' : 'ies'} removed.`)}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div style={{ fontWeight: 600 }}>Backfill failed</div>
                                <div>{backfillResult.error}</div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* ── Recording Maintenance ── */}
            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: theme.colors.charcoal, marginTop: 40, marginBottom: 8 }}>Recording Maintenance</h2>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 16, lineHeight: 1.6 }}>
                Cleans up On Demand recordings whose Mux asset has been deleted, and repairs duration for recordings where the first webhook event arrived before Mux finalized the stream. Safe to run anytime — it only removes recordings Mux confirms are gone.
            </p>
            <div style={{ background: theme.colors.white, borderRadius: 16, padding: '20px 24px', border: `1px solid ${theme.colors.creamDark}` }}>
                <button className="mom-btn mom-btn-primary" onClick={runRecordingsSync} disabled={syncing} style={{ padding: '10px 28px', fontSize: 14 }}>
                    {syncing ? 'Syncing…' : 'Sync with Mux'}
                </button>
                {syncResult && (
                    <div style={{ marginTop: 14, padding: '12px 16px', borderRadius: 12, fontFamily: 'var(--mom-font-sans)', fontSize: 13, lineHeight: 1.6,
                        background: syncResult.ok ? 'color-mix(in srgb, var(--mom-primary) 10%, transparent)' : 'color-mix(in srgb, #c05050 12%, transparent)',
                        border: `1px solid ${syncResult.ok ? 'var(--mom-primary)' : '#c05050'}`,
                        color: syncResult.ok ? 'var(--mom-primary)' : '#8a3030',
                    }}>
                        {syncResult.ok ? (
                            <>
                                <div style={{ fontWeight: 600 }}>Sync complete</div>
                                <div>Checked {syncResult.checked} recording{syncResult.checked === 1 ? '' : 's'} · Removed {syncResult.deleted} · Fixed duration on {syncResult.durationFixed}</div>
                                {syncResult.errors && syncResult.errors.length > 0 && (
                                    <div style={{ marginTop: 6, color: theme.colors.warmGray, fontSize: 12 }}>
                                        {syncResult.errors.length} warning{syncResult.errors.length === 1 ? '' : 's'} (see server logs)
                                    </div>
                                )}
                                {syncResult.capped && (
                                    <div style={{ marginTop: 6, color: theme.colors.warmGray, fontSize: 12 }}>
                                        Only the first 200 recordings were checked — run again to continue.
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div style={{ fontWeight: 600 }}>Sync failed</div>
                                <div>{syncResult.error}</div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  WAITLISTS TAB
// ═══════════════════════════════════════════════════════════════
// One program's waitlist — collapsed by default (header + count + Email all always
// visible; the user list expands on click) so the tab stays calm with long lists.
function WaitlistProgramCard({ programName, entries, mailtoUrl, onRemove, removing }) {
    const [open, setOpen] = useState(false);
    return (
        <div style={{ background: theme.colors.white, borderRadius: 16, boxShadow: theme.shadows.sm, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '18px 24px', flexWrap: 'wrap' }}>
                <button onClick={() => setOpen(!open)} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                    <span style={{ fontSize: 16, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>▾</span>
                    <div>
                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 2 }}>{programName}</h3>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray }}>{entries.length} user{entries.length !== 1 ? 's' : ''} waiting</p>
                    </div>
                </button>
                {mailtoUrl && (
                    <a href={mailtoUrl} style={{ textDecoration: 'none', flexShrink: 0 }}>
                        <button className="mom-btn" style={{ padding: '8px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: theme.colors.charcoal, color: theme.colors.white, border: 'none', cursor: 'pointer' }}>Email all</button>
                    </a>
                )}
            </div>
            {open && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 24px 20px' }}>
                    {entries.map((entry) => (
                        <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: theme.colors.cream, borderRadius: 10, flexWrap: 'wrap', gap: 8 }}>
                            <div>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.charcoal, fontWeight: 500 }}>{entry.userEmail || 'No email'}</p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>
                                    Joined {entry.joinedAt ? new Date(entry.joinedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown'}
                                </p>
                            </div>
                            <button onClick={() => onRemove(entry.userId, entry.programId)}
                                disabled={removing === `${entry.userId}_${entry.programId}`}
                                style={{ background: 'none', border: 'none', fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, cursor: 'pointer', padding: '4px 8px', opacity: removing === `${entry.userId}_${entry.programId}` ? 0.4 : 1 }}>
                                {removing === `${entry.userId}_${entry.programId}` ? 'Removing...' : 'Remove'}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function WaitlistsTab() {
    const [waitlistData, setWaitlistData] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removing, setRemoving] = useState(null);

    const loadData = async () => {
        setLoading(true);
        const [entries, progs] = await Promise.all([
            getAllProgramWaitlists(),
            fetchPrograms(),
        ]);
        setWaitlistData(entries || []);
        setPrograms(progs || []);
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    // Group entries by programId
    const byProgram = {};
    waitlistData.forEach((entry) => {
        if (!byProgram[entry.programId]) byProgram[entry.programId] = [];
        byProgram[entry.programId].push(entry);
    });

    // Get program names and filter to only programs with waitlist entries
    const programsWithWaitlist = Object.keys(byProgram).map((programId) => {
        const prog = programs.find((p) => p.id === programId);
        return {
            programId,
            programName: prog?.name || byProgram[programId][0]?.programName || programId,
            entries: byProgram[programId].sort((a, b) => (a.joinedAt || '').localeCompare(b.joinedAt || '')),
        };
    });

    const handleRemove = async (userId, programId) => {
        setRemoving(`${userId}_${programId}`);
        await removeFromProgramWaitlist(userId, programId);
        setWaitlistData((prev) => prev.filter((e) => !(e.userId === userId && e.programId === programId)));
        setRemoving(null);
    };

    if (loading) {
        return (
            <div>
                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 20 }}>Program Waitlists</h2>
                <div className="mom-skeleton" style={{ height: 120, borderRadius: 16 }} />
            </div>
        );
    }

    return (
        <div>
            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 8 }}>Program Waitlists</h2>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray, marginBottom: 24 }}>
                Users who expressed interest in programs with no open groups. They're automatically removed when they purchase.
            </p>

            {programsWithWaitlist.length === 0 ? (
                <div style={{ background: theme.colors.white, borderRadius: 16, padding: '40px 24px', textAlign: 'center', boxShadow: theme.shadows.sm }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: theme.colors.warmGray }}>No waitlist signups yet.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {programsWithWaitlist.map(({ programId, programName, entries }) => {
                        const emails = entries.map((e) => e.userEmail).filter(Boolean);
                        const mailtoUrl = emails.length > 0
                            ? 'mailto:hello@daniellenbarr.com?bcc=' + encodeURIComponent(emails.join(',')) + '&subject=' + encodeURIComponent(programName + ' — Enrollment Update')
                            : null;

                        return (
                            <WaitlistProgramCard key={programId} programName={programName} entries={entries} mailtoUrl={mailtoUrl} onRemove={handleRemove} removing={removing} />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Theme Tab ────────────────────────────────────────────
function ThemeTab({ unsavedRef }) {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [fontsLoaded, setFontsLoaded] = useState(false);
    const [fontsOpen, setFontsOpen] = useState(false);
    const [colorsOpen, setColorsOpen] = useState(false);

    useEffect(() => { fetchSiteContent().then((c) => { setContent(c); setLoading(false); }); }, []);

    // Pre-load all font options so previews render correctly
    useEffect(() => {
        if (fontsLoaded) return;
        const families = [
            ...HEADING_FONT_OPTIONS.filter((f) => f.google).map((f) => f.google),
            ...BODY_FONT_OPTIONS.filter((f) => f.google).map((f) => f.google),
        ];
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join('&')}&display=swap`;
        document.head.appendChild(link);
        const selfHosted = [...HEADING_FONT_OPTIONS, ...BODY_FONT_OPTIONS].filter((f) => f.selfHosted && f.files);
        if (selfHosted.length > 0) {
            const fontFaces = selfHosted.flatMap((f) =>
                f.files.map((file) =>
                    `@font-face { font-family: ${f.family.split(',')[0]}; src: url('${file.file}') format('woff2'); font-weight: ${file.weight}; font-style: normal; font-display: swap; }`
                )
            );
            const style = document.createElement('style');
            style.id = 'mom-preview-self-hosted-fonts';
            style.textContent = fontFaces.join('\n');
            document.head.appendChild(style);
        }
        setFontsLoaded(true);
    }, [fontsLoaded]);

    const handleChange = (key, value) => { setContent((prev) => ({ ...prev, [key]: value })); setSaved(false); if (unsavedRef) unsavedRef.current = true; };
    const handleSave = async () => {
        setSaving(true);
        const ok = await updateSiteContent(content);
        setSaving(false);
        if (ok) {
            setSaved(true);
            if (unsavedRef) unsavedRef.current = false;
            const custom = {};
            if (content.themeCream) custom.cream = content.themeCream;
            if (content.themeCreamDark) custom.creamDark = content.themeCreamDark;
            if (content.themePrimary) custom.terracotta = content.themePrimary;
            if (content.themePrimaryDark) custom.terracottaDark = content.themePrimaryDark;
            if (content.themePrimaryLight) custom.terracottaLight = content.themePrimaryLight;
            if (content.themeCharcoal) custom.charcoal = content.themeCharcoal;
            if (content.themeWarmGray) custom.warmGray = content.themeWarmGray;
            if (content.themeSuccess) custom.success = content.themeSuccess;
            if (content.themeDanger) custom.danger = content.themeDanger;
            if (content.themeLink) custom.link = content.themeLink;
            if (content.themeLinkHover) custom.linkHover = content.themeLinkHover;
            if (content.themeCardBg) custom.cardBg = content.themeCardBg;
            if (content.themeNavCardBg) custom.navCardBg = content.themeNavCardBg;
            if (content.themeTileBg) custom.tileBg = content.themeTileBg;
            if (content.themeStat) custom.stat = content.themeStat;

            const fonts = {};
            if (content.themeHeadingFont) {
                const heading = HEADING_FONT_OPTIONS.find((f) => f.key === content.themeHeadingFont);
                if (heading) fonts.serif = heading.family;
            }
            if (content.themeBodyFont) {
                const body = BODY_FONT_OPTIONS.find((f) => f.key === content.themeBodyFont);
                if (body) fonts.sans = body.family;
            }
            if (content.themeHeadingFont || content.themeBodyFont) {
                loadGoogleFonts(content.themeHeadingFont, content.themeBodyFont);
            }
            applyThemeToDOM(
                Object.keys(custom).length > 0 ? custom : {},
                Object.keys(fonts).length > 0 ? fonts : null
            );
            setTimeout(() => setSaved(false), 3000);
        }
    };
    const handleReset = () => {
        setContent((prev) => ({
            ...prev,
            themeCream: '', themeCreamDark: '', themePrimary: '', themePrimaryDark: '',
            themePrimaryLight: '', themeCharcoal: '', themeWarmGray: '', themeSuccess: '', themeDanger: '',
            themeLink: '', themeLinkHover: '', themeCardBg: '', themeNavCardBg: '', themeTileBg: '', themeStat: '',
            themeHeadingFont: '', themeBodyFont: '',
        }));
        setSaved(false);
    };

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><FaSpinner style={{ fontSize: 28, color: theme.colors.terracotta, animation: 'spin 1.2s linear infinite' }} /></div>;
    if (!content) return null;

    const colorFields = [
        { key: 'themePrimary', group: 'Brand', label: 'Primary Color', desc: 'Main brand accent \u2014 buttons, badges, progress bars, loading spinners, enrolled/completed tags, hero accents', defaultVal: DEFAULT_COLORS.terracotta },
        { key: 'themePrimaryDark', label: 'Primary Dark', desc: 'Hover state for primary buttons, gradient endpoints for accent cards and hero sections', defaultVal: DEFAULT_COLORS.terracottaDark },
        { key: 'themePrimaryLight', label: 'Primary Light', desc: 'Text selection highlight, hero tagline on dark backgrounds, testimonial avatar gradients', defaultVal: DEFAULT_COLORS.terracottaLight },
        { key: 'themeStat', label: 'Stat Numbers', desc: 'Large stat numbers on the homepage signature program section and dashboard welcome area. Falls back to Primary Color if not set.', defaultVal: DEFAULT_COLORS.terracotta },
        { key: 'themeCream', group: 'Backgrounds & Borders', label: 'Page Background', desc: 'Main background for every page, filter bar backgrounds, inactive input backgrounds', defaultVal: DEFAULT_COLORS.cream },
        { key: 'themeCreamDark', label: 'Border / Divider', desc: 'Card borders, input field borders, section dividers, inactive toggle outlines', defaultVal: DEFAULT_COLORS.creamDark },
        { key: 'themeCharcoal', group: 'Text', label: 'Dark / Text', desc: 'Headings, body text, nav bar text, dark section backgrounds (method section, footer), video placeholder backgrounds', defaultVal: DEFAULT_COLORS.charcoal },
        { key: 'themeWarmGray', label: 'Muted Text', desc: 'Subtitles, descriptions, placeholders, "with Danielle" tagline, Sign Out button, secondary labels', defaultVal: DEFAULT_COLORS.warmGray },
        { key: 'themeSuccess', group: 'States', label: 'Success', desc: 'Completed week badges, active pod status, program completion progress bar fill', defaultVal: DEFAULT_COLORS.success },
        { key: 'themeDanger', label: 'Danger', desc: 'Delete buttons, "FULL" pod badge, error states', defaultVal: DEFAULT_COLORS.danger },
        { key: 'themeLink', group: 'Links', label: 'Link Color', desc: 'Active nav menu items, "Continue program \u2192", "Learn more \u2192", "Browse Programs \u2192", inline markdown links, nav underline', defaultVal: DEFAULT_COLORS.terracotta },
        { key: 'themeLinkHover', label: 'Link Hover', desc: 'Hover state for all text links and ghost buttons', defaultVal: DEFAULT_COLORS.terracottaDark },
        { key: 'themeCardBg', label: 'Content Card', desc: 'Video cards, program cards on browse page, purchased program tiles on dashboard', defaultVal: DEFAULT_COLORS.white },
        { key: 'themeNavCardBg', label: 'Category Card', desc: 'Dashboard explore tiles ("Programs", "Free Workouts")', defaultVal: DEFAULT_COLORS.cream },
        { key: 'themeTileBg', label: 'Feature Tile Background', desc: 'The feature cards/boxes on the landing page (the section with emoji, title, and description). Leave empty for white.', defaultVal: DEFAULT_COLORS.white },
        { key: 'themeFilterPillBg', group: 'Filter Pills', label: 'Filter Pill (Unselected)', desc: 'Background color of unselected filter pills on workout pages. Falls back to Page Background if not set.', defaultVal: DEFAULT_COLORS.cream },
        { key: 'themeFilterPillText', label: 'Filter Pill Text (Unselected)', desc: 'Text color of unselected filter pills. Falls back to Dark / Text if not set.', defaultVal: DEFAULT_COLORS.charcoal },
        { key: 'themeFilterPillActiveBg', label: 'Filter Pill (Selected)', desc: 'Background color of selected/active filter pills. Falls back to Primary Color if not set.', defaultVal: DEFAULT_COLORS.terracotta },
        { key: 'themeFilterPillActiveText', label: 'Filter Pill Text (Selected)', desc: 'Text color of selected/active filter pills. Falls back to white if not set.', defaultVal: DEFAULT_COLORS.white },
    ];

    const selectedHeading = content.themeHeadingFont || '';
    const selectedBody = content.themeBodyFont || '';

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: theme.colors.charcoal, marginBottom: 4 }}>Theme</h3>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: theme.colors.warmGray }}>Customize colors and fonts. Leave empty to use defaults.</p>
                </div>
                <button className="mom-btn mom-btn-ghost" onClick={handleReset} style={{ fontSize: 13, color: theme.colors.warmGray, flexShrink: 0 }}>Reset All</button>
            </div>

            {/* Fonts (collapsible) */}
            <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, marginBottom: 12, boxShadow: theme.shadows.sm, overflow: 'hidden' }}>
                <button onClick={() => setFontsOpen(!fontsOpen)} style={{ width: '100%', padding: '20px 28px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, textAlign: 'left' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: theme.colors.charcoal }}>Fonts</h3>
                            {(selectedHeading || selectedBody) && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.terracotta, background: hexToRgba(theme.colors.terracotta, 0.08), padding: '2px 10px', borderRadius: 20 }}>Customized</span>}
                        </div>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginTop: 4 }}>Choose heading and body fonts for your site</p>
                    </div>
                    <span style={{ fontSize: 18, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: fontsOpen ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>&#9662;</span>
                </button>
                {fontsOpen && (
                    <div style={{ padding: '0 28px 24px', borderTop: `1px solid ${theme.colors.creamDark}` }}>
                        <div style={{ paddingTop: 20 }}>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 4 }}>Heading Font</p>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 14 }}>Used for program titles, page headings, and section headers</p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 24 }}>
                                <div onClick={() => handleChange('themeHeadingFont', '')}
                                    style={{ padding: '14px 16px', borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s', border: !selectedHeading ? `2px solid ${theme.colors.terracotta}` : `1.5px solid ${theme.colors.creamDark}`, background: !selectedHeading ? hexToRgba(theme.colors.terracotta, 0.04) : theme.colors.white }}>
                                    <span style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 18, fontStyle: 'italic', color: theme.colors.charcoal, display: 'block', marginBottom: 4 }}>Libre Baskerville</span>
                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>Default</span>
                                </div>
                                {HEADING_FONT_OPTIONS.filter((f) => f.key !== 'libre-baskerville').map((font) => (
                                    <div key={font.key} onClick={() => handleChange('themeHeadingFont', font.key)}
                                        style={{ padding: '14px 16px', borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s', border: selectedHeading === font.key ? `2px solid ${theme.colors.terracotta}` : `1.5px solid ${theme.colors.creamDark}`, background: selectedHeading === font.key ? hexToRgba(theme.colors.terracotta, 0.04) : theme.colors.white }}>
                                        <span style={{ fontFamily: font.family, fontSize: 18, fontStyle: 'italic', color: theme.colors.charcoal, display: 'block', marginBottom: 4 }}>{font.label}</span>
                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray }}>Aa Bb Cc 123</span>
                                    </div>
                                ))}
                            </div>

                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: theme.colors.charcoal, marginBottom: 4 }}>Body Font</p>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: theme.colors.warmGray, marginBottom: 14 }}>Used for descriptions, buttons, navigation, and all body text</p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                                <div onClick={() => handleChange('themeBodyFont', '')}
                                    style={{ padding: '14px 16px', borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s', border: !selectedBody ? `2px solid ${theme.colors.terracotta}` : `1.5px solid ${theme.colors.creamDark}`, background: !selectedBody ? hexToRgba(theme.colors.terracotta, 0.04) : theme.colors.white }}>
                                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: theme.colors.charcoal, display: 'block', marginBottom: 4 }}>DM Sans</span>
                                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: theme.colors.warmGray }}>Default — The quick brown fox jumps over the lazy dog</span>
                                </div>
                                {BODY_FONT_OPTIONS.filter((f) => f.key !== 'dm-sans').map((font) => (
                                    <div key={font.key} onClick={() => handleChange('themeBodyFont', font.key)}
                                        style={{ padding: '14px 16px', borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s', border: selectedBody === font.key ? `2px solid ${theme.colors.terracotta}` : `1.5px solid ${theme.colors.creamDark}`, background: selectedBody === font.key ? hexToRgba(theme.colors.terracotta, 0.04) : theme.colors.white }}>
                                        <span style={{ fontFamily: font.family, fontSize: 15, color: theme.colors.charcoal, display: 'block', marginBottom: 4 }}>{font.label}</span>
                                        <span style={{ fontFamily: font.family, fontSize: 11, color: theme.colors.warmGray }}>The quick brown fox jumps over the lazy dog</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Colors (collapsible) */}
            <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, marginBottom: 12, boxShadow: theme.shadows.sm, overflow: 'hidden' }}>
                <button onClick={() => setColorsOpen(!colorsOpen)} style={{ width: '100%', padding: '20px 28px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, textAlign: 'left' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: theme.colors.charcoal }}>Colors</h3>
                            {colorFields.some((f) => content[f.key]) && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.terracotta, background: hexToRgba(theme.colors.terracotta, 0.08), padding: '2px 10px', borderRadius: 20 }}>Customized</span>}
                        </div>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: theme.colors.warmGray, marginTop: 4 }}>Customize your brand palette — hex values or use the color picker</p>
                    </div>
                    <span style={{ fontSize: 18, color: theme.colors.warmGray, transition: 'transform 0.2s', transform: colorsOpen ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>&#9662;</span>
                </button>
                {colorsOpen && (
                    <div style={{ padding: '0 28px 24px', borderTop: `1px solid ${theme.colors.creamDark}` }}>
                        <div style={{ paddingTop: 20 }}>
                            {colorFields.map((field, fi) => {
                                const currentVal = content[field.key] || '';
                                const displayColor = currentVal || field.defaultVal;
                                return (
                                    <React.Fragment key={field.key}>
                                        {field.group && (
                                            <div style={{ marginTop: fi > 0 ? 20 : 0, marginBottom: 12 }}>
                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, color: theme.colors.charcoal, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{field.group}</p>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${theme.colors.cream}` }}>
                                            <div style={{ width: 32, height: 32, borderRadius: 8, border: `2px solid ${theme.colors.creamDark}`, background: displayColor, flexShrink: 0, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)' }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: theme.colors.charcoal }}>{field.label}</p>
                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: theme.colors.warmGray, lineHeight: 1.3 }}>{field.desc}</p>
                                            </div>
                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                                                <input type="color" value={displayColor} onChange={(e) => handleChange(field.key, e.target.value)}
                                                    style={{ width: 32, height: 28, border: 'none', borderRadius: 6, cursor: 'pointer', padding: 0, background: 'transparent' }} />
                                                <input className="mom-input" type="text" value={currentVal} onChange={(e) => handleChange(field.key, e.target.value)}
                                                    placeholder={field.defaultVal} style={{ fontSize: 12, padding: '6px 10px', fontFamily: "'DM Sans', monospace", width: 100 }} />
                                                {currentVal && (
                                                    <button className="mom-btn mom-btn-ghost" onClick={() => handleChange(field.key, '')}
                                                        style={{ padding: '4px 8px', fontSize: 11, color: theme.colors.warmGray, whiteSpace: 'nowrap' }}>Clear</button>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <StickyBar saving={saving} saved={saved} onSave={handleSave} label="Save Theme" hasChanges={!saved} />
        </div>
    );
}

export default AdminDashboard;